import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import { buildPromptForBatch, buildPromptOcrEnglish, buildPromptEnglishGrading } from '../utils/buildPrompt';
import { buildPromptUrdu, buildPromptUrduOcr, buildPromptUrduGrading } from '../utils/buildPromptUrdu';
import { copyToClipboard } from '../utils/clipboard';
import { stripQuestionPaperText, restorePaperNotes } from '../utils/questionPaper';
import {
  gradeFromTable,
  tableToOneLineComments,
  correctTable,
  keepLastTableBlock,
  extractRubricQuestionIds,
  extractRubricQuestionMarks,
  paperTotalFromRubric,
  listRubricQuestions,
  normalizeQuestionId,
  formatQuestionLabel,
  listUnmatchedQuestionLabels,
  splitRawMarkdownCells,
  splitMarkdownRowCells
} from '../utils/tableToOneLine';
import ConfirmDialog from './ConfirmDialog';
import { folderDisplayTitle, rubricNameWithDate } from '../utils/folderDisplay';
import { deleteRegradeRequest, REGRADE_CLEARED_EVENT } from '../utils/regradeBatchTitle';
import { formatMissingImageNumbers, pageSpanFromTranscript, parseMissingPages } from '../utils/ocrMissing';
import { activeChatterFindings, isSuspectedNote, noiseNotes, parseChatterOk } from '../utils/ocrNoise';
import { markPageActualBlank, unexpectedBlankPages } from '../utils/unexpectedBlank';
import { latePaperOpenFailure, listLatePapers, rollFromLabel } from '../utils/latePapers';
import FailureNotice from './FailureNotice';
import { listRegradeStudents } from '../utils/regradeStudents';
import ZoomableImage from './ZoomableImage';
import AnnotatableImage from './AnnotatableImage';
import useAiStatus from '../utils/useAiStatus';
import { autoCheckIsOn, refreshAuthToken, requestLoginAfterExpiry } from '../utils/authSession';
import { LOCAL_MODELS, GEMINI_MODELS, isKnownLocalModel } from '../utils/localModels';
import {
  clearWorkingTableLocalStorage,
  CLEAR_WORKING_TABLES_EVENT,
  WORKING_TABLES_RELOADED_EVENT
} from '../utils/clearWorkingTables';
import html2canvas from 'html2canvas';
import { GradingMachineBadge, GRADING_MACHINE_LEGEND, stageModelLabel } from '../utils/gradingMachine';
import { MatrixText } from './FractionText';
import '../styles/AssessmentTab.css';
import {
  findQuestionSpan,
  findTranscriptSpan,
  rowDeduction,
  transcriptPageIndex,
  revealTextareaSpan,
  MarkedTextarea
} from '../utils/questionFocus';
import { acceptThinkingText, asidePhraseRanges, firstAsidePhraseRange, hasMarksExceeded, renderCommentsWithOverMaxHighlight, replaceAsidePhrases, thinkingPhrasesIn } from '../utils/marksValidation';
import { INTERNET_ERROR_MESSAGE, isBrowserNetworkError } from '../utils/internetError';

// The dedicated auto-check accounts. The browser-side engine only runs for
// these; conversely, MANUAL step reporting (reportManualCheckStep below) is
// skipped for them so the engine's own step reports aren't duplicated.
// Add/remove usernames here to change the worker-pool size.
const REGRADE_SNAPSHOT_KEY = 'regradeMarksSnapshot';
const REGRADESUMMARY_VIEW_KEY = 'regradeSummaryView';

function saveRegradeSummaryView(summary) {
  try {
    sessionStorage.setItem(REGRADESUMMARY_VIEW_KEY, JSON.stringify(summary));
  } catch (_) { /* ignore quota */ }
}

function loadRegradeSummaryView() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(REGRADESUMMARY_VIEW_KEY) || 'null');
    if (!raw || !Array.isArray(raw.rows) || !raw.rows.length) return null;
    return {
      id: raw.id || null,
      rows: raw.rows,
      folderPath: raw.folderPath || '',
      kind: raw.kind === 'checked' ? 'checked' : (raw.kind === 'rechecked' ? 'rechecked' : ''),
      className: raw.className || '',
      section: raw.section || '',
      subject: raw.subject || '',
      testDate: raw.testDate || '',
      assignmentName: raw.assignmentName || '',
      paperType: raw.paperType || '',
      createdAt: raw.createdAt || null,
      createdBy: raw.createdBy || ''
    };
  } catch (_) {
    return null;
  }
}

function formatTestDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatSavedAt(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });
}

function formatSummaryClassLine(summary) {
  if (!summary) return '';
  return rubricNameWithDate({
    class: summary.className || summary.class || '',
    subject: summary.subject || '',
    paper_type: summary.paperType || summary.paper_type || ''
  }, formatTestDate(summary.testDate || summary.test_date));
}

function regradeKindLabel(kind) {
  return kind === 'checked' ? 'Checked' : 'Rechecked';
}

const REGRADE_SORT_COLUMNS = [
  { label: 'Student Name', column: 'name', title: 'Sort by student name' },
  { label: 'Change', column: 'change', title: 'Sort by the change in marks' },
  { label: 'Old Marks', column: 'old', title: 'Sort by old marks' },
  { label: 'New Marks', column: 'new', title: 'Sort by new marks' }
];

function compareRegradeRows(a, b, sort) {
  if (!sort) return 0;
  const sign = sort.direction === 'asc' ? 1 : -1;
  if (sort.column === 'name') {
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
      numeric: true,
      sensitivity: 'base'
    }) * sign;
  }
  const key = sort.column === 'old' ? 'oldMarks' : sort.column === 'new' ? 'newMarks' : 'delta';
  const av = a[key];
  const bv = b[key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  return (av - bv) * sign;
}

function summaryFromServer(row) {
  return {
    id: row.id,
    folderPath: row.folder_path || '',
    className: row.class_label || '',
    section: row.section || '',
    subject: row.subject || '',
    testDate: row.test_date || '',
    assignmentName: row.assignment_name || '',
    kind: row.kind === 'checked' ? 'checked' : 'rechecked',
    rows: Array.isArray(row.rows) ? row.rows : [],
    createdBy: row.created_by || '',
    createdAt: row.created_at || null
  };
}

function MarkWithMachine({ marks, host }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
      {formatMarkNumber(marks)}
      <GradingMachineBadge host={host} />
    </span>
  );
}

const OCR_WHITE_PEN = ['#ffffff'];

function imageNumberLabel(name) {
  const s = String(name || '').trim();
  const match = s.match(/Image_(\d+)/i);
  return match ? match[1] : s.replace(/\.[a-z0-9]+$/i, '');
}

function CommentPageChips({ pages, previewImage, pickedPages, onToggle, onOpen }) {
  const hostRef = useRef(null);
  const [twoRows, setTwoRows] = useState(false);
  const pageKey = pages.join('\n');

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const measure = () => {
      const chips = host.querySelectorAll('.comment-ocr-chip');
      if (chips.length < 2) {
        setTwoRows(false);
        return;
      }
      let total = 0;
      chips.forEach((chip) => { total += chip.offsetWidth; });
      total += 4 * (chips.length - 1);
      const next = total > host.clientWidth + 1;
      setTwoRows((prev) => (prev === next ? prev : next));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    measure();
    return () => observer.disconnect();
  }, [pageKey]);

  const openName = previewImage && previewImage.images && previewImage.images[previewImage.index]
    ? previewImage.images[previewImage.index].name
    : '';
  const indexed = pages.map((name, index) => ({ name, index }));
  const firstCount = twoRows ? Math.ceil(indexed.length / 2) : indexed.length;
  const lines = twoRows ? [indexed.slice(0, firstCount), indexed.slice(firstCount)] : [indexed];

  const renderChip = ({ name, index }) => {
    const number = index + 1;
    const viewing = openName === name;
    const on = pickedPages.includes(number);
    return (
      <span
        key={`${name}-${number}`}
        className={on ? 'comment-ocr-chip on' : 'comment-ocr-chip'}
        style={viewing ? { boxShadow: '0 0 0 2px #2563EB' } : undefined}
      >
        <input
          type="checkbox"
          checked={on}
          title={`Send image ${number} for OCR`}
          aria-label={`Send image ${number} for OCR`}
          onChange={() => onToggle(number)}
        />
        <button
          type="button"
          title={viewing ? `Close image ${number}` : `Open image ${number}: ${name}`}
          onClick={() => onOpen(number)}
        >
          {number}
          <span>{imageNumberLabel(name)}</span>
        </button>
      </span>
    );
  };

  return (
    <div ref={hostRef} style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4, overflow: 'hidden' }}>
      {lines.map((line, lineIndex) => (
        <div key={lineIndex} style={{ display: 'flex', flexWrap: 'nowrap', gap: 4, alignItems: 'center' }}>
          {line.map((page) => renderChip(page))}
        </div>
      ))}
    </div>
  );
}

// Every page of the student whose transcript is open. Sorted rows store the
// front of each sheet; a scanned back is the next file in the folder. A PDF
// import already lists every page, so those names are the whole paper.
function blankQueueAdvanceLabel(cursor) {
  const queue = cursor && Array.isArray(cursor.queue) ? cursor.queue : [];
  const index = queue.findIndex((student) => student && student.name === (cursor && cursor.name));
  if (!queue.length || index < 0) return '';
  return index + 1 < queue.length ? 'Save & Next' : 'Save & Close';
}

function firstProblemPage(text, ocrText) {
  const named = String(text || '').match(/pages?\s+([\d, and]+)/i);
  if (named) {
    const match = named[1].match(/(\d+)/);
    if (match) return Number(match[1]);
  }
  if (!/cut off/i.test(String(text || ''))) return 0;
  const parts = String(ocrText || '').split(/===\s*Page\s+(\d+)(?:\s*===)?/i);
  for (let i = 1; i < parts.length; i += 2) {
    const compact = String(parts[i + 1] || '').slice(-500).replace(/\s+/g, '');
    const ending = compact.slice(-60);
    const marks = (ending.match(/[.．\u2026\-–—_]/g) || []).length;
    if (compact.length >= 8 && marks >= 8 && marks / Math.max(ending.length, 1) >= 0.8) {
      return Number(parts[i]);
    }
  }
  return 0;
}

function pagesForOcrRow(row, analyzedRows, scanNames) {
  // The blank-page window sends this student's own files. The folder neighbor
  // is not added: that file is the next student's page.
  if (Array.isArray(row && row.own_scans)) {
    return row.own_scans.map((name) => String(name || '').trim()).filter(Boolean);
  }
  const frontName = String((row && row.image_name) || '').split(',')[0].trim();
  const rollMatch = String((row && row.student_name) || '').match(/\(([^)]+)\)/);
  const roll = rollMatch ? rollMatch[1].trim().toLowerCase() : '';
  const rows = Array.isArray(analyzedRows) ? analyzedRows : [];
  let matched = roll
    ? rows.filter((item) => item && String(item.unique_code || '').trim().toLowerCase() === roll && item.unique_code !== '000')
    : [];
  if (!matched.length && frontName) {
    const hit = rows.find((item) => {
      const img = String((item && (item.image_no || item.image_name)) || '').trim();
      return img.toLowerCase() === frontName.toLowerCase();
    });
    if (hit && hit.unique_code && hit.unique_code !== '000') {
      matched = rows.filter((item) => item && String(item.unique_code) === String(hit.unique_code));
    }
  }
  const fronts = [];
  const seenFronts = new Set();
  for (const item of matched) {
    const img = String((item && (item.image_no || item.image_name)) || '').trim();
    const key = img.toLowerCase();
    if (!img || seenFronts.has(key)) continue;
    seenFronts.add(key);
    fronts.push(img);
  }
  fronts.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  if (!fronts.length) return frontName ? [frontName] : [];
  const pdf = matched.some((item) => String(item.model_type || '').toLowerCase() === 'pdf');
  const sortedScans = (Array.isArray(scanNames) ? scanNames : [])
    .map((name) => String(name || '').trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  if (pdf || sortedScans.length === 0) return fronts;
  const pages = [];
  const seenPages = new Set();
  const push = (name) => {
    const key = String(name || '').trim().toLowerCase();
    if (!key || seenPages.has(key)) return;
    seenPages.add(key);
    pages.push(String(name).trim());
  };
  for (const front of fronts) {
    const idx = sortedScans.findIndex((name) => name.toLowerCase() === front.toLowerCase());
    push(idx >= 0 ? sortedScans[idx] : front);
    if (idx >= 0 && sortedScans[idx + 1]) push(sortedScans[idx + 1]);
  }
  return pages;
}

function recheckImageKey(row) {
  const fromName = String((row && row.student_name) || '').match(/(Image_\d+\.\w+)/i);
  if (fromName) return fromName[1].toLowerCase();
  return String((row && row.image_name) || '').trim().toLowerCase();
}

function rememberRegradeSnapshot(rows, mode, meta) {
  if (mode === 'ocr_only') {
    sessionStorage.removeItem(REGRADE_SNAPSHOT_KEY);
    return;
  }
  const info = meta || {};
  const students = (rows || []).map((row) => ({
    name: String(row.student_name || 'Unknown').replace(/\s+/g, ' ').trim() || 'Unknown',
    imageName: recheckImageKey(row),
    oldMarks: row.marks == null ? '' : String(row.marks),
    oldHost: row.grading_host || null
  }));
  try {
    sessionStorage.setItem(REGRADE_SNAPSHOT_KEY, JSON.stringify({
      mode,
      kind: info.kind === 'checked' ? 'checked' : 'rechecked',
      folderPath: info.folderPath || '',
      className: info.className || '',
      section: info.section || '',
      subject: info.subject || '',
      testDate: info.testDate || '',
      assignmentName: info.assignmentName || '',
      students
    }));
  } catch (_) { /* ignore quota */ }
}

function clearRegradeSnapshot() {
  sessionStorage.removeItem(REGRADE_SNAPSHOT_KEY);
}

function parseMarkNumber(value) {
  const match = String(value ?? '').trim().match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const n = parseFloat(match[0]);
  return Number.isFinite(n) ? n : null;
}

function formatMarkNumber(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

function formatMarkDelta(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const rounded = Math.round(n * 100) / 100;
  const text = Number.isInteger(rounded) ? String(Math.abs(rounded)) : String(Math.abs(rounded));
  if (rounded > 0) return `+${text}`;
  if (rounded < 0) return `-${text}`;
  return '0';
}

function formatStepDuration(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return null;
  const totalSec = Math.round(n / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatPktClock(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return null;
  return new Date(n).toLocaleString('en-PK', {
    timeZone: 'Asia/Karachi',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

// Wall-clock for the task currently running on this page. Seconds stay
// visible so the chip ticks every second, including after an hour.
function formatLiveDuration(ms) {
  const totalSec = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

const TASK_TIMER_KEY = 'assessmentTaskTimer';

function readTaskTimer(stillOpen) {
  try {
    const raw = JSON.parse(localStorage.getItem(TASK_TIMER_KEY) || 'null');
    if (!raw || !Number.isFinite(Number(raw.startedAt))) return null;
    const timer = {
      startedAt: Number(raw.startedAt),
      endedAt: Number.isFinite(Number(raw.endedAt)) && Number(raw.endedAt) > 0 ? Number(raw.endedAt) : null,
      label: typeof raw.label === 'string' && raw.label ? raw.label : 'Task',
      sessionId: raw.sessionId || null
    };
    if (!timer.endedAt && !stillOpen) return null;
    return timer;
  } catch {
    return null;
  }
}

function writeTaskTimer(timer) {
  try {
    if (!timer) localStorage.removeItem(TASK_TIMER_KEY);
    else localStorage.setItem(TASK_TIMER_KEY, JSON.stringify(timer));
  } catch {}
}

function labelForRunningTask({ paused, isProcessing, isGettingText, assessSnapshotBusy, approveLoading, quickOcrOpening, autoCheckStatus, progress }) {
  if (paused) return 'Paused';
  if (isProcessing) {
    const text = `${autoCheckStatus || ''} ${progress || ''}`;
    if (/re-?grad/i.test(text)) return 'Re-grading';
    if (/re-?check/i.test(text)) return 'Recheck';
    if (/ocr-only|ocr only|re-ocr|text step/i.test(text)) return 'OCR';
    if (/paus/i.test(text)) return 'Pausing';
    if (/resum/i.test(text)) return 'Resume';
    if (/\bgrad/i.test(text)) return 'Grading';
    return 'Checking';
  }
  if (isGettingText) return 'Get text';
  if (assessSnapshotBusy) return 'Saving table';
  if (approveLoading) return 'Approving';
  if (quickOcrOpening) return 'Opening';
  return 'Task';
}

const SAVE_APPROVE_RESULT_LABEL = {
  approved: 'Approved',
  'approving-failed': 'Approving failed',
  'saving-failed': 'Saving failed',
  'both-failed': 'Approve and Save failed'
};

const APPROVED_FOLDER_STATUSES = new Set(['copy_to_edupage', 'completed']);
const APPROVED_TABLE_KEY = 'fgApprovedTableKey';

function tableActionKey(rows) {
  const parts = (Array.isArray(rows) ? rows : []).map((row) => [
    String((row && row.student_name) || '').replace(/\s+/g, ' ').trim(),
    String((row && row.image_name) || '').trim(),
    String(row && row.marks != null ? row.marks : '').trim(),
    String((row && row.comments) || '').trim(),
    String((row && row.table_comments) || '').trim(),
    String((row && row.status) || '').trim()
  ].join('\u001f'));
  parts.sort();
  return parts.join('\u001e');
}

function readApprovedTableKey(folderPath) {
  try {
    const all = JSON.parse(localStorage.getItem(APPROVED_TABLE_KEY) || '{}');
    return String((all && all[folderPath]) || '');
  } catch {
    return '';
  }
}

function writeApprovedTableKey(folderPath, key) {
  if (!folderPath) return;
  try {
    const all = JSON.parse(localStorage.getItem(APPROVED_TABLE_KEY) || '{}');
    const next = all && typeof all === 'object' ? all : {};
    if (key) next[folderPath] = key;
    else delete next[folderPath];
    localStorage.setItem(APPROVED_TABLE_KEY, JSON.stringify(next));
  } catch {
    /* the button still follows this visit's state */
  }
}

function saveApproveResultTone(label) {
  if (label === 'Approved') return 'success';
  if (label === 'Approving failed' || label === 'Saving failed' || label === 'Approve and Save failed') return 'failed';
  return '';
}

function TaskElapsedChip({ startedAt, endedAt, label, detail }) {
  const [now, setNow] = React.useState(() => Date.now());
  const running = startedAt != null && !endedAt;
  React.useEffect(() => {
    if (!running) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running, startedAt]);
  if (startedAt == null) return null;
  const elapsed = Math.max(0, (endedAt || now) - startedAt);
  const stoppedAt = running ? null : formatPktClock(endedAt);
  const tone = running ? '' : saveApproveResultTone(label);
  return (
    <div
      title={[detail || label || 'Task', stoppedAt].filter(Boolean).join(' · ')}
      style={{
        background: running
          ? '#fffbeb'
          : tone === 'success'
            ? '#ecfdf5'
            : tone === 'failed'
              ? '#fef2f2'
              : '#f3f4f6',
        color: running
          ? '#b45309'
          : tone === 'success'
            ? '#047857'
            : tone === 'failed'
              ? '#b91c1c'
              : '#374151',
        border: `1px solid ${running ? '#fde68a' : tone === 'success' ? '#bbf7d0' : tone === 'failed' ? '#fecaca' : '#e5e7eb'}`,
        height: 28,
        padding: '0 10px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums'
      }}
    >
      <i className="fa-solid fa-clock"></i>
      <span style={{ opacity: 0.9, fontWeight: 600 }}>{label || 'Task'}</span>
      <span>{formatLiveDuration(elapsed)}</span>
      {stoppedAt && (
        <span style={{ opacity: 0.9, fontWeight: 600 }}>{stoppedAt}</span>
      )}
    </div>
  );
}

// A step clock is the moment that step went idle. While sheets of that step
// are still open, endedAt is only the previous gap and is not a finish time.
function finishedStepAt(slot) {
  if (!slot || slot.endedAt == null) return null;
  if (Number(slot.open) > 0) return null;
  const n = Number(slot.endedAt);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const STEP_TIMES_KEY = 'assessmentStepTimesByFolder';
function readStepTimesMap() {
  try {
    const raw = JSON.parse(localStorage.getItem(STEP_TIMES_KEY) || '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
}

function buildRegradeSummary(results) {
  let raw = null;
  try {
    raw = JSON.parse(sessionStorage.getItem(REGRADE_SNAPSHOT_KEY) || 'null');
  } catch (_) {
    raw = null;
  }
  sessionStorage.removeItem(REGRADE_SNAPSHOT_KEY);
  if (!raw || raw.mode === 'ocr_only' || !Array.isArray(raw.students) || !raw.students.length) return null;
  const pool = Array.isArray(results) ? results : [];
  const used = new Set();
  const norm = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const rows = raw.students.map((student) => {
    const image = String(student.imageName || '').trim().toLowerCase();
    const name = norm(student.name);
    const rollMatch = String(student.name || '').match(/\(([^)]+)\)/);
    const roll = rollMatch ? rollMatch[1].trim().toLowerCase() : '';
    let match = null;
    if (image) {
      match = pool.find((row) => row && !used.has(row.id) && recheckImageKey(row) === image);
    }
    if (!match && name) {
      match = pool.find((row) => row && !used.has(row.id) && norm(row.student_name) === name);
    }
    if (!match && roll && roll !== '000' && roll !== '0000') {
      match = pool.find((row) => {
        if (!row || used.has(row.id)) return false;
        const rowRoll = (String(row.student_name || '').match(/\(([^)]+)\)/) || [])[1];
        return !!(rowRoll && rowRoll.trim().toLowerCase() === roll);
      });
    }
    if (match) used.add(match.id);
    const oldMarks = parseMarkNumber(student.oldMarks);
    const newMarks = match ? parseMarkNumber(match.marks) : null;
    const delta = oldMarks != null && newMarks != null
      ? Math.round((newMarks - oldMarks) * 100) / 100
      : null;
    return {
      name: (match && match.student_name) || student.name || 'Unknown',
      oldMarks,
      newMarks,
      delta,
      oldGradingHost: student.oldHost || null,
      newGradingHost: match ? (match.grading_host || null) : null,
      gradingHost: match ? (match.grading_host || null) : null
    };
  });
  return {
    rows,
    kind: raw.kind === 'checked' ? 'checked' : 'rechecked',
    folderPath: raw.folderPath || '',
    className: raw.className || '',
    section: raw.section || '',
    subject: raw.subject || '',
    testDate: raw.testDate || '',
    assignmentName: raw.assignmentName || ''
  };
}

const AUTO_CHECK_USERS = ['ai1', 'ai2', 'ai3', 'ai4'];

function autoCheckBoardName(folder) {
  if (!folder) return '';
  const title = String(folder.title || '').trim();
  if (title) return title;
  const cls = String(folder.class || '').trim();
  const sub = String(folder.subject || '').trim();
  if (cls && sub) return `${cls} - ${sub}`;
  return cls || sub || `Folder ${folder.id}`;
}

function autoCheckSheetProgress(folder) {
  if (!folder) return '';
  const total = Number(folder.total) || 0;
  if (total <= 0) return '';
  const cap = (n) => Math.min(total, Math.max(0, Number(n) || 0));
  return `OCR ${cap(folder.ocr)}/${total} · Grading ${cap(folder.processed)}/${total}`;
}

function autoCheckHoldDetail(folder) {
  if (!folder) return '';
  const bits = [];
  const reason = String(folder.reason || '').trim();
  if (reason) bits.push(reason);
  const sheets = autoCheckSheetProgress(folder);
  if (sheets) bits.push(sheets);
  else {
    const processed = Number(folder.processed) || 0;
    if (processed > 0) bits.push(`${processed} paper${processed === 1 ? '' : 's'} finished`);
  }
  return bits.join(' · ');
}

function autoCheckFailureReason(err, interrupted) {
  const raw = String((err && (err.message || err)) || '').trim();
  if (/paused by user/i.test(raw)) return 'Paused by user';
  if (interrupted || /^auto-check cancelled$/i.test(raw)) return 'Stopped by user';
  if ((err && err.internetError) || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
    return INTERNET_ERROR_MESSAGE;
  }
  if (isBrowserNetworkError(err || { message: raw })) {
    if (!raw || /^(failed to fetch|networkerror|network request failed|load failed)$/i.test(raw)) {
      return INTERNET_ERROR_MESSAGE;
    }
    if (/internet error/i.test(raw)) return raw.slice(0, 500);
    return `${raw} — ${INTERNET_ERROR_MESSAGE}`.slice(0, 500);
  }
  return (raw || 'Check failed').slice(0, 500);
}

function autoCheckOutcomeLine(kind, title, reason) {
  const name = title || 'this class';
  if (kind === 'paused') return `Paused "${name}": ${reason}`;
  if (kind === 'stopped') return `Stopped "${name}": ${reason}`;
  return `Auto-check failed on "${name}": ${reason}`;
}

function AutoCheckStatusPanel({ dispatch, hostOn, board, switches, present, onToggleAccount, switchBusy, compact = false }) {
  const [statusOpen, setStatusOpen] = useState(() => {
    try { return localStorage.getItem('fgCheckStatusOpen') !== '0'; }
    catch { return true; }
  });
  const toggleStatus = () => {
    setStatusOpen((current) => {
      const next = !current;
      try { localStorage.setItem('fgCheckStatusOpen', next ? '1' : '0'); } catch { /* the choice still applies to this window */ }
      return next;
    });
  };
  const gpu = (dispatch && dispatch.gpu) || {};
  const mac = (dispatch && dispatch.mac) || {};
  const gemini = (dispatch && dispatch.gemini) || {};
  const roleLabel = (role) => (role === 'grading' ? 'grading' : role === 'ocr' ? 'OCR' : '');
  const gpuOff = !(hostOn && hostOn('gpu'));
  const macOff = !(hostOn && hostOn('mac'));
  const gpuRole = roleLabel(gpu.role);
  const macAutoRole = Number(mac.ocr) > 0 ? 'OCR' : Number(mac.grading) > 0 ? 'grading' : '';
  const macManualBusy = Number(mac.manual) > 0;
  const geminiActive = Number(gemini.active) || 0;
  const geminiMax = Number(gemini.max) > 0 ? Number(gemini.max) : 3;
  const geminiWaiting = Number(gemini.waiting) || 0;
  const chips = [
    {
      key: 'gpu',
      label: 'Spc',
      text: gpuOff ? 'off' : (gpu.busy ? (gpuRole || 'busy') : 'free'),
      off: gpuOff,
      busy: !gpuOff && !!gpu.busy,
      kind: gpuRole,
      tip: gpuOff
        ? 'Spc is off.'
        : gpu.busy
          ? `Spc’s slot is ${gpuRole || 'busy'}.${gpu.loadedModel ? ` Model: ${gpu.loadedModel}.` : ''}`
          : 'Spc’s slot is free.'
    }
  ];
  if (macOff) {
    chips.push({
      key: 'mac',
      label: 'Mac',
      text: 'off',
      off: true,
      busy: false,
      kind: '',
      tip: 'Mac is off. The auto slot and the manual slot are unused.'
    });
  } else {
    chips.push({
      key: 'mac-auto',
      label: 'Mac',
      text: macAutoRole || 'free',
      off: false,
      busy: !!macAutoRole,
      kind: macAutoRole,
      tip: macAutoRole
        ? `Mac auto slot is ${macAutoRole}.`
        : 'Mac auto slot is free. It reads or grades one auto-check at a time.'
    });
    chips.push({
      key: 'mac-manual',
      label: 'Mac manual',
      text: macManualBusy ? 'busy' : 'free',
      off: false,
      busy: macManualBusy,
      kind: macManualBusy ? 'manual' : '',
      tip: macManualBusy
        ? 'Mac manual slot is in use. Only a manual check can take this slot.'
        : 'Mac manual slot is free. Only a manual check can use it.'
    });
  }
  chips.push({
    key: 'gemini',
    label: 'Gemini',
    text: geminiWaiting > 0
      ? `${geminiActive}/${geminiMax} · ${geminiWaiting} waiting`
      : `${geminiActive}/${geminiMax}`,
    off: false,
    busy: geminiActive > 0,
    kind: 'gemini',
    tip: `${geminiActive} of ${geminiMax} Gemini slots are in use. ${geminiWaiting} waiting.`
  });
  const fill = (chip) => {
    if (chip.off) return { background: '#e5e7eb', color: '#6b7280', border: 'none' };
    if (!chip.busy) return { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color, #d1d5db)' };
    if (chip.kind === 'OCR') return { background: '#1d4ed8', color: 'white', border: 'none' };
    if (chip.kind === 'manual') return { background: '#7c3aed', color: 'white', border: 'none' };
    if (chip.kind === 'gemini') return { background: '#0f766e', color: 'white', border: 'none' };
    return { background: '#059669', color: 'white', border: 'none' };
  };
  const checkers = board && board.checkers && board.checkers.length
    ? board.checkers
    : AUTO_CHECK_USERS.map((account) => ({ account, state: 'free', folder: null, held: [] }));
  const queue = (board && board.queue) || [];
  const sends = Array.isArray(dispatch && dispatch.active) ? dispatch.active : [];
  const chipJustify = compact ? 'flex-start' : 'flex-end';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: compact ? 'stretch' : 'flex-end', minWidth: 0, width: compact ? '100%' : undefined }}>
      <button
        type="button"
        onClick={toggleStatus}
        title={statusOpen ? 'Hide the check status' : 'Show the check status'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          alignSelf: compact ? 'flex-end' : undefined,
          gap: 5,
          padding: '3px 8px',
          border: '1px solid var(--border-color, #d1d5db)',
          borderRadius: 6,
          background: 'var(--panel-bg, white)',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          fontSize: '0.72rem',
          fontWeight: 700,
          whiteSpace: 'nowrap'
        }}
      >
        <i className={`fa-solid fa-${statusOpen ? 'eye-slash' : 'eye'}`}></i>
        {statusOpen ? 'Hide' : 'Show'}
      </button>
      {statusOpen && (
      <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: chipJustify }}>
        {chips.map((chip) => {
          const paint = fill(chip);
          return (
            <span
              key={chip.key}
              title={chip.tip}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 7px',
                borderRadius: 999,
                fontSize: '0.72rem',
                fontWeight: 700,
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
                background: paint.background,
                color: paint.color,
                border: paint.border
              }}
            >
              <span>{chip.label}</span>
              <span style={{ fontWeight: 600 }}>{chip.text}</span>
            </span>
          );
        })}
      </div>
      {!board ? (
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #94a3b8)', fontStyle: 'italic' }}>Loading check line…</span>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: chipJustify }}>
            {checkers.map((row) => {
              const busy = row.state && row.state !== 'free';
              const name = row.folder ? autoCheckBoardName(row.folder) : 'free';
              const sheets = row.state === 'running' ? autoCheckSheetProgress(row.folder) : '';
              const stateWord = row.state === 'running' ? 'checking' : (row.state || 'free');
              const heldNote = (row.held || []).map((folder) => {
                const extra = autoCheckHoldDetail(folder);
                const label = autoCheckBoardName(folder);
                return extra ? `${label} (${extra})` : label;
              }).join(', ');
              const detail = autoCheckHoldDetail(row.folder);
              const tip = row.folder
                ? `${row.account} is ${stateWord} ${name}${row.folder.medium ? ` (${row.folder.medium})` : ''}${row.folder.regrade ? ' · re-grade' : ''}${detail ? `. ${detail}` : ''}${heldNote ? `. Also holding: ${heldNote}` : ''}`
                : `${row.account} is free`;
              const chipBg = row.state === 'running'
                ? '#059669'
                : row.state === 'paused'
                  ? '#d97706'
                  : (row.state === 'failed' || row.state === 'stopped')
                    ? '#dc2626'
                    : 'transparent';
              const chipColor = busy ? 'white' : 'var(--text-secondary)';
              const accountOn = !switches || switches[row.account] !== false;
              const here = present == null || present[row.account] === true;
              const awayTip = here ? '' : `${row.account} is logged out, or its Check tab is closed. `;
              return (
                <span key={row.account} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, opacity: here ? 1 : 0.38 }}>
                <span
                  title={`${awayTip}${tip}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    maxWidth: 460,
                    padding: '2px 7px',
                    borderRadius: 999,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    lineHeight: 1.3,
                    color: chipColor,
                    background: chipBg,
                    border: busy ? 'none' : '1px solid var(--border-color, #d1d5db)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <span>{row.account}</span>
                  <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                    {busy ? `${stateWord === 'checking' ? '' : `${stateWord} `}${name}` : 'free'}
                    {(row.held || []).length > 0 ? ` +${row.held.length}` : ''}
                  </span>
                  {sheets ? (
                    <span style={{ fontWeight: 800, flexShrink: 0 }}>{sheets}</span>
                  ) : null}
                </span>
                {typeof onToggleAccount === 'function' && (
                  <button
                    type="button"
                    disabled={switchBusy === row.account}
                    onClick={() => onToggleAccount(row.account, !accountOn)}
                    title={here
                      ? (accountOn
                        ? `Stop auto-check on ${row.account}. Its open Check tab releases the current class and does not claim another.`
                        : `Start auto-check on ${row.account}. Takes effect while that account’s Check tab is open.`)
                      : `${row.account} is logged out, or its Check tab is closed. ${accountOn ? 'Stop' : 'Start'} is saved until that tab is open.`}
                    style={{
                      border: 'none',
                      borderRadius: 999,
                      padding: '2px 7px',
                      fontSize: '0.66rem',
                      fontWeight: 800,
                      color: 'white',
                      cursor: switchBusy === row.account ? 'wait' : 'pointer',
                      opacity: switchBusy === row.account ? 0.7 : 1,
                      background: accountOn
                        ? 'linear-gradient(135deg, #ef4444, #b91c1c)'
                        : 'linear-gradient(135deg, #2563eb, #1d4ed8)'
                    }}
                  >
                    {accountOn ? 'Stop' : 'Start'}
                  </button>
                )}
                </span>
              );
            })}
          </div>
          <div
            title={queue.length
              ? queue.map((folder, index) => `${index + 1}. ${autoCheckBoardName(folder)}${folder.regrade ? ' (re-grade)' : ''}`).join('\n')
              : 'No folders are waiting.'}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
              justifyContent: chipJustify,
              alignItems: 'center',
              maxWidth: '100%'
            }}
          >
            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.3 }}>
              Next
            </span>
            {queue.length === 0 ? (
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #94a3b8)', fontStyle: 'italic' }}>none waiting</span>
            ) : queue.slice(0, 8).map((folder, index) => (
              <span
                key={folder.id}
                title={`${index + 1}. ${autoCheckBoardName(folder)}${folder.regrade ? ' · re-grade' : ''}${folder.medium ? ` · ${folder.medium}` : ''}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  maxWidth: 220,
                  padding: '1px 6px',
                  borderRadius: 999,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: '#334155',
                  background: folder.regrade ? '#ede9fe' : '#e2e8f0',
                  whiteSpace: 'nowrap'
                }}
              >
                <span style={{ fontWeight: 800 }}>{index + 1}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{autoCheckBoardName(folder)}</span>
              </span>
            ))}
            {queue.length > 8 && (
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                +{queue.length - 8}
              </span>
            )}
          </div>
        </>
      )}
      {sends.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: chipJustify, maxWidth: '100%' }}>
          {sends.map((send, index) => (
            <span
              key={`${send.role}|${send.name}|${send.machine}|${index}`}
              title={`${send.role}${send.name ? ` ${send.name}` : ''} → ${send.machine}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                maxWidth: 280,
                padding: '2px 7px',
                borderRadius: 999,
                fontSize: '0.72rem',
                fontWeight: 700,
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
                color: '#1e3a8a',
                background: '#eff6ff',
                border: '1px solid #bfdbfe'
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {send.role}{send.name ? ` ${send.name}` : ''} → {send.machine}
              </span>
            </span>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}
const currentUsernameFromStorage = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}').username || ''; }
  catch { return ''; }
};

// Classes the teacher paused. Auto-check must not pick these up and wipe
// the table — Resume is what continues them.
const PAUSED_FOLDERS_KEY = 'fgPausedFolders';
const readPausedFolderSet = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(PAUSED_FOLDERS_KEY) || '[]');
    return new Set(Array.isArray(raw) ? raw : []);
  } catch {
    return new Set();
  }
};
const rememberPausedFolder = (folderPath) => {
  if (!folderPath) return;
  const set = readPausedFolderSet();
  set.add(folderPath);
  localStorage.setItem(PAUSED_FOLDERS_KEY, JSON.stringify([...set]));
};
const forgetPausedFolder = (folderPath) => {
  if (!folderPath) return;
  const set = readPausedFolderSet();
  set.delete(folderPath);
  localStorage.setItem(PAUSED_FOLDERS_KEY, JSON.stringify([...set]));
};

// Build the scans-folder comment payload for one graded row: keep the
// verification table (or one-line comments) and append "[Image] [N sheets]".
// N must come from the grading trailer on `comments` (backend sheet_count),
// NOT from image_name.split(',').length — image_name stores only the first
// front, so that count is almost always 1 and was saving "[1 sheet]" into
// Scans while Evaluate still showed the real "[2 sheets]".
function commentsForScansFolder(row) {
  const base = row.table_comments || row.comments || '';
  if (/\[\d+\s*sheets?\]/i.test(base)) return base;

  const imgRaw = (row.image_name || '').toString().trim();
  const imgList = imgRaw ? imgRaw.split(',').map(s => s.trim()).filter(Boolean) : [];
  const fromOneLine = String(row.comments || '').match(/\[(\d+)\s*sheets?\]/i);
  const sheetCount = fromOneLine
    ? parseInt(fromOneLine[1], 10)
    : (imgList.length || 0);
  if (imgList.length === 0 && sheetCount === 0) return base;

  const footerParts = [];
  if (imgList.length > 0) footerParts.push(`[${imgList.join(', ')}]`);
  if (sheetCount > 0) footerParts.push(`[${sheetCount} sheet${sheetCount === 1 ? '' : 's'}]`);
  const footer = footerParts.join(' ');
  return footer ? `${base}\n\n${footer}` : base;
}

function formatPaperTotal(n) {
  const r = Math.round(Number(n) * 100) / 100;
  return String(r);
}

const OCR_FRAME_PAD = 8;
const OCR_FRAME_MIN_W = 520;
const OCR_FRAME_MIN_H = 320;
const OCR_RESIZE_EDGES = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

function ocrFrameLimits() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    vw,
    vh,
    minW: Math.min(OCR_FRAME_MIN_W, Math.max(280, vw)),
    minH: Math.min(OCR_FRAME_MIN_H, Math.max(220, vh))
  };
}

function defaultOcrFrame() {
  const pad = OCR_FRAME_PAD;
  const { vw, vh, minW, minH } = ocrFrameLimits();
  const w = Math.max(minW, vw - pad * 2);
  const h = Math.max(minH, vh - pad * 2);
  return {
    x: Math.max(0, Math.min(pad, vw - w)),
    y: Math.max(0, Math.min(pad, vh - h)),
    w: Math.min(w, vw),
    h: Math.min(h, vh)
  };
}

function fitOcrFrame(frame) {
  const { vw, vh, minW, minH } = ocrFrameLimits();
  const w = Math.max(minW, Math.min(frame.w, vw));
  const h = Math.max(minH, Math.min(frame.h, vh));
  return {
    x: Math.max(0, Math.min(frame.x, Math.max(0, vw - w))),
    y: Math.max(0, Math.min(frame.y, Math.max(0, vh - h))),
    w,
    h
  };
}

function dragOcrFrame(start, dx, dy) {
  const { vw, vh } = ocrFrameLimits();
  const w = Math.min(start.ow, vw);
  const h = Math.min(start.oh, vh);
  return {
    x: Math.max(0, Math.min(start.ox + dx, vw - w)),
    y: Math.max(0, Math.min(start.oy + dy, vh - h)),
    w,
    h
  };
}

function resizeOcrFrame(start, dx, dy) {
  const edge = start.edge;
  const { vw, vh, minW, minH } = ocrFrameLimits();
  let x = start.ox;
  let y = start.oy;
  let w = start.ow;
  let h = start.oh;
  if (edge.includes('e')) w = Math.max(minW, Math.min(start.ow + dx, vw - start.ox));
  if (edge.includes('s')) h = Math.max(minH, Math.min(start.oh + dy, vh - start.oy));
  if (edge.includes('w')) {
    const right = Math.min(start.ox + start.ow, vw);
    x = Math.max(0, Math.min(start.ox + dx, right - minW));
    w = right - x;
  }
  if (edge.includes('n')) {
    const bottom = Math.min(start.oy + start.oh, vh);
    y = Math.max(0, Math.min(start.oy + dy, bottom - minH));
    h = bottom - y;
  }
  return { x, y, w, h };
}

function ocrResizeCursor(edge) {
  if (edge === 'n' || edge === 's') return 'ns-resize';
  if (edge === 'e' || edge === 'w') return 'ew-resize';
  if (edge === 'ne' || edge === 'sw') return 'nesw-resize';
  return 'nwse-resize';
}

function ocrPointerOnControl(target) {
  return !!(target && target.closest && target.closest('button, input, textarea, select, a, label, [data-ocr-control]'));
}

// The OCR and grading dropdowns list Gemini and local models together.
// The choice sets the provider: a gemini-* model calls Gemini (key MR1),
// and a local model uses Spc and the Mac. Hosts are not pinned.
function ocrChoiceLabel(value) {
  const hit = GEMINI_MODELS.find((m) => m.value === value)
    || LOCAL_MODELS.find((m) => m.value === value);
  return hit ? hit.label : (value || 'the selected model');
}

// Question rows on the open verification table, in paper order. A partial
// re-grade replaces these exact rows and leaves every other row as it is.
function questionsInGradeTable(md) {
  const out = [];
  const seen = new Set();
  for (const raw of String(md || '').split('\n')) {
    const line = raw.trim();
    if (!line.startsWith('|')) continue;
    if (/^\|\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line)) continue;
    const label = line.split('|').map((cell) => cell.replace(/\*\*/g, '').trim()).find(Boolean) || '';
    if (!label || /^question$/i.test(label)) continue;
    if (/grand\s*total/i.test(label)) break;
    if (/total/i.test(label)) continue;
    const id = normalizeQuestionId(label);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, label: formatQuestionLabel(id) });
  }
  return out;
}

function providersForCheckModels(ocrName, gradeName) {
  const ocrGemini = String(ocrName || '').startsWith('gemini-');
  const gradeGemini = String(gradeName || '').startsWith('gemini-');
  const ocrLocal = isKnownLocalModel(ocrName);
  const gradeLocal = isKnownLocalModel(gradeName);
  const provider = (gradeLocal || (ocrLocal && !gradeGemini)) ? 'local' : 'gemini';
  let ocrProvider = null;
  if (ocrGemini && provider !== 'gemini') ocrProvider = 'gemini';
  if (ocrLocal && provider === 'gemini') ocrProvider = 'local';
  return { provider, ocrProvider, usesLocal: provider === 'local' || ocrProvider === 'local' };
}

function savedCheckModel(key, fallback) {
  try {
    const saved = localStorage.getItem(key);
    if (typeof saved === 'string' && (saved.startsWith('gemini-') || isKnownLocalModel(saved))) return saved;
  } catch { /* keep the fallback */ }
  return fallback;
}

// A list refresh must not put the grader's aside back onto a paper the
// teacher just accepted.
function keepAcceptedMarks(open, next) {
  if (!open || !next) return next;
  const same = String(open.id) === String(next.id)
    || (open.image_name && open.image_name === next.image_name)
    || (String(open.student_name || '').trim()
      && String(open.student_name || '').trim() === String(next.student_name || '').trim());
  if (!same) return next;
  const openTable = open.table_comments || '';
  const nextTable = next.table_comments != null ? next.table_comments : openTable;
  const openComments = open.comments || '';
  const nextComments = next.comments != null ? next.comments : openComments;
  const table = acceptThinkingText(nextTable) !== nextTable
    && acceptThinkingText(openTable) === openTable
    && openTable
    ? openTable
    : nextTable;
  const comments = acceptThinkingText(nextComments) !== nextComments
    && acceptThinkingText(openComments) === openComments
    && openComments
    ? openComments
    : nextComments;
  if (table === next.table_comments && comments === next.comments) return next;
  return { ...next, table_comments: table, comments };
}

function ThinClassChevron({ innerProps }) {
  return (
    <span
      {...innerProps}
      style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto', paddingRight: 3, color: '#64748b', lineHeight: 0 }}
    >
      <svg width="7" height="7" viewBox="0 0 8 8" fill="none" aria-hidden="true">
        <path d="M1.2 2.6 L4 5.4 L6.8 2.6" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

export default function AssessmentTab({ rubricText, pdfRubric, onAddResults, onRecheckStudent, quickTranscript = null, onQuickTranscriptClose = null, onChatterResolved = null, onThinkingAccepted = null }) {
  const quickCloseRef = useRef(onQuickTranscriptClose);
  quickCloseRef.current = onQuickTranscriptClose;
  const [folderPath, setFolderPath] = useState('');
  const [studentRange, setStudentRange] = useState('All');
  const [model] = useState('gemini-3.8-flash');
  // Two-step pipeline: OCR with ocrModel, grading with gradingModel.
  // Each dropdown lists Gemini and the local models. The saved choice is
  // kept when it is one of those; otherwise OCR starts on 3.8-flash and
  // grading starts on gemma.
  const [ocrModel, setOcrModel] = useState(() => savedCheckModel('urduOcrModel', 'gemini-3.8-flash'));
  const [gradingModel, setGradingModel] = useState(() => savedCheckModel('urduGradingModel', 'ggml'));
  // Which LM Studio machine runs each two-step phase (gpu=Server107 | mac).
  const [ocrHost, setOcrHost] = useState(() => localStorage.getItem('ocrHost') || 'gpu');
  const [gradingHost, setGradingHost] = useState(() => localStorage.getItem('gradingHost') || 'mac');
  const [imageSize, setImageSize] = useState('full');
  const [ocrSend, setOcrSend] = useState(() => {
    try {
      // Older builds stored "one page per call" on every visit, so a missing
      // choice and that old default both open on all pages in one call.
      if (localStorage.getItem('ocrSendDefault') !== 'all') {
        localStorage.setItem('ocrSendDefault', 'all');
        localStorage.setItem('ocrSend', 'all');
        return 'all';
      }
      return localStorage.getItem('ocrSend') === 'per_page' ? 'per_page' : 'all';
    } catch { return 'all'; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('urduOcrModel', ocrModel || ''); } catch {}
  }, [ocrModel]);
  React.useEffect(() => {
    try { localStorage.setItem('urduGradingModel', gradingModel || ''); } catch {}
  }, [gradingModel]);
  React.useEffect(() => {
    try { localStorage.setItem('ocrHost', ocrHost || ''); } catch {}
  }, [ocrHost]);
  React.useEffect(() => {
    try { localStorage.setItem('gradingHost', gradingHost || ''); } catch {}
  }, [gradingHost]);
  React.useEffect(() => {
    try { localStorage.removeItem('assessmentImageSize'); } catch {}
  }, []);
  React.useEffect(() => {
    try { localStorage.setItem('ocrSend', ocrSend); } catch {}
  }, [ocrSend]);
  // Thinking toggle for local reasoning models (Qwen3, QwQ, Nemotron). When
  // off, we send `enable_thinking: false` so the answer comes back in
  // `content` instead of `reasoning_content`. Defaults to OFF for grading.
  const [thinkingEnabled] = useState(() => {
    return localStorage.getItem('aiThinkingEnabled') === 'true';
  });
  React.useEffect(() => {
    try { localStorage.setItem('aiThinkingEnabled', thinkingEnabled ? 'true' : 'false'); } catch {}
  }, [thinkingEnabled]);
  // % of (unclear) words at which step 1 (OCR) is treated as failed and step 2
  // (grading) is skipped. Backend re-checks this; frontend mirrors it for
  // tooltips and display.
  const OCR_UNCLEAR_THRESHOLD = 0.30;
  const [subjectType, setSubjectType] = useState('none');
  // Pipeline mode is now independent of language, so the teacher can pick any
  // of the four combinations: English-1 (single-call), English-2 (two-step),
  // Urdu-1 (single-call), Urdu-2 (two-step). Default is 'single' — Urdu-2
  // remains the most-used path but every Urdu run was previously hardcoded to
  // two-step, so we restore that on first load if subjectType is already urdu.
  const [pipelineMode, setPipelineMode] = useState(() =>
    localStorage.getItem('assessmentPipelineMode') || 'single'
  );
  React.useEffect(() => {
    try { localStorage.setItem('assessmentPipelineMode', pipelineMode || 'single'); } catch {}
  }, [pipelineMode]);

  // The Medium dropdown stores a composite value (none / english_single /
  // english_two_step / urdu_single / urdu_two_step). The two underlying state
  // fields — subjectType + pipelineMode — are derived from it on change so
  // the rest of the component (and the backend payload) keep using the same
  // independent fields. `mediumOption` is purely a UI convenience.
  const mediumOption = subjectType === 'none'
    ? 'none'
    : `${subjectType === 'urdu' ? 'urdu' : 'english'}_${pipelineMode === 'two_step' ? 'two_step' : 'single'}`;
  const handleMediumChange = (val) => {
    if (val === 'none') {
      setSubjectType('none');
      return;
    }
    const isUrdu = val.startsWith('urdu');
    const isTwoStep = val.endsWith('two_step');
    setSubjectType(isUrdu ? 'urdu' : 'other');
    setPipelineMode(isTwoStep ? 'two_step' : 'single');
  };
  const [isProcessing, setIsProcessing] = useState(() => {
    const saved = localStorage.getItem('assessmentProcessing');
    return saved ? JSON.parse(saved) : false;
  });
  // Which step this run is doing. A re-grade is grading only, an OCR run
  // is OCR only, and a normal check does both. Kept across a refresh so
  // the progress card can hide the step this run is skipping.
  const [activeRunMode, setActiveRunMode] = useState(() => {
    try {
      const saved = localStorage.getItem('assessmentRunMode');
      return saved === 'ocr_only' || saved === 'grading_only' || saved === 'full' ? saved : '';
    } catch {
      return '';
    }
  });
  const rememberRunMode = (mode) => {
    const next = mode === 'ocr_only' || mode === 'grading_only' ? mode : 'full';
    setActiveRunMode(next);
    try { localStorage.setItem('assessmentRunMode', next); } catch {}
  };
  const [isPausing, setIsPausing] = useState(false);
  const pauseRequestedRef = useRef(false);
  const publishAutoCheckNoticeRef = useRef(() => {});
  const clearAutoCheckNoticeRef = useRef(() => {});
  const autoCheckNoticeRef = useRef('');
  const [progress, setProgress] = useState(() => {
    return localStorage.getItem('assessmentProgress') || '';
  });
  const [analyzedData, setAnalyzedData] = useState([]);
  const [message, setMessage] = useState({ show: false, text: '', type: '' });
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem('assessmentSessionId') || null;
  });
  const [progressDetails, setProgressDetails] = useState(() => {
    const saved = localStorage.getItem('assessmentProgressDetails');
    return saved ? JSON.parse(saved) : null;
  });
  const [stepTimesByFolder, setStepTimesByFolder] = useState(readStepTimesMap);
  const [taskTimer, setTaskTimer] = useState(() => {
    let running = false;
    let paused = false;
    try { running = JSON.parse(localStorage.getItem('assessmentProcessing') || 'false'); } catch {}
    try {
      const details = JSON.parse(localStorage.getItem('assessmentProgressDetails') || 'null');
      paused = !!(details && (details.status === 'paused' || details.status === 'pausing'));
    } catch {}
    return readTaskTimer(!!running || paused);
  });
  const [autoRubricText, setAutoRubricText] = useState('');
  const [autoPdfRubric, setAutoPdfRubric] = useState(null);
  const [rubricLoading, setRubricLoading] = useState(false);
  // True only after this folder's rubric text has been stored. Until then the
  // repair must not move rows: a list from the previous folder drops every
  // question it does not contain.
  const [folderRubricReady, setFolderRubricReady] = useState(false);
  const [rubricMetadata, setRubricMetadata] = useState(null); // Store rubric class, subject, section info
  // Question IDs allowed in BLOCK A (from the active rubric). Used to drop /
  // merge phantom rows like Q3.iv when the rubric only has Q3.iii.
  const allowedQuestionIds = React.useMemo(() => {
    const body =
      autoRubricText ||
      rubricText ||
      (autoPdfRubric && autoPdfRubric.text) ||
      (pdfRubric && pdfRubric.text) ||
      '';
    return extractRubricQuestionIds(body);
  }, [autoRubricText, rubricText, autoPdfRubric, pdfRubric]);
  // Per-question max from the same rubric, so a mislabeled answer is scored
  // out of the question it actually matches (3-mark Q5 vs 5-mark Q7).
  const rubricQuestionMarks = React.useMemo(() => {
    const body =
      autoRubricText ||
      rubricText ||
      (autoPdfRubric && autoPdfRubric.text) ||
      (pdfRubric && pdfRubric.text) ||
      '';
    return extractRubricQuestionMarks(body);
  }, [autoRubricText, rubricText, autoPdfRubric, pdfRubric]);
  const rubricPaperTotal = React.useMemo(() => {
    const body =
      autoRubricText ||
      rubricText ||
      (autoPdfRubric && autoPdfRubric.text) ||
      (pdfRubric && pdfRubric.text) ||
      '';
    return paperTotalFromRubric(body);
  }, [autoRubricText, rubricText, autoPdfRubric, pdfRubric]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [commentRegradeIds, setCommentRegradeIds] = useState([]);
  const [pendingRegrade, setPendingRegrade] = useState(null);
  const [regradeQueue, setRegradeQueue] = useState(null);
  const regradeQueueRef = useRef(null);
  const regradeClassPinnedRef = useRef('');
  const regradeAdvanceRef = useRef(null);
  const finishQueuedRegradeRef = useRef(null);
  regradeQueueRef.current = regradeQueue;
  const [regradeVoice, setRegradeVoice] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyRecording, setReplyRecording] = useState(false);
  const [replyFile, setReplyFile] = useState(null);
  const [replyPreviewUrl, setReplyPreviewUrl] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyAction, setReplyAction] = useState('');
  const replyRecorderRef = useRef(null);
  const replyStreamRef = useRef(null);
  const replyChunksRef = useRef([]);
  const replyPreviewUrlRef = useRef('');
  const replyDiscardRef = useRef(false);
  const rubricFillKeyRef = useRef('');
  const rubricQuestions = React.useMemo(() => {
    const body =
      autoRubricText ||
      rubricText ||
      (autoPdfRubric && autoPdfRubric.text) ||
      (pdfRubric && pdfRubric.text) ||
      '';
    return listRubricQuestions(body);
  }, [autoRubricText, rubricText, autoPdfRubric, pdfRubric]);
  // The poll that finishes a check keeps the autoCorrect function from the
  // render when checking started. These refs are what that function reads, so
  // a rubric that arrives during the run is the list used when the run ends.
  const allowedQuestionIdsRef = useRef(allowedQuestionIds);
  const rubricQuestionMarksRef = useRef(rubricQuestionMarks);
  const rubricPaperTotalRef = useRef(rubricPaperTotal);
  const folderRubricReadyRef = useRef(false);
  // Folder whose rubric is currently in the refs. A new class must not
  // inherit the previous class's question list, and the previous class's
  // rows must not be repaired with the new list.
  const rubricFolderRef = useRef('');
  if (rubricFolderRef.current === folderPath) {
    allowedQuestionIdsRef.current = allowedQuestionIds;
    rubricQuestionMarksRef.current = rubricQuestionMarks;
    rubricPaperTotalRef.current = rubricPaperTotal;
    folderRubricReadyRef.current = folderRubricReady;
  } else {
    folderRubricReadyRef.current = false;
  }
  const emptyRepair = { allowedIds: new Set(), rubricMarks: new Map(), paperTotal: null };
  const captureRepair = (folder) => {
    if (!folder || rubricFolderRef.current !== folder || !folderRubricReadyRef.current) {
      return { allowedIds: new Set(), rubricMarks: new Map(), paperTotal: null };
    }
    return {
      allowedIds: new Set(allowedQuestionIdsRef.current || []),
      rubricMarks: new Map(rubricQuestionMarksRef.current || []),
      paperTotal: rubricPaperTotalRef.current
    };
  };
  const rubricMatchesOpenFolder = rubricFolderRef.current === folderPath && folderRubricReady;
  const screenRepair = rubricMatchesOpenFolder
    ? { allowedIds: allowedQuestionIds, rubricMarks: rubricQuestionMarks, paperTotal: rubricPaperTotal }
    : emptyRepair;
  const repairForResult = (result) => {
    const rowFolder = result && result.folder_path;
    if (!rubricMatchesOpenFolder) return emptyRepair;
    if (rowFolder && folderPath && rowFolder !== folderPath) return emptyRepair;
    return screenRepair;
  };
  const partialQuestionLabels = rubricQuestions
    .filter(q => selectedQuestionIds.includes(q.id))
    .map(q => q.label);
  useEffect(() => {
    setSelectedQuestionIds([]);
  }, [folderPath]);
  useEffect(() => {
    setSelectedQuestionIds(prev => {
      const allowed = new Set(rubricQuestions.map(q => q.id));
      const next = prev.filter(id => allowed.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [rubricQuestions]);
  const [assessmentResults, setAssessmentResults] = useState([]);
  // Reviewed sort for this folder. null until the snapshot request finishes.
  const [reviewedRows, setReviewedRows] = useState(null);
  // Image numbers the watcher added and deleted after this folder was sorted.
  // A checked student whose new numbers are on the current sort joins Late papers.
  const [sheetChanges, setSheetChanges] = useState({ added: [], deleted: [] });
  const [latePaperOpening, setLatePaperOpening] = useState('');
  const [latePaperFailure, setLatePaperFailure] = useState(null);
  const [selectedResults, setSelectedResults] = useState([]);
  const selectedMissingRows = assessmentResults.filter((row) => (
    selectedResults.includes(row.id) && parseMissingPages(row.ocr_missing_pages).length > 0
  ));
  const [approveForm, setApproveForm] = useState({
    class: '',
    section: '',
    subject: ''
  });
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [controlsCollapsed, setControlsCollapsed] = useState(
    () => localStorage.getItem('checkControlsCollapsed') === 'true'
  );
  const [studentLists, setStudentLists] = useState([]);
  const [approveLoading, setApproveLoading] = useState(false);
  const [selectedStudentList, setSelectedStudentList] = useState(null); // Single course combo for approve
  const [approveSearchInput, setApproveSearchInput] = useState(''); // Search input for the approve dropdown
  // ── Scans-folder picker (pre-approve step) ──────────────────────────────
  // The Approve flow now starts with a scans-folder picker (was previously
  // the "3. Ready to Print" button on StudentsTab). After the folder is
  // chosen, the existing student-list approve form opens. handleApprove then
  // POSTs to /students/approve AND /checked-results/finalize in sequence so
  // the comments land in StudentsTab AND the list is saved into the chosen
  // scans folder in one click.
  const [scansFoldersForApprove, setScansFoldersForApprove] = useState([]);
  const [classSwitchQuery, setClassSwitchQuery] = useState('');
  const [selectedScanFolderForApprove, setSelectedScanFolderForApprove] = useState(null); // folder id
  const [showScanFolderPicker, setShowScanFolderPicker] = useState(false);
  const [scanFolderSearchInput, setScanFolderSearchInput] = useState('');
  const [scanFolderSavePending, setScanFolderSavePending] = useState(false); // true while finalize POST is in flight
  const [viewingComment, setViewingComment] = useState(null);
  const [checkReloadToken, setCheckReloadToken] = useState(0);
  const [checkRefreshing, setCheckRefreshing] = useState(false);
  const commentRegradeQuestions = React.useMemo(() => {
    const fromTable = questionsInGradeTable(viewingComment && viewingComment.table_comments);
    if (fromTable.length) return fromTable;
    return rubricQuestions;
  }, [viewingComment && viewingComment.table_comments, rubricQuestions]);
  useEffect(() => {
    setCommentRegradeIds([]);
  }, [viewingComment && viewingComment.id]);
  // Separate modal for the Urdu OCR transcript (step-1 output). Keeping it
  // distinct from `viewingComment` so the user can have either open without
  // them fighting for the same modal slot.
  const [viewingOcr, setViewingOcr] = useState(null);
  const ocrFrameRef = useRef(null);
  const ocrGeomRef = useRef(null);
  const ocrDragMovedRef = useRef(false);
  const ocrGestureCleanupRef = useRef(null);
  const [ocrDiffOn, setOcrDiffOn] = useState(false);
  const [ocrCompareText, setOcrCompareText] = useState(null);
  const [ocrRunDeleting, setOcrRunDeleting] = useState(false);
  const [ocrSelection, setOcrSelection] = useState(null);
  const [ocrMarkSaving, setOcrMarkSaving] = useState(false);
  // A re-OCR deletes the row and inserts a new one. Keep the open transcript
  // window on that image so the new run appears beside the older ones.
  useEffect(() => {
    if (!viewingOcr) return;
    const byId = assessmentResults.find((row) => row && String(row.id) === String(viewingOcr.id));
    const byImage = assessmentResults.find((row) => row && row.image_name && row.image_name === viewingOcr.image_name);
    const next = keepAcceptedMarks(viewingOcr, byId || byImage);
    if (!next) return;
    if (next === viewingOcr) return;
    if (next.id === viewingOcr.id
      && (next.ocr_text == null || next.ocr_text === viewingOcr.ocr_text)
      && (next.ocr_history == null || next.ocr_history === viewingOcr.ocr_history)
      && next.ocr_model === viewingOcr.ocr_model) return;
    setViewingOcr(next.id === viewingOcr.id
      ? {
        ...next,
        ocr_text: next.ocr_text != null ? next.ocr_text : viewingOcr.ocr_text,
        ocr_history: next.ocr_history != null ? next.ocr_history : viewingOcr.ocr_history,
        ocr_chatter_ok: viewingOcr.ocr_chatter_ok != null ? viewingOcr.ocr_chatter_ok : next.ocr_chatter_ok,
        ocr_noise_notes: viewingOcr.ocr_noise_notes != null ? viewingOcr.ocr_noise_notes : next.ocr_noise_notes
      }
      : next);
  }, [assessmentResults, viewingOcr]);
  // A re-grade replaces the verification table. Keep this window on the same
  // student so the new marks show up without closing it.
  useEffect(() => {
    if (!viewingComment) return;
    const byId = assessmentResults.find((row) => row && String(row.id) === String(viewingComment.id));
    const byImage = assessmentResults.find((row) => row && row.image_name && row.image_name === viewingComment.image_name);
    const next = keepAcceptedMarks(viewingComment, byId || byImage);
    if (!next || next === viewingComment) return;
    const same = next.id === viewingComment.id
      && next.table_comments === viewingComment.table_comments
      && next.comments === viewingComment.comments
      && String(next.marks ?? '') === String(viewingComment.marks ?? '')
      && next.grading_model === viewingComment.grading_model
      && next.table_history === viewingComment.table_history
      && next.ocr_text === viewingComment.ocr_text
      && next.ocr_model === viewingComment.ocr_model
      && next.ocr_status === viewingComment.ocr_status;
    if (same) return;
    setViewingComment({
      ...next,
      own_scans: next.own_scans || viewingComment.own_scans,
      table_comments: next.table_comments != null ? next.table_comments : viewingComment.table_comments,
      comments: next.comments != null ? next.comments : viewingComment.comments,
      ocr_text: next.ocr_text != null ? next.ocr_text : viewingComment.ocr_text,
      ocr_history: next.ocr_history != null ? next.ocr_history : viewingComment.ocr_history
    });
  }, [assessmentResults, viewingComment]);
  useEffect(() => {
    const onResize = () => {
      if (!ocrGeomRef.current) return;
      const next = fitOcrFrame(ocrGeomRef.current);
      ocrGeomRef.current = next;
      const el = ocrFrameRef.current;
      if (!el) return;
      el.style.left = `${next.x}px`;
      el.style.top = `${next.y}px`;
      el.style.width = `${next.w}px`;
      el.style.height = `${next.h}px`;
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (ocrGestureCleanupRef.current) ocrGestureCleanupRef.current();
    };
  }, []);
  const paintOcrFrame = (frame) => {
    const next = fitOcrFrame(frame);
    ocrGeomRef.current = next;
    const el = ocrFrameRef.current;
    if (!el) return next;
    el.style.left = `${next.x}px`;
    el.style.top = `${next.y}px`;
    el.style.width = `${next.w}px`;
    el.style.height = `${next.h}px`;
    return next;
  };
  const beginOcrGesture = (kind, edge) => (event) => {
    if (event.button != null && event.button !== 0) return;
    if (kind === 'drag' && ocrPointerOnControl(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
    const current = ocrGeomRef.current || defaultOcrFrame();
    const start = {
      edge: edge || '',
      sx: event.clientX,
      sy: event.clientY,
      ox: current.x,
      oy: current.y,
      ow: current.w,
      oh: current.h
    };
    ocrDragMovedRef.current = false;
    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = kind === 'drag' ? 'grabbing' : ocrResizeCursor(edge);
    const onMove = (ev) => {
      const dx = ev.clientX - start.sx;
      const dy = ev.clientY - start.sy;
      if (Math.abs(dx) + Math.abs(dy) > 2) ocrDragMovedRef.current = true;
      paintOcrFrame(kind === 'drag' ? dragOcrFrame(start, dx, dy) : resizeOcrFrame(start, dx, dy));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      ocrGestureCleanupRef.current = null;
      if (ocrDragMovedRef.current) {
        setTimeout(() => { ocrDragMovedRef.current = false; }, 0);
      }
    };
    if (ocrGestureCleanupRef.current) ocrGestureCleanupRef.current();
    ocrGestureCleanupRef.current = onUp;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const restoreOcrFrame = (event) => {
    if (ocrPointerOnControl(event.target)) return;
    paintOcrFrame(defaultOcrFrame());
  };
  // The grid omits transcripts. Load this student's text when the window opens.
  useEffect(() => {
    if (!viewingOcr || viewingOcr.ocr_text != null) return undefined;
    const rubricMatch = String(folderPath || viewingOcr.folder_path || '').match(/rubrics\/(\d+)/);
    const studentName = String(viewingOcr.student_name || '').trim();
    if (!rubricMatch || !studentName) return undefined;
    const rowId = viewingOcr.id;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        const params = new URLSearchParams({ rubricId: rubricMatch[1], name: studentName, live: '1' });
        const res = await fetch(`${window.API_BASE_URL}/assess/student-ocr?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        const loaded = data && data.result ? data.result : null;
        setViewingOcr((prev) => {
          if (!prev || prev.id !== rowId || prev.ocr_text != null) return prev;
          return {
            ...prev,
            ocr_text: loaded && loaded.ocr_text != null ? loaded.ocr_text : '',
            ocr_history: loaded && loaded.ocr_history != null ? loaded.ocr_history : prev.ocr_history,
            ocr_status: (loaded && loaded.ocr_status) || prev.ocr_status,
            ocr_image_note: (loaded && loaded.ocr_image_note) || prev.ocr_image_note
          };
        });
      } catch { /* the window stays open with an empty transcript */ }
    })();
    return () => { cancelled = true; };
  }, [viewingOcr, folderPath]);

  // The Re-Grade chip opens the page with the students already known. The
  // voice file is loaded after the table, so the click does not wait on it.
  useEffect(() => {
    if (!regradeVoice || regradeVoice.audioUrl || regradeVoice.slot == null) return undefined;
    const match = String(regradeVoice.folderPath || '').match(/rubrics\/(\d+)/);
    if (!match) return undefined;
    const slot = regradeVoice.slot;
    const folderPathForVoice = regradeVoice.folderPath;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/analyze/folders/${match[1]}/regrade-requests/${slot}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok || !data.request) return;
        setRegradeVoice((prev) => {
          if (!prev || Number(prev.slot) !== Number(slot) || prev.folderPath !== folderPathForVoice || prev.audioUrl) return prev;
          return {
            ...prev,
            audioUrl: data.request.audio_url || '',
            note: data.request.note || prev.note,
            createdBy: data.request.created_by || prev.createdBy
          };
        });
      } catch { /* the banner stays up without the audio */ }
    })();
    return () => { cancelled = true; };
  }, [regradeVoice]);

  const [ocrPages, setOcrPages] = useState([]);
  const [pickedOcrPages, setPickedOcrPages] = useState([]);
  const [clearingOcrMissing, setClearingOcrMissing] = useState(false);
  const [focusedOcrPage, setFocusedOcrPage] = useState(null);
  // The scan opened beside the transcript. page is 1-based, matching the chips.
  const [ocrSideImage, setOcrSideImage] = useState(null);
  const [ocrSideImageLoading, setOcrSideImageLoading] = useState(false);
  const [ocrSideImageError, setOcrSideImageError] = useState('');
  const [ocrImageSaving, setOcrImageSaving] = useState(false);
  const [markingActualBlank, setMarkingActualBlank] = useState(false);
  // Blank-page numbers from the Sort-card click. They stay marked on the
  // chips until this window closes. 1-based, matching the chip numbers.
  const [blankChipPages, setBlankChipPages] = useState([]);
  // The flagged-student list from the Unexpected blank hover row. Save & Next
  // moves this cursor. The prop stays on the student who was clicked.
  const [quickCursor, setQuickCursor] = useState(null);
  const blankAdvancingRef = useRef(false);
  // Set once the Sort-card comments window has this student's row, so the
  // scan can open after folderPath is in state.
  const quickCommentPendingRef = useRef(false);
  const ocrImageReqRef = useRef(0);
  const ocrBlobRef = useRef('');
  // Page number (1-based) to open beside the transcript after a Sort-card click.
  const pendingOcrPageRef = useRef(0);
  // Student id whose problem page was already opened, so closing the scan
  // or looking at another page is not pulled back to the problem page.
  const openedOcrForRef = useRef(null);
  // Sort-card transcript: Close returns to Sort Papers instead of the Check table.
  const returnToSortRef = useRef(false);
  const scanNameCache = React.useRef(new Map());
  React.useEffect(() => {
    if (!viewingOcr) {
      setOcrPages([]);
      return undefined;
    }
    const folder = viewingOcr.folder_path || folderPath;
    const rubricMatch = String(folder || '').match(/rubrics\/(\d+)/);
    const rubricId = rubricMatch ? rubricMatch[1] : '';
    let cancelled = false;
    const apply = (names) => {
      if (!cancelled) setOcrPages(pagesForOcrRow(viewingOcr, analyzedData, names));
    };
    if (Array.isArray(viewingOcr.own_scans)) {
      apply([]);
      return undefined;
    }
    if (!rubricId) {
      apply([]);
      return undefined;
    }
    const cached = scanNameCache.current.get(rubricId);
    if (cached) {
      apply(cached);
      return undefined;
    }
    apply([]);
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/scans/${rubricId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !Array.isArray(data.scans)) {
          apply([]);
          return;
        }
        const names = data.scans.map((scan) => scan && scan.file_name).filter(Boolean);
        scanNameCache.current.set(rubricId, names);
        apply(names);
      } catch {
        apply([]);
      }
    })();
    return () => { cancelled = true; };
  }, [viewingOcr, analyzedData, folderPath]);
  React.useEffect(() => {
    if (!viewingComment) {
      setCommentPages([]);
      return undefined;
    }
    const folder = viewingComment.folder_path || folderPath;
    const rubricMatch = String(folder || '').match(/rubrics\/(\d+)/);
    const rubricId = rubricMatch ? rubricMatch[1] : '';
    let cancelled = false;
    const apply = (names) => {
      if (!cancelled) setCommentPages(pagesForOcrRow(viewingComment, analyzedData, names));
    };
    if (Array.isArray(viewingComment.own_scans)) {
      apply([]);
      return undefined;
    }
    if (!rubricId) {
      apply([]);
      return undefined;
    }
    const cached = scanNameCache.current.get(rubricId);
    if (cached) {
      apply(cached);
      return undefined;
    }
    apply([]);
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/scans/${rubricId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !Array.isArray(data.scans)) {
          apply([]);
          return;
        }
        const names = data.scans.map((scan) => scan && scan.file_name).filter(Boolean);
        scanNameCache.current.set(rubricId, names);
        apply(names);
      } catch {
        apply([]);
      }
    })();
    return () => { cancelled = true; };
  }, [viewingComment && viewingComment.id, viewingComment && viewingComment.image_name, viewingComment && viewingComment.own_scans, analyzedData, folderPath]);
  useEffect(() => {
    if (pendingOcrPageRef.current) return;
    setFocusedOcrPage(null);
    setOcrSideImage(null);
    setOcrSideImageError('');
    setOcrSideImageLoading(false);
    ocrImageReqRef.current += 1;
  }, [viewingOcr && viewingOcr.id]);
  // Once this student's scan list is ready, open the problem page. A click
  // that arrives before the scan names, or before the transcript text, waits
  // and then opens that image. Closing the image afterwards stays closed.
  useEffect(() => {
    if (!viewingOcr) {
      pendingOcrPageRef.current = 0;
      openedOcrForRef.current = null;
      return;
    }
    if (openedOcrForRef.current === viewingOcr.id) return;
    let page = pendingOcrPageRef.current;
    if (!page) {
      const failed = viewingOcr.ocr_status === 'unclear' || viewingOcr.ocr_status === 'failed'
        || /cut off/i.test(viewingOcr.comments || '');
      page = failed ? firstProblemPage(viewingOcr.comments, viewingOcr.ocr_text) : 0;
      if (page) pendingOcrPageRef.current = page;
    }
    if (!page) return;
    const folder = viewingOcr.folder_path || folderPath;
    const rubricMatch = String(folder || '').match(/rubrics\/(\d+)/);
    const rubricId = rubricMatch ? rubricMatch[1] : '';
    const isolated = Array.isArray(viewingOcr.own_scans);
    if (!isolated && rubricId && !scanNameCache.current.has(rubricId)) return;
    const expected = pagesForOcrRow(
      viewingOcr,
      analyzedData,
      rubricId ? (scanNameCache.current.get(rubricId) || []) : []
    );
    if (expected.length !== ocrPages.length) return;
    if (expected.some((name, index) => name !== ocrPages[index])) return;
    if (!ocrPages[page - 1]) return;
    openedOcrForRef.current = viewingOcr.id;
    pendingOcrPageRef.current = 0;
    loadOcrPageImage(page);
  }, [viewingOcr, ocrPages, folderPath, analyzedData]);
  useEffect(() => {
    if (!viewingOcr) {
      setPickedOcrPages([]);
      return;
    }
    const limit = ocrPages.length;
    setPickedOcrPages(
      parseMissingPages(viewingOcr.ocr_missing_pages).filter((n) => !limit || n <= limit)
    );
  }, [viewingOcr, ocrPages.length]);
  const releaseOcrBlob = () => {
    if (ocrBlobRef.current) {
      URL.revokeObjectURL(ocrBlobRef.current);
      ocrBlobRef.current = '';
    }
  };
  const closeOcrPageImage = () => {
    ocrImageReqRef.current += 1;
    releaseOcrBlob();
    setOcrSideImage(null);
    setOcrSideImageError('');
    setOcrSideImageLoading(false);
    setFocusedOcrPage(null);
  };
  const loadOcrPageImage = async (number, { toggle } = {}) => {
    const name = ocrPages[number - 1];
    if (!name || !viewingOcr) return;
    if (toggle && ocrSideImage && ocrSideImage.page === number) {
      closeOcrPageImage();
      return;
    }
    setFocusedOcrPage(number);
    const folder = viewingOcr.folder_path || folderPath;
    const rubricMatch = String(folder || '').match(/rubrics\/(\d+)/);
    const rubricId = rubricMatch ? rubricMatch[1] : '';
    const requestId = ++ocrImageReqRef.current;
    setOcrSideImageLoading(true);
    setOcrSideImageError('');
    releaseOcrBlob();
    setOcrSideImage({ page: number, name, url: '' });
    if (!rubricId) {
      if (ocrImageReqRef.current !== requestId) return;
      setOcrSideImageLoading(false);
      setOcrSideImageError('This folder has no scans to open.');
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      const rawResp = await fetch(
        `${API_BASE_URL}/scans/${rubricId}/preview/${encodeURIComponent(name)}/raw`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (ocrImageReqRef.current !== requestId) return;
      if (!rawResp.ok) {
        const data = await rawResp.json().catch(() => ({}));
        throw new Error((data && data.message) || 'Image not found');
      }
      const blob = await rawResp.blob();
      const url = URL.createObjectURL(blob);
      if (ocrImageReqRef.current !== requestId) {
        URL.revokeObjectURL(url);
        return;
      }
      if (ocrBlobRef.current) URL.revokeObjectURL(ocrBlobRef.current);
      ocrBlobRef.current = url;
      setOcrSideImage({ page: number, name, url });
    } catch (err) {
      if (ocrImageReqRef.current !== requestId) return;
      setOcrSideImageError(err.message || 'Could not open the image');
    } finally {
      if (ocrImageReqRef.current === requestId) setOcrSideImageLoading(false);
    }
  };
  const openOcrPageImage = (number) => loadOcrPageImage(number, { toggle: true });
  const saveOcrPageImage = async (blob) => {
    if (!viewingOcr || !ocrSideImage || !ocrSideImage.name || ocrImageSaving) return;
    const folder = viewingOcr.folder_path || folderPath;
    const rubricMatch = String(folder || '').match(/rubrics\/(\d+)/);
    const rubricId = rubricMatch ? rubricMatch[1] : '';
    if (!rubricId) {
      showMessage('This folder has no scans to update.', 'error');
      return;
    }
    const ok = window.confirm(`Replace ${ocrSideImage.name} with this white-pen edit? The stored scan is overwritten.`);
    if (!ok) return;
    setOcrImageSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const body = new FormData();
      body.append('file', blob, ocrSideImage.name);
      const res = await fetch(
        `${API_BASE_URL}/scans/${rubricId}/preview/${encodeURIComponent(ocrSideImage.name)}`,
        { method: 'PUT', headers: { Authorization: `Bearer ${token}` }, body }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error((data && data.message) || `HTTP ${res.status}`);
      showMessage('Scan image updated', 'success');
      await loadOcrPageImage(ocrSideImage.page);
    } catch (err) {
      showMessage('Could not update the scan: ' + err.message, 'error');
    } finally {
      setOcrImageSaving(false);
    }
  };
  const closeViewingOcr = () => {
    setBlankChipPages([]);
    setViewingOcr(null);
    if (quickTranscript) {
      const close = quickCloseRef.current;
      if (typeof close === 'function') close();
      return;
    }
    if (!returnToSortRef.current) return;
    returnToSortRef.current = false;
    navigate('/evaluate/sort');
  };
  const advanceBlankQueue = () => {
    const queue = quickCursor && Array.isArray(quickCursor.queue) ? quickCursor.queue : [];
    const index = queue.findIndex((student) => student && student.name === (quickCursor && quickCursor.name));
    const next = index >= 0 ? queue[index + 1] : null;
    if (!next) {
      closeViewingOcr();
      return;
    }
    blankAdvancingRef.current = true;
    setOcrSideImage(null);
    setFocusedOcrPage(null);
    setOcrSideImageError('');
    setViewingOcr(null);
    setBlankChipPages([]);
    setQuickOcrOpening(true);
    setQuickCursor((prev) => (prev ? {
      ...prev,
      name: next.name,
      pages: Array.isArray(next.pages) ? next.pages : [],
      page: (Array.isArray(next.pages) && next.pages[0]) || ''
    } : prev));
  };
  const acceptOpenChatter = async (page, localOk) => {
    if (!viewingOcr || !page) {
      throw new Error(!page ? 'Could not tell which page this warning is on.' : 'This transcript is not open.');
    }
    const token = localStorage.getItem('authToken');
    const fp = viewingOcr.folder_path || folderPath || '';
    // The window can be showing the saved snapshot while the live check row
    // belongs to another pass. Accept by folder and student so both copies
    // are cleared, and keep the key for the text on screen.
    const byStudent = !!(fp && viewingOcr.student_name);
    const res = await fetch(
      byStudent ? `${API_BASE_URL}/assess/snapshots/ocr` : `${API_BASE_URL}/assess/results/${viewingOcr.id}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(byStudent
          ? { folder_path: fp, student_name: viewingOcr.student_name, accept_chatter_page: page }
          : { accept_chatter_page: page })
      }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
    const merged = {
      ...parseChatterOk(data.ocr_chatter_ok),
      ...(localOk && typeof localOk === 'object' ? localOk : {})
    };
    const ocrChatterOk = JSON.stringify(merged);
    const apply = (row) => {
      if (!row) return row;
      const sameId = row.id === viewingOcr.id;
      const sameStudent = fp && row.folder_path === fp && row.student_name === viewingOcr.student_name;
      if (!sameId && !sameStudent) return row;
      return {
        ...row,
        ocr_chatter_ok: ocrChatterOk,
        ocr_noise_notes: data.ocr_noise_notes
      };
    };
    setAssessmentResults((prev) => prev.map(apply));
    setViewingOcr((prev) => (prev ? apply(prev) : prev));
    showMessage('Marked as the student\'s handwriting', 'success');
    if (typeof onChatterResolved === 'function') onChatterResolved();
  };
  const markOpenPageActualBlank = async () => {
    if (!viewingOcr || !ocrSideImage || markingActualBlank) return;
    const current = String(viewingOcr.ocr_text || '');
    const next = markPageActualBlank(current, ocrSideImage.page, ocrSideImage.name);
    if (next == null) return;
    setMarkingActualBlank(true);
    try {
      const token = localStorage.getItem('authToken');
      const fp = viewingOcr.folder_path || folderPath || '';
      const byStudent = !!(fp && viewingOcr.student_name);
      const send = (url, body) => fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      let res = await send(
        byStudent ? `${API_BASE_URL}/assess/snapshots/ocr` : `${API_BASE_URL}/assess/results/${viewingOcr.id}`,
        byStudent
          ? { folder_path: fp, student_name: viewingOcr.student_name, ocr_text: next }
          : { ocr_text: next, ocr_status: 'ok', ocr_unclear_ratio: null }
      );
      if (!res.ok && byStudent) {
        res = await send(`${API_BASE_URL}/assess/results/${viewingOcr.id}`, {
          ocr_text: next,
          ocr_status: 'ok',
          ocr_unclear_ratio: null
        });
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      const apply = (row) => {
        if (!row) return row;
        const sameId = row.id === viewingOcr.id;
        const sameStudent = fp && row.folder_path === fp && row.student_name === viewingOcr.student_name;
        if (!sameId && !sameStudent) return row;
        return {
          ...row,
          ocr_text: next,
          ocr_history: ocrHistoryAfterEdit(row.ocr_history, next),
          ocr_status: 'ok',
          ocr_unclear_ratio: null
        };
      };
      setAssessmentResults((prev) => prev.map(apply));
      setViewingOcr((prev) => (prev ? apply(prev) : prev));
      showMessage('Marked as actual blank', 'success');
      refreshUnexpectedBlankFolders();
      if (blankChipPages.length) {
        const savedPage = ocrSideImage.page;
        const upcoming = unexpectedBlankPages(next)
          .map((label) => {
            const match = String(label).match(/(\d+)/);
            return match ? Number(match[1]) : 0;
          })
          .filter((n) => n > savedPage)
          .sort((a, b) => a - b);
        if (upcoming.length) openOcrPageImage(upcoming[0]);
      }
    } catch (err) {
      showMessage('Could not mark this page: ' + err.message, 'error');
    } finally {
      setMarkingActualBlank(false);
    }
  };
  const [showAllComments, setShowAllComments] = useState(false);
  // "View All OCR" — sister modal to View All Comments. Lists every
  // assessment result's stored ocr_text + ocr_status side by side so the
  // teacher can scan transcripts without opening each row individually.
  // Only meaningful for two-step (English-2 / Urdu-2) runs that persist
  // ocr_text; legacy single-call rows show "(no transcript)".
  const [showAllOcr, setShowAllOcr] = useState(false);
  const allOcrFetchRef = useRef(false);
  // View All OCR needs every transcript. The grid open leaves them out.
  // One full read per open, including when the table arrives after the click.
  useEffect(() => {
    if (!showAllOcr) {
      allOcrFetchRef.current = false;
      return undefined;
    }
    if (allOcrFetchRef.current) return undefined;
    const missing = (assessmentResults || []).some((row) => row && row.ocr_text == null && row.ocr_status);
    if (!missing) return undefined;
    allOcrFetchRef.current = true;
    fetchAssessmentResults();
    return undefined;
  }, [showAllOcr, assessmentResults]);
  // Which view the "All Comments" modal is in: 'oneline' (default — the
  // readable summary that gets pasted into EduPage) or 'table' (the
  // markdown deduction table from BLOCK A). The two buttons at the top of
  // the modal flip between them; only one is rendered at a time.
  const [allCommentsView, setAllCommentsView] = useState('oneline');
  const [editingComment, setEditingComment] = useState(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentEditNote, setCommentEditNote] = useState('');
  // "Rubrics" side-pane inside the All Comments modal. When true, the loaded
  // rubric is shown in a fixed panel on the right (with its own close button)
  // so the teacher can read the marking guide next to the comments. Mutually
  // exclusive with the image preview's right pane (opening one closes the
  // other) to keep the split layout sane.
  const [showRubricPane, setShowRubricPane] = useState(false);
  // The single-student comments window can show the transcript on its own,
  // whether or not a scan is open. All Comments still ties the transcript to the scan.
  const [showCommentTranscript, setShowCommentTranscript] = useState(false);
  const [commentPages, setCommentPages] = useState([]);
  const [commentPickedPages, setCommentPickedPages] = useState([]);
  // Classes that still have a Late papers chip. The comments window shows
  // two names at a time. The same two boxes can switch to Re-grade: students
  // named on this class's re-grade requests, and classes that have any.
  const [latePaperFolders, setLatePaperFolders] = useState([]);
  const [latePaperFolderPage, setLatePaperFolderPage] = useState(0);
  const [paperListMode, setPaperListMode] = useState(() => (
    quickTranscript && quickTranscript.list === 'regrade' ? 'regrade' : 'late'
  ));
  const [regradeByFolder, setRegradeByFolder] = useState(null);
  const [regradeFolderPage, setRegradeFolderPage] = useState(0);
  const [regradeStudentOpening, setRegradeStudentOpening] = useState('');
  const [unexpectedBlankFolders, setUnexpectedBlankFolders] = useState([]);
  const [unexpectedBlankFolderPage, setUnexpectedBlankFolderPage] = useState(0);
  const [thinkingFolders, setThinkingFolders] = useState([]);
  const [thinkingFolderPage, setThinkingFolderPage] = useState(0);
  const [ocrListMode, setOcrListMode] = useState(() => {
    if (quickTranscript && quickTranscript.list === 'missing') return 'missing';
    if (quickTranscript && quickTranscript.list === 'thinking') return 'thinking';
    return 'blank';
  });
  const [ocrMissingFolderPage, setOcrMissingFolderPage] = useState(0);
  const [showOcrTranscriptPane, setShowOcrTranscriptPane] = useState(true);
  const [showOcrRubricPane, setShowOcrRubricPane] = useState(false);
  // The verification table shares the transcript window with the OCR runs.
  // Hiding it leaves the runs the full width. It stays mounted so an unsaved edit survives.
  const [showOcrVerificationTable, setShowOcrVerificationTable] = useState(true);
  const [ocrRegradeIds, setOcrRegradeIds] = useState([]);
  const [ocrCheckReloadToken, setOcrCheckReloadToken] = useState(0);
  const [transcriptStudentOpening, setTranscriptStudentOpening] = useState('');
  const ocrRegradeQuestions = React.useMemo(() => {
    const fromTable = questionsInGradeTable(viewingOcr && viewingOcr.table_comments);
    if (fromTable.length) return fromTable;
    return rubricQuestions;
  }, [viewingOcr && viewingOcr.table_comments, rubricQuestions]);
  useEffect(() => {
    setOcrRegradeIds([]);
  }, [viewingOcr && viewingOcr.id]);
  useEffect(() => {
    if (!viewingComment) {
      setCommentPickedPages([]);
      return;
    }
    const limit = commentPages.length;
    setCommentPickedPages(
      parseMissingPages(viewingComment.ocr_missing_pages).filter((n) => !limit || n <= limit)
    );
  }, [viewingComment && viewingComment.id, viewingComment && viewingComment.ocr_missing_pages, commentPages.length]);

  const refreshLatePaperFolders = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/assess/late-papers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return;
      setLatePaperFolders(Array.isArray(data.data) ? data.data : []);
    } catch {
      setLatePaperFolders([]);
    }
  };

  const refreshRegradeFolders = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/analyze/folder-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        setRegradeByFolder({});
        return;
      }
      const requests = data.requests && typeof data.requests === 'object' ? data.requests : {};
      setRegradeByFolder(requests);
    } catch {
      setRegradeByFolder({});
    }
  };

  useEffect(() => {
    if (!viewingComment) return undefined;
    refreshLatePaperFolders();
    refreshRegradeFolders();
    return undefined;
  // The lists are for the open comments window. Refresh when that window
  // opens and when the class changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingComment && viewingComment.id, folderPath]);

  useEffect(() => {
    if (!regradeVoice) return undefined;
    refreshRegradeFolders();
    return undefined;
  // The check page opened from a Re-Grade chip lists every class that has one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regradeVoice && regradeVoice.folderPath]);

  useEffect(() => {
    const onClear = (event) => {
      const detail = (event && event.detail) || {};
      if (!detail.folderId || detail.slot == null) return;
      setRegradeByFolder((prev) => {
        if (!prev) return prev;
        const key = String(detail.folderId);
        const list = prev[key];
        if (!Array.isArray(list)) return prev;
        return {
          ...prev,
          [key]: list.filter((row) => Number(row.slot) !== Number(detail.slot))
        };
      });
    };
    window.addEventListener(REGRADE_CLEARED_EVENT, onClear);
    return () => window.removeEventListener(REGRADE_CLEARED_EVENT, onClear);
  }, []);

  const refreshUnexpectedBlankFolders = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/assess/unexpected-blank-pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return;
      setUnexpectedBlankFolders(Array.isArray(data.data) ? data.data : []);
    } catch {
      setUnexpectedBlankFolders([]);
    }
  };

  const refreshThinkingFolders = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/assess/thinking-in-marks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return;
      setThinkingFolders(Array.isArray(data.data) ? data.data : []);
    } catch {
      setThinkingFolders([]);
    }
  };

  const noteThinkingAccepted = () => {
    refreshThinkingFolders();
    if (typeof onThinkingAccepted === 'function') onThinkingAccepted();
  };

  useEffect(() => {
    if (!viewingOcr) {
      setShowOcrTranscriptPane(true);
      setShowOcrRubricPane(false);
      return undefined;
    }
    refreshUnexpectedBlankFolders();
    refreshThinkingFolders();
    return undefined;
  // The class list is for the open transcript. Refresh when that window
  // opens and when the class changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingOcr && viewingOcr.id, folderPath]);
  // "Save Table" snapshot state. snapshotMeta carries the last saved-at
  // timestamp + record count when a snapshot exists for the currently
  // loaded folder; snapshotBusy gates the button while the request is in
  // flight. The snapshot lives in assessment_result_snapshots and is keyed
  // by `folder_path` (= the rubric path like "rubrics/123") so the Sort tab
  // can auto-restore it on snapshot import for the same folder.
  const [assessSnapshotMeta, setAssessSnapshotMeta] = useState(null);
  const [assessSnapshotBusy, setAssessSnapshotBusy] = useState(false);
  const [snapshotSaveKind, setSnapshotSaveKind] = useState(null);
  const [buttonHint, setButtonHint] = useState(null);
  const [savedTableKey, setSavedTableKey] = useState('');
  const [approvedTableKey, setApprovedTableKey] = useState('');
  const [tableKeysReady, setTableKeysReady] = useState(false);
  const tableKeyGenRef = useRef(0);
  // Rubric preview is collapsed by default — the title line tells the user
  // which rubric is loaded; clicking "Show" expands the marking guidelines.
  const [rubricPreviewExpanded, setRubricPreviewExpanded] = useState(false);
  // The status card shows as soon as this folder's rubric has loaded.
  // Show rubric only opens the marking-text preview.
  const [rubricVisible, setRubricVisible] = useState(false);
  const rubricLoadGenRef = useRef(0);
  // Login Edupage extension bridge — same pattern as AnalyzeTab (Sort).
  // Tracks whether content_bridge.js answered our presence ping.
  const [eduBridgeReady, setEduBridgeReady] = useState(false);
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [editingCell, setEditingCell] = useState(null); // {id, field, value}
  const [recheckingResultIds, setRecheckingResultIds] = useState(() => {
    const saved = localStorage.getItem('recheckingResultIds');
    return saved ? JSON.parse(saved) : [];
  });
  const [regradeSummary, setRegradeSummary] = useState(() => loadRegradeSummaryView());
  const [regradeSummaryOpen, setRegradeSummaryOpen] = useState(false);
  const [regradeHistory, setRegradeHistory] = useState([]);
  const [regradeHistoryOpen, setRegradeHistoryOpen] = useState(false);
  const [regradeHistoryLoading, setRegradeHistoryLoading] = useState(false);
  const [regradeHistoryScope, setRegradeHistoryScope] = useState('all');
  const [regradeSaveError, setRegradeSaveError] = useState('');
  const [regradeSort, setRegradeSort] = useState(null);
  const [regradeCopyNotice, setRegradeCopyNotice] = useState('');
  const [regradeCopying, setRegradeCopying] = useState(false);
  const regradeCardRef = useRef(null);
  // Mode for choosing which students to check: 'range' (predefined ranges)
  // or 'custom' (user hand-picks specific students from the Sorted list)
  const [selectionMode, setSelectionMode] = useState('range');
  const [selectedStudentCodes, setSelectedStudentCodes] = useState([]); // array of unique_code strings
  const [studentPickerSearch, setStudentPickerSearch] = useState('');
  const [infoDialog, setInfoDialog] = useState(null); // { title, message } | null
  // Rubric ids whose local folder and website/scans image counts disagree
  // (folder-server sort-block / "Upload incomplete"). Blocks Start Checking
  // the same way Sort Papers blocks Start Sorting.
  const [uploadMismatchIds, setUploadMismatchIds] = useState(() => new Set());
  const [uploadMismatchErrors, setUploadMismatchErrors] = useState(() => new Map());

  // Styled in-app confirm dialog (replaces native window.confirm so the
  // popup matches the rest of the FastGrader UI). `onConfirm` runs the
  // destructive action; `onCancel` (or clicking Cancel) just closes the
  // dialog. Buttons render the labels passed in via confirmLabel /
  // cancelLabel — defaults to "Confirm" / "Cancel".
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    text: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    onConfirm: null,
    onCancel: null
  });
  const showConfirm = (text, onConfirm, opts = {}) => {
    setConfirmDialog({
      show: true,
      text,
      confirmLabel: opts.confirmLabel || 'Confirm',
      cancelLabel: opts.cancelLabel || 'Cancel',
      onConfirm,
      onCancel: typeof opts.onCancel === 'function' ? opts.onCancel : null
    });
  };
  const closeConfirm = () => setConfirmDialog({
    show: false,
    text: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    onConfirm: null,
    onCancel: null
  });
  const handleConfirmYes = () => {
    const cb = confirmDialog.onConfirm;
    closeConfirm();
    if (typeof cb === 'function') cb();
  };
  // Cancel button: in addition to closing, run any onCancel callback the
  // caller passed in. Used by the pre-Start "delete existing results?"
  // dialog where Cancel means "keep them, continue anyway".
  const handleConfirmNo = () => {
    const cb = confirmDialog.onCancel;
    closeConfirm();
    if (typeof cb === 'function') cb();
  };

  const navigate = useNavigate();
  const location = useLocation();
  const autoOpenHandledRef = useRef(null);
  // Set by the Sort-tab "Re-grade" shortcut after the saved table is loaded.
  // A later effect waits for medium + rubric, then kicks Check Step 2.
  const [pendingAutoRegrade, setPendingAutoRegrade] = useState(null);
  // Result sheet name click: after this folder's table loads, open that
  // student's comments the same way the row's eye button does.
  const [pendingOpenComment, setPendingOpenComment] = useState(null);
  const [pendingOpenOcr, setPendingOpenOcr] = useState(null);
  const [quickOcrOpening, setQuickOcrOpening] = useState(false);
  const checkUsesLocal = isKnownLocalModel(ocrModel) || isKnownLocalModel(gradingModel);
  const [previewImage, setPreviewImage] = useState(null); // { images: [{url, name}], index: number }
  const [linkedQuestion, setLinkedQuestion] = useState(null);
  const checkOcrRef = useRef(null);
  const checkRubricRef = useRef(null);
  const [checkOcrDraft, setCheckOcrDraft] = useState('');
  const [checkOcrSaving, setCheckOcrSaving] = useState(false);
  const [checkOcrNote, setCheckOcrNote] = useState('');
  const checkOcrStudentRef = useRef(null);
  const checkOcrBaseRef = useRef('');
  const [checkRubricDraft, setCheckRubricDraft] = useState('');
  const [checkRubricSaving, setCheckRubricSaving] = useState(false);
  const [checkRubricReloading, setCheckRubricReloading] = useState(false);
  const [checkRubricNote, setCheckRubricNote] = useState('');
  const checkRubricBaseRef = useRef('');
  const checkRubricForceRef = useRef(false);
  const [previewLoading, setPreviewLoading] = useState(null); // filename being loaded
  const API_BASE_URL = window.API_BASE_URL;

  // The reviewed sort is the list a late paper is measured against. A folder
  // that is only saved, not reviewed, contributes no late papers.
  useEffect(() => {
    if (!folderPath) {
      setReviewedRows([]);
      return undefined;
    }
    let cancelled = false;
    setReviewedRows(null);
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(
          `${API_BASE_URL}/analyze/snapshots?folder_path=${encodeURIComponent(folderPath)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        setReviewedRows(response.ok && Array.isArray(data.data) ? data.data : []);
      } catch {
        if (!cancelled) setReviewedRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [folderPath, API_BASE_URL]);

  useEffect(() => {
    const match = String(folderPath || '').match(/rubrics\/(\d+)/);
    if (!match) {
      setSheetChanges({ added: [], deleted: [] });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/analyze/folders/image-deltas`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        const row = (data.data || []).find((item) => String(item.id) === match[1]);
        setSheetChanges({
          added: row && Array.isArray(row.added) ? row.added.map(String) : [],
          deleted: row && Array.isArray(row.deleted) ? row.deleted.map(String) : []
        });
      } catch {
        if (!cancelled) setSheetChanges({ added: [], deleted: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [folderPath, API_BASE_URL]);

  // Report a checking step to the Sort-tab Run/Save/Scans/Approve/Absent
  // boxes when the flow is driven BY HAND — so a manually checked folder
  // fills the same boxes an auto-checked one does. The auto-check engine
  // (ai1/ai2/ai3) reports its own steps with richer error context, so this
  // no-ops for those accounts to avoid duplicate/conflicting reports.
  // Best-effort telemetry: a failed report must never break the actual flow.
  const reportManualCheckStep = async (fp, step, status, error) => {
    if (!fp) return;
    if (AUTO_CHECK_USERS.includes(currentUsernameFromStorage())) return;
    try {
      const token = localStorage.getItem('authToken');
      await fetch(`${API_BASE_URL}/analyze/auto-check/step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          folder_path: fp, step, status,
          error: error ? String(error).slice(0, 500) : undefined
        })
      });
    } catch (_) { /* best-effort — never break the flow over a telemetry blip */ }
  };

  useEffect(() => {
    if (!previewImage || !previewImage.images) return;
    const onKey = (e) => {
      const tag = e.target && e.target.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return;
      if (e.key === 'Escape') {
        setPreviewImage(null);
      } else if (e.key === 'ArrowLeft') {
        setPreviewImage(p => (p && p.index > 0) ? { ...p, index: p.index - 1 } : p);
      } else if (e.key === 'ArrowRight') {
        setPreviewImage(p => (p && p.index < p.images.length - 1) ? { ...p, index: p.index + 1 } : p);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewImage]);

  const ocrPaneResult = showAllComments
    ? ((previewImage && previewImage.result) || null)
    : ((showCommentTranscript && viewingComment) || null);
  useEffect(() => {
    const result = ocrPaneResult;
    if (!result) return;
    const text = (result && result.ocr_text) || '';
    const id = result ? result.id : null;
    if (id !== checkOcrStudentRef.current) {
      checkOcrStudentRef.current = id;
      checkOcrBaseRef.current = text;
      setCheckOcrDraft(text);
      setCheckOcrNote('');
      return;
    }
    setCheckOcrDraft((current) => {
      const unchanged = current === checkOcrBaseRef.current;
      checkOcrBaseRef.current = text;
      return unchanged ? text : current;
    });
  }, [ocrPaneResult && ocrPaneResult.id, ocrPaneResult && ocrPaneResult.ocr_text]);

  // The results grid leaves ocr_text off the row. The transcript pane reads
  // that field, so load this student's transcript when the pane opens.
  useEffect(() => {
    const result = ocrPaneResult;
    if (!result || result.ocr_text != null) return undefined;
    if (!showAllComments && !viewingComment) return undefined;
    const rubricMatch = String(folderPath || result.folder_path || '').match(/rubrics\/(\d+)/);
    const studentName = String(result.student_name || '').trim();
    if (!rubricMatch || !studentName) return undefined;
    const rowId = result.id;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        const params = new URLSearchParams({
          rubricId: rubricMatch[1],
          name: studentName,
          live: '1'
        });
        const res = await fetch(`${API_BASE_URL}/assess/student-ocr?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        const loaded = data && data.result ? data.result : null;
        const patch = {
          ocr_text: loaded && loaded.ocr_text != null ? loaded.ocr_text : '',
          ocr_history: loaded && loaded.ocr_history != null ? loaded.ocr_history : result.ocr_history,
          ocr_status: (loaded && loaded.ocr_status) || result.ocr_status || (loaded && loaded.ocr_text ? 'ok' : result.ocr_status)
        };
        const apply = (row) => (
          row && row.id === rowId && row.ocr_text == null ? { ...row, ...patch } : row
        );
        setPreviewImage((prev) => {
          if (!prev || !prev.result || prev.result.id !== rowId || prev.result.ocr_text != null) return prev;
          return { ...prev, result: { ...prev.result, ...patch } };
        });
        setAssessmentResults((prev) => prev.map(apply));
        setViewingComment((prev) => apply(prev));
      } catch { /* the pane stays empty */ }
    })();
    return () => { cancelled = true; };
  }, [ocrPaneResult && ocrPaneResult.id, ocrPaneResult && ocrPaneResult.ocr_text, showAllComments, viewingComment, folderPath, API_BASE_URL]);

  useEffect(() => {
    const text = stripQuestionPaperText(autoRubricText || rubricText || '');
    setCheckRubricDraft((current) => {
      const force = checkRubricForceRef.current;
      checkRubricForceRef.current = false;
      const unchanged = force || current === checkRubricBaseRef.current;
      checkRubricBaseRef.current = text;
      return unchanged ? text : current;
    });
  }, [folderPath, autoRubricText, rubricText]);

  const saveCheckOcr = async () => {
    const result = ocrPaneResult;
    if (!result || checkOcrSaving) return;
    if (checkOcrDraft === (result.ocr_text || '')) return;
    setCheckOcrSaving(true);
    setCheckOcrNote('');
    try {
      const token = localStorage.getItem('authToken');
      const fp = folderPath || result.folder_path || '';
      const res = await fetch(
        fp && result.student_name
          ? `${API_BASE_URL}/assess/snapshots/ocr`
          : `${API_BASE_URL}/assess/results/${result.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(
            fp && result.student_name
              ? { folder_path: fp, student_name: result.student_name, ocr_text: checkOcrDraft }
              : { ocr_text: checkOcrDraft, ocr_status: 'ok', ocr_unclear_ratio: null }
          )
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Save failed (${res.status})`);
      const apply = (row) => (
        row && (row.id === result.id || (fp && row.folder_path === fp && row.student_name === result.student_name))
          ? { ...row, ocr_text: checkOcrDraft, ocr_status: 'ok', ocr_unclear_ratio: null }
          : row
      );
      setAssessmentResults((prev) => prev.map(apply));
      setPreviewImage((prev) => prev && prev.result ? { ...prev, result: apply(prev.result) } : prev);
      setViewingComment((prev) => apply(prev));
      setCheckOcrNote('Saved');
    } catch (err) {
      setCheckOcrNote(err.message || 'Save failed');
    } finally {
      setCheckOcrSaving(false);
    }
  };

  const saveCheckRubric = async () => {
    const match = String(folderPath || '').match(/rubrics\/(\d+)/);
    if (!match) {
      setCheckRubricNote('Open a folder before saving the rubric.');
      return;
    }
    const full = autoRubricText || rubricText || '';
    const saved = stripQuestionPaperText(full);
    if (checkRubricSaving || checkRubricDraft === saved) return;
    setCheckRubricSaving(true);
    setCheckRubricNote('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/rubrics/${match[1]}/content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: restorePaperNotes(checkRubricDraft, full) })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Save failed (${res.status})`);
      setAutoRubricText(restorePaperNotes(checkRubricDraft, full));
      setCheckRubricNote('Saved');
    } catch (err) {
      setCheckRubricNote(err.message || 'Save failed');
    } finally {
      setCheckRubricSaving(false);
    }
  };

  const reloadCheckRubric = async () => {
    if (checkRubricReloading || checkRubricSaving) return;
    if (!folderPath) {
      setCheckRubricNote('Open a folder before reloading the rubric.');
      return;
    }
    setCheckRubricReloading(true);
    setCheckRubricNote('');
    try {
      const loaded = await fetchRubricForFolder(folderPath, { quiet: true, preserve: true });
      if (!loaded) {
        checkRubricForceRef.current = false;
        setCheckRubricNote('Could not reload the rubric.');
        return;
      }
      const text = stripQuestionPaperText(loaded.text || '');
      checkRubricBaseRef.current = text;
      checkRubricForceRef.current = true;
      setCheckRubricDraft(text);
      const shown = stripQuestionPaperText(autoRubricText || rubricText || '');
      if (shown === text) checkRubricForceRef.current = false;
      setCheckRubricNote('Reloaded');
    } catch (err) {
      checkRubricForceRef.current = false;
      setCheckRubricNote(err.message || 'Could not reload the rubric.');
    } finally {
      setCheckRubricReloading(false);
    }
  };

  // Streams per-paper results mid-run by refetching whenever prog.completed grows
  const lastStreamedCountRef = useRef(0);
  const progressMissesRef = useRef(0);
  const loginWaitNotifiedRef = useRef(false);
  const concludeOnceRef = useRef(false);
  // Persist auto-correct history across re-fetches: { [id]: { from, details } }
  const autoCorrectHistoryRef = useRef({});
  // One reconcile at a time while grades are still arriving. A second poll
  // waits, so the same new sheet is not written twice.
  const reconcileChainRef = useRef(Promise.resolve());
  // id -> marks|name|table length already reconciled. A placeholder that
  // later receives a grade changes this and is corrected again.
  const reconciledSigRef = useRef(new Map());
  // Merge auto-correct history into a results array before setting state
  const applyAutoCorrectHistory = (results) => {
    const history = autoCorrectHistoryRef.current;
    if (Object.keys(history).length === 0) return results;
    return results.map(r => history[r.id] ? { ...r, _autoCorrect: history[r.id] } : r);
  };

  // The marks column is supposed to hold ONLY the obtained value (e.g. "10.4").
  // Some models return "10.4/20" — a fraction with the max bolted on. Strip the
  // trailing "/N" so the value displayed and submitted is the bare obtained
  // number, and tag the row with `_marksStripped` so the table can colour it
  // (pink) and surface the original string for the user.
  const MARKS_WITH_SLASH_RE = /^\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)\s*$/;
  const normalizeMarksOnResults = (results) => {
    if (!Array.isArray(results)) return results;
    return results.map(r => {
      const raw = r && r.marks != null ? String(r.marks) : '';
      const m = raw.match(MARKS_WITH_SLASH_RE);
      if (!m) return r;
      return { ...r, marks: m[1], _marksStripped: { from: raw.trim(), total: m[2] } };
    });
  };

  // Lock student names to the canonical roll/name from the Sort tab.
  // The AI sometimes outputs a different name than what the teacher set in
  // the sort tab; we treat the sort tab as source of truth and overwrite the
  // AI-supplied `student_name` whenever we can match a result back to its
  // analyzed row by image filename.
  const analyzedDataRef = useRef([]);
  useEffect(() => { analyzedDataRef.current = analyzedData; }, [analyzedData]);
  const folderPathRef = useRef(folderPath);
  useEffect(() => { folderPathRef.current = folderPath; }, [folderPath]);
  const resultsFetchGenRef = useRef(0);
  // Bumps on each folder open so a slow copy for folder A cannot paint over folder B.
  const savedTableSyncGenRef = useRef(0);
  // Auto-check and the Sort-tab auto-open already load the table themselves.
  // While this is set, a folderPath change must not copy the saved table again.
  const suppressSavedTableSyncRef = useRef(false);
  // Until the saved snapshot has been copied, do not paint the working table.
  // An earlier GET (polling, or a cached response) is the stale marks a refresh
  // otherwise replaces.
  const blockResultsUntilSyncRef = useRef(true);
  // After a saved table is copied in, show those marks as saved. Auto-correct
  // rebuilds the verification table against whatever rubric is already in
  // memory and was dropping Q.1 (Usman 10 → 3.8) before the folder rubric
  // was the one on screen.
  const preserveSavedMarksRef = useRef(false);

  const checkMoreResultsUrl = ({ omitOcr = true, ...extra } = {}) => {
    const params = new URLSearchParams({ source: 'check_more', ...extra });
    if (omitOcr) params.set('omit', 'ocr');
    const fp = folderPathRef.current || folderPath;
    if (fp) params.set('folder_path', fp);
    return `${API_BASE_URL}/assess/results?${params.toString()}`;
  };

  const lockNamesFromSort = (results, { persist = false } = {}) => {
    if (!Array.isArray(results) || results.length === 0) return results;
    const analyzed = analyzedDataRef.current || [];
    if (analyzed.length === 0) return results;

    // image filename → canonical { roll_name, unique_code } from sort tab.
    // A sort row may carry a comma-separated list of image_no values when
    // multiple pages were merged into one student, so we explode them.
    const byImage = new Map();
    for (const a of analyzed) {
      if (!a || !a.image_no || !a.roll_name) continue;
      const imgs = String(a.image_no).split(',').map(s => s.trim()).filter(Boolean);
      for (const img of imgs) {
        if (!byImage.has(img)) byImage.set(img, { roll_name: a.roll_name, unique_code: a.unique_code || '' });
      }
    }
    if (byImage.size === 0) return results;

    const toPersist = [];
    const corrected = results.map(r => {
      if (!r || !r.image_name) return r;
      const canonical = byImage.get(String(r.image_name).trim());
      if (!canonical) return r;
      if (r.student_name === canonical.roll_name) return r;
      if (persist && r.id) toPersist.push({ id: r.id, student_name: canonical.roll_name });
      return { ...r, student_name: canonical.roll_name };
    });

    if (persist && toPersist.length > 0) {
      const tkn = localStorage.getItem('authToken');
      // Fire and forget — these PUTs make the lock durable across refetches.
      // Failures are non-fatal; the in-memory state is already correct and
      // the lock will re-apply next time anyway.
      Promise.all(toPersist.map(p =>
        fetch(`${API_BASE_URL}/assess/results/${p.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tkn}` },
          body: JSON.stringify({ student_name: p.student_name })
        }).catch(err => console.warn(`[lockNamesFromSort] failed to persist id=${p.id}:`, err))
      ));
    }

    return corrected;
  };

  // Mid-run refresh. Each sheet that already has a grade is corrected here,
  // while later sheets are still being graded: the verification-table total
  // replaces a wrong model total, and a misread name is replaced from the
  // sorted list. Sheets that are not graded yet are left alone.
  const fetchAssessmentResultsLite = async () => {
    // Capture the generation. Do not bump it — a poll must not cancel the
    // post-import fetch that is the source of truth for this open.
    const gen = resultsFetchGenRef.current;
    const folder = folderPathRef.current;
    const leftThisFolder = () => gen !== resultsFetchGenRef.current
      || blockResultsUntilSyncRef.current
      || (folder && folderPathRef.current !== folder);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(checkMoreResultsUrl(), {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-store'
      });
      console.log('[fetchAssessmentResultsLite] status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('[fetchAssessmentResultsLite] payload:', { count: data && data.count, rows: Array.isArray(data && data.data) ? data.data.length : 'n/a', first: Array.isArray(data && data.data) ? data.data[0] : null });
        if (leftThisFolder()) return;
        const normalized = normalizeMarksOnResults(data.data || []);
        const repair = captureRepair(folder);
        const sigOf = (r) => `${r.marks}|${r.student_name}|${r.table_comments ? String(r.table_comments).length : 0}`;
        const job = reconcileChainRef.current.then(async () => {
          if (leftThisFolder()) return normalized;
          const pending = [];
          const pendingAt = [];
          normalized.forEach((r, i) => {
            if (!r || !r.id) return;
            if (reconciledSigRef.current.get(r.id) === sigOf(r)) return;
            pending.push(r);
            pendingAt.push(i);
          });
          let merged = normalized;
          if (pending.length) {
            const corrected = await autoCorrectResults(pending, {
              folder,
              repair,
              gen,
              isCancelled: leftThisFolder
            });
            if (leftThisFolder()) return normalized;
            merged = normalized.slice();
            corrected.forEach((row, j) => { merged[pendingAt[j]] = row; });
          }
          if (leftThisFolder()) return normalized;
          const locked = lockNamesFromSort(applyAutoCorrectHistory(merged), { persist: true });
          for (const row of locked) {
            if (row && row.id) reconciledSigRef.current.set(row.id, sigOf(row));
          }
          return locked;
        });
        reconcileChainRef.current = job.then(() => {}, () => {});
        const reconciled = await job;
        if (leftThisFolder()) return;
        setAssessmentResults(reconciled);
      } else {
        const errBody = await response.text().catch(() => '');
        console.warn('[fetchAssessmentResultsLite] non-ok response:', errBody.slice(0, 200));
      }
    } catch (error) {
      console.error('Error fetching assessment results (lite):', error);
    }
  };

  // Load analyzed folders and assessment results on mount
  useEffect(() => {
    // A paper opened on top of Sort Papers must not load the Check page.
    // The class chips in that window still need the folder list.
    if (quickTranscript) {
      fetchScansFoldersForApprove();
      return;
    }
    // If we arrived via the Sort tab's "Auto-Checked" badge, the auto-open
    // effect below loads that specific folder + its checked table. Skip the
    // most-recent auto-select and the plain results fetch so they don't race
    // with (or override) the requested folder.
    const arrivingViaAutoOpen = !!(location.state && location.state.autoOpenFolder);
    if (!arrivingViaAutoOpen) {
      fetchAnalyzedFolders();
    }
    fetchStudentLists();
    fetchScansFoldersForApprove();
    fetchUploadMismatchStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sort-tab Retest/Review wipes working tables then reloads snapshots.
  // Drop stale in-memory Check state immediately, then refetch after reload.
  useEffect(() => {
    const onClear = () => {
      setAssessmentResults([]);
      setAnalyzedData([]);
      setSelectedResults([]);
      setIsProcessing(false);
      setSessionId(null);
      setProgress('');
      setProgressDetails(null);
      setRecheckingResultIds([]);
    };
    const onReloaded = (e) => {
      const fp = e && e.detail && e.detail.folder_path;
      if (fp) {
        setFolderPath(fp);
        fetchAnalyzedData(fp);
        // Same folder is already selected, so the folderPath effect will not
        // run. Copy the saved table here. A new folder is copied by that effect.
        if (fp === folderPathRef.current && !suppressSavedTableSyncRef.current) {
          const gen = ++savedTableSyncGenRef.current;
          ++resultsFetchGenRef.current;
          blockResultsUntilSyncRef.current = true;
          preserveSavedMarksRef.current = true;
          setAssessmentResults([]);
          (async () => {
            const copied = await copyLatestSavedTable(fp);
            if (gen !== savedTableSyncGenRef.current) return;
            blockResultsUntilSyncRef.current = false;
            preserveSavedMarksRef.current = copied;
            await fetchAssessmentResults({ omitOcr: true });
          })();
        }
      } else if (!blockResultsUntilSyncRef.current) {
        fetchAssessmentResults({ omitOcr: true });
      }
    };
    window.addEventListener(CLEAR_WORKING_TABLES_EVENT, onClear);
    window.addEventListener(WORKING_TABLES_RELOADED_EVENT, onReloaded);
    return () => {
      window.removeEventListener(CLEAR_WORKING_TABLES_EVENT, onClear);
      window.removeEventListener(WORKING_TABLES_RELOADED_EVENT, onReloaded);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local ≠ website image counts — same signal Sort Papers uses for the red
  // "LOCAL ≠ WEB" card. Refreshed on load and whenever the selected folder
  // changes so a mismatch set on Sort Papers blocks checking here too.
  const fetchUploadMismatchStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const statusRes = await fetch(`${API_BASE_URL}/analyze/folders/auto-sort-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!statusRes.ok) return;
      const statusData = await statusRes.json();
      const mismatch = new Set();
      const errors = new Map();
      for (const row of (statusData.data || [])) {
        if (/^Upload incomplete/i.test(row.last_error || '')) {
          mismatch.add(String(row.id));
          errors.set(String(row.id), row.last_error);
        }
      }
      setUploadMismatchIds(mismatch);
      setUploadMismatchErrors(errors);
    } catch (err) {
      console.warn('[AssessmentTab] Failed to fetch upload-mismatch status:', err);
    }
  };

  useEffect(() => {
    fetchUploadMismatchStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPath]);

  useEffect(() => {
    if (!folderPath) return;
    if (suppressSavedTableSyncRef.current) {
      // Auto-check loads the next class itself. Drop the previous class's
      // rows so the rubric that arrives for the new folder cannot rewrite them.
      ++resultsFetchGenRef.current;
      setAssessmentResults([]);
      return;
    }
    const gen = ++savedTableSyncGenRef.current;
    // Drop any GET that started before this copy (polling, or the working
    // table from the last visit). Those rows are what a refresh used to fix.
    ++resultsFetchGenRef.current;
    blockResultsUntilSyncRef.current = true;
    preserveSavedMarksRef.current = true;
    setAssessmentResults([]);
    let cancelled = false;
    (async () => {
      const copied = await copyLatestSavedTable(folderPath);
      if (cancelled || gen !== savedTableSyncGenRef.current) return;
      blockResultsUntilSyncRef.current = false;
      preserveSavedMarksRef.current = !!copied;
      await fetchAssessmentResults({ omitOcr: true });
    })();
    return () => {
      cancelled = true;
      ++savedTableSyncGenRef.current;
      ++resultsFetchGenRef.current;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPath]);

  // Re-probe assessment snapshot meta whenever the active folder changes
  // (either via folderPath state or via the folder embedded in the first
  // assessment result). Keeps the "Save Table" button label in sync with
  // whether a snapshot already exists for the current folder.
  useEffect(() => {
    const fp = folderPath
      || (assessmentResults && assessmentResults[0] && assessmentResults[0].folder_path)
      || null;
    fetchAssessSnapshotMeta(fp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPath, assessmentResults.length]);

  const resultsFolderPath = (assessmentResults.find((row) => row.folder_path) || {}).folder_path || '';
  useEffect(() => {
    loadTableActionKeys(folderPath || resultsFolderPath || '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPath, resultsFolderPath]);
  const liveTableKey = tableActionKey(assessmentResults);
  const tableNeedsSave = tableKeysReady && assessmentResults.length > 0 && liveTableKey !== savedTableKey;
  const approveNeedsClick = tableKeysReady
    && assessmentResults.length > 0
    && !tableNeedsSave
    && !!savedTableKey
    && savedTableKey !== approvedTableKey;

  // Fetch scans folders for the pre-approve folder picker. Mirrors the call
  // StudentsTab used for the now-removed "3. Ready to Print" modal. Filters
  // out MCQ folders since this tab is subjective-only.
  const fetchScansFoldersForApprove = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/scans/folders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      const folders = (data.folders || []).filter(f => f.type !== 'mcq');
      folders.sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { numeric: true, sensitivity: 'base' }));
      setScansFoldersForApprove(folders);
    } catch (err) {
      console.error('Failed to fetch scans folders for approve:', err);
    }
  };

  // Fallback: if no sorted folder is auto-loaded but the global active rubric
  // is set (rubricText / pdfRubric props from AppContext), fetch its metadata
  // directly so the "(class - subject date)" label and teacher-email pill still
  // render. Without this the user sees the bare "Active Rubric Loaded" line.
  useEffect(() => {
    if (folderPath || rubricMetadata) return;
    if (!rubricText && !pdfRubric) return;
    const loadActiveRubricMetadata = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/rubrics/active`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.rubric) {
            setRubricMetadata({
              class: data.rubric.class || '',
              section: data.rubric.section || '',
              subject: data.rubric.subject || '',
              title: data.rubric.title || '',
              test_date: data.rubric.test_date || null,
              created_by: data.rubric.created_by || '',
              creator_email: data.rubric.creator_email || '',
              assignment_name: data.rubric.assignment_name || '',
              paper_type: data.rubric.paper_type || ''
            });
          }
        }
      } catch (err) {
        console.error('Error loading active rubric metadata:', err);
      }
    };
    loadActiveRubricMetadata();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPath, rubricText, pdfRubric, rubricMetadata]);

  // Opening a folder loads its rubric into the status card. The preview
  // stays collapsed until Show rubric is clicked.
  useEffect(() => {
    if (!folderPath) return undefined;
    rubricLoadGenRef.current += 1;
    setRubricPreviewExpanded(false);
    setRubricVisible(false);
    fetchRubricForFolder(folderPath);
    return undefined;
  // fetchRubricForFolder is recreated each render; the folder path is the trigger.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPath]);

  // The finish clocks live on the newest job. A later open of this folder
  // reads them back; a live poll overwrites them as each step goes idle.
  useEffect(() => {
    if (!folderPath) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(
          `${API_BASE_URL}/assess/step-clocks?folder_path=${encodeURIComponent(folderPath)}&source=check_more`,
          { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const clocks = data && data.stepTimes;
        if (!clocks || cancelled) return;
        setStepTimesByFolder((prev) => {
          const cur = prev[folderPath] || {};
          const next = {
            ...prev,
            [folderPath]: {
              ocrMs: cur.ocrMs != null ? cur.ocrMs : clocks.ocrMs,
              gradingMs: cur.gradingMs != null ? cur.gradingMs : clocks.gradingMs,
              ocrEndedAt: cur.ocrEndedAt || clocks.ocrEndedAt || null,
              gradingEndedAt: cur.gradingEndedAt || clocks.gradingEndedAt || null
            }
          };
          try { localStorage.setItem(STEP_TIMES_KEY, JSON.stringify(next)); } catch {}
          return next;
        });
      } catch (err) {
        console.warn('[Assessment] step clocks lookup failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [folderPath]);

  // ── Login Edupage extension bridge ──────────────────────────────────────
  // Header "Login" hands the status card to the Login Edupage extension
  // (matches creator email against its credential CSV). Page scripts can't
  // call chrome.* APIs, so we post a window message that content_bridge.js
  // relays to the background service worker.
  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.source !== window) return;
      const d = ev.data;
      if (!d || typeof d !== 'object' || d.source !== 'edupage-selector') return;
      if (d.type === 'EDU_BRIDGE_READY') {
        setEduBridgeReady(true);
      } else if (d.type === 'EDU_LOGIN_RESULT') {
        if (d.ok) {
          showMessage(`Opening EduPage for ${d.username || 'this account'}…`, 'success');
        } else {
          showMessage(
            d.reason === 'not_found'
              ? "EduPage login: this rubric's creator email isn't in the extension's credential list."
              : 'EduPage login failed — is the Login Edupage extension installed and updated?',
            'error'
          );
        }
      }
    };
    window.addEventListener('message', onMsg);
    window.postMessage({ source: 'fastgrader-edu', type: 'EDU_BRIDGE_PING' }, '*');
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist progress state to localStorage.
  // A transcript opened on Sort must not wipe the Check tab's saved session.
  useEffect(() => {
    if (quickTranscript) return;
    localStorage.setItem('assessmentProcessing', JSON.stringify(isProcessing));
  }, [isProcessing, quickTranscript]);

  useEffect(() => {
    if (quickTranscript) return;
    localStorage.setItem('assessmentProgress', progress);
  }, [progress, quickTranscript]);

  useEffect(() => {
    if (quickTranscript) return;
    if (sessionId) {
      localStorage.setItem('assessmentSessionId', sessionId);
    } else {
      localStorage.removeItem('assessmentSessionId');
    }
  }, [sessionId, quickTranscript]);

  useEffect(() => {
    if (quickTranscript) return;
    if (progressDetails) {
      localStorage.setItem('assessmentProgressDetails', JSON.stringify(progressDetails));
    } else {
      localStorage.removeItem('assessmentProgressDetails');
    }
  }, [progressDetails, quickTranscript]);

  // Persist recheckingResultIds to localStorage
  useEffect(() => {
    if (quickTranscript) return;
    if (recheckingResultIds.length > 0) {
      localStorage.setItem('recheckingResultIds', JSON.stringify(recheckingResultIds));
    } else {
      localStorage.removeItem('recheckingResultIds');
    }
  }, [recheckingResultIds, quickTranscript]);

  useEffect(() => {
    if (!regradeCopyNotice) return undefined;
    const timer = setTimeout(() => setRegradeCopyNotice(''), 2500);
    return () => clearTimeout(timer);
  }, [regradeCopyNotice]);

  useEffect(() => {
    setRegradeSort(null);
  }, [regradeSummary]);

  useEffect(() => {
    if (!regradeSummaryOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setRegradeSummaryOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [regradeSummaryOpen]);

  // Auto-populate search input with rubric class so dropdown shows filtered suggestions
  useEffect(() => {
    if (!rubricMetadata || studentLists.length === 0) return;
    // Only auto-populate if user hasn't already picked one
    if (selectedStudentList) return;

    const { class: cls, section, subject } = rubricMetadata;
    // Try exact match (class + section + subject) — auto-select it
    const exactMatch = studentLists.find(
      l => l.class === cls && l.section === section && l.subject === subject
    );
    if (exactMatch) {
      const exactCls = (exactMatch.class || '').trim();
      const exactSub = (exactMatch.subject || '').trim();
      const exactBase = exactCls && exactSub ? `${exactCls} - ${exactSub}` : (exactCls || exactSub);
      const option = {
        value: `${exactMatch.class}|${exactMatch.section}|${exactMatch.subject}`,
        label: `${exactBase} (${exactMatch.student_count} students)`,
        classVal: exactMatch.class,
        section: exactMatch.section,
        subject: exactMatch.subject
      };
      setSelectedStudentList(option);
      setApproveForm({ class: exactMatch.class, section: exactMatch.section, subject: exactMatch.subject });
      return;
    }
    // No exact match — pre-fill the search box with the full rubric name
    // (class + subject, without the date) so the dropdown shows the best matches
    // right away (e.g. "9A+9B - comp - computer-a").
    const rawCls = (cls || '').trim();
    const rawSub = (subject || '').trim();
    const searchVal = rawCls && rawSub
      ? `${rawCls} - ${rawSub}`
      : (rawCls || rawSub);
    if (searchVal) {
      setApproveSearchInput(searchVal);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rubricMetadata, studentLists]);

  // The checker worker keeps a queued/running job after the browser login
  // expires. This is how the page finds that job again.
  const lookupActiveCheck = async (fp) => {
    if (!fp) return null;
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    const activeRes = await fetch(
      `${API_BASE_URL}/assess/active-job?folder_path=${encodeURIComponent(fp)}&source=check_more`,
      { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }
    );
    if (!activeRes.ok) return null;
    const active = await activeRes.json();
    if (!active || !active.session_id) return null;
    return active;
  };

  const applyActiveCheck = (active) => {
    if (!active || !active.session_id) return;
    progressMissesRef.current = 0;
    loginWaitNotifiedRef.current = false;
    const prog = active.progress;
    if (prog) setProgressDetails(prog);
    if (prog && prog.currentStudent) {
      setProgress(`Processing ${prog.currentStudent} (${prog.completed}/${prog.total} completed)...`);
    } else {
      setProgress((prog && prog.message) || 'Reconnected — checking continues…');
    }
    setSessionId(active.session_id);
    setIsProcessing(true);
  };

  // Poll for progress updates
  useEffect(() => {
    if (!sessionId || !isProcessing) {
      console.log('[Assessment] Polling inactive - sessionId:', sessionId, 'isProcessing:', isProcessing);
      return;
    }

    console.log('[Assessment] Setting up polling for sessionId:', sessionId);
    progressMissesRef.current = 0;
    concludeOnceRef.current = false;
    let pollCancelled = false;

    // The progress card and the results table are different reads. When the
    // progress poll drops, the card keeps its last snapshot ("AI Processing")
    // even after every mark is already stored. The saved rows are enough to
    // end the check so auto-check can save the table and move on.
    const savedRowsCoverClass = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(checkMoreResultsUrl(), {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store'
        });
        if (!response.ok) return false;
        const data = await response.json();
        const rows = data.data || [];
        const codes = (analyzedDataRef.current || [])
          .map((row) => String(row && row.unique_code || '').trim().toLowerCase())
          .filter((code) => code && code !== '000');
        if (codes.length === 0) return false;
        const settled = new Set();
        for (const row of rows) {
          const name = String((row && row.student_name) || '');
          const fromName = name.match(/\(([^)]+)\)/);
          const code = String((row && row.unique_code) || (fromName && fromName[1]) || '').trim().toLowerCase();
          if (!code) continue;
          const marks = String(row && row.marks != null ? row.marks : '').trim();
          const status = String((row && row.status) || '').toLowerCase();
          const hasMarks = marks !== '' && marks.toUpperCase() !== 'ERROR' && marks !== '-';
          if (hasMarks || status === 'error') settled.add(code);
        }
        return codes.every((code) => settled.has(code));
      } catch (err) {
        console.warn('[Assessment] saved-table check failed:', err);
        return false;
      }
    };

    const dropStaleProgressSnapshot = () => {
      setProgressDetails((prev) => {
        if (!prev || (!prev.currentPhase && !prev.currentStudent)) return prev;
        return { ...prev, currentPhase: null, currentStudent: null };
      });
    };

    const concludeProcessing = async (completedCount) => {
      if (concludeOnceRef.current) return;
      concludeOnceRef.current = true;

      const savedRecheckIds = JSON.parse(localStorage.getItem('recheckingResultIds') || '[]');
      if (savedRecheckIds.length > 0) {
        console.log('[Recheck] Cleaning up old results:', savedRecheckIds);
        await deleteOldRecheckResults(savedRecheckIds);
        setRecheckingResultIds([]);
      }

      await reconcileChainRef.current.catch(() => {});
      let shownResults = await fetchAssessmentResults();

      if (rubricMetadata && shownResults && shownResults.length > 0) {
        const courseOnly = `${rubricMetadata.class}${rubricMetadata.section ? `-${rubricMetadata.section}` : ''} ${rubricMetadata.subject}`.trim();
        const dateStr = (rubricMetadata.test_date
          ? new Date(rubricMetadata.test_date)
          : new Date()
        ).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        const courseLabel = `${courseOnly} ${dateStr}`.trim();
        if (courseLabel) {
          const tkn = localStorage.getItem('authToken');
          const toUpdate = shownResults.filter(r => r.comments && !r.comments.includes(courseLabel));
          if (toUpdate.length > 0) {
            await Promise.all(toUpdate.map(r =>
              fetch(`${API_BASE_URL}/assess/results/${r.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${tkn}` },
                body: JSON.stringify({ comments: `${r.comments} | ${courseLabel}` })
              }).catch(err => console.error(`Failed to append course name to result ${r.id}:`, err))
            ));
            shownResults = await fetchAssessmentResults();
          }
        }
      }

      await publishRegradeSummary(shownResults, folderPath);

      const isRecheck = savedRecheckIds.length > 0;
      const count = Number(completedCount) || (shownResults && shownResults.length) || 0;
      lastStreamedCountRef.current = 0;
      setProgress(isRecheck ? 'Recheck completed! Old results replaced.' : 'Assessment completed! Review results below.');
      showMessage(
        isRecheck
          ? `Successfully rechecked ${count} student(s). Old results replaced with new ones.`
          : `Successfully assessed ${count} students. Review results and move to Tables tab.`,
        'success'
      );
      setProgressDetails(null);
      setIsProcessing(false);
      setSessionId(null);
      const finishedSlot = regradeAdvanceRef.current;
      regradeAdvanceRef.current = null;
      if (finishedSlot != null && finishQueuedRegradeRef.current) {
        await finishQueuedRegradeRef.current(shownResults, finishedSlot);
      }
      reportManualCheckStep(folderPath, 'run', 'done');
      forgetPausedFolder(folderPath);
      pauseRequestedRef.current = false;
      setIsPausing(false);
    };

    const finishIfSavedTableCoversClass = async () => {
      if (concludeOnceRef.current) return;
      if (progressMissesRef.current < 3) return;
      const covered = await savedRowsCoverClass();
      if (!covered || concludeOnceRef.current) return;
      console.log('[Assessment] Progress channel is down and every sheet is already saved — ending the check');
      const total = (analyzedDataRef.current || []).length;
      await concludeProcessing(total);
    };

    const pollProgress = async () => {
      try {
        const token = localStorage.getItem('authToken');
        console.log('[Assessment] Polling progress for session:', sessionId);
        const response = await fetch(`${API_BASE_URL}/assess/progress/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (pollCancelled) return;
        if (response.ok) {
          progressMissesRef.current = 0;
          loginWaitNotifiedRef.current = false;
          const data = await response.json();
          const prog = data.progress;
          if (pollCancelled || !prog) return;
          console.log('[Assessment] Progress update:', prog);
          setProgressDetails(prog);
          if (Number(prog.startedAt) > 0) {
            const serverStart = Number(prog.startedAt);
            setTaskTimer((prev) => {
              if (!prev || prev.endedAt) return prev;
              if (prev.sessionId && prev.sessionId !== sessionId) return prev;
              const startedAt = Math.min(prev.startedAt, serverStart);
              if (prev.sessionId === sessionId && startedAt === prev.startedAt) return prev;
              const next = { ...prev, startedAt, sessionId };
              writeTaskTimer(next);
              return next;
            });
          }
          if (folderPath && prog.stepTimes && (prog.stepTimes.ocrMs != null || prog.stepTimes.gradingMs != null)) {
            setStepTimesByFolder((prev) => {
              const next = {
                ...prev,
                [folderPath]: {
                  ocrMs: prog.stepTimes.ocrMs,
                  gradingMs: prog.stepTimes.gradingMs,
                  ocrEndedAt: finishedStepAt(prog.stepTimes.ocr),
                  gradingEndedAt: finishedStepAt(prog.stepTimes.grading)
                }
              };
              try { localStorage.setItem(STEP_TIMES_KEY, JSON.stringify(next)); } catch {}
              return next;
            });
          }

          if (prog.status === 'completed') {
            console.log('[Assessment] Status is COMPLETED - prog.completed:', prog.completed);
            await concludeProcessing(prog.completed);
          } else if (prog.status === 'pausing') {
            setIsPausing(true);
            setProgress(prog.message || 'Pausing — finishing the sheet already with the model…');
            lastStreamedCountRef.current = Number(prog.completed) || 0;
            fetchAssessmentResultsLite();
          } else if (prog.status === 'paused') {
            regradeAdvanceRef.current = null;
            setProgress('Paused. Click Resume to continue from the next sheet that has no marks.');
            showMessage('Paused. Saved work is kept. Click Resume to continue.', 'info');
            setIsProcessing(false);
            setIsPausing(false);
            setSessionId(null);
            setRecheckingResultIds([]);
            lastStreamedCountRef.current = 0;
            localStorage.removeItem('assessmentProcessing');
            localStorage.removeItem('assessmentSessionId');
            rememberPausedFolder(folderPath);
            clearRegradeSnapshot();
            await fetchAssessmentResults();
          } else if (prog.status === 'failed') {
            regradeAdvanceRef.current = null;
            setProgress('');
            showMessage(prog.error || 'Assessment failed', 'error');
            setIsProcessing(false);
            setSessionId(null);
            setRecheckingResultIds([]);
            lastStreamedCountRef.current = 0;
            clearRegradeSnapshot();
            reportManualCheckStep(folderPath, 'run', 'error', prog.error || 'Assessment failed');
          } else if (prog.status === 'cancelled') {
            regradeAdvanceRef.current = null;
            setProgress('Assessment cancelled');
            showMessage('Assessment was cancelled', 'info');
            setIsProcessing(false);
            setIsPausing(false);
            pauseRequestedRef.current = false;
            setSessionId(null);
            setRecheckingResultIds([]);
            lastStreamedCountRef.current = 0;
            localStorage.removeItem('assessmentProcessing');
            localStorage.removeItem('assessmentSessionId');
            clearRegradeSnapshot();
            await fetchAssessmentResults(); // Refresh to show partial results
          } else if (prog.status === 'processing' || prog.status === 'started' || prog.status === 'queued') {
            if (prog.currentStudent) {
              setProgress(`Processing ${prog.currentStudent} (${prog.completed}/${prog.total} completed)...`);
            } else {
              setProgress(prog.message || `Processing... ${prog.completed || 0}/${prog.total || 0} completed`);
            }
            // Stream: refresh the table from the DB on every poll while
            // processing is in flight. Always refetch — after an API restart
            // `completed` can briefly read as 0 even though rows already exist.
            lastStreamedCountRef.current = Number(prog.completed) || 0;
            fetchAssessmentResultsLite();
          }
        } else {
          let errorMessage = 'Unable to fetch assessment progress';
          try {
            const errData = await response.json();
            if (errData?.message) errorMessage = errData.message;
          } catch (_) {
            // Ignore JSON parse errors for non-JSON responses
          }

          if (response.status === 404) {
            // API restart: the job lives in the checker worker + DB. Re-attach
            // to the folder's active job, then retry the original session id
            // for ~40s before treating it as gone.
            progressMissesRef.current += 1;
            if (progressMissesRef.current === 1) {
              setProgress('Reconnecting to server — checking continues in the background…');
            }
            try {
              const active = await lookupActiveCheck(folderPath);
              if (active && active.session_id && active.session_id !== sessionId) {
                console.log('[Assessment] Re-attached to in-flight check', active.session_id);
                applyActiveCheck(active);
                fetchAssessmentResultsLite();
                return;
              }
            } catch (attachErr) {
              console.warn('[Assessment] active-job lookup failed:', attachErr);
            }
            dropStaleProgressSnapshot();
            if (progressMissesRef.current < 20) {
              console.warn('[Assessment] Progress 404 (API restart?) — retry', progressMissesRef.current);
              await finishIfSavedTableCoversClass();
              return;
            }
            setIsProcessing(false);
            setSessionId(null);
            setProgress('');
            setProgressDetails(null);
            localStorage.removeItem('assessmentProcessing');
            localStorage.removeItem('assessmentSessionId');
            localStorage.removeItem('assessmentProgress');
            localStorage.removeItem('assessmentProgressDetails');
            showMessage('Assessment session expired. Please start again.', 'error');
          } else if (response.status === 401) {
            // The login token died. The worker is still grading this session.
            // During auto-check, renew the token in place. If it is already
            // dead, open login once; the next sign-in returns to this page.
            if (autoCheckIsOn()) {
              const renewed = await refreshAuthToken();
              if (pollCancelled) return;
              if (renewed) {
                loginWaitNotifiedRef.current = false;
                return;
              }
              if (!loginWaitNotifiedRef.current) {
                loginWaitNotifiedRef.current = true;
                setProgress('Login expired. Sign in again — Check will reopen on this run.');
                showMessage('Login expired. Sign in again. Check will reopen and reconnect.', 'error');
                requestLoginAfterExpiry();
              }
              return;
            }
            if (!loginWaitNotifiedRef.current) {
              loginWaitNotifiedRef.current = true;
              setProgress('Login expired — checking continues in the background. Sign in again to restore live progress.');
              showMessage('Login expired. Checking continues. Sign in again and this page will reconnect.', 'error');
            }
            try {
              const active = await lookupActiveCheck(folderPath);
              if (active && active.session_id && active.session_id !== sessionId) {
                console.log('[Assessment] Re-attached after login expiry', active.session_id);
                applyActiveCheck(active);
                fetchAssessmentResultsLite();
              }
            } catch (attachErr) {
              console.warn('[Assessment] active-job lookup after 401 failed:', attachErr);
            }
          } else if (response.status === 403) {
            setIsProcessing(false);
            setSessionId(null);
            setProgress('');
            setProgressDetails(null);
            localStorage.removeItem('assessmentProcessing');
            localStorage.removeItem('assessmentSessionId');
            localStorage.removeItem('assessmentProgress');
            localStorage.removeItem('assessmentProgressDetails');
            showMessage('Session unauthorized. Please login again.', 'error');
          } else {
            console.error('[Assessment] Progress polling failed:', response.status, errorMessage);
          }
        }
      } catch (error) {
        // Network blip while the API restarts — keep polling; the worker is
        // still grading.
        progressMissesRef.current += 1;
        if (progressMissesRef.current === 1) {
          setProgress('Reconnecting to server — checking continues in the background…');
        }
        dropStaleProgressSnapshot();
        await finishIfSavedTableCoversClass();
        console.error('Error polling progress:', error);
      }
    };

    // Poll every 2 seconds
    const interval = setInterval(pollProgress, 2000);
    pollProgress(); // Initial poll

    return () => {
      pollCancelled = true;
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isProcessing]);

  // After a re-login the Check page mounts with no live poll. If this folder
  // still has a queued or running job, attach to it and start polling.
  useEffect(() => {
    if (!folderPath || isProcessing) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const active = await lookupActiveCheck(folderPath);
        if (cancelled || !active) return;
        console.log('[Assessment] Re-attached to running check for this folder', active.session_id);
        applyActiveCheck(active);
        setAutoCheckStatus('Reconnected — this class is still being checked.');
        fetchAssessmentResultsLite();
      } catch (err) {
        console.warn('[Assessment] active-job lookup on open failed:', err);
      }
    })();
    return () => { cancelled = true; };
  // lookupActiveCheck and applyActiveCheck are recreated each render.
  // The folder and the processing flag are the triggers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPath, isProcessing]);

  const fetchAnalyzedFolders = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/analyze/folders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data && data.data.length > 0) {
          // Auto-select the most recent folder
          setFolderPath(data.data[0].folder_path);
          fetchAnalyzedData(data.data[0].folder_path);
        }
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const fetchAnalyzedData = async (path) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/analyze/results?folder_path=${encodeURIComponent(path)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalyzedData(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching analyzed data:', error);
    }
  };

  const fetchRubricForFolder = async (path, { quiet = false, preserve = false } = {}) => {
    const match = String(path || '').match(/rubrics\/(\d+)/);
    if (!match) {
      console.log('[Assessment] Could not extract rubric ID from path:', path);
      return null;
    }

    const rubricId = match[1];
    const gen = rubricLoadGenRef.current;
    if (!preserve) {
      rubricFolderRef.current = '';
      folderRubricReadyRef.current = false;
      setFolderRubricReady(false);
    }
    setRubricLoading(true);
    let cardShown = false;
    const applyCard = (rubric) => {
      setRubricMetadata({
        class: rubric.class || '',
        section: rubric.section || '',
        subject: rubric.subject || '',
        title: rubric.title || '',
        test_date: rubric.test_date || null,
        created_by: rubric.created_by || '',
        creator_email: rubric.creator_email || '',
        assignment_name: rubric.assignment_name || '',
        paper_type: rubric.paper_type || ''
      });
      setRubricVisible(true);
    };
    try {
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };
      const cardRes = await fetch(`${API_BASE_URL}/rubrics/${rubricId}?omit=content`, { headers });
      if (gen !== rubricLoadGenRef.current) return null;
      if (!cardRes.ok) {
        console.log('[Assessment] Rubric not found for ID:', rubricId);
        if (!preserve) {
          setAutoRubricText('');
          setAutoPdfRubric(null);
          setRubricMetadata(null);
        }
        if (!quiet) showMessage('Could not load the rubric for this folder.', 'error');
        return null;
      }
      const cardData = await cardRes.json();
      if (!cardData.rubric || gen !== rubricLoadGenRef.current) return null;
      applyCard(cardData.rubric);
      cardShown = true;
      if (gen === rubricLoadGenRef.current) setRubricLoading(false);

      const response = await fetch(`${API_BASE_URL}/rubrics/${rubricId}`, { headers });
      if (gen !== rubricLoadGenRef.current) return null;
      if (!response.ok) return null;
      const data = await response.json();
      const rubric = data.rubric;
      if (!rubric || gen !== rubricLoadGenRef.current) return null;

      let pdf = null;
      if (rubric.issues_json) {
        try {
          const issues = JSON.parse(rubric.issues_json);
          if (issues.pages && issues.pages.length > 0) {
            pdf = { pages: issues.pages, text: rubric.content || '' };
          }
        } catch (e) {
          console.error('[Assessment] Error parsing rubric JSON:', e);
        }
      }
      const text = rubric.content || '';
      allowedQuestionIdsRef.current = extractRubricQuestionIds(text);
      rubricQuestionMarksRef.current = extractRubricQuestionMarks(text);
      rubricPaperTotalRef.current = paperTotalFromRubric(text);
      rubricFolderRef.current = path;
      folderRubricReadyRef.current = true;
      setAutoRubricText(text);
      setAutoPdfRubric(pdf);
      setFolderRubricReady(true);
      applyCard(rubric);
      return { text, pdf };
    } catch (error) {
      console.error('Error fetching rubric:', error);
      if (gen !== rubricLoadGenRef.current) return null;
      if (!cardShown && !preserve) {
        setAutoRubricText('');
        setAutoPdfRubric(null);
        setRubricMetadata(null);
        if (!quiet) showMessage('Could not load the rubric for this folder.', 'error');
      }
      return null;
    } finally {
      if (gen === rubricLoadGenRef.current) setRubricLoading(false);
    }
  };

  const showRubricPreview = async () => {
    if (rubricVisible && rubricPreviewExpanded) {
      setRubricPreviewExpanded(false);
      return;
    }
    if (!autoRubricText && !(autoPdfRubric && autoPdfRubric.text)) {
      const loaded = await fetchRubricForFolder(folderPath);
      const gotFolder = loaded && (loaded.text || (loaded.pdf && loaded.pdf.text));
      if (!gotFolder && !rubricText && !(pdfRubric && pdfRubric.text)) return;
    }
    setRubricVisible(true);
    setRubricPreviewExpanded(true);
  };

  const toggleRubricPane = async () => {
    if (showRubricPane) {
      setShowRubricPane(false);
      return;
    }
    if (!autoRubricText && !(autoPdfRubric && autoPdfRubric.text)) {
      const loaded = await fetchRubricForFolder(folderPath);
      const gotFolder = loaded && (loaded.text || (loaded.pdf && loaded.pdf.text));
      if (!gotFolder && !rubricText && !(pdfRubric && pdfRubric.text)) return;
    }
    setRubricVisible(true);
    setShowRubricPane(true);
  };

  const handlePauseProcessing = async () => {
    const token = localStorage.getItem('authToken');
    const sid = sessionId;
    pauseRequestedRef.current = true;
    setIsPausing(true);
    rememberPausedFolder(folderPath);
    try {
      const response = await fetch(
        sid
          ? `${API_BASE_URL}/assess/pause/${sid}`
          : `${API_BASE_URL}/assess/pause-active`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        showMessage(
          data.message || 'Pausing — the sheet already with the model will be saved. Resume continues the rest.',
          'info'
        );
        setProgress('Pausing — finishing the sheet already with the model…');
      } else {
        pauseRequestedRef.current = false;
        setIsPausing(false);
        showMessage(data.message || 'Failed to pause', 'error');
      }
    } catch (error) {
      pauseRequestedRef.current = false;
      setIsPausing(false);
      showMessage('Failed to pause: ' + error.message, 'error');
    }
  };

  const handleStopProcessing = async ({ releaseAutoCheck = false } = {}) => {
    const token = localStorage.getItem('authToken');
    const sid = sessionId;
    try {
      if (sid) {
        await fetch(`${API_BASE_URL}/assess/cancel/${sid}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      // Always cancel leftover queued/running jobs for this account. Stop
      // Auto-Check used to only pause the browser engine, leaving a backend
      // job that kept this account locked ("already checking another folder").
      const response = await fetch(`${API_BASE_URL}/assess/cancel-active`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (releaseAutoCheck) {
        const stopRes = await fetch(`${API_BASE_URL}/analyze/auto-check/stop`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const stopData = await stopRes.json().catch(() => ({}));
        const titles = Array.isArray(stopData.titles) ? stopData.titles.filter(Boolean) : [];
        if (titles.length) {
          publishAutoCheckNoticeRef.current(
            titles.map((title) => `Stopped "${title}": Stopped by user.`).join(' ')
          );
        }
      }
      if (response.ok) {
        const n = Number(data.cancelled) || 0;
        showMessage(
          n > 0 || sid
            ? 'Checking stopped for this account.'
            : 'No active check was running for this account.',
          'info'
        );
      } else {
        showMessage(data.message || 'Failed to stop assessment', 'error');
      }
    } catch (error) {
      console.error('Error stopping assessment:', error);
      const reason = autoCheckFailureReason(error, false);
      const line = reason === INTERNET_ERROR_MESSAGE ? reason : `Failed to stop assessment: ${reason}`;
      publishAutoCheckNoticeRef.current(line);
      showMessage(line, 'error');
    } finally {
      // Clear frontend state regardless of backend response
      setIsProcessing(false);
      setSessionId(null);
      setProgress('');
      setProgressDetails(null);
      setRecheckingResultIds([]);
      localStorage.removeItem('assessmentProcessing');
      localStorage.removeItem('assessmentSessionId');
      localStorage.removeItem('assessmentProgress');
      localStorage.removeItem('assessmentProgressDetails');
    }
  };

  // Hard gate: every "start an AI run" entry point (Start Checking, OCR-only,
  // Edupage "Copy & Start", Recheck) calls this BEFORE anything else. If no
  // Medium is picked we pop the blocking infoDialog and return false so the
  // caller bails out — no Edupage alert, no confirm dialogs, no AI request.
  const requireMediumOrPrompt = () => {
    if (subjectType !== 'none') return true;
    setInfoDialog({
      title: 'Medium is required',
      message: 'Please select a Medium (English-2 or Urdu-2) before starting. The check cannot proceed without one.'
    });
    return false;
  };

  // ocrOnlyRun=true runs ONLY step 1 of the two-step pipeline (no grading).
  // The new rows land with the OCR transcript saved and an empty marks
  // column; the teacher can review the transcript, then click "Re-grade"
  // (which uses recheck_mode='grading_only' against the stored ocr_text)
  // when they're happy with the captured text.
  // Only meaningful when pipelineMode === 'two_step'; the button is hidden
  // for single-call runs because there's no separate OCR step to skip.
  const handleStartAssessment = async ({ ocrOnlyRun = false, dispatchAuto = false, resume = false, ocrModelOverride = null, ocrProviderOverride = null, providerOverride = null, gradingModelOverride = null } = {}) => {
    preserveSavedMarksRef.current = false;
    if (!folderPath) {
      showMessage('Please select a folder first', 'error');
      return;
    }

    // Local folder vs website/scans image counts disagree — refuse to check
    // until Upload on Sort Papers brings them back in sync.
    const mismatchId = String(folderPath).match(/rubrics\/(\d+)/)?.[1];
    if (mismatchId && uploadMismatchIds.has(mismatchId)) {
      setInfoDialog({
        title: 'Local ≠ website image counts',
        message: (uploadMismatchErrors.get(mismatchId)
          || 'The local folder and the website/scans folder do not have the same number of images.')
          + ' Go to Sort Papers, click Upload on the folder card to sync them, then try checking again.'
      });
      return;
    }

    if (subjectType === 'none') {
      setInfoDialog({
        title: 'Please select a Medium',
        message: 'You need to choose a Medium Type (English Medium or Urdu Medium) before starting the check.'
      });
      return;
    }

    if (ocrOnlyRun && pipelineMode !== 'two_step') {
      showMessage('OCR-only runs are only available for English-2 / Urdu-2 (two-step) Mediums.', 'error');
      return;
    }

    // Custom selection mode must have at least one student picked
    if (selectionMode === 'custom' && selectedStudentCodes.length === 0) {
      showMessage('Please pick at least one student, or switch back to Student Range', 'error');
      return;
    }

    // Loaded on Show rubric, or right now if this run is the first time it's needed.
    let activeRubricText = autoRubricText || rubricText;
    let activePdfRubric = autoPdfRubric || pdfRubric;
    if (!activeRubricText && !activePdfRubric) {
      const loaded = await fetchRubricForFolder(folderPath, { quiet: true });
      activeRubricText = (loaded && loaded.text) || '';
      activePdfRubric = (loaded && loaded.pdf) || null;
    }

    if (!activeRubricText && !activePdfRubric) {
      showMessage('No rubric found for this folder.', 'error');
      return;
    }

    // In custom mode: collect every image name that belongs to a picked student
    // from the sorted analyzed data. The backend's /assess/recheck endpoint
    // accepts an explicit list of `image_names` and only processes those, which
    // is exactly what we need to limit processing to the picked students.
    let pickedImageNames = [];
    if (selectionMode === 'custom') {
      const pickedSet = new Set(selectedStudentCodes);
      const collected = (analyzedData || [])
        .filter(a => a && a.unique_code && pickedSet.has(a.unique_code))
        .map(a => a.image_no)
        .filter(Boolean);
      pickedImageNames = Array.from(new Set(collected));

      if (pickedImageNames.length === 0) {
        showMessage('Could not find any sorted images for the picked students. Please re-sort the folder or pick different students.', 'error');
        return;
      }
    }

    if (resume) forgetPausedFolder(folderPath);
    pauseRequestedRef.current = false;
    setIsPausing(false);
    reconciledSigRef.current.clear();
    clearRegradeSnapshot();
    setIsProcessing(true);
    rememberRunMode(ocrOnlyRun ? 'ocr_only' : 'full');
    setProgress(
      resume
        ? 'Resuming — graded sheets are skipped and saved OCR is kept.'
        : ocrOnlyRun
        ? (selectionMode === 'custom'
            ? `Starting OCR-only run for ${selectedStudentCodes.length} picked student(s)...`
            : 'Starting OCR-only run...')
        : (selectionMode === 'custom'
            ? `Starting check for ${selectedStudentCodes.length} picked student(s)...`
            : 'Starting assessment...')
    );
    setMessage({ show: false, text: '', type: '' });
    setProgressDetails(null);

    try {
      // Build system prompt on frontend
      const systemPrompt = subjectType === 'urdu'
        ? buildPromptUrdu(activeRubricText, activePdfRubric)
        : buildPromptForBatch(activeRubricText, activePdfRubric);

      const token = localStorage.getItem('authToken');

      // Custom mode uses the recheck endpoint so only the picked students are
      // processed. Range mode uses the original check-papers endpoint.
      // Resume always uses check-papers: recheck deletes the chosen rows
      // first, which would throw away saved OCR and start those sheets over.
      const useRecheck = selectionMode === 'custom' && !resume;
      const endpoint = useRecheck
        ? `${API_BASE_URL}/assess/recheck`
        : `${API_BASE_URL}/assess/check-papers`;

      // Two-step pipeline fields, only sent when the user picked English-2 or
      // Urdu-2. Backend ignores them on the single-call path. `recheck_mode`
      // is omitted here — it's a recheck-only concept and defaults to 'full'
      // on a fresh run. The combined `system_prompt` (above) stays as a
      // single-call fallback for backends that don't yet route by step; new
      // backends should use `ocr_system_prompt` for step 1 and
      // `grading_system_prompt` for step 2.
      //
      // Thinking is FORCED OFF for OCR. Mechanical transcription has nothing
      // to "reason" about, and Qwen-class models with `enable_thinking: true`
      // dump output into `reasoning_content` and leave `content` empty —
      // which the backend reads as "AI returned empty response". Grading can
      // still use the user's choice (`thinkingEnabled`).
      const isTwoStep = pipelineMode === 'two_step';
      // English auto-check passes ggml for OCR and grading here. The
      // dropdowns (and their localStorage keys) stay on whatever the
      // teacher picked.
      const ocrModelForRun = (typeof ocrModelOverride === 'string' && ocrModelOverride)
        ? ocrModelOverride
        : ocrModel;
      const gradingModelForRun = (typeof gradingModelOverride === 'string' && gradingModelOverride)
        ? gradingModelOverride
        : gradingModel;
      const derived = providersForCheckModels(ocrModelForRun, gradingModelForRun);
      const currentProvider = providerOverride || derived.provider;
      const ocrProviderForRun = ocrProviderOverride || derived.ocrProvider;
      const requestUsesLocal = currentProvider === 'local' || ocrProviderForRun === 'local';
      const twoStepFields = isTwoStep
        ? {
            pipeline: 'two_step',
            ocr_model: ocrModelForRun,
            grading_model: gradingModelForRun,
            ...(ocrProviderForRun ? { ocr_provider: ocrProviderForRun } : {}),
            // Manual local work shares Spc and the Mac. Auto-check
            // sets dispatch_auto and keeps the same free-machine rule, with
            // the Mac's second slot left for a manual run.
            ...(!dispatchAuto && requestUsesLocal ? { dispatch_manual: true } : {}),
            ocr_unclear_threshold: OCR_UNCLEAR_THRESHOLD,
            ocr_system_prompt: subjectType === 'urdu'
              ? buildPromptUrduOcr()
              : buildPromptOcrEnglish(),
            grading_system_prompt: subjectType === 'urdu'
              ? buildPromptUrduGrading(activeRubricText, activePdfRubric)
              : buildPromptEnglishGrading(activeRubricText, activePdfRubric),
            ocr_enable_thinking: false,
            grading_enable_thinking: thinkingEnabled,
            // Fresh OCR-only run: backend uses recheck_mode === 'ocr_only'
            // to skip step 2 and persist a placeholder grading row that the
            // teacher will fill in later by clicking "Re-grade".
            ...(ocrOnlyRun ? { recheck_mode: 'ocr_only' } : {}),
            // Auto-check uses the auto slots. A button click that still
            // needs a local machine sets dispatch_manual above.
            ...(dispatchAuto ? { dispatch_auto: true } : {})
          }
        : {};

      // For two-step runs the user's model selection lives in the OCR/Grading
      // dropdowns — the main "Model" dropdown is irrelevant. Override the
      // legacy `model` and `local_model` fields with `ocrModel` so backends
      // that haven't yet learned about `pipeline: 'two_step'` still run with
      // the chosen model (single-call fallback uses the OCR-side selection).
      // Two-step-aware backends should look at `ocr_model` / `grading_model`.
      const effectiveModel = isTwoStep ? ocrModelForRun : model;
      // UrOG sends Gemini only for OCR. The legacy local_model field is the
      // grading model, so a fallback path does not try to load 3.8-flash locally.
      const legacyLocalModel = ocrProviderForRun === 'gemini' ? gradingModelForRun : effectiveModel;
      // Same reasoning as above: legacy `enable_thinking` becomes the OCR-side
      // toggle for two-step runs, so it's hard-off (the single-call fallback
      // is dominated by transcription). Single-call runs keep the user's
      // choice regardless of language.
      const effectiveThinking = isTwoStep ? false : thinkingEnabled;

      const body = useRecheck
        ? {
            folder_path: folderPath,
            image_names: pickedImageNames,
            // No existing results to replace on a first-time run.
            result_ids: [],
            limit: 'MR1',
            model: currentProvider === 'gemini' ? effectiveModel : 'simple',
            subject_type: subjectType,
            system_prompt: systemPrompt,
            pdf_rubric: activePdfRubric,
            provider: currentProvider,
            source: 'check_more',
            enable_thinking: effectiveThinking,
            ...(requestUsesLocal && legacyLocalModel ? { local_model: legacyLocalModel } : {}),
            ...twoStepFields,
            image_size: dispatchAuto ? 'full' : (imageSize === 'full' ? 'full' : 'resized'),
            ocr_send: dispatchAuto ? 'all' : (ocrSend === 'all' ? 'all' : 'per_page'),
            ...(resume ? { resume: true } : {})
          }
        : {
            folder_path: folderPath,
            student_range: studentRange,
            limit: 'MR1',
            model: currentProvider === 'gemini' ? effectiveModel : 'simple',
            subject_type: subjectType,
            system_prompt: systemPrompt,
            pdf_rubric: activePdfRubric,
            provider: currentProvider,
            source: 'check_more',
            enable_thinking: effectiveThinking,
            ...(requestUsesLocal && legacyLocalModel ? { local_model: legacyLocalModel } : {}),
            ...twoStepFields,
            image_size: dispatchAuto ? 'full' : (imageSize === 'full' ? 'full' : 'resized'),
            ocr_send: dispatchAuto ? 'all' : (ocrSend === 'all' ? 'all' : 'per_page'),
            ...(resume ? { resume: true } : {})
          };

      // DEBUG — confirm the two-step prompts and flags actually go on the wire.
      // Check the browser console + Network tab for the full request payload.
      // Long prompts are summarized so the log stays readable.
      if (isTwoStep) {
        const summarize = (s) => {
          if (s == null) return '<<missing>>';
          const str = String(s);
          return `${str.length} chars · starts with: ${JSON.stringify(str.slice(0, 120))}`;
        };
        console.log(`[Assessment][${subjectType === 'urdu' ? 'Urdu' : 'English'}-2] Outgoing request summary:`, {
          endpoint,
          pipeline: body.pipeline,
          model_legacy: body.model,
          local_model_legacy: body.local_model,
          ocr_model: body.ocr_model,
          grading_model: body.grading_model,
          enable_thinking_legacy: body.enable_thinking,
          ocr_enable_thinking: body.ocr_enable_thinking,
          grading_enable_thinking: body.grading_enable_thinking,
          system_prompt_legacy: summarize(body.system_prompt),
          ocr_system_prompt: summarize(body.ocr_system_prompt),
          grading_system_prompt: summarize(body.grading_system_prompt)
        });
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      console.log('[Assessment] Response status:', response.status);
      console.log('[Assessment] Response data:', data);

      if (response.status === 202 && data.session_id) {
        // Assessment started, now poll for progress
        console.log('[Assessment] Starting progress polling with sessionId:', data.session_id);
        setSessionId(data.session_id);
        setProgress(resume
          ? 'Resuming — graded sheets are skipped and saved OCR is kept.'
          : 'Assessment started. Processing students...');
        // Manual run kicked off — light the Sort-tab "Run" box. The progress
        // poller flips it to done/error when the session finishes.
        reportManualCheckStep(folderPath, 'run', 'running');
        return 'started';
      } else if (response.ok) {
        // Immediate completion — either the legacy single-call path, or the
        // resume pre-scan finding everything already checked (nothing to do).
        console.log('[Assessment] Immediate completion');
        showMessage(
          data.message || `Successfully assessed ${data.processed_count} students. Results saved to Tables tab.`,
          'success'
        );
        setProgress(data.resumed
          ? 'Nothing to re-check — all students already graded.'
          : 'Checking completed! Go to Tables Tab to view results.');
        setIsProcessing(false);
        await fetchAssessmentResults();
        reportManualCheckStep(folderPath, 'run', 'done');
        return 'completed';
      } else {
        // 409 = the shared local AI is busy with the other checking browser.
        // Surface it distinctly so the auto-check engine treats it as
        // "wait & retry" rather than a folder failure.
        console.log('[Checking] Request failed', response.status);
        showMessage(
          data.message || 'Checking failed',
          response.status === 409 ? 'info' : 'error',
          response.status === 409 ? { canStopActive: true, otherFolder: data.other_folder || null } : {}
        );
        setProgress('');
        setIsProcessing(false);
        return response.status === 409 ? 'busy' : 'error';
      }
    } catch (error) {
      console.error('Error assessing:', error);
      showMessage('Failed to assess: ' + error.message, 'error');
      setProgress('');
      setIsProcessing(false);
      return 'error';
    }
  };

  // Probe whether the active folder has a saved assessment snapshot so the
  // "Save Table" button can show a "Last Saved: …" pill / "Update" label
  // instead of a plain "Save". Folder-scoped, mirrors the Sort tab probe.
  const fetchAssessSnapshotMeta = async (path) => {
    if (!path) {
      setAssessSnapshotMeta(null);
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/assess/snapshots/meta?folder_path=${encodeURIComponent(path)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAssessSnapshotMeta(data && data.meta ? data.meta : null);
      } else {
        setAssessSnapshotMeta(null);
      }
    } catch (err) {
      console.error('[AssessmentTab] Snapshot meta fetch failed:', err);
      setAssessSnapshotMeta(null);
    }
  };

  // Saved-table key versus the last approved key. Update Saved Table fills
  // while the screen differs from the saved rows. Approve fills once that
  // save matches the screen and this class has not been approved for it.
  const loadTableActionKeys = async (fp, options = {}) => {
    const gen = ++tableKeyGenRef.current;
    if (!fp) {
      setSavedTableKey('');
      setApprovedTableKey('');
      setTableKeysReady(true);
      return;
    }
    if (!options.quiet) setTableKeysReady(false);
    try {
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };
      const [snapRes, sortRes] = await Promise.all([
        fetch(`${API_BASE_URL}/assess/snapshots?folder_path=${encodeURIComponent(fp)}`, { headers }),
        fetch(`${API_BASE_URL}/analyze/snapshots/meta?folder_path=${encodeURIComponent(fp)}`, { headers })
      ]);
      if (gen !== tableKeyGenRef.current) return;
      const snapData = await snapRes.json().catch(() => ({}));
      const sortData = await sortRes.json().catch(() => ({}));
      const rows = snapRes.ok && Array.isArray(snapData.data) ? snapData.data : [];
      const saved = tableActionKey(rows);
      const status = sortRes.ok && sortData.meta ? String(sortData.meta.status || '') : '';
      const stored = readApprovedTableKey(fp);
      let approved = '';
      if (APPROVED_FOLDER_STATUSES.has(status)) {
        approved = stored || saved;
        if (!stored && saved) writeApprovedTableKey(fp, saved);
      } else if (stored) {
        writeApprovedTableKey(fp, '');
      }
      setSavedTableKey(saved);
      setApprovedTableKey(approved);
    } catch (err) {
      console.error('[AssessmentTab] Saved-table compare failed:', err);
      if (gen !== tableKeyGenRef.current) return;
    } finally {
      if (gen === tableKeyGenRef.current) setTableKeysReady(true);
    }
  };

  // Marks and comments for the two copies Update Saved Table refreshes.
  // Print keeps the verification table. EduPage keeps the one-line comment.
  const fieldsForSavedCopies = (result) => {
    let base = (result.comments || '').trim();
    if (result.table_comments) {
      try {
        const repair = repairForResult(result);
        const rebuilt = tableToOneLineComments({
          tableMd: result.table_comments,
          studentName: result.student_name,
          existingComments: result.comments || '',
          allowedIds: repair.allowedIds,
          rubricMarks: repair.rubricMarks,
          paperTotal: repair.paperTotal
        });
        if (rebuilt) base = rebuilt.trim();
      } catch (_) { /* keep stored comments */ }
    }
    const className = (approveForm.class || (rubricMetadata && rubricMetadata.class) || result.class || '').trim();
    const section = (approveForm.section || (rubricMetadata && rubricMetadata.section) || result.section || '').trim();
    const subject = (approveForm.subject || (rubricMetadata && rubricMetadata.subject) || result.subject || '').trim();
    const courseOnly = `${className}${section ? `-${section}` : ''} ${subject}`.trim();
    const dateStr = (rubricMetadata && rubricMetadata.test_date
      ? new Date(rubricMetadata.test_date)
      : new Date()
    ).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const courseLabel = `${courseOnly} ${dateStr}`.trim();
    const comments = courseLabel && base && !base.includes(courseLabel)
      ? `${base} | ${courseLabel}`
      : (base || courseLabel);
    let marks = result.marks;
    if (result.table_comments) {
      try {
        const grand = gradeFromTable(result.table_comments, repairForResult(result));
        if (grand && grand.obtained != null && !isNaN(grand.obtained)) {
          marks = String(Math.round(grand.obtained * 100) / 100);
        }
      } catch (_) { /* keep result.marks */ }
    }
    return {
      name: result.student_name || '',
      marks: marks == null ? '' : String(marks),
      comments
    };
  };

  // Save the current in-memory assessmentResults as the folder's snapshot.
  // The folder_path is taken from the currently active rubric — every row
  // already carries `folder_path = "rubrics/<id>"`, so we re-use that.
  // The chip keeps saying "Approving" or "Saving table" until this writes the
  // finished wording. Called only after that step has actually failed or the
  // approve has finished, so a validation miss does not count as a failure.
  const settleSaveApproveTimer = (outcome) => {
    const label = SAVE_APPROVE_RESULT_LABEL[outcome];
    if (!label) return;
    setTaskTimer((prev) => {
      if (!prev || (prev.label !== 'Approving' && prev.label !== 'Saving table')) return prev;
      const next = { ...prev, label };
      writeTaskTimer(next);
      return next;
    });
  };

  const handleSaveAssessmentSnapshot = async (options) => {
    const opts = (options && typeof options === 'object' && !options.nativeEvent) ? options : {};
    const silent = !!opts.silent;
    const tableOnly = !!opts.tableOnly;
    const sourceRows = Array.isArray(opts.rows) && opts.rows.length ? opts.rows : assessmentResults;
    if (assessSnapshotBusy) return false;
    if (!Array.isArray(sourceRows) || sourceRows.length === 0) {
      if (!silent) showMessage('No assessment results to save.', 'error');
      return false;
    }
    // Resolve the folder_path from the first row (all rows in a check run
    // share the same folder) or fall back to the loaded folderPath state.
    const fp = (sourceRows.find(r => r.folder_path) || {}).folder_path || folderPath;
    if (!fp) {
      if (!silent) showMessage('Cannot resolve folder for snapshot — no active rubric.', 'error');
      return false;
    }

    const doSave = async () => {
      setAssessSnapshotBusy(true);
      setSnapshotSaveKind(tableOnly ? 'table' : 'copies');
      try {
        const token = localStorage.getItem('authToken');
        const payload = {
          folder_path: fp,
          rows: sourceRows.map(r => ({
            student_name: r.student_name,
            image_name: r.image_name,
            class: r.class,
            section: r.section,
            subject: r.subject,
            marks: r.marks,
            comments: r.comments,
            table_comments: r.table_comments,
            comments_custom: r.comments_custom ? 1 : 0,
            status: r.status,
            source: r.source,
            ocr_text: r.ocr_text,
            ocr_status: r.ocr_status,
            ocr_unclear_ratio: r.ocr_unclear_ratio,
            ocr_model: r.ocr_model,
            grading_model: r.grading_model,
            pipeline: r.pipeline,
            grading_host: r.grading_host || null,
            ocr_image_note: r.ocr_image_note || null,
            ocr_prompt_tokens: r.ocr_prompt_tokens ?? null,
          ocr_history: r.ocr_history ?? null,
            table_history: r.table_history ?? null,
            ocr_completion_tokens: r.ocr_completion_tokens ?? null
          }))
        };
        const response = await fetch(`${API_BASE_URL}/assess/snapshots`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok) {
          let note = tableOnly
            ? `Saved ${payload.rows.length} rows in this table.`
            : (data.message || `Saved ${payload.rows.length} rows`);
          if (!tableOnly) try {
            const ridMatch = String(fp).match(/rubrics\/(\d+)/);
            const copies = sourceRows.map((row) => {
              const fields = fieldsForSavedCopies(row);
              return { row, fields };
            });
            const syncRes = await fetch(`${API_BASE_URL}/assess/snapshots/sync-copies`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({
                folder_path: fp,
                rubric_id: ridMatch ? Number(ridMatch[1]) : null,
                class: (approveForm.class || (rubricMetadata && rubricMetadata.class) || ''),
                section: (approveForm.section || (rubricMetadata && rubricMetadata.section) || ''),
                subject: (approveForm.subject || (rubricMetadata && rubricMetadata.subject) || ''),
                scans: copies.map(({ row, fields }) => ({
                  student_name: row.student_name || '',
                  marks: fields.marks,
                  comments: commentsForScansFolder(row)
                })),
                students: copies.map(({ fields }) => ({
                  name: fields.name,
                  marks: fields.marks,
                  comments: fields.comments
                }))
              })
            });
            const sync = await syncRes.json().catch(() => ({}));
            if (syncRes.ok) {
              const printNote = sync.scans && sync.scans.found
                ? `Print updated for ${sync.scans.updated} students`
                : 'No print list on this folder yet';
              const listNote = sync.students && sync.students.found
                ? `EduPage list updated for ${sync.students.updated} students`
                : 'No approved student list for this folder yet';
              note = `${note}. ${printNote}. ${listNote}`;
            } else {
              note = `${note}. Print and EduPage lists were not updated`;
            }
          } catch (syncErr) {
            note = `${note}. Print and EduPage lists were not updated`;
          }
          if (!silent) showMessage(note, 'success');
          ++tableKeyGenRef.current;
          setSavedTableKey(tableActionKey(sourceRows));
          setTableKeysReady(true);
          fetchAssessSnapshotMeta(fp);
          // Manual table save — fill the Sort-tab "Save" box (backfills Run).
          reportManualCheckStep(fp, 'save', 'done');
          return true;
        }
        if (!silent) showMessage(data.message || 'Failed to save snapshot', 'error');
        settleSaveApproveTimer('saving-failed');
        return false;
      } catch (err) {
        if (!silent) showMessage('Save failed: ' + err.message, 'error');
        settleSaveApproveTimer('saving-failed');
        return false;
      } finally {
        setAssessSnapshotBusy(false);
        setSnapshotSaveKind(null);
      }
    };

    if (!silent && !tableOnly && assessSnapshotMeta) {
      showConfirm(
        `Overwrite the existing saved table for this folder? (Saved ${new Date(assessSnapshotMeta.saved_at).toLocaleString()})`,
        doSave,
        { confirmLabel: 'Overwrite', cancelLabel: 'Cancel' }
      );
      return false;
    }
    return doSave();
  };

  const openApprovePicker = () => {
    if (showApproveForm) {
      setShowApproveForm(false);
      return;
    }
    let prefill = '';
    if (rubricMetadata) {
      const cls = (rubricMetadata.class || '').trim();
      const sub = (rubricMetadata.subject || '').trim();
      prefill = cls && sub ? `${cls} - ${sub}` : (cls || sub);
    }
    let autoFolderId = null;
    if (rubricMetadata && prefill && scansFoldersForApprove.length) {
      const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
      const dayOf = (d) => { try { return d ? new Date(d).toISOString().slice(0, 10) : null; } catch { return null; } };
      const wantName = norm(prefill);
      const wantDay = dayOf(rubricMetadata.test_date);
      const nameMatches = scansFoldersForApprove.filter(f => {
        const t = norm(f.title);
        return t === wantName || t.startsWith(wantName) || t.includes(wantName);
      });
      let pick = wantDay
        ? nameMatches.find(f => dayOf(f.test_date) === wantDay)
        : null;
      if (!pick && nameMatches.length === 1) pick = nameMatches[0];
      if (pick) autoFolderId = pick.id;
    }
    setSelectedScanFolderForApprove(autoFolderId);
    setScanFolderSearchInput(autoFolderId ? '' : prefill);
    setShowScanFolderPicker(true);
  };

  const applyQueuedRegrade = async (request, queue) => {
    if (!request || !queue) return;
    let audioUrl = request.audioUrl || '';
    let note = request.note || '';
    let createdBy = request.createdBy || request.created_by || '';
    let students = Array.isArray(request.students) ? request.students : [];
    let questions = Array.isArray(request.questions) ? request.questions : [];
    if (!audioUrl) {
      try {
        const token = localStorage.getItem('authToken');
        const match = String(queue.folderPath || '').match(/rubrics\/(\d+)/);
        if (match) {
          const res = await fetch(`${API_BASE_URL}/analyze/folders/${match[1]}/regrade-requests/${request.slot}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data.request) {
            audioUrl = data.request.audio_url || '';
            note = data.request.note || note;
            createdBy = data.request.created_by || createdBy;
            if (Array.isArray(data.request.students) && data.request.students.length) students = data.request.students;
            if (Array.isArray(data.request.questions) && data.request.questions.length) questions = data.request.questions;
          }
        }
      } catch { /* keep the list copy */ }
    }
    setRegradeVoice({
      slot: request.slot,
      audioUrl,
      note,
      title: queue.title || queue.folderPath,
      folderPath: queue.folderPath,
      createdBy
    });
    setPendingRegrade({ folderPath: queue.folderPath, students, questions });
  };

  finishQueuedRegradeRef.current = async (rows, slot) => {
    const queue = regradeQueueRef.current;
    if (!queue || !Array.isArray(queue.requests) || !queue.requests.length) return;
    const index = queue.requests.findIndex((item) => Number(item.slot) === Number(slot));
    if (index < 0) return;
    const saved = await handleSaveAssessmentSnapshot({ silent: true, rows });
    if (!saved) {
      showMessage(`Re-Grade ${slot} finished, but the table was not saved. The next request was not opened.`, 'error');
      return;
    }
    const next = queue.requests[index + 1];
    if (!next) {
      showMessage(`Re-Grade ${slot} saved. That was the last request.`, 'success');
      return;
    }
    const updated = { ...queue, index: index + 1 };
    regradeQueueRef.current = updated;
    setRegradeQueue(updated);
    showMessage(`Re-Grade ${slot} saved. Opening Re-Grade ${next.slot}.`, 'success');
    await applyQueuedRegrade(next, updated);
  };

  // One click from the open paper: write this student into the saved
  // assessment table, the scans print list, and the approved student list.
  const publishOpenStudent = async (patch, source) => {
    const open = source || viewingComment;
    if (!open) {
      showMessage('Open a student first.', 'error');
      return;
    }
    const row = {
      ...open,
      table_comments: patch && patch.table_comments != null ? patch.table_comments : open.table_comments,
      comments: patch && patch.comments != null ? patch.comments : open.comments,
      marks: patch && patch.marks != null ? patch.marks : open.marks,
      comments_custom: patch && patch.comments_custom != null ? patch.comments_custom : open.comments_custom
    };
    const fp = String(row.folder_path || folderPath || '').trim();
    const studentName = String(row.student_name || '').trim();
    if (!fp || !studentName) {
      showMessage('This student has no saved folder to update.', 'error');
      return;
    }
    const graded = fieldsForSavedCopies(row);
    let listComments = graded.comments;
    if (Number(row.comments_custom) === 1) {
      listComments = fieldsForSavedCopies({ ...row, table_comments: '' }).comments;
    }
    const ridMatch = fp.match(/rubrics\/(\d+)/);
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/assess/snapshots/sync-student`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({
        folder_path: fp,
        rubric_id: ridMatch ? Number(ridMatch[1]) : null,
        class: (approveForm.class || (rubricMetadata && rubricMetadata.class) || row.class || ''),
        section: (approveForm.section || (rubricMetadata && rubricMetadata.section) || row.section || ''),
        subject: (approveForm.subject || (rubricMetadata && rubricMetadata.subject) || row.subject || ''),
        student_name: studentName,
        image_name: row.image_name || '',
        result_id: row.id,
        marks: graded.marks,
        comments: row.comments || '',
        table_comments: row.table_comments || '',
        comments_custom: row.comments_custom ? 1 : 0,
        scan_comments: commentsForScansFolder(row),
        list_comments: listComments
      })
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'Update failed');
    const assessNote = data.snapshot && data.snapshot.found
      ? 'assessment table updated'
      : 'no assessment row for this student';
    const scanNote = data.scans && data.scans.found
      ? (data.scans.updated ? 'scan list updated' : 'not on the scan list')
      : 'no scan list yet';
    const listNote = data.students && data.students.found
      ? (data.students.updated ? 'student list updated' : 'not on the student list')
      : 'no student list yet';
    const allOk = data.snapshot && data.snapshot.found
      && data.scans && data.scans.updated
      && data.students && data.students.updated;
    showMessage(`${studentName}: ${assessNote}; ${scanNote}; ${listNote}`, allOk ? 'success' : 'info');
    if (data.snapshot && data.snapshot.found) loadTableActionKeys(fp, { quiet: true });
  };

  // Replace this login's working copy with the folder's latest saved table,
  // then the caller reloads the screen from that copy. A folder with no
  // saved table is left as-is (404).
  const copyLatestSavedTable = async (fp) => {
    if (!fp) return false;
    const token = localStorage.getItem('authToken');
    try {
      const resp = await fetch(`${API_BASE_URL}/assess/snapshots/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ folder_path: fp, source: 'check_more' })
      });
      if (resp.status === 404) return false;
      if (!resp.ok) {
        console.warn('[AssessmentTab] Could not copy the saved table:', resp.status);
        return false;
      }
      return true;
    } catch (err) {
      console.warn('[AssessmentTab] Could not copy the saved table:', err);
      return false;
    }
  };

  const fetchAssessmentResults = async (options) => {
    const opts = (options && typeof options === 'object' && !options.nativeEvent) ? options : {};
    // The Check page paints from marks and comments. Transcripts stay on the
    // server until View, View All OCR, or the auto-check save asks for them.
    const omitOcr = !!opts.omitOcr;
    const gen = ++resultsFetchGenRef.current;
    const folder = folderPathRef.current || folderPath;
    const leftThisFolder = () => gen !== resultsFetchGenRef.current
      || (folder && folderPathRef.current !== folder)
      || blockResultsUntilSyncRef.current;
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(checkMoreResultsUrl({ omitOcr }), {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        cache: 'no-store'
      });

      if (response.ok) {
        const data = await response.json();
        const results = data.data || [];

        // Strip "/N" suffixes the model sometimes leaves on marks BEFORE
        // running auto-correct so consistency checks see the bare obtained
        // value. Any stripped row carries `_marksStripped` for table colouring.
        const normalized = normalizeMarksOnResults(results);

        // A just-imported snapshot is the saved table. Rebuilding it against
        // the rubric already in memory rewrites marks before this folder's
        // own rubric is applied, so the open screen shows the older totals.
        const repair = captureRepair(folder);
        const corrected = preserveSavedMarksRef.current
          ? normalized
          : await autoCorrectResults(normalized, {
            folder,
            repair,
            gen,
            isCancelled: leftThisFolder
          });
        const withHistory = applyAutoCorrectHistory(corrected);
        // Authoritative path: overwrite AI-supplied names with sort-tab
        // canonical names AND persist the correction to the backend so the
        // lock survives a page refresh / re-fetch.
        const locked = lockNamesFromSort(withHistory, { persist: !leftThisFolder() });
        if (!leftThisFolder()) {
          setAssessmentResults(locked);
        }
        return locked;
      }
    } catch (error) {
      console.error('Error fetching assessment results:', error);
    }
    return [];
  };

  // ── Open a folder straight from the Sort tab ──
  // The "Auto-Checked" / "Check Failed" badges and the "Re-grade" shortcut
  // navigate here with { autoOpenFolder } in router state. Import the folder's
  // sorted list + saved checked table, then load both into this tab. A plain
  // badge click lands on a ready-to-review table; autoRegrade also selects
  // every row and starts Check Step 2. Guarded by location.key so each
  // navigation runs once.
  useEffect(() => {
    const ao = location.state && location.state.autoOpenFolder;
    if (!ao || !ao.folder_path) return;
    if (autoOpenHandledRef.current === location.key) return;
    autoOpenHandledRef.current = location.key;
    (async () => {
      const token = localStorage.getItem('authToken');
      let quickOcrError = '';
      try {
        // A blank-page name opens this student's transcript only. The class
        // table, snapshot import, and working-table wipe stay unused.
        if (ao.openOcrStudentName) {
          returnToSortRef.current = true;
          suppressSavedTableSyncRef.current = true;
          blockResultsUntilSyncRef.current = true;
          setQuickOcrOpening(true);
          setFolderPath(ao.folder_path);
          setProgress('');
          const rubricMatch = String(ao.folder_path).match(/rubrics\/(\d+)/);
          const rubricId = rubricMatch ? rubricMatch[1] : '';
          const params = new URLSearchParams({
            rubricId,
            name: ao.openOcrStudentName
          });
          const response = await fetch(`${API_BASE_URL}/assess/student-ocr?${params}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok || !data.result) {
            throw new Error((data && data.message) || 'Could not load this transcript');
          }
          setAnalyzedData(Array.isArray(data.analyzed) ? data.analyzed : []);
          setAssessmentResults([data.result]);
          const pageMatch = String(ao.openOcrPage || '').match(/(\d+)/);
          pendingOcrPageRef.current = pageMatch ? Number(pageMatch[1]) : 0;
          setViewingOcr(data.result);
          setQuickOcrOpening(false);
          return;
        }
        // Which auto-check STEP box was clicked (from the Sort-tab boxes), so we
        // can set the tab up to fix exactly that step. Absent for a plain
        // "Auto-Checked"/"Check Failed" badge click → default (load table).
        const focusStep = ao.focusStep || null;
        if (ao.switchClass) {
          if (ao.medium === 'english') handleMediumChange('english_two_step');
          else if (ao.medium === 'urdu' || ao.medium === 'urog') handleMediumChange('urdu_two_step');
        }
        // This open loads the table itself. Don't let the folderPath effect
        // copy the saved table a second time (and race with a failed-run wipe).
        suppressSavedTableSyncRef.current = true;
        setFolderPath(ao.folder_path);
        setProgress(`Loading "${ao.title || ao.folder_path}"…`);

        // Clear the Sort working list for this folder. The Check copy stays
        // when it is already as new as the saved table; a failed run still
        // drops this folder's Check rows so Start Checking begins empty.
        clearWorkingTableLocalStorage();
        const wipeHeaders = { Authorization: `Bearer ${token}` };
        const jsonHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
        await fetch(
          `${API_BASE_URL}/analyze/results?folder_path=${encodeURIComponent(ao.folder_path)}`,
          { method: 'DELETE', headers: wipeHeaders }
        ).catch(() => {});
        if (focusStep === 'run') {
          await fetch(
            `${API_BASE_URL}/assess/results?source=check_more&folder_path=${encodeURIComponent(ao.folder_path)}`,
            { method: 'DELETE', headers: wipeHeaders }
          ).catch(() => {});
        }
        setAssessmentResults([]);
        setAnalyzedData([]);
        setSelectedResults([]);

        // The sorted list and the saved marks table copy together. Transcripts
        // stay on the server; the grid read below leaves them out.
        const copies = [
          fetch(`${API_BASE_URL}/analyze/snapshots/import`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify({ folder_path: ao.folder_path })
          }).catch(() => {})
        ];
        if (focusStep !== 'run') {
          copies.push(fetch(`${API_BASE_URL}/assess/snapshots/import`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify({ folder_path: ao.folder_path, source: 'check_more' })
          }).catch(() => {}));
        }
        await Promise.all(copies);

        if (focusStep === 'run') {
          // Run box failed → grading never produced a usable table. Sorted
          // list is loaded; Check was already wiped above — clean slate.
          setShowApproveForm(false);
          await fetchAnalyzedData(ao.folder_path);
          blockResultsUntilSyncRef.current = false;
          preserveSavedMarksRef.current = false;
          await fetchAssessmentResults({ omitOcr: true });
          setProgress('');
          showMessage(`Loaded "${ao.title || ao.folder_path}" and cleared the table — click Start Checking to re-run the grading.`, 'success');
        } else {
          // Save / Scans / Approve (and a plain badge click) all work off the
          // saved checked table, so restore it and load both lists.
          preserveSavedMarksRef.current = true;
          await fetchAnalyzedData(ao.folder_path);
          blockResultsUntilSyncRef.current = false;
          preserveSavedMarksRef.current = true;
          const loadedRows = await fetchAssessmentResults({ omitOcr: true });
          setProgress('');
          if (ao.autoRegrade) {
            // Sort-tab "Re-grade": switch to two-step, select every saved
            // row, then a later effect clicks Check Step 2 / Re-grade.
            const subjectText = `${ao.subject || ''} ${ao.title || ''}`;
            const subjectIsEnglish = /english/i.test(subjectText);
            const englishGrading = ao.medium !== 'urog' && (ao.medium === 'english' || subjectIsEnglish);
            handleMediumChange(englishGrading ? 'english_two_step' : 'urdu_two_step');
            const allIds = (loadedRows || []).map(x => x.id).filter(Boolean);
            if (allIds.length === 0) {
              showMessage(`No saved table rows found for "${ao.title || ao.folder_path}". Save the table in the Check tab first, then try Re-grade again.`, 'error');
            } else {
              setSelectedResults(allIds);
              setPendingAutoRegrade({
                title: ao.title || ao.folder_path,
                ids: allIds,
                rows: loadedRows
              });
              showMessage(`Loaded "${ao.title || ao.folder_path}" — selecting all ${allIds.length} rows and starting Re-grade…`, 'info');
            }
          } else if (focusStep === 'approve') {
            // Approve box failed → open the student-list approve box directly.
            setShowApproveForm(true);
            showMessage(`Loaded "${ao.title || ao.folder_path}" — pick the student list and Approve.`, 'success');
          } else if (focusStep === 'scans') {
            // Scans box failed → surface the "Save to Scans Folder" box (it
            // renders in the results view when the approve form is closed).
            setShowApproveForm(false);
            showMessage(`Loaded "${ao.title || ao.folder_path}" — pick the scans folder and click "Save to Scans Folder".`, 'success');
          } else if (focusStep === 'absent') {
            // Absent box failed → approve already succeeded (marks are in the
            // list); the fix lives in the Students tab, not here. Point them at
            // its "1. Mark Absent" button.
            setShowApproveForm(false);
            showMessage(`"${ao.title || ao.folder_path}" is already approved — open the Students tab, pick ${ao.class || 'the'} list and click "1. Mark Absent" to fill the absentees.`, 'success');
          } else if (ao.openStudentName) {
            setPendingOpenComment({
              folderPath: ao.folder_path,
              name: ao.openStudentName
            });
          } else if (ao.regradeRequest) {
            const subjectText = `${ao.subject || ''} ${ao.title || ''}`;
            const english = ao.medium !== 'urog' && (ao.medium === 'english' || /english/i.test(subjectText));
            handleMediumChange(english ? 'english_two_step' : 'urdu_two_step');
            setRegradeVoice({
              slot: ao.regradeRequest.slot,
              audioUrl: ao.regradeRequest.audioUrl || '',
              note: ao.regradeRequest.note || '',
              title: ao.title || ao.folder_path,
              folderPath: ao.folder_path,
              createdBy: ao.regradeRequest.createdBy || ''
            });
            if (Array.isArray(ao.regradeQueue) && ao.regradeQueue.length) {
              const index = ao.regradeQueue.findIndex((item) => Number(item.slot) === Number(ao.regradeRequest.slot));
              setRegradeQueue({
                folderPath: ao.folder_path,
                title: ao.title || ao.folder_path,
                requests: ao.regradeQueue,
                index: index < 0 ? 0 : index
              });
            }
            setPendingRegrade({
              folderPath: ao.folder_path,
              students: Array.isArray(ao.regradeRequest.students) ? ao.regradeRequest.students : [],
              questions: Array.isArray(ao.regradeRequest.questions) ? ao.regradeRequest.questions : []
            });
            showMessage(`Loaded "${ao.title || ao.folder_path}" — selecting Re-Grade ${ao.regradeRequest.slot}.`, 'info');
          } else if (ao.intent === 'recheck') {
            showMessage(`Loaded "${ao.title || ao.folder_path}" — the checked table is ready. OCR or recheck from here.`, 'success');
          } else if (ao.openFirstComment) {
            const rows = (loadedRows || []).filter((row) => row && row.student_name);
            rows.sort((a, b) => String(a.student_name || '').localeCompare(
              String(b.student_name || ''),
              undefined,
              { numeric: true, sensitivity: 'base' }
            ));
            const first = rows[0];
            if (first && first.student_name) {
              setPendingOpenComment({
                folderPath: ao.folder_path,
                name: first.student_name
              });
            } else {
              setViewingComment(null);
              showMessage(`Loaded "${ao.title || ao.folder_path}" but there is no checked paper to open.`, 'error');
            }
          } else if (ao.openFirstTranscript) {
            const rows = (loadedRows || []).filter((row) => row && row.student_name);
            rows.sort((a, b) => String(a.student_name || '').localeCompare(
              String(b.student_name || ''),
              undefined,
              { numeric: true, sensitivity: 'base' }
            ));
            const thinking = ao.openThinkingOcr
              ? rows.find((row) => thinkingPhrasesIn(`${row.comments || ''}\n${row.table_comments || ''}`).length)
              : null;
            const missing = ao.openMissingOcr
              ? rows.find((row) => parseMissingPages(row.ocr_missing_pages).length)
              : null;
            const blankPagesOf = (row) => (
              row.ocr_text != null
                ? unexpectedBlankPages(row.ocr_text)
                : String(row.unexpected_blank_pages || '').split(',').map((part) => part.trim()).filter(Boolean)
            );
            const blank = ao.openThinkingOcr ? null : rows.find((row) => blankPagesOf(row).length);
            const first = thinking || missing || blank || rows[0];
            if (ao.openThinkingOcr) setOcrListMode('thinking');
            else if (ao.openMissingOcr) setOcrListMode('missing');
            if (first && first.student_name) {
              setPendingOpenOcr({
                folderPath: ao.folder_path,
                name: first.student_name,
                page: missing ? String(parseMissingPages(missing.ocr_missing_pages)[0] || '') : ''
              });
            } else {
              setViewingOcr(null);
              showMessage(`Loaded "${ao.title || ao.folder_path}" but there is no transcript to open.`, 'error');
            }
          } else if (!ao.switchClass) {
            // focusStep === 'save', or a plain "Auto-Checked" badge click.
            // An in-page class switch stays quiet — the table just replaces.
            showMessage(`Loaded "${ao.title || ao.folder_path}" — review the table, then click Approve.`, 'success');
          }
        }
      } catch (e) {
        setProgress('');
        setQuickOcrOpening(false);
        const text = (e && e.message) ? e.message : String(e);
        if (returnToSortRef.current) quickOcrError = text;
        else showMessage('Failed to open folder: ' + text, 'error');
      } finally {
        suppressSavedTableSyncRef.current = false;
        blockResultsUntilSyncRef.current = false;
        // Clear router state so a refresh / back-forward doesn't re-trigger.
        if (quickOcrError) {
          returnToSortRef.current = false;
          navigate('/evaluate/sort', { replace: true, state: { notice: quickOcrError } });
        } else {
          navigate(location.pathname, { replace: true, state: {} });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    if (!quickTranscript || !quickTranscript.folderPath || !quickTranscript.name) {
      setQuickCursor(null);
      return;
    }
    setQuickOcrOpening(true);
    if (quickTranscript.list === 'regrade') setPaperListMode('regrade');
    else if (quickTranscript.list === 'late') setPaperListMode('late');
    if (quickTranscript.list === 'missing') setOcrListMode('missing');
    else if (quickTranscript.list === 'thinking') setOcrListMode('thinking');
    else if (quickTranscript.list === 'blank') setOcrListMode('blank');
    setQuickCursor({
      folderPath: quickTranscript.folderPath,
      name: quickTranscript.name,
      page: quickTranscript.page || '',
      pages: Array.isArray(quickTranscript.pages) ? quickTranscript.pages : [],
      mode: quickTranscript.mode || '',
      refresh: quickTranscript.refresh || '',
      queue: Array.isArray(quickTranscript.queue) ? quickTranscript.queue : null
    });
  }, [quickTranscript]);

  // Transcript opened on the Sort page. Sort stays mounted; only this
  // student's saved row is loaded. Save & Next replaces quickCursor and
  // loads the next flagged student in the same window.
  useEffect(() => {
    if (!quickCursor || !quickCursor.folderPath || !quickCursor.name) return undefined;
    let cancelled = false;
    const token = localStorage.getItem('authToken');
    (async () => {
      try {
        suppressSavedTableSyncRef.current = true;
        blockResultsUntilSyncRef.current = true;
        setQuickOcrOpening(true);
        setFolderPath(quickCursor.folderPath);
        setProgress('');
        const latePaper = quickCursor.mode === 'late';
        let response;
        let data;
        if (latePaper) {
          const rollMatch = String(quickCursor.name || '').match(/\(([^)]+)\)/);
          const roll = rollMatch ? rollMatch[1].trim().toLowerCase() : '';
          if (!roll || roll === '000' || roll === '0000' || roll === '0') {
            throw new Error('This late paper has no roll number');
          }
          response = await fetch(`${API_BASE_URL}/assess/late-paper`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ folder_path: quickCursor.folderPath, roll })
          });
          data = await response.json().catch(() => ({}));
        } else {
          const rubricMatch = String(quickCursor.folderPath).match(/rubrics\/(\d+)/);
          const rubricId = rubricMatch ? rubricMatch[1] : '';
          const params = new URLSearchParams({
            rubricId,
            name: quickCursor.name
          });
          if (quickCursor.mode === 'comment') params.set('kind', 'comment');
          response = await fetch(`${API_BASE_URL}/assess/student-ocr?${params}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          data = await response.json().catch(() => ({}));
        }
        if (cancelled) return;
        if (!response.ok || !data.result) {
          throw new Error((data && data.message) || (latePaper
            ? 'Could not open this late paper'
            : 'Could not load this transcript'));
        }
        const ownScans = Array.isArray(data.ownScans)
          ? data.ownScans
          : (Array.isArray(data.result.own_scans) ? data.result.own_scans : null);
        const result = ownScans
          ? { ...data.result, own_scans: ownScans }
          : data.result;
        setAnalyzedData(Array.isArray(data.analyzed) ? data.analyzed : []);
        setAssessmentResults([result]);
        blankAdvancingRef.current = false;
        if (quickCursor.mode === 'comment' || quickCursor.mode === 'late') {
          setBlankChipPages([]);
          pendingOcrPageRef.current = 0;
          setViewingOcr(null);
          setViewingComment(result);
          setShowCommentTranscript(true);
          setShowRubricPane(true);
          quickCommentPendingRef.current = true;
          await fetchRubricForFolder(quickCursor.folderPath);
        } else {
          const labels = Array.isArray(quickCursor.pages) && quickCursor.pages.length
            ? quickCursor.pages
            : [quickCursor.page];
          const numbers = [];
          for (const label of labels) {
            const match = String(label || '').match(/(\d+)/);
            const n = match ? Number(match[1]) : 0;
            if (n > 0 && !numbers.includes(n)) numbers.push(n);
          }
          numbers.sort((a, b) => a - b);
          setBlankChipPages(quickCursor.refresh === 'blank' ? numbers : []);
          openedOcrForRef.current = null;
          pendingOcrPageRef.current = numbers[0] || 0;
          setViewingOcr(result);
        }
        setQuickOcrOpening(false);
      } catch (e) {
        if (cancelled) return;
        blankAdvancingRef.current = false;
        setQuickOcrOpening(false);
        const close = quickCloseRef.current;
        if (typeof close === 'function') close((e && e.message) ? e.message : String(e));
      } finally {
        if (cancelled) return;
        suppressSavedTableSyncRef.current = false;
        blockResultsUntilSyncRef.current = false;
      }
    })();
    return () => { cancelled = true; };
  }, [quickCursor]);

  useEffect(() => {
    if (!pendingOpenComment) return;
    if (folderPath !== pendingOpenComment.folderPath) return;
    if (!assessmentResults.length) return;
    const want = String(pendingOpenComment.name || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const wantRoll = extractRollNumber(pendingOpenComment.name);
    const matches = assessmentResults.filter((row) => {
      const name = String(row.student_name || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (want && name === want) return true;
      const roll = extractRollNumber(row.student_name);
      return !!(wantRoll && roll && wantRoll === roll);
    });
    const match = matches.find((row) => row.table_comments) || matches[0];
    setPendingOpenComment(null);
    if (match) handleViewComments(match);
    else showMessage(`Could not find ${pendingOpenComment.name} in the checked table.`, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenComment, folderPath, assessmentResults]);

  useEffect(() => {
    if (!pendingOpenOcr) return;
    if (folderPath !== pendingOpenOcr.folderPath) return;
    if (!assessmentResults.length) return;
    const want = String(pendingOpenOcr.name || '').replace(/\s+/g, ' ').trim().toLowerCase();
    const wantRoll = extractRollNumber(pendingOpenOcr.name);
    const matches = assessmentResults.filter((row) => {
      const name = String(row.student_name || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (want && name === want) return true;
      const roll = extractRollNumber(row.student_name);
      return !!(wantRoll && roll && wantRoll === roll);
    });
    const match = matches.find((row) => row.ocr_text) || matches[0];
    const pageMatch = String(pendingOpenOcr.page || '').match(/(\d+)/);
    const page = pageMatch ? Number(pageMatch[1]) : 0;
    const missingName = pendingOpenOcr.name;
    setPendingOpenOcr(null);
    if (!match) {
      showMessage(`Could not find ${missingName} in the checked table.`, 'error');
      return;
    }
    pendingOcrPageRef.current = page > 0 ? page : 0;
    setViewingOcr(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenOcr, folderPath, assessmentResults]);

  // A folder-card Re-Grade badge opens this tab with the request's students,
  // questions, and voice note. Student rows can be ticked as soon as the table
  // is loaded. Question chips wait until the rubric is parsed, because an
  // earlier effect clears the question selection whenever the folder changes
  // and drops any id the rubric does not list.
  useEffect(() => {
    if (!pendingRegrade) return;
    if (folderPath !== pendingRegrade.folderPath) return;
    if (!assessmentResults.length) return;

    const wantRolls = new Set();
    const wantNames = new Set();
    for (const student of pendingRegrade.students || []) {
      const roll = String(
        student.roll || extractRollNumber(student.studentName || student.name || '') || ''
      ).trim().toLowerCase();
      if (roll) wantRolls.add(roll);
      const name = String(student.studentName || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (name) wantNames.add(name);
    }
    const ids = assessmentResults.filter((row) => {
      const name = String(row.student_name || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (name && wantNames.has(name)) return true;
      const roll = String(extractRollNumber(row.student_name) || row.unique_code || '').trim().toLowerCase();
      return !!(roll && roll !== '000' && wantRolls.has(roll));
    }).map((row) => row.id).filter(Boolean);
    setSelectedResults(ids);

    const wanted = (pendingRegrade.questions || []).map((id) => normalizeQuestionId(id)).filter(Boolean);
    const rubricTextReady = !!(
      autoRubricText ||
      rubricText ||
      (autoPdfRubric && autoPdfRubric.text) ||
      (pdfRubric && pdfRubric.text)
    );
    if (wanted.length && !rubricQuestions.length && !rubricTextReady && rubricLoading) return;

    const want = new Set(wanted);
    const questionIds = [];
    for (const question of rubricQuestions) {
      if (want.has(question.id)) {
        questionIds.push(question.id);
        continue;
      }
      for (const id of want) {
        if (question.id.startsWith(id + '.') || id.startsWith(question.id + '.')) {
          questionIds.push(question.id);
          break;
        }
      }
    }
    setSelectedQuestionIds(questionIds);
    const slot = regradeVoice && regradeVoice.slot;
    const questionCount = questionIds.length || wanted.length;
    showMessage(
      `Re-Grade ${slot || ''} — ${ids.length} student${ids.length === 1 ? '' : 's'} and ${questionCount} question${questionCount === 1 ? '' : 's'} selected. Play the voice note, then run Re-grade.`,
      ids.length ? 'success' : 'error'
    );
    setPendingRegrade(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRegrade, folderPath, assessmentResults, rubricQuestions, autoRubricText, rubricText, autoPdfRubric, pdfRubric, rubricLoading]);

  // Auto-correct marks that don't match sub-scores in comments
  const autoCorrectResults = async (results, opts = {}) => {
    if (!results || results.length === 0) return results;
    const folder = opts.folder || folderPathRef.current || '';
    const repair = opts.repair || captureRepair(folder);
    const isCancelled = typeof opts.isCancelled === 'function' ? opts.isCancelled : () => false;
    const stale = () => isCancelled()
      || (folder && folderPathRef.current && folderPathRef.current !== folder);
    if (stale()) return results;
    const token = localStorage.getItem('authToken');
    const corrected = [...results];
    let correctionCount = 0;
    const historyIds = [];
    // Collect the per-row DB writes here and flush them in parallel AFTER the
    // loop (bounded concurrency), instead of one blocking PUT per row. The loop
    // used to `await fetch` on every corrected row, so a 40-student table meant
    // ~40 sequential round-trips on every table load — the main "it's slow"
    // culprit. Local state (corrected[i] + history) is updated inline as before.
    const updateTasks = [];

    for (let i = 0; i < corrected.length; i++) {
      const r = corrected[i];

      // ── Table-first override ──────────────────────────────────────────
      // When the AI emitted BLOCK A (markdown deduction table), trust the
      // sub-question rows as the source of truth and rebuild the one-line
      // from them. The marks cell is that total, written when the row is saved.
      // The AI's bold total rows are routinely wrong, so we never trust them.
      if (folder && r && r.folder_path && r.folder_path !== folder) continue;
      if (r.table_comments) {
        const { md: fixedTable, changed: tableChanged } = correctTable(r.table_comments, repair);
        const tableForGrade = tableChanged ? fixedTable : r.table_comments;
        const grand = gradeFromTable(tableForGrade, repair);
        if (grand && !isNaN(grand.obtained)) {
          const rebuilt = tableToOneLineComments({
            tableMd: tableForGrade,
            studentName: r.student_name,
            existingComments: r.comments || '',
            allowedIds: repair.allowedIds,
            rubricMarks: repair.rubricMarks,
            paperTotal: repair.paperTotal
          });

          const commentsMismatch = !!rebuilt && rebuilt !== r.comments;

          if (rebuilt && (commentsMismatch || tableChanged)) {
            const payload = { comments: rebuilt };
            if (tableChanged) payload.table_comments = fixedTable;
            const reasons = [];
            if (tableChanged) reasons.push('table totals recomputed');
            if (!reasons.length) reasons.push('one-line rebuilt');
            const acEntry = {
              from: r.marks,
              details: `Verification table: ${reasons.join('; ')}`
            };
            autoCorrectHistoryRef.current[r.id] = acEntry;
            historyIds.push(r.id);
            corrected[i] = {
              ...r,
              comments: rebuilt,
              table_comments: tableChanged ? fixedTable : r.table_comments,
              _autoCorrect: acEntry
            };
            correctionCount++;
            // Persist in parallel after the loop (see flush below).
            updateTasks.push({ id: r.id, payload });
            continue;
          }
        }
      }

      if (r.table_comments) continue;
      const c = validateConsistency(r.marks, r.comments);

      if (!c.isConsistent && c.subScores.length > 0 && c.calculatedTotal !== null) {
        // Determine the correct total — always trust the sum of header sub-scores.
        // (Detail-section reconciliation was removed: it caused double-counting when
        //  the AI uses different Q-label formats in the header vs. the detail body,
        //  e.g. "Q1.i→" in the header but "Q.i:" in the detail.)
        let correctTotal = c.calculatedTotal;

        // If self-correction detected ("Adjusted to X"), use the adjusted value
        if (c.details && c.details.includes('Self-correction')) {
          const adjMatch = (r.comments || '').match(/[Aa]djusted\s+to\s+([\d.]+)\/[\d.]+/i);
          if (adjMatch) {
            // Recalculate: replace the last sub-score with the adjusted value
            const adjustedVal = parseFloat(adjMatch[1]);
            if (!isNaN(adjustedVal) && c.subScores.length > 0) {
              const adjusted = [...c.subScores];
              adjusted[adjusted.length - 1] = adjustedVal;
              correctTotal = Math.round(adjusted.reduce((s, v) => s + v, 0) * 100) / 100;
            }
          }
        }

        const correctMarks = String(correctTotal);
        // Also fix the in-comment header total if it states the wrong total.
        // Matches both "Total marks: X/Y" (English) and "مجموعی نمبر: X/Y"
        // (Urdu) so auto-correct works for both mediums.
        const correctedComments = (r.comments || '').replace(
          /((?:Total\s+marks?|مجموعی\s+نمبر)[:\s]+)[\d.]+(\s*\/\s*[\d.]+)/i,
          `$1${correctMarks}$2`
        );
        const marksChanged    = correctMarks !== r.marks;
        const commentsChanged = correctedComments !== r.comments;

        if (marksChanged || commentsChanged) {
          console.log(`[AutoCorrect] ${r.student_name}: marks ${r.marks} → ${correctMarks}, comments header patched: ${commentsChanged} (${c.details})`);

          const payload = { marks: correctMarks };
          if (commentsChanged) payload.comments = correctedComments;
          const acEntry = { from: r.marks, details: c.details };
          autoCorrectHistoryRef.current[r.id] = acEntry;
          historyIds.push(r.id);
          corrected[i] = { ...r, marks: correctMarks, comments: correctedComments, _autoCorrect: acEntry };
          correctionCount++;
          // Persist in parallel after the loop (see flush below).
          updateTasks.push({ id: r.id, payload });
        }
      }
    }

    // Flush all corrections to the backend in parallel, in bounded batches so
    // we don't fire dozens of requests at once. Turns up to N sequential
    // round-trips into ceil(N / CONCURRENCY) batches — the big load-time win.
    if (stale()) {
      for (const id of historyIds) delete autoCorrectHistoryRef.current[id];
      return results;
    }
    if (updateTasks.length > 0) {
      const CONCURRENCY = 8;
      for (let s = 0; s < updateTasks.length; s += CONCURRENCY) {
        if (stale()) break;
        const slice = updateTasks.slice(s, s + CONCURRENCY);
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(slice.map(async (u) => {
          try {
            const res = await fetch(`${API_BASE_URL}/assess/results/${u.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify(u.payload)
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data && data.marks != null && data.marks !== '') {
              const at = corrected.findIndex((row) => row && row.id === u.id);
              if (at >= 0) corrected[at] = { ...corrected[at], marks: data.marks };
            }
          } catch (err) {
            console.error(`[AutoCorrect] Failed to persist ${u.id}:`, err);
          }
        }));
      }
    }

    if (correctionCount > 0) {
      console.log(`[AutoCorrect] Corrected ${correctionCount} result(s)`);
    }
    return corrected;
  };

  // Results often load before the folder rubric. Once this folder's rubric is
  // in hand, fill any question the table skipped and put back any rubric row
  // that was parked under the Grand Total. A later poll of the same rows does
  // not write again.
  useEffect(() => {
    if (!folderRubricReady) return undefined;
    if (rubricFolderRef.current !== folderPath) return undefined;
    if (!rubricQuestionMarks || rubricQuestionMarks.size === 0) return undefined;
    const rows = assessmentResults.filter((row) => !row || !row.folder_path || row.folder_path === folderPath);
    if (!rows.length) return undefined;
    const repair = captureRepair(folderPath);
    if (!repair.rubricMarks || repair.rubricMarks.size === 0) return undefined;
    const sig = rows.map((r) => `${r.id}:${r.table_comments ? String(r.table_comments).length : 0}`).join('|');
    const key = `${folderPath}|${String(rubricPaperTotal)}|${sig}`;
    if (rubricFillKeyRef.current === key) return undefined;
    rubricFillKeyRef.current = key;
    const gen = resultsFetchGenRef.current;
    let cancelled = false;
    (async () => {
      const corrected = await autoCorrectResults(rows, {
        folder: folderPath,
        repair,
        gen,
        isCancelled: () => cancelled
          || gen !== resultsFetchGenRef.current
          || folderPathRef.current !== folderPath
      });
      if (cancelled || gen !== resultsFetchGenRef.current || folderPathRef.current !== folderPath) return;
      if (corrected.some((row, i) => row !== rows[i])) {
        const byId = new Map(corrected.map((row) => [row && row.id, row]));
        setAssessmentResults((prev) => applyAutoCorrectHistory(prev.map((row) => byId.get(row.id) || row)));
      }
    })();
    return () => { cancelled = true; };
    // autoCorrectResults closes over the rubric maps from this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rubricQuestionMarks, rubricPaperTotal, folderPath, assessmentResults, folderRubricReady]);

  // Delete old results that were replaced by recheck
  const deleteOldRecheckResults = async (oldResultIds) => {
    if (!oldResultIds || oldResultIds.length === 0) return;
    const token = localStorage.getItem('authToken');
    console.log('[Recheck] Deleting old results:', oldResultIds);
    const deletePromises = oldResultIds.map(id =>
      fetch(`${API_BASE_URL}/assess/results/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(err => console.error(`[Recheck] Failed to delete old result ${id}:`, err))
    );
    await Promise.all(deletePromises);
    console.log('[Recheck] Old results deleted successfully');
  };

  // Anchor for shift-click range selection — remembers the index of the
  // last row the user toggled with a plain click. A subsequent shift-click
  // selects every row between the anchor and the new row (inclusive),
  // matching the standard file-manager / spreadsheet pattern.
  const lastSelectedIndexRef = useRef(null);

  const handleSelectResult = (id, rowIndex = null, shiftKey = false) => {
    // Shift+click: expand selection across the range from the previous
    // anchor to the row that was just clicked. We add (never remove) so
    // accidentally shift-clicking inside an already-selected range doesn't
    // wipe it. The anchor itself is unchanged so the user can shift-click
    // again from the same starting point.
    if (shiftKey && lastSelectedIndexRef.current !== null && Number.isInteger(rowIndex)) {
      const anchor = lastSelectedIndexRef.current;
      const start = Math.min(anchor, rowIndex);
      const end = Math.max(anchor, rowIndex);
      const rangeIds = sortedAssessmentResults.slice(start, end + 1).map(r => r.id);
      setSelectedResults(prev => {
        const next = new Set(prev);
        for (const rid of rangeIds) next.add(rid);
        return Array.from(next);
      });
      return;
    }

    // Plain click: toggle this row and update the anchor for future
    // shift-clicks.
    setSelectedResults(prev =>
      prev.includes(id) ? prev.filter(resultId => resultId !== id) : [...prev, id]
    );
    if (Number.isInteger(rowIndex)) lastSelectedIndexRef.current = rowIndex;
  };

  const handleSelectAll = () => {
    if (selectedResults.length === assessmentResults.length) {
      setSelectedResults([]);
    } else {
      setSelectedResults(assessmentResults.map(r => r.id));
    }
  };

  const handleDeleteResult = (id) => {
    showConfirm(
      'Are you sure you want to delete this result?',
      async () => {
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(`${API_BASE_URL}/assess/results/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            showMessage('Result deleted successfully', 'success');
            await fetchAssessmentResults();
          } else {
            const data = await response.json();
            showMessage(data.message || 'Failed to delete', 'error');
          }
        } catch (error) {
          console.error('Error deleting result:', error);
          showMessage('Failed to delete: ' + error.message, 'error');
        }
      },
      { confirmLabel: 'Delete', cancelLabel: 'Cancel' }
    );
  };

  // Copy a single row's marks + comments to the clipboard so the teacher can
  // paste straight into Excel / EduPage. Tab-separated puts marks and comments
  // into two adjacent cells on an Excel paste; a plain text field (EduPage)
  // just shows them separated by a tab. Comments are flattened to a single
  // line — embedded newlines would otherwise spill into extra Excel rows.
  // A stored comment that lost its question list ("Name: |  | Total marks → 13.9")
  // is not what the teacher sees on screen. Copy and repair rebuild it from the table.
  const commentHasQuestions = (text) => /Q\.?\s*\d+[^\n|]{0,40}→/.test(String(text || ''));
  const commentForResult = (result) => {
    let marks = result.marks == null ? '' : String(result.marks);
    const rowRepair = repairForResult(result);
    if (result.table_comments) {
      try {
        const grand = gradeFromTable(result.table_comments, rowRepair);
        if (grand && Number.isFinite(Number(grand.obtained))) {
          marks = String(Math.round(Number(grand.obtained) * 100) / 100);
        }
      } catch (_) { /* keep result.marks */ }
    }
    let comments = String(result.comments || '');
    const keepCustom = Number(result.comments_custom) === 1 && commentHasQuestions(comments);
    if (result.table_comments && !keepCustom) {
      try {
        const rebuilt = tableToOneLineComments({
          tableMd: result.table_comments,
          studentName: result.student_name,
          existingComments: comments,
          allowedIds: rowRepair.allowedIds,
          rubricMarks: rowRepair.rubricMarks,
          paperTotal: rowRepair.paperTotal
        });
        if (rebuilt && commentHasQuestions(rebuilt)) comments = rebuilt;
      } catch (_) { /* keep stored comments */ }
    }
    if (!/\[\s*\d+\s+sheets?\s*\]/i.test(comments)) {
      const peer = assessmentResults.find((row) => row.id !== result.id && /\[\s*\d+\s+sheets?\s*\]/i.test(row.comments || ''));
      const peerTail = peer ? String(peer.comments).match(/(\[\s*\d+\s+sheets?\s*\][\s\S]*)$/i) : null;
      const sheetsAndCourse = peerTail ? peerTail[1].trim() : '[1 sheets]';
      const image = String(result.image_name || '').split(',')[0].trim();
      comments = `${comments}${image ? ` [${image}]` : ''} ${sheetsAndCourse}`.trim();
    }
    comments = comments.replace(/\s*\r?\n\s*/g, ' ').trim();
    if (marks) {
      comments = comments.replace(/((?:Total marks|مجموعی نمبر)\s*[:：→]\s*)[\d.]+/i, `$1${marks}`);
    }
    return { marks, comments };
  };
  const handleCopyResult = async (result) => {
    const { marks, comments } = commentForResult(result);
    const payload = `${marks}\t${comments}`;
    const ok = await copyToClipboard(payload);
    showMessage(
      ok ? 'Marks & comments copied' : 'Copy failed',
      ok ? 'success' : 'error'
    );
    return ok ? payload : '';
  };

  // Reload the open student's saved grade from the server and replace the
  // copy on screen, including unsaved edits in the check window.
  const refreshOpenCheck = async () => {
    if (!viewingComment || checkRefreshing) return;
    const openId = viewingComment.id;
    const openImage = viewingComment.image_name;
    setCheckRefreshing(true);
    try {
      const rows = await fetchAssessmentResults();
      const next = (rows || []).find((row) => row && row.id === openId)
        || (rows || []).find((row) => row && openImage && row.image_name === openImage);
      if (!next) {
        showMessage('Could not reload the saved grade', 'error');
        return;
      }
      setViewingComment({
        ...next,
        own_scans: next.own_scans || viewingComment.own_scans,
        table_comments: next.table_comments != null ? next.table_comments : viewingComment.table_comments,
        comments: next.comments != null ? next.comments : viewingComment.comments,
        ocr_text: next.ocr_text != null ? next.ocr_text : viewingComment.ocr_text,
        ocr_history: next.ocr_history != null ? next.ocr_history : viewingComment.ocr_history
      });
      setCheckReloadToken((token) => token + 1);
      showMessage('Saved marks and comments reloaded', 'success');
    } catch (err) {
      showMessage(err.message || 'Could not reload the saved grade', 'error');
    } finally {
      setCheckRefreshing(false);
    }
  };

  const refreshOpenTranscript = async () => {
    if (!viewingOcr || checkRefreshing) return;
    const openId = viewingOcr.id;
    const openImage = viewingOcr.image_name;
    setCheckRefreshing(true);
    try {
      const rows = await fetchAssessmentResults();
      const next = (rows || []).find((row) => row && row.id === openId)
        || (rows || []).find((row) => row && openImage && row.image_name === openImage);
      if (!next) {
        showMessage('Could not reload the saved grade', 'error');
        return;
      }
      setViewingOcr({
        ...next,
        own_scans: next.own_scans || viewingOcr.own_scans,
        table_comments: next.table_comments != null ? next.table_comments : viewingOcr.table_comments,
        comments: next.comments != null ? next.comments : viewingOcr.comments,
        ocr_text: next.ocr_text != null ? next.ocr_text : viewingOcr.ocr_text,
        ocr_history: next.ocr_history != null ? next.ocr_history : viewingOcr.ocr_history
      });
      setOcrCheckReloadToken((token) => token + 1);
      showMessage('Saved marks and comments reloaded', 'success');
    } catch (err) {
      showMessage(err.message || 'Could not reload the saved grade', 'error');
    } finally {
      setCheckRefreshing(false);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedResults.length === 0) {
      showMessage('Please select results to delete', 'error');
      return;
    }
    showConfirm(
      `Delete ${selectedResults.length} selected result(s)? This action cannot be undone.`,
      async () => {
        try {
          const token = localStorage.getItem('authToken');
          for (const id of selectedResults) {
            await fetch(`${API_BASE_URL}/assess/results/${id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
            });
          }
          showMessage(`Deleted ${selectedResults.length} result(s)`, 'success');
          setSelectedResults([]);
          await fetchAssessmentResults();
        } catch (error) {
          console.error('Error deleting selected:', error);
          showMessage('Failed to delete selected: ' + error.message, 'error');
        }
      },
      { confirmLabel: `Delete Selected (${selectedResults.length})`, cancelLabel: 'Cancel' }
    );
  };

  const handleDeleteAll = () => {
    if (assessmentResults.length === 0) return;
    showConfirm(
      `Are you sure you want to delete all ${assessmentResults.length} assessment results? This action cannot be undone.`,
      async () => {
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch(checkMoreResultsUrl(), {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json();
          if (response.ok) {
            showMessage(`Deleted ${data.deleted_count || assessmentResults.length} assessment results`, 'success');
            setAssessmentResults([]);
            setSelectedResults([]);
          } else {
            showMessage(data.message || 'Failed to delete all results', 'error');
          }
        } catch (error) {
          console.error('Error deleting all results:', error);
          showMessage('Failed to delete all: ' + error.message, 'error');
        }
      },
      { confirmLabel: 'Delete All', cancelLabel: 'Cancel' }
    );
  };


  const fetchStudentLists = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/students/lists`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const sortedLists = (data || []).sort((a, b) => {
          const titleA = `${a.class || ''}-${a.section || ''} ${a.subject || ''}`;
          const titleB = `${b.class || ''}-${b.section || ''} ${a.subject || ''}`;
          return titleA.localeCompare(titleB, undefined, { 
            numeric: true, 
            sensitivity: 'base' 
          });
        });
        setStudentLists(sortedLists);
      }
    } catch (err) {
      console.error('Error fetching student lists:', err);
    }
  };

  // Build options for the student list searchable dropdown.
  // Desired display: "<class> - <subject> (<N> students)"
  // (e.g. "9A+9B - comp - computer-a (38 students)"). The raw class value is
  // preserved as-is so its internal ' - ' is kept; the auto-extracted section
  // is intentionally omitted from the visible label.
  const getStudentListOptions = () => {
    return studentLists.map(list => {
      const cls = (list.class || '').trim();
      const sub = (list.subject || '').trim();
      const base = cls && sub ? `${cls} - ${sub}` : (cls || sub);
      return {
        value: `${list.class}|${list.section}|${list.subject}`,
        label: `${base} (${list.student_count} students)`,
        classVal: list.class,
        section: list.section,
        subject: list.subject
      };
    });
  };

  const handleApprove = async ({ auto = false } = {}) => {
    setApproveLoading(true);
    let saveFailed = false;
    let saveOk = false;

    try {
      const { class: className, section, subject } = approveForm;

      if (!className || !section || !subject) {
        showMessage('Please select a student list', 'error');
        setApproveLoading(false);
        return false;
      }

      if (selectedResults.length === 0) {
        showMessage('Please select results to approve', 'error');
        setApproveLoading(false);
        return false;
      }

      // Declare token early so the clearing block can use it
      const token = localStorage.getItem('authToken');

      // Auto-save the assessment table to the scans folder so the teacher
      // doesn't have to click "Update Saved Table" before Approve. Shows
      // the same success toast the manual button does ("Saved N assessment
      // rows to folder snapshot") so the user can see the save landed.
      // Skips the overwrite-confirm dialog — Approve already implies the
      // teacher is committing this version. Mirrors the payload built by
      // handleSaveAssessmentSnapshot.
      try {
        const fp = (assessmentResults.find(r => r.folder_path) || {}).folder_path || folderPath;
        if (fp && Array.isArray(assessmentResults) && assessmentResults.length > 0) {
          const snapshotPayload = {
            folder_path: fp,
            rows: assessmentResults.map(r => ({
              student_name: r.student_name,
              image_name: r.image_name,
              class: r.class,
              section: r.section,
              subject: r.subject,
              marks: r.marks,
              comments: r.comments,
              table_comments: r.table_comments,
              comments_custom: r.comments_custom ? 1 : 0,
              status: r.status,
              source: r.source,
              ocr_text: r.ocr_text,
              ocr_status: r.ocr_status,
              ocr_unclear_ratio: r.ocr_unclear_ratio,
              ocr_model: r.ocr_model,
              grading_model: r.grading_model,
              pipeline: r.pipeline,
              grading_host: r.grading_host || null,
              ocr_image_note: r.ocr_image_note || null,
              ocr_prompt_tokens: r.ocr_prompt_tokens ?? null,
          ocr_history: r.ocr_history ?? null,
            table_history: r.table_history ?? null,
              ocr_completion_tokens: r.ocr_completion_tokens ?? null
            }))
          };
          const snapRes = await fetch(`${API_BASE_URL}/assess/snapshots`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(snapshotPayload)
          });
          const snapData = await snapRes.json().catch(() => ({}));
          if (snapRes.ok) {
            saveOk = true;
            // Same wording the manual Update Saved Table button uses, so
            // the visual feedback feels identical.
            showMessage(snapData.message || `Saved ${snapshotPayload.rows.length} assessment rows to folder snapshot`, 'success');
            ++tableKeyGenRef.current;
            setSavedTableKey(tableActionKey(assessmentResults));
            setTableKeysReady(true);
            fetchAssessSnapshotMeta(fp);
            // Pre-approve auto-save — fill the Sort-tab "Save" box.
            if (!auto) reportManualCheckStep(fp, 'save', 'done');
          } else {
            saveFailed = true;
            showMessage(snapData.message || 'Failed to auto-save assessment snapshot before approve', 'error');
          }
        }
      } catch (snapErr) {
        saveFailed = true;
        showMessage('Failed to auto-save assessment snapshot before approve: ' + snapErr.message, 'error');
        // Don't block the approve flow on a save failure — the teacher
        // can still hit Update Saved Table manually.
      }

      // Clear ALL existing data in the selected student list before submitting new data
      try {
        const studentsRes = await fetch(
          `${API_BASE_URL}/students?class=${encodeURIComponent(className)}&section=${encodeURIComponent(section)}&subject=${encodeURIComponent(subject)}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (studentsRes.ok) {
          const existingStudents = await studentsRes.json();
          if (existingStudents.length > 0) {
            await Promise.all(
              existingStudents.map(student =>
                fetch(`${API_BASE_URL}/students/${student.id}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                  body: JSON.stringify({ student_name: student.student_name, first_name: '', marks: '', comments: '' })
                })
                .then(r => { if (!r.ok) console.error(`Failed to clear student ${student.id}: ${r.status}`); })
                .catch(e => console.error(`Error clearing student ${student.id}:`, e))
              )
            );
          }
        } else {
          console.error('Failed to fetch students for clearing:', studentsRes.status);
        }
      } catch (clearErr) {
        console.error('Error clearing existing student data:', clearErr);
        // Continue with approve even if clearing fails
      }

      // Guarantee the class-subject label + test date are at the END of every
      // comment that lands in StudentsTab. The completion-handler PUT at line
      // ~466 normally adds this, but it silently skips rows where
      // `rubricMetadata` wasn't loaded, the comment was empty (e.g. an absent
      // student), or a later edit / autoCorrect rebuild dropped the suffix.
      // We use the approve form's class/section/subject — the list the data
      // is being assigned to — as the source of truth so the label matches
      // the destination. The date is the rubric's test_date if known,
      // otherwise today's date (so the comment is still self-describing).
      const courseOnly = `${className}${section ? `-${section}` : ''} ${subject}`.trim();
      const approveDateStr = (rubricMetadata?.test_date
        ? new Date(rubricMetadata.test_date)
        : new Date()
      ).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      const courseLabel = `${courseOnly} ${approveDateStr}`.trim();

      // Get selected results and format for approval
      const selectedData = assessmentResults
        .filter(r => selectedResults.includes(r.id))
        .map(result => {
          let base = (result.comments || '').trim();
          if (result.table_comments) {
            try {
              const approveRepair = repairForResult(result);
              const rebuilt = tableToOneLineComments({
                tableMd: result.table_comments,
                studentName: result.student_name,
                existingComments: result.comments || '',
                allowedIds: approveRepair.allowedIds,
                rubricMarks: approveRepair.rubricMarks,
                paperTotal: approveRepair.paperTotal
              });
              if (rebuilt) base = rebuilt.trim();
            } catch (_) { /* keep stored comments */ }
          }
          const comments = courseLabel && !base.includes(courseLabel)
            ? (base ? `${base} | ${courseLabel}` : courseLabel)
            : base;
          // Authoritative mark = the verification table's grand total — the
          // SAME source of truth auto-correct uses and the Check tab shows.
          // Sending result.marks alone could ship the AI's RAW (often wrong)
          // total to the student list when a row was approved before
          // auto-correct recomputed it (the bug: Check shows 7.9, Students got
          // the AI's 3). Recompute from the table here so Approve always sends
          // the corrected total. Falls back to result.marks when there's no
          // verification table (single-call English-1 rows, OCR-failed/error
          // rows) so those are sent exactly as-is.
          let marks = result.marks;
          if (result.table_comments) {
            try {
              const grand = gradeFromTable(result.table_comments, repairForResult(result));
              if (grand && grand.obtained != null && !isNaN(grand.obtained)) {
                marks = String(Math.round(grand.obtained * 100) / 100);
              }
            } catch (_) { /* keep result.marks */ }
          }
          return {
            name: result.student_name,
            pdf_name: result.image_name,
            marks,
            comments
          };
        });
      
      const response = await fetch(`${API_BASE_URL}/students/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          class: className,
          section,
          subject,
          checkedData: selectedData,
          folder_path: (assessmentResults.find(r => r.folder_path) || {}).folder_path || folderPath || '',
          folder_name: (rubricMetadata && rubricMetadata.title) || '',
          assignment_name: (rubricMetadata && rubricMetadata.assignment_name) || ''
        })
      });
      
      if (response.ok) {
        const result = await response.json();

        let message = result.message;
        if (result.unmatched && result.unmatched.length > 0) {
          message += `. ${result.unmatched.length} unmatched - saved to Mistakes tab`;
        }

        // Make every unmatched (wrong-roll-number) paper stick in MistakesTab
        // by re-posting them with the PERMANENT: prefix. The backend's approve
        // call already inserted them as TEMPORARY rows for the StudentsTab
        // matching workflow, but those rows get auto-purged the next time
        // approve runs within an hour. PERMANENT: records are never purged,
        // so the teacher always has a durable history in MistakesTab.
        if (result.unmatched && result.unmatched.length > 0) {
          for (const u of result.unmatched) {
            try {
              await fetch(`${API_BASE_URL}/students/unmatched`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  class: className,
                  section,
                  subject,
                  name: u.name || u.student_name || '',
                  marks: u.marks || '',
                  comments: 'PERMANENT:' + (u.comments || 'Unmatched on approve — wrong roll number'),
                  teacher_name: rubricMetadata?.created_by || '',
                  test_date: rubricMetadata?.test_date || ''
                })
              });
            } catch (err) {
              console.error('[AssessmentTab] Failed to mirror unmatched paper to Mistakes:', err);
            }
          }
        }

        // After /students/approve succeeds, also save the now-updated student
        // list into the scans folder the user picked at the start of this
        // approve flow (formerly the "3. Ready to Print" button on
        // StudentsTab). We re-fetch the student list so the finalize payload
        // carries the freshly approved marks + comments (not the pre-approve
        // empty rows). Failure here is non-fatal — the approve itself already
        // succeeded; we just warn the user so they know to retry from
        // StudentsTab if they really need it in the folder.
        // Scans-folder save happens up front — on the folder-picker's
        // "Next — Save & Pick Student List" button — not here. By the time
        // we reach handleApprove the assessment table has already been
        // saved to the chosen scans folder. We just clear the lingering
        // folder-selection state so a subsequent approve run starts clean,
        // and do nothing else with student_lists (no mark-absent, no
        // refetch). The /students/approve POST above is the only write
        // that touches student_lists.
        if (selectedScanFolderForApprove) {
          setSelectedScanFolderForApprove(null);
        }

        // Mark the folder's sorted-snapshot 'copy_to_edupage' so the Sort
        // tab badge flips from "Reviewed" → "Copy to Edupage". Runs for both
        // manual and auto approves. Non-fatal: a missing snapshot (404) just
        // means there's nothing to flip. fp = the folder these results came
        // from.
        try {
          const fpForStatus = (assessmentResults.find(r => r.folder_path) || {}).folder_path || folderPath;
          if (fpForStatus) {
            await fetch(`${API_BASE_URL}/analyze/snapshots/status`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ folder_path: fpForStatus, status: 'copy_to_edupage' })
            });
          }
        } catch (statusErr) {
          console.warn('[AssessmentTab] Failed to flip snapshot status to copy_to_edupage:', statusErr);
        }

        // Manual approve — fill the Sort-tab "Approve" box. The backend
        // backfills Run/Save/Scans to done (a later step proves the earlier
        // ones), so a hand-checked folder shows the same green boxes as an
        // auto-checked one. The engine (auto=true) reports its own steps.
        if (!auto) {
          const fpForSteps = (assessmentResults.find(r => r.folder_path) || {}).folder_path || folderPath;
          reportManualCheckStep(fpForSteps, 'approve', 'done');
          if (saveOk) {
            try {
              const tokenHeld = localStorage.getItem('authToken');
              const heldRes = await fetch(`${API_BASE_URL}/analyze/auto-check/finish-held`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenHeld}` },
                body: JSON.stringify({ folder_path: fpForSteps })
              });
              if (heldRes.ok) {
                clearAutoCheckNoticeRef.current();
                setAutoCheckStatus('');
              }
            } catch (_) { /* the chip stays until a later approve */ }
          }
        }

        showMessage(message, 'success');
        setShowApproveForm(false);
        setSelectedResults([]);

        // Save the rubric class-subject label (with date) for display in Students tab.
        // Desired format: "<class> - <subject> <date>"
        // (e.g. "9A+9B - comp - computer-a Apr 16, 2026"). The raw class value is
        // preserved as-is so any internal ' - ' stays intact.
        const rawClass = rubricMetadata ? (rubricMetadata.class || '') : (className || '');
        const rawSubject = rubricMetadata ? (rubricMetadata.subject || '') : (subject || '');
        const cls = rawClass.trim();
        const sub = rawSubject.trim();
        const courseName = cls && sub ? `${cls} - ${sub}` : (cls || sub);
        const testDateStr = rubricMetadata?.test_date
          ? new Date(rubricMetadata.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
          : '';
        const approvedLabel = rubricNameWithDate(
          { class: cls, subject: sub, paper_type: rubricMetadata?.paper_type },
          testDateStr
        ) || courseName;
        localStorage.setItem('lastApprovedAssessmentLabel', approvedLabel);
        // Separate fields for Login Edupage Login (Students tab) — the
        // combined label appends the date onto the course line, which would
        // make the extension fuzzy-match the wrong EduPage course.
        localStorage.setItem('lastApprovedCourseName', courseName);
        if (testDateStr) localStorage.setItem('lastApprovedDate', testDateStr);
        else localStorage.removeItem('lastApprovedDate');
        if (rubricMetadata?.assignment_name) {
          localStorage.setItem('lastApprovedAssignmentName', rubricMetadata.assignment_name);
        } else {
          localStorage.removeItem('lastApprovedAssignmentName');
        }
        const teacherEmail = rubricMetadata?.creator_email || rubricMetadata?.created_by || '';
        if (teacherEmail) {
          localStorage.setItem('lastApprovedCreatedBy', teacherEmail);
        }
        
        // Navigate to Students tab and auto-open the approved list. Skipped
        // for the auto-check engine — navigating away would unmount this tab
        // and kill the engine loop mid-run.
        if (!auto) {
          navigate('/students', {
            state: {
              autoOpenList: {
                class: className,
                section,
                subject
              }
            }
          });
        }
        settleSaveApproveTimer(saveFailed ? 'saving-failed' : 'approved');
        const approvedFp = (assessmentResults.find((row) => row.folder_path) || {}).folder_path || folderPath;
        const approvedKey = tableActionKey(assessmentResults);
        ++tableKeyGenRef.current;
        setSavedTableKey(approvedKey);
        setApprovedTableKey(approvedKey);
        setTableKeysReady(true);
        writeApprovedTableKey(approvedFp, approvedKey);
        return true;
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to approve');
      }
    } catch (err) {
      showMessage(err.message, 'error');
      settleSaveApproveTimer(saveFailed ? 'both-failed' : 'approving-failed');
      return false;
    } finally {
      setApproveLoading(false);
    }
  };

  const handlePreviewImage = async (clickedResult) => {
    // The image preview, OCR pane and Rubrics pane can all be open together
    // (4-pane All Comments layout), so opening an image leaves the rubric
    // pane as-is.
    const rubricIdMatch = folderPath.match(/rubrics\/(\d+)/);
    if (!rubricIdMatch) return;
    const rubricId = rubricIdMatch[1];

    // assessmentResults rows don't carry unique_code; the roll code is in
    // student_name like "(A43)Saman Riaz". Resolve a stable code via either
    // unique_code or by extracting from student_name, then match both ways.
    const codeFor = (r) => (r && r.unique_code) || extractRollNumber(r && r.student_name);
    const code = codeFor(clickedResult);
    const pool = [
      ...(Array.isArray(assessmentResults) ? assessmentResults : []),
      ...(Array.isArray(analyzedData) ? analyzedData : []),
      clickedResult
    ];
    const rowsForStudent = code
      ? pool.filter(r => r && codeFor(r) === code)
      : [clickedResult];

    // Increment the trailing number in a filename (e.g. Image_12312.jpg -> Image_12313.jpg).
    // Preserves leading zeros and the extension. Returns null if no trailing number.
    const nextImageFilename = (name) => {
      const m = String(name).match(/^(.*?)(\d+)(\.[^.]+)?$/);
      if (!m) return null;
      const prefix = m[1];
      const numStr = m[2];
      const ext = m[3] || '';
      const nextNum = String(parseInt(numStr, 10) + 1).padStart(numStr.length, '0');
      return `${prefix}${nextNum}${ext}`;
    };

    // Collect the analyzed (front) filenames for this student, then for each
    // append its N+1 companion (the back side of a double-sided scan). The
    // back is fetched below; if it doesn't exist on the server we silently
    // drop it.
    const seen = new Set();
    const names = [];
    const addName = (n) => {
      if (n && !seen.has(n)) {
        seen.add(n);
        names.push(n);
      }
    };
    const ownScans = Array.isArray(clickedResult.own_scans)
      ? clickedResult.own_scans.map((name) => String(name || '').trim()).filter(Boolean)
      : [];
    if (ownScans.length) {
      ownScans.forEach(addName);
    }
    for (const row of ownScans.length ? [] : rowsForStudent) {
      // analyzedData rows use `image_no`; assessmentResults rows use `image_name`.
      const raw = (row && row.image_name) || (row && row.image_no);
      if (!raw) continue;
      for (const n of String(raw).split(',')) {
        const front = n.trim();
        if (!front) continue;
        addName(front);
        const back = nextImageFilename(front);
        if (back && back !== front) addName(back);
      }
    }
    if (names.length === 0) return;

    const clickedRaw = clickedResult.image_name || clickedResult.image_no || '';
    const clickedName = String(clickedRaw).split(',')[0].trim();

    setPreviewLoading(clickedResult.image_name);
    const token = localStorage.getItem('authToken');
    const fetchOne = async (n) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/scans/${rubricId}/preview/${encodeURIComponent(n)}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) return null;
        const data = await response.json();
        if (data && data.success) return { url: data.downloadUrl, name: n };
      } catch (_) { /* silently drop — e.g. missing back or transient 500 */ }
      return null;
    };

    // Kick off all fetches in parallel. Show the modal as soon as the clicked
    // image resolves, then upgrade to the full gallery once the rest arrive.
    const promises = names.map(fetchOne);
    const clickedIdx = Math.max(0, names.indexOf(clickedName));

    try {
      const first = await promises[clickedIdx];
      if (first) {
        // Carry the clicked result so the All-Comments 3-pane layout can
        // render this student's OCR transcript in the right-hand pane.
        setPreviewImage({ images: [first], index: 0, result: clickedResult });
      }
    } finally {
      setPreviewLoading(null);
    }

    // Backfill the rest without blocking the modal.
    Promise.all(promises).then((results) => {
      const images = results.filter(Boolean);
      if (images.length === 0) return;
      const finalIndex = Math.max(0, images.findIndex(img => img.name === clickedName));
      setPreviewImage(prev => {
        if (!prev) return prev;
        const loaded = prev.result
          && prev.result.id === clickedResult.id
          && prev.result.ocr_text != null
          ? prev.result
          : clickedResult;
        return { images, index: finalIndex, result: loaded };
      });
    });
  };

  const openCommentPage = async (number) => {
    const name = commentPages[number - 1];
    if (!name || !viewingComment) return;
    const images = previewImage && previewImage.images;
    const current = images && images[previewImage.index];
    if (current && current.name === name) {
      setPreviewImage(null);
      return;
    }
    if (images) {
      const index = images.findIndex((img) => img.name === name);
      if (index >= 0) {
        setPreviewImage((prev) => (prev ? { ...prev, index, result: prev.result || viewingComment } : prev));
        return;
      }
    }
    const folder = viewingComment.folder_path || folderPath;
    const rubricMatch = String(folder || '').match(/rubrics\/(\d+)/);
    const rubricId = rubricMatch ? rubricMatch[1] : '';
    if (!rubricId) {
      showMessage('This folder has no scans to open.', 'error');
      return;
    }
    setPreviewLoading(name);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/scans/${rubricId}/preview/${encodeURIComponent(name)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) throw new Error((data && data.message) || 'Image not found');
      setPreviewImage((prev) => {
        const existing = prev && prev.images ? prev.images.slice() : [];
        const found = existing.findIndex((img) => img.name === name);
        if (found >= 0) {
          existing[found] = { url: data.downloadUrl, name };
          return { images: existing, index: found, result: (prev && prev.result) || viewingComment };
        }
        existing.push({ url: data.downloadUrl, name });
        return { images: existing, index: existing.length - 1, result: (prev && prev.result) || viewingComment };
      });
    } catch (err) {
      showMessage(err.message || 'Could not open the image', 'error');
    } finally {
      setPreviewLoading(null);
    }
  };

  // The amber-triangle window: open this student's scan once the row and
  // folder path from the fetch above are both in state.
  useEffect(() => {
    if (!quickCommentPendingRef.current) return;
    if (!viewingComment || !folderPath) return;
    quickCommentPendingRef.current = false;
    handlePreviewImage(viewingComment);
  }, [viewingComment, folderPath, analyzedData]);

  // recheckMode: 'full' (re-OCR + re-grade — default, identical to old behaviour)
  //              | 'grading_only' (two-step only — re-runs step 2 with the
  //                stored ocr_text, skips step 1; backend falls back to 'full'
  //                for any row whose ocr_text is missing or whose ocr_status
  //                is not 'ok').
  //              | 'ocr_only' (two-step only — re-runs step 1 only and
  //                preserves the previously-saved marks / comments. Useful
  //                when you want to fix the OCR transcript without paying
  //                to re-grade. Backend falls back to writing an error row
  //                if the prior grading isn't on file.)
  const handleRecheckSelected = async (recheckMode = 'full', { skipConfirm = false, resultIds = null, results = null, folderPath: folderPathOverride = null, dispatchAuto = false, questionIds = null, summaryKind = null, ocrPages = null, ocrModelOverride = null, ocrProviderOverride = null, providerOverride = null, gradingModelOverride = null, forceTwoStep = false, subjectTypeOverride = null } = {}) => {
    preserveSavedMarksRef.current = false;
    const ids = Array.isArray(resultIds) && resultIds.length ? resultIds : selectedResults;
    const table = Array.isArray(results) && results.length ? results : assessmentResults;
    const fp = folderPathOverride || folderPath;
    console.log('[Recheck] Button clicked, mode:', recheckMode);
    console.log('[Recheck] Selected results:', ids);
    console.log('[Recheck] Folder path:', fp);
    
    if (ids.length === 0) {
      showMessage('Please select results to recheck', 'error');
      return;
    }

    if (!fp) {
      showMessage('No folder path available for recheck', 'error');
      return;
    }

    const runSubject = (subjectTypeOverride === 'urdu' || subjectTypeOverride === 'other')
      ? subjectTypeOverride
      : subjectType;
    if (runSubject === 'none' && !requireMediumOrPrompt()) return;

    let activeRubricText = autoRubricText || rubricText;
    let activePdfRubric = autoPdfRubric || pdfRubric;
    if (!activeRubricText && !activePdfRubric) {
      const loaded = await fetchRubricForFolder(fp, { quiet: true });
      activeRubricText = (loaded && loaded.text) || '';
      activePdfRubric = (loaded && loaded.pdf) || null;
    }

    if (!activeRubricText && !activePdfRubric) {
      showMessage('No rubric found for this folder. Cannot recheck.', 'error');
      return;
    }

    // Get selected results data and extract image names
    const selectedData = table.filter(r => ids.includes(r.id));
    const imageNames = selectedData.map(r => {
      // Extract actual image name from student_name if it's a failed result like "(0000)Image_02996.jpg"
      if (r.student_name && r.student_name.includes('Image_')) {
        const match = r.student_name.match(/(Image_\d+\.\w+)/i);
        if (match) return match[1];
      }
      return r.image_name;
    }).filter(Boolean);

    console.log('[Recheck] Image names to recheck:', imageNames);

    if (imageNames.length === 0) {
      showMessage('No valid images found to recheck', 'error');
      return;
    }

    const pickedQuestionIds = recheckMode === 'grading_only' && Array.isArray(questionIds)
      ? questionIds.filter(Boolean)
      : [];
    const pickedLabels = pickedQuestionIds.map((id) => {
      const hit = rubricQuestions.find((q) => q.id === id);
      return hit ? hit.label : formatQuestionLabel(id);
    });
    const partialOcrLines = ocrPages
      ? selectedData.map((row) => {
          const pages = ocrPages[row.image_name];
          if (!pages || !pages.length) return '';
          return `${row.student_name}: ${formatMissingImageNumbers(pages)}`;
        }).filter(Boolean)
      : [];
    const confirmMsg =
      partialOcrLines.length
        ? `Re-OCR only these images and keep the rest of the transcript?\n\n${partialOcrLines.join('\n')}`
      : recheckMode === 'grading_only' && pickedLabels.length
        ? `Re-grade ${pickedLabels.join(', ')} for ${imageNames.length} student(s) using the stored transcript? Other questions keep their current marks.`
      : recheckMode === 'grading_only'
        ? `Re-grade ${imageNames.length} image(s) using the stored OCR transcript? (Skips re-OCR — fast & cheap.)`
      : recheckMode === 'ocr_only'
        ? `Re-OCR ${imageNames.length} image(s) and KEEP the existing marks/comments? (Useful for fixing the transcript without re-grading.)`
        : `Recheck ${imageNames.length} image(s) and all related student images?`;
    if (!skipConfirm && !window.confirm(confirmMsg)) {
      return;
    }

    const queued = regradeQueueRef.current;
    const queuedRequest = queued && Array.isArray(queued.requests) ? queued.requests[queued.index] : null;
    regradeAdvanceRef.current = (recheckMode === 'grading_only' && queuedRequest && !dispatchAuto) ? queuedRequest.slot : null;

    rememberRegradeSnapshot(selectedData, recheckMode, {
      kind: summaryKind === 'checked' ? 'checked' : 'rechecked',
      folderPath: fp,
      className: (rubricMetadata && rubricMetadata.class) || '',
      section: (rubricMetadata && rubricMetadata.section) || '',
      subject: (rubricMetadata && rubricMetadata.subject) || '',
      testDate: (rubricMetadata && rubricMetadata.test_date) || '',
      assignmentName: (rubricMetadata && rubricMetadata.assignment_name) || ''
    });

    // Save the IDs of results being rechecked so we can delete them after recheck completes
    setRecheckingResultIds([...ids]);

    // Start recheck process directly
    reconciledSigRef.current.clear();
    setIsProcessing(true);
    rememberRunMode(recheckMode);
    setProgress('Starting recheck...');
    setMessage({ show: false, text: '', type: '' });
    setProgressDetails(null);

    try {
      // Build system prompt on frontend
      const systemPrompt = runSubject === 'urdu'
        ? buildPromptUrdu(activeRubricText, activePdfRubric)
        : buildPromptForBatch(activeRubricText, activePdfRubric);

      const token = localStorage.getItem('authToken');
      // English auto-check passes ggml here. The dropdowns stay as saved.
      const ocrModelForRun = (typeof ocrModelOverride === 'string' && ocrModelOverride)
        ? ocrModelOverride
        : ocrModel;
      const gradingModelForRun = (typeof gradingModelOverride === 'string' && gradingModelOverride)
        ? gradingModelOverride
        : gradingModel;
      const derived = providersForCheckModels(ocrModelForRun, gradingModelForRun);
      const currentProvider = providerOverride || derived.provider;
      const ocrProviderForRun = ocrProviderOverride || derived.ocrProvider;
      const requestUsesLocal = currentProvider === 'local' || ocrProviderForRun === 'local';
      // Two-step fields for two-step rechecks (English-2 / Urdu-2).
      // `recheck_mode` tells the backend whether to redo OCR+grading ('full')
      // or only grading on the stored ocr_text ('grading_only'). For
      // 'grading_only' the backend ignores `ocr_system_prompt`; we still send
      // it so a fallback to 'full' (no stored OCR) has the prompt available
      // without an extra request. OCR thinking is hard-off (transcription is
      // mechanical); grading thinking follows the user's toggle.
      const isTwoStep = !!(forceTwoStep || pipelineMode === 'two_step');
      const twoStepFields = isTwoStep
        ? {
            pipeline: 'two_step',
            ocr_model: ocrModelForRun,
            grading_model: gradingModelForRun,
            ...(ocrProviderForRun ? { ocr_provider: ocrProviderForRun } : {}),
            ...(!dispatchAuto && requestUsesLocal ? { dispatch_manual: true } : {}),
            ocr_unclear_threshold: OCR_UNCLEAR_THRESHOLD,
            recheck_mode: recheckMode,
            ocr_system_prompt: runSubject === 'urdu'
              ? buildPromptUrduOcr()
              : buildPromptOcrEnglish(),
            grading_system_prompt: runSubject === 'urdu'
              ? buildPromptUrduGrading(activeRubricText, activePdfRubric)
              : buildPromptEnglishGrading(activeRubricText, activePdfRubric),
            ocr_enable_thinking: false,
            grading_enable_thinking: thinkingEnabled,
            ...(dispatchAuto ? { dispatch_auto: true } : {}),
            ...(pickedQuestionIds.length ? { question_ids: pickedQuestionIds } : {})
          }
        : {};
      // For two-step rechecks, route the legacy `model` / `local_model` fields
      // to the grading model when re-grading-only, otherwise the OCR model —
      // so the single-call fallback path (old backend) still uses the right
      // model for whichever step is dominant in this request.
      const effectiveModel = isTwoStep
        ? (recheckMode === 'grading_only' ? gradingModelForRun : ocrModelForRun)
        : model;
      // For grading-only rechecks the dominant step is grading, so honour the
      // user's thinking toggle. For full two-step rechecks the dominant step
      // (under single-call fallback) is OCR, so thinking is forced off.
      const effectiveThinking = isTwoStep
        ? (recheckMode === 'grading_only' ? thinkingEnabled : false)
        : thinkingEnabled;
      const response = await fetch(`${API_BASE_URL}/assess/recheck`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          folder_path: fp,
          image_names: imageNames,
          result_ids: ids,
          limit: 'MR1',
          model: currentProvider === 'gemini' ? effectiveModel : 'simple',
          subject_type: runSubject,
          system_prompt: systemPrompt,
          pdf_rubric: activePdfRubric,
          provider: currentProvider,
          source: 'check_more',
          enable_thinking: effectiveThinking,
          ...(requestUsesLocal && effectiveModel ? { local_model: effectiveModel } : {}),
          ...twoStepFields,
          image_size: dispatchAuto ? 'full' : (imageSize === 'full' ? 'full' : 'resized'),
          ocr_send: dispatchAuto ? 'all' : (ocrSend === 'all' ? 'all' : 'per_page'),
          ...(ocrPages ? { ocr_pages: ocrPages } : {})
        })
      });

      const data = await response.json();
      console.log('[Recheck] Response status:', response.status);
      console.log('[Recheck] Response data:', data);

      if (response.status === 202 && data.session_id) {
        // Recheck started, now poll for progress
        console.log('[Recheck] Starting progress polling with sessionId:', data.session_id);
        setSessionId(data.session_id);
        setProgress('Recheck started. Processing students...');
        setSelectedResults([]); // Clear selection after starting recheck
        // Manual (re)check kicked off — light the Sort-tab "Run" box; the
        // progress poller flips it to done/error when the session finishes.
        reportManualCheckStep(fp, 'run', 'running');
        return 'started';
      } else if (response.ok) {
        // Immediate completion — delete old results and refresh
        await deleteOldRecheckResults([...ids]);
        setRecheckingResultIds([]);
        const loadedRows = await fetchAssessmentResults();
        await publishRegradeSummary(loadedRows, fp || folderPath);
        const finishedSlot = regradeAdvanceRef.current;
        regradeAdvanceRef.current = null;
        if (finishedSlot != null && finishQueuedRegradeRef.current) {
          await finishQueuedRegradeRef.current(loadedRows, finishedSlot);
        } else {
          showMessage('Successfully rechecked. Old results replaced with new ones.', 'success');
        }
        setProgress('Recheck completed! Old results replaced.');
        setIsProcessing(false);
        setSelectedResults([]);
        reportManualCheckStep(fp, 'run', 'done');
        return 'completed';
      } else {
        // 409 = shared local AI busy with the other checking browser. The
        // backend rejects BEFORE deleting the prior rows, so nothing is lost;
        // the auto-check engine retries on its next tick.
        console.log('[Recheck] Request failed', response.status);
        regradeAdvanceRef.current = null;
        showMessage(
          data.message || 'Recheck failed',
          response.status === 409 ? 'info' : 'error',
          response.status === 409 ? { canStopActive: true, otherFolder: data.other_folder || null } : {}
        );
        setProgress('');
        setIsProcessing(false);
        setRecheckingResultIds([]);
        clearRegradeSnapshot();
        return response.status === 409 ? 'busy' : 'error';
      }
    } catch (error) {
      console.error('Error rechecking:', error);
      regradeAdvanceRef.current = null;
      showMessage('Failed to recheck: ' + error.message, 'error');
      setProgress('');
      setIsProcessing(false);
      setRecheckingResultIds([]);
      clearRegradeSnapshot();
      return 'error';
    }
  };

  const copyRegradeSummaryText = async () => {
    if (!regradeSummary || !Array.isArray(regradeSummary.rows) || regradeSummary.rows.length === 0) return;
    const lines = [...regradeSummary.rows]
      .sort((a, b) => compareRegradeRows(a, b, regradeSort))
      .map((row) => {
        const change = `New (${formatMarkNumber(row.newMarks)}) - Old (${formatMarkNumber(row.oldMarks)}) = ${formatMarkDelta(row.delta)}`;
        return `${row.name || ''}\t${change}`;
      });
    const ok = await copyToClipboard(lines.join('\n'));
    showMessage(ok ? 'Copied student names and changes' : 'Failed to copy', ok ? 'success' : 'error');
  };

  const copyRegradeSummaryImage = async () => {
    const node = regradeCardRef.current;
    if (!node || regradeCopying) return;
    setRegradeCopying(true);
    // The dialog is capped to the window and the student list scrolls inside
    // it, so a direct capture stops at the last visible row. Paint a copy
    // with no height limit and photograph that instead.
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('title', 'regrade-summary-capture');
    const cardWidth = Math.max(node.offsetWidth || 0, 640);
    Object.assign(iframe.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      width: `${cardWidth}px`,
      height: '600px',
      opacity: '0.01',
      pointerEvents: 'none',
      border: '0',
      zIndex: '2147483646',
      background: '#fff'
    });
    document.body.appendChild(iframe);
    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      const fontFamily = window.getComputedStyle(node).fontFamily || 'system-ui, sans-serif';
      doc.open();
      doc.write('<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;background:#fff"></body></html>');
      doc.close();
      doc.body.style.fontFamily = fontFamily;
      const clone = node.cloneNode(true);
      const unlock = (el) => {
        if (!el || !el.style) return;
        el.style.maxHeight = 'none';
        el.style.overflow = 'visible';
        el.style.height = 'auto';
        el.style.minHeight = 'auto';
        el.style.flex = 'none';
      };
      unlock(clone);
      unlock(clone.querySelector('[data-regrade-body]'));
      clone.style.width = `${cardWidth}px`;
      clone.style.background = '#fff';
      doc.body.appendChild(clone);
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const width = Math.ceil(Math.max(clone.scrollWidth, clone.offsetWidth, cardWidth));
      const height = Math.ceil(Math.max(clone.scrollHeight, clone.offsetHeight));
      iframe.style.width = `${width}px`;
      iframe.style.height = `${height + 8}px`;
      clone.style.width = `${width}px`;
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const canvas = await html2canvas(clone, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        width,
        height,
        windowWidth: width,
        windowHeight: height,
        ignoreElements: (el) => el.dataset && el.dataset.skipCopy === '1'
      });
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((file) => (file ? resolve(file) : reject(new Error('Empty image'))), 'image/png');
      });
      if (!navigator.clipboard || typeof navigator.clipboard.write !== 'function' || typeof window.ClipboardItem !== 'function' || !window.isSecureContext) {
        throw new Error('Clipboard image copy needs HTTPS or localhost.');
      }
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setRegradeCopyNotice('Summary image copied to clipboard!');
    } catch (err) {
      setRegradeCopyNotice(err && err.message ? err.message : 'Could not copy the summary image.');
    } finally {
      try { document.body.removeChild(iframe); } catch (_) { /* already gone */ }
      setRegradeCopying(false);
    }
  };

  const fetchRegradeHistory = async () => {
    setRegradeHistoryLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/assess/regrade-summaries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Failed to load re-grading history');
      setRegradeHistory((Array.isArray(data.summaries) ? data.summaries : []).map(summaryFromServer));
    } catch (err) {
      console.error('[Regrade] history load failed:', err);
    } finally {
      setRegradeHistoryLoading(false);
    }
  };

  const publishRegradeSummary = async (results, folderFallback) => {
    const built = buildRegradeSummary(results);
    if (!built) return;
    const stored = {
      rows: built.rows,
      folderPath: built.folderPath || folderFallback || folderPath || '',
      kind: built.kind === 'checked' ? 'checked' : 'rechecked',
      className: built.className || '',
      section: built.section || '',
      subject: built.subject || '',
      testDate: built.testDate || '',
      assignmentName: built.assignmentName || '',
      paperType: rubricMetadata?.paper_type || ''
    };
    saveRegradeSummaryView(stored);
    setRegradeSaveError('');
    setRegradeSummary(stored);
    setRegradeSummaryOpen(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/assess/regrade-summaries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          folder_path: stored.folderPath,
          kind: stored.kind,
          class_label: stored.className,
          section: stored.section,
          subject: stored.subject,
          test_date: stored.testDate || null,
          assignment_name: stored.assignmentName,
          rows: stored.rows
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.summary) {
        throw new Error(data.message || 'Failed to save re-grading summary');
      }
      const saved = summaryFromServer(data.summary);
      saveRegradeSummaryView(saved);
      setRegradeSummary(saved);
      setRegradeHistory((prev) => [saved, ...prev.filter((item) => item.id !== saved.id)]);
    } catch (err) {
      console.error('[Regrade] history save failed:', err);
      setRegradeSaveError('This summary is open, but it could not be saved to history.');
    }
  };

  const openRegradeHistory = () => {
    setRegradeHistoryScope('all');
    setRegradeHistoryOpen(true);
    fetchRegradeHistory();
  };

  const openSavedRegradeSummary = (summary) => {
    if (!summary || !Array.isArray(summary.rows) || !summary.rows.length) return;
    setRegradeSaveError('');
    setRegradeSummary(summary);
    setRegradeHistoryOpen(false);
    setRegradeSummaryOpen(true);
  };

  useEffect(() => {
    fetchRegradeHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!regradeHistoryOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setRegradeHistoryOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [regradeHistoryOpen]);

  const handleViewComments = (result) => {
    setViewingComment(result);
    setShowCommentTranscript(true);
    const hasRubric = !!(autoRubricText || rubricText || (autoPdfRubric || pdfRubric)?.text);
    if (hasRubric) setShowRubricPane(true);
    handlePreviewImage(result);
  };

  // Other students in this class folder — for the name dropdown on the
  // comments window. Same folder first; if rows have no folder_path, use
  // every result currently on the Check page.
  const commentClassmates = React.useMemo(() => {
    if (!viewingComment) return [];
    const folder = viewingComment.folder_path || folderPath || '';
    const rows = (assessmentResults || []).filter((row) => row && row.id != null);
    const sameFolder = folder
      ? rows.filter((row) => (row.folder_path || folderPath || '') === folder)
      : rows;
    const list = sameFolder.length ? sameFolder : rows;
    if (viewingComment.id != null && !list.some((row) => row.id === viewingComment.id)) {
      list.push(viewingComment);
    }
    return list.slice().sort((a, b) => String(a.student_name || '').localeCompare(
      String(b.student_name || ''),
      undefined,
      { numeric: true, sensitivity: 'base' }
    ));
  }, [viewingComment, assessmentResults, folderPath]);

  const switchViewingComment = (result) => {
    if (!result || (viewingComment && result.id === viewingComment.id)) return;
    setViewingComment(result);
    handlePreviewImage(result);
  };

  // Never-checked rolls, plus a checked roll whose sheets were deleted and
  // replaced. Null while the snapshot is loading.
  const latePapers = React.useMemo(() => {
    if (reviewedRows == null) return null;
    return listLatePapers(reviewedRows, assessmentResults, sheetChanges);
  }, [reviewedRows, assessmentResults, sheetChanges]);

  const latePaperClassList = React.useMemo(() => {
    const byId = new Map((scansFoldersForApprove || []).map((folder) => [String(folder.id), folder]));
    return (latePaperFolders || []).map((row) => {
      const folder = byId.get(String(row && row.rubric_id));
      if (!folder) return null;
      const dateStr = folder.test_date
        ? new Date(folder.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : '';
      return {
        id: folder.id,
        folder,
        label: rubricNameWithDate(folder, dateStr) || folder.title || `Folder #${folder.id}`
      };
    }).filter(Boolean);
  }, [latePaperFolders, scansFoldersForApprove]);

  const regradeStudents = React.useMemo(() => {
    if (regradeByFolder == null) return null;
    const folder = (viewingComment && viewingComment.folder_path) || folderPath || '';
    const match = String(folder).match(/rubrics\/(\d+)/);
    const id = match ? match[1] : '';
    return listRegradeStudents(id ? regradeByFolder[id] : []);
  }, [regradeByFolder, viewingComment, folderPath]);

  const regradeClassList = React.useMemo(() => {
    const byId = new Map((scansFoldersForApprove || []).map((folder) => [String(folder.id), folder]));
    return Object.keys(regradeByFolder || {}).map((id) => {
      if (!listRegradeStudents(regradeByFolder[id]).length) return null;
      const folder = byId.get(String(id));
      if (!folder) return null;
      const dateStr = folder.test_date
        ? new Date(folder.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : '';
      return {
        id: folder.id,
        folder,
        label: rubricNameWithDate(folder, dateStr) || folder.title || `Folder #${folder.id}`
      };
    }).filter(Boolean).sort((a, b) => Number(a.id) - Number(b.id));
  }, [regradeByFolder, scansFoldersForApprove]);

  useEffect(() => {
    if (!regradeVoice) return;
    const path = regradeVoice.folderPath || folderPath;
    if (!path || regradeClassPinnedRef.current === path) return;
    const index = regradeClassList.findIndex((item) => `rubrics/${item.id}` === path);
    if (index < 0) return;
    regradeClassPinnedRef.current = path;
    setRegradeFolderPage(Math.floor(index / 2));
  }, [regradeVoice, folderPath, regradeClassList]);

  const showingRegradeList = paperListMode === 'regrade';
  const commentListStudents = showingRegradeList ? regradeStudents : latePapers;
  const commentListOpening = showingRegradeList ? regradeStudentOpening : latePaperOpening;
  const commentListClasses = showingRegradeList ? regradeClassList : latePaperClassList;
  const commentListPage = showingRegradeList ? regradeFolderPage : latePaperFolderPage;
  const setCommentListPage = showingRegradeList ? setRegradeFolderPage : setLatePaperFolderPage;
  const commentListTone = showingRegradeList
    ? { border: '#7C3AED', bg: '#F5F3FF', text: '#5B21B6', fill: '#7C3AED', nextBorder: '#6D28D9' }
    : { border: '#F59E0B', bg: '#FFFBEB', text: '#92400E', fill: '#F59E0B', nextBorder: '#D97706' };

  const ocrClassmates = React.useMemo(() => {
    if (!viewingOcr) return [];
    const folder = viewingOcr.folder_path || folderPath || '';
    const rows = (assessmentResults || []).filter((row) => row && row.id != null);
    const sameFolder = folder
      ? rows.filter((row) => (row.folder_path || folderPath || '') === folder)
      : rows;
    const list = sameFolder.length ? sameFolder : rows;
    if (viewingOcr.id != null && !list.some((row) => row.id === viewingOcr.id)) {
      list.push(viewingOcr);
    }
    return list.slice().sort((a, b) => String(a.student_name || '').localeCompare(
      String(b.student_name || ''),
      undefined,
      { numeric: true, sensitivity: 'base' }
    ));
  }, [viewingOcr, assessmentResults, folderPath]);

  const switchViewingOcr = (result) => {
    if (!result || (viewingOcr && result.id === viewingOcr.id)) return;
    setViewingOcr(result);
  };

  const studentNameKey = (name) => String(name || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const studentRollKey = (name) => {
    const match = String(name || '').match(/\(([^)]+)\)/);
    return match ? match[1].trim().toLowerCase() : '';
  };
  const sameStudentName = (a, b) => {
    const left = studentNameKey(a);
    const right = studentNameKey(b);
    if (left && left === right) return true;
    const rollLeft = studentRollKey(a);
    const rollRight = studentRollKey(b);
    return !!(rollLeft && rollLeft === rollRight);
  };

  // The folder summary is the list behind "OCR missing (3)". The open row
  // often does not carry those page numbers, so both sources are read.
  const openStudentMissingPages = () => {
    if (!viewingOcr) return [];
    const nums = new Set();
    const add = (pages) => {
      for (const n of parseMissingPages(pages)) nums.add(n);
    };
    add(viewingOcr.ocr_missing_pages);
    for (const row of ocrClassmates) {
      if (sameStudentName(row && row.student_name, viewingOcr.student_name)) add(row.ocr_missing_pages);
    }
    const folder = viewingOcr.folder_path || folderPath || '';
    const rubricMatch = String(folder).match(/rubrics\/(\d+)/);
    const rubricId = rubricMatch ? rubricMatch[1] : '';
    const folderRow = (scansFoldersForApprove || []).find((item) => String(item.id) === String(rubricId));
    for (const item of (folderRow && folderRow.ocr_missing) || []) {
      if (sameStudentName(item && item.student, viewingOcr.student_name)) add(item.pages);
    }
    return [...nums].sort((a, b) => a - b);
  };

  const clearOrphanOcrMissing = async () => {
    if (!viewingOcr || clearingOcrMissing) return;
    const limit = ocrPages.length;
    if (!limit) return;
    const stored = openStudentMissingPages();
    const kept = stored.filter((n) => n >= 1 && n <= limit);
    const dropped = stored.filter((n) => n < 1 || n > limit);
    if (!dropped.length) return;
    const fp = viewingOcr.folder_path || folderPath || '';
    const rubricMatchEarly = String(fp).match(/rubrics\/(\d+)/);
    const rubricIdEarly = rubricMatchEarly ? rubricMatchEarly[1] : '';
    const folderRowEarly = (scansFoldersForApprove || []).find((item) => String(item.id) === String(rubricIdEarly));
    const listed = ((folderRowEarly && folderRowEarly.ocr_missing) || []).find((item) => (
      sameStudentName(item && item.student, viewingOcr.student_name)
    ));
    const name = (listed && listed.student) || viewingOcr.student_name;
    if (!fp || !name) {
      showMessage('This transcript has no folder or student to update.', 'error');
      return;
    }
    setClearingOcrMissing(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/assess/snapshots/ocr`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          folder_path: fp,
          student_name: name,
          ocr_missing_keep: kept.join(',')
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      const value = data.ocr_missing_pages == null ? kept.join(',') : String(data.ocr_missing_pages);
      const key = studentNameKey(name);
      const applyRow = (row) => (
        row && studentNameKey(row.student_name) === key && (row.folder_path || fp) === fp
          ? { ...row, ocr_missing_pages: value }
          : row
      );
      setAssessmentResults((prev) => prev.map(applyRow));
      setViewingOcr((prev) => (prev ? { ...applyRow(prev), ocr_missing_pages: value } : prev));
      setPickedOcrPages(parseMissingPages(value));
      const rubricMatch = String(fp).match(/rubrics\/(\d+)/);
      const rubricId = rubricMatch ? rubricMatch[1] : '';
      if (rubricId) {
        setScansFoldersForApprove((prev) => prev.map((folder) => {
          if (String(folder.id) !== String(rubricId)) return folder;
          const list = Array.isArray(folder.ocr_missing) ? folder.ocr_missing : [];
          const next = [];
          for (const item of list) {
            if (studentNameKey(item && item.student) !== key && !sameStudentName(item && item.student, name)) {
              next.push(item);
              continue;
            }
            if (value) next.push({ ...item, pages: value });
          }
          return { ...folder, ocr_missing: next };
        }));
      }
      showMessage(
        value
          ? `Cleared ${formatMissingImageNumbers(dropped)}. Pages ${formatMissingImageNumbers(value)} still have no transcript.`
          : `Cleared ${formatMissingImageNumbers(dropped)}.`,
        'success'
      );
      window.dispatchEvent(new CustomEvent('ocr-missing-cleared'));
    } catch (err) {
      showMessage('Could not clear OCR missing: ' + err.message, 'error');
    } finally {
      setClearingOcrMissing(false);
    }
  };

  const unexpectedBlankStudents = React.useMemo(() => {
    if (!viewingOcr) return [];
    const folder = viewingOcr.folder_path || folderPath || '';
    const rubricMatch = String(folder).match(/rubrics\/(\d+)/);
    const rubricId = rubricMatch ? rubricMatch[1] : '';
    const apiEntry = (unexpectedBlankFolders || []).find((row) => String(row && row.rubric_id) === String(rubricId));
    const byName = new Map();
    for (const row of ocrClassmates) {
      const pages = unexpectedBlankPages(row.ocr_text);
      if (!pages.length) continue;
      const key = studentNameKey(row.student_name) || String(row.id);
      if (!byName.has(key)) byName.set(key, { name: row.student_name || `Student ${row.id}`, pages, row });
    }
    for (const student of (apiEntry && apiEntry.students) || []) {
      const key = studentNameKey(student && student.name);
      if (!key || byName.has(key)) continue;
      const live = ocrClassmates.find((row) => studentNameKey(row.student_name) === key);
      if (live && live.ocr_text != null && !unexpectedBlankPages(live.ocr_text).length) continue;
      const pages = Array.isArray(student.pages) ? student.pages : [];
      byName.set(key, { name: student.name, pages, row: live || null });
    }
    return Array.from(byName.values()).sort((a, b) => String(a.name || '').localeCompare(
      String(b.name || ''),
      undefined,
      { numeric: true, sensitivity: 'base' }
    ));
  }, [viewingOcr, ocrClassmates, unexpectedBlankFolders, folderPath]);

  const unexpectedBlankClassList = React.useMemo(() => {
    const byId = new Map((scansFoldersForApprove || []).map((folder) => [String(folder.id), folder]));
    return (unexpectedBlankFolders || []).map((row) => {
      const folder = byId.get(String(row && row.rubric_id));
      if (!folder) return null;
      const dateStr = folder.test_date
        ? new Date(folder.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : '';
      return {
        id: folder.id,
        folder,
        label: rubricNameWithDate(folder, dateStr) || folder.title || `Folder #${folder.id}`
      };
    }).filter(Boolean);
  }, [unexpectedBlankFolders, scansFoldersForApprove]);

  const thinkingStudents = React.useMemo(() => {
    if (!viewingOcr) return [];
    const folder = viewingOcr.folder_path || folderPath || '';
    const rubricMatch = String(folder).match(/rubrics\/(\d+)/);
    const rubricId = rubricMatch ? rubricMatch[1] : '';
    const apiEntry = (thinkingFolders || []).find((row) => String(row && row.rubric_id) === String(rubricId));
    const byName = new Map();
    const add = (name, phrases, row) => {
      const key = studentNameKey(name);
      const list = (Array.isArray(phrases) ? phrases : []).filter(Boolean);
      if (!key || !list.length) return;
      if (!byName.has(key)) byName.set(key, { name: String(name).replace(/\s+/g, ' ').trim(), phrases: list, row: row || null });
    };
    for (const row of ocrClassmates) {
      add(row.student_name, thinkingPhrasesIn(`${row.comments || ''}\n${row.table_comments || ''}`), row);
    }
    for (const student of (apiEntry && apiEntry.students) || []) {
      const key = studentNameKey(student && student.name);
      if (!key || byName.has(key)) continue;
      const live = ocrClassmates.find((row) => studentNameKey(row.student_name) === key);
      if (live && !thinkingPhrasesIn(`${live.comments || ''}\n${live.table_comments || ''}`).length) continue;
      add(student.name, student.phrases, live || null);
    }
    return Array.from(byName.values()).sort((a, b) => String(a.name || '').localeCompare(
      String(b.name || ''),
      undefined,
      { numeric: true, sensitivity: 'base' }
    ));
  }, [viewingOcr, ocrClassmates, thinkingFolders, folderPath]);

  const thinkingClassList = React.useMemo(() => {
    const byId = new Map((scansFoldersForApprove || []).map((folder) => [String(folder.id), folder]));
    return (thinkingFolders || []).map((row) => {
      const folder = byId.get(String(row && row.rubric_id));
      if (!folder) return null;
      const dateStr = folder.test_date
        ? new Date(folder.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : '';
      return {
        id: folder.id,
        folder,
        label: rubricNameWithDate(folder, dateStr) || folder.title || `Folder #${folder.id}`
      };
    }).filter(Boolean);
  }, [thinkingFolders, scansFoldersForApprove]);

  const ocrMissingStudents = React.useMemo(() => {
    if (!viewingOcr) return [];
    const folder = viewingOcr.folder_path || folderPath || '';
    const rubricMatch = String(folder).match(/rubrics\/(\d+)/);
    const rubricId = rubricMatch ? rubricMatch[1] : '';
    const folderRow = (scansFoldersForApprove || []).find((item) => String(item.id) === String(rubricId));
    const byName = new Map();
    const add = (name, pages) => {
      const key = studentNameKey(name);
      const nums = parseMissingPages(pages);
      if (!key || !nums.length) return;
      if (!byName.has(key)) byName.set(key, { name: String(name).replace(/\s+/g, ' ').trim(), pages: nums.map(String) });
    };
    for (const row of ocrClassmates) add(row.student_name, row.ocr_missing_pages);
    for (const item of (folderRow && folderRow.ocr_missing) || []) add(item && item.student, item && item.pages);
    return Array.from(byName.values()).sort((a, b) => String(a.name || '').localeCompare(
      String(b.name || ''),
      undefined,
      { numeric: true, sensitivity: 'base' }
    ));
  }, [viewingOcr, ocrClassmates, scansFoldersForApprove, folderPath]);

  const ocrMissingClassList = React.useMemo(() => {
    return (scansFoldersForApprove || []).filter((folder) => (
      Array.isArray(folder.ocr_missing) && folder.ocr_missing.length > 0
    )).map((folder) => {
      const dateStr = folder.test_date
        ? new Date(folder.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : '';
      return {
        id: folder.id,
        folder,
        label: rubricNameWithDate(folder, dateStr) || folder.title || `Folder #${folder.id}`
      };
    });
  }, [scansFoldersForApprove]);

  const showingMissingOcr = ocrListMode === 'missing';
  const showingThinkingMarks = ocrListMode === 'thinking';
  const ocrFlagStudents = showingThinkingMarks ? thinkingStudents : showingMissingOcr ? ocrMissingStudents : unexpectedBlankStudents;
  const ocrFlagClasses = showingThinkingMarks ? thinkingClassList : showingMissingOcr ? ocrMissingClassList : unexpectedBlankClassList;
  const ocrFlagPage = showingThinkingMarks ? thinkingFolderPage : showingMissingOcr ? ocrMissingFolderPage : unexpectedBlankFolderPage;
  const setOcrFlagPage = showingThinkingMarks ? setThinkingFolderPage : showingMissingOcr ? setOcrMissingFolderPage : setUnexpectedBlankFolderPage;
  const ocrFlagLabel = showingThinkingMarks ? 'Thinking in marks' : showingMissingOcr ? 'OCR missing' : 'Unexpected blank page';
  const ocrFlagEmpty = showingThinkingMarks ? 'No thinking in marks' : showingMissingOcr ? 'No OCR missing' : 'No unexpected blank page';
  const ocrFlagTone = showingThinkingMarks
    ? { border: '#FECACA', bg: '#FEF2F2', text: '#991B1B', fill: '#DC2626', nextBorder: '#DC2626' }
    : showingMissingOcr
      ? { border: '#FDBA74', bg: '#FFF7ED', text: '#C2410C', fill: '#EA580C', nextBorder: '#C2410C' }
      : { border: '#F59E0B', bg: '#FFFBEB', text: '#92400E', fill: '#F59E0B', nextBorder: '#D97706' };

  const openTranscriptByName = async (name, pageLabel) => {
    const want = studentNameKey(name);
    if (!want || transcriptStudentOpening) return;
    const pageMatch = String(pageLabel || '').match(/(\d+)/);
    const page = pageMatch ? Number(pageMatch[1]) : 0;
    const armPage = () => {
      if (!page) return;
      pendingOcrPageRef.current = page;
      openedOcrForRef.current = null;
    };
    const local = ocrClassmates.find((row) => studentNameKey(row.student_name) === want);
    if (local) {
      armPage();
      if (viewingOcr && local.id === viewingOcr.id) {
        setShowOcrTranscriptPane(true);
        if (page && ocrPages[page - 1]) loadOcrPageImage(page);
        return;
      }
      switchViewingOcr(local);
      setShowOcrTranscriptPane(true);
      return;
    }
    const fp = (viewingOcr && viewingOcr.folder_path) || folderPath;
    if (!fp) return;
    setTranscriptStudentOpening(want);
    try {
      const token = localStorage.getItem('authToken');
      const rubricMatch = String(fp).match(/rubrics\/(\d+)/);
      const params = new URLSearchParams({
        rubricId: rubricMatch ? rubricMatch[1] : '',
        name
      });
      const response = await fetch(`${API_BASE_URL}/assess/student-ocr?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.result) {
        throw new Error((data && data.message) || 'Could not open this transcript');
      }
      if (Array.isArray(data.analyzed) && data.analyzed.length) {
        setAnalyzedData((prev) => {
          const seen = new Set((prev || []).map((row) => String(row.image_no || row.image_name || '').trim().toLowerCase()));
          const extra = data.analyzed.filter((row) => {
            const key = String(row.image_no || row.image_name || '').trim().toLowerCase();
            return key && !seen.has(key);
          });
          return extra.length ? [...(prev || []), ...extra] : prev;
        });
      }
      const opened = data.result;
      setAssessmentResults((prev) => {
        const rest = (prev || []).filter((row) => !row || row.id !== opened.id);
        return [...rest, opened];
      });
      armPage();
      setViewingOcr(opened);
      setShowOcrTranscriptPane(true);
    } catch (err) {
      showMessage((err && err.message) || 'Could not open this transcript', 'error');
    } finally {
      setTranscriptStudentOpening('');
    }
  };

  const toggleOcrRubricPane = async () => {
    if (showOcrRubricPane) {
      setShowOcrRubricPane(false);
      return;
    }
    if (!autoRubricText && !(autoPdfRubric && autoPdfRubric.text)) {
      const loaded = await fetchRubricForFolder(folderPath || (viewingOcr && viewingOcr.folder_path) || '');
      const gotFolder = loaded && (loaded.text || (loaded.pdf && loaded.pdf.text));
      if (!gotFolder && !rubricText && !(pdfRubric && pdfRubric.text)) return;
    }
    setRubricVisible(true);
    setShowOcrRubricPane(true);
  };

  const openLatePaper = async (roll) => {
    const student = (latePapers || []).find((item) => item.roll === roll);
    if (!student || !folderPath || latePaperOpening) return;
    setLatePaperOpening(roll);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/assess/late-paper`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ folder_path: folderPath, roll: student.roll })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.result) {
        throw new Error((data && data.message) || 'Could not open this late paper');
      }
      const opened = {
        ...data.result,
        own_scans: Array.isArray(data.result.own_scans) ? data.result.own_scans : []
      };
      if (Array.isArray(data.analyzed) && data.analyzed.length) {
        setAnalyzedData((prev) => {
          const seen = new Set((prev || []).map((row) => String(row.image_no || row.image_name || '').trim().toLowerCase()));
          const extra = data.analyzed.filter((row) => {
            const key = String(row.image_no || row.image_name || '').trim().toLowerCase();
            return key && !seen.has(key);
          });
          return extra.length ? [...(prev || []), ...extra] : prev;
        });
      }
      setAssessmentResults((prev) => {
        const rest = (prev || []).filter((row) => !row || row.id !== opened.id);
        return [...rest, opened];
      });
      handleViewComments(opened);
      refreshLatePaperFolders();
    } catch (err) {
      setLatePaperFailure(latePaperOpenFailure(
        (err && err.message) || 'Could not open this late paper',
        student && student.name
      ));
    } finally {
      setLatePaperOpening('');
    }
  };

  const openRegradeStudent = async (key) => {
    const student = (regradeStudents || []).find((item) => item.key === key);
    if (!student || regradeStudentOpening) return;
    const wantName = studentNameKey(student.name);
    const wantRoll = String(student.roll || '').trim().toLowerCase();
    const matches = commentClassmates.filter((row) => {
      const name = studentNameKey(row.student_name);
      if (wantName && name === wantName) return true;
      const roll = rollFromLabel(row.student_name);
      return !!(wantRoll && roll && wantRoll === roll);
    });
    const local = matches.find((row) => row.table_comments) || matches[0];
    if (local) {
      switchViewingComment(local);
      return;
    }
    const fp = (viewingComment && viewingComment.folder_path) || folderPath;
    if (!fp) return;
    setRegradeStudentOpening(key);
    try {
      const token = localStorage.getItem('authToken');
      const rubricMatch = String(fp).match(/rubrics\/(\d+)/);
      const params = new URLSearchParams({
        rubricId: rubricMatch ? rubricMatch[1] : '',
        name: student.name,
        kind: 'comment'
      });
      const response = await fetch(`${API_BASE_URL}/assess/student-ocr?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.result) {
        throw new Error((data && data.message) || 'Could not open this re-grade student');
      }
      if (Array.isArray(data.analyzed) && data.analyzed.length) {
        setAnalyzedData((prev) => {
          const seen = new Set((prev || []).map((row) => String(row.image_no || row.image_name || '').trim().toLowerCase()));
          const extra = data.analyzed.filter((row) => {
            const imageKey = String(row.image_no || row.image_name || '').trim().toLowerCase();
            return imageKey && !seen.has(imageKey);
          });
          return extra.length ? [...(prev || []), ...extra] : prev;
        });
      }
      const opened = {
        ...data.result,
        own_scans: Array.isArray(data.result.own_scans) ? data.result.own_scans : []
      };
      setAssessmentResults((prev) => {
        const rest = (prev || []).filter((row) => !row || row.id !== opened.id);
        return [...rest, opened];
      });
      handleViewComments(opened);
    } catch (err) {
      showMessage((err && err.message) || 'Could not open this re-grade student', 'error');
    } finally {
      setRegradeStudentOpening('');
    }
  };

  const closeViewComments = () => {
    setViewingComment(null);
    setShowCommentTranscript(false);
    if (!showAllComments) {
      setPreviewImage(null);
      setShowRubricPane(false);
    }
    if (quickTranscript && quickTranscript.mode === 'comment') {
      const close = quickCloseRef.current;
      if (typeof close === 'function') close();
    }
  };

  const showMessage = (text, type, extra = {}) => {
    setMessage({ show: true, text, type, ...extra });
  };

  // Hand the rubric to the Login Edupage extension (same as Sort-tab Login).
  // Build the card text ourselves — do NOT scrape the header innerText. The
  // visible title appends the date onto the course line ("10A+10B - urdu-a
  // Jul 31, 2026"), and the extension would then fuzzy-match the wrong EduPage
  // course (e.g. "10A+10B - URDU"). Sort cards put each field on its own line;
  // mirror that layout here.
  const loginToEduPageForCard = (e) => {
    e.stopPropagation();
    const meta = rubricMetadata || {};
    const course =
      meta.class && meta.subject
        ? `${meta.class} - ${meta.subject}`
        : (meta.class || meta.subject || '');
    const dateStr = meta.test_date
      ? new Date(meta.test_date).toLocaleDateString('en-US', {
          year: 'numeric', month: 'short', day: 'numeric'
        })
      : '';
    const lines = [
      course,
      meta.assignment_name || '',
      dateStr,
      meta.creator_email || ''
    ].map((l) => String(l).trim()).filter(Boolean);
    const text = lines.join('\n');
    if (!text.trim()) return;
    window.postMessage({ source: 'fastgrader-edu', type: 'EDU_LOGIN_CARD', text }, '*');
    showMessage(
      eduBridgeReady
        ? `Logging into EduPage for ${meta.creator_email || 'this account'}…`
        : 'Sent to Login Edupage — if nothing opens, install/update the extension and reload.',
      'info'
    );
  };

  // ---------------------------------------------------------------------------
  // Get Text — populate the OCR column from .docx files on the teacher's PC
  // ---------------------------------------------------------------------------
  // Fully automatic: the page already knows the class/section/subject and
  // test_date from the active rubric, so we ask the local docx-server
  // (port 3100, npm run docx-server) to resolve <BASE_PATH>/<Month>/<Day>/<class
  // folder>/, read every .docx in there via mammoth, and return per-file text.
  // We then group those transcripts by student using analyzedData (image →
  // unique_code) and POST the batch to /assess/results/from-text so the rows
  // are persisted in the DB and survive a refresh.
  //
  // Prereq: GET_TEXT_BASE_PATH (or SCANS_BASE_PATH) in .env points at your
  // Subjective-Scans root. Follow the host the app was opened from so the
  // docx-server on that SAME machine is reached — not the browser PC's localhost.
  const DOCX_SERVER_URL = `http://${window.location.hostname}:3100`;
  const [isGettingText, setIsGettingText] = useState(false);

  // Strip extension and lowercase — "Image_12345.JPG" / "Image_12345.docx" both
  // collapse to "image_12345" so the lookup is case- and extension-insensitive.
  const stripExtLower = (name) => {
    const s = String(name || '');
    const dot = s.lastIndexOf('.');
    return (dot > 0 ? s.slice(0, dot) : s).toLowerCase();
  };

  const handleGetText = async () => {
    preserveSavedMarksRef.current = false;
    if (isProcessing || isGettingText) return;
    if (!analyzedData || analyzedData.length === 0) {
      showMessage('Run Sort first so students are grouped before clicking Urdu Step 1.', 'error');
      return;
    }
    const meta = rubricMetadata || {};
    if (!meta.test_date) {
      showMessage('No test_date on the active rubric — cannot resolve the day folder for Urdu Step 1.', 'error');
      return;
    }
    if (!meta.class || !meta.subject) {
      showMessage('Active rubric is missing class or subject — cannot resolve the local folder for Urdu Step 1.', 'error');
      return;
    }

    // "Pick Students" mode: restrict Text Step 1 to just the selected
    // student(s) — same behaviour as Start Eng Checking / OCR. We still read
    // the whole day folder's .docx (the local-server resolves it by
    // class/section/subject/date), but only the picked students' transcripts
    // are grouped and saved below, so no one else's row is created.
    if (selectionMode === 'custom' && selectedStudentCodes.length === 0) {
      showMessage('Please pick at least one student, or switch back to Student Range.', 'error');
      return;
    }
    const pickedCodeSet = selectionMode === 'custom' ? new Set(selectedStudentCodes) : null;

    setIsGettingText(true);
    setProgress(pickedCodeSet
      ? `Asking local server for docx text (${selectedStudentCodes.length} picked student${selectedStudentCodes.length === 1 ? '' : 's'})...`
      : 'Asking local server for docx text...');

    try {
      // 1) Ask the local-server to read every .docx in the matching folder.
      const resp = await fetch(`${DOCX_SERVER_URL}/get-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          class: meta.class,
          section: meta.section || '',
          subject: meta.subject,
          test_date: meta.test_date
        })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) {
        const detail = data && data.error ? data.error : `HTTP ${resp.status}`;
        const hint = data && data.available && data.available.length > 0
          ? ` Folders present in that day: ${data.available.slice(0, 10).join(', ')}${data.available.length > 10 ? ', …' : ''}`
          : '';
        throw new Error(`${detail}.${hint}`);
      }
      const filesFromServer = Array.isArray(data.files) ? data.files : [];
      if (filesFromServer.length === 0) {
        // Surface the local-server's diagnostic so the teacher can see WHY
        // the folder appeared empty (cloud placeholders, wrong extensions,
        // truly empty, etc.).
        const bits = [`No .docx files found in ${data.folder || 'the day folder'}.`];
        // The full resolved path is the most useful diagnostic: a missing
        // SCANS_BASE_PATH in .env makes the local-server read from a stale
        // OneDrive default instead of the Drive folder the teacher meant.
        if (data.base_path) bits.push(`Read path: ${data.base_path}`);
        if (data.note) bits.push(data.note);
        if (typeof data.entries_total === 'number') {
          const split = (typeof data.entries_node === 'number' && typeof data.entries_win === 'number')
            ? ` (Node fs: ${data.entries_node}, Win dir: ${data.entries_win})`
            : '';
          bits.push(`Folder has ${data.entries_total} entry/entries total${split}.`);
        }
        if (Array.isArray(data.entries_preview) && data.entries_preview.length > 0) {
          bits.push(`First entries: ${data.entries_preview.slice(0, 8).join(', ')}${data.entries_preview.length > 8 ? ', …' : ''}.`);
        }
        showMessage(bits.join(' '), 'error');
        console.warn('[GetText] empty-folder diagnostic:', data);
        return;
      }

      // 2) Index server text by lowercased basename for lookup, AND keep a
      //    parallel canonical-name map (case-preserved + .jpg extension) so
      //    the names we write to assessment_results.image_name actually match
      //    what `analyzed_papers.image_no` stores (e.g. "Image_40137.jpg") —
      //    otherwise the recheck endpoint can't resolve the student.
      //
      //    Sorted basenames let us derive back / continuation pages from
      //    each front-page row in analyzedData; the front-only convention of
      //    analyzed_papers means siblings are implicit (the next sequential
      //    scans). `batch_size_final` tells us how many consecutive images
      //    belong to one sheet (defaults to 2 = front + back).
      const textByKey = new Map();          // 'image_40137' -> '<plain text>'
      const canonicalByKey = new Map();     // 'image_40137' -> 'Image_40137.jpg'

      // Pull the canonical image extension from the first analyzedData row
      // (almost always .jpg in this codebase, but honour whatever's stored).
      let canonicalExt = '.jpg';
      for (const item of analyzedData) {
        const ref = item && (item.image_no || item.image_name);
        if (ref && /\.[a-z0-9]+$/i.test(ref)) {
          const m = ref.match(/\.[a-z0-9]+$/i);
          if (m) { canonicalExt = m[0]; break; }
        }
      }

      for (const f of filesFromServer) {
        if (!f || !f.image_name) continue;
        const baseName = String(f.image_name); // case-preserved (.docx already stripped server-side)
        const key = baseName.toLowerCase();
        textByKey.set(key, (f.text || '').trim());
        canonicalByKey.set(key, `${baseName}${canonicalExt}`);
      }
      const sortedKeys = Array.from(textByKey.keys()).sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      );
      const indexByKey = new Map(sortedKeys.map((k, i) => [k, i]));

      // 3) Build the per-student image list by EXPANDING each analyzedData
      //    row from its front page into that sheet's front+back only.
      //    One Sort row = one sheet front. Do NOT use batch_size_final here:
      //    that is the student's TOTAL page count (sheets×2). Applying it on
      //    every front walked consecutive docs into the next student's pages
      //    for multi-sheet students (e.g. Mahoor picking up Hania's OCR).
      const grouped = new Map();
      for (const item of analyzedData) {
        if (!item || !item.unique_code) continue;
        // Pick Students mode: skip everyone who isn't in the selection so only
        // the picked student(s)' docx transcript is built and saved.
        if (pickedCodeSet && !pickedCodeSet.has(item.unique_code)) continue;
        const code = item.unique_code;
        if (!grouped.has(code)) {
          grouped.set(code, {
            unique_code: code,
            roll_name: item.roll_name || item.student_name || code,
            class: item.class || '',
            section: item.section || '',
            subject: item.subject || '',
            images: []
          });
        }
        const frontRaw = item.image_no || item.image_name;
        if (!frontRaw) continue;
        const frontKey = stripExtLower(frontRaw);
        const frontIdx = indexByKey.get(frontKey);

        // One Sort row = one sheet: front + back only.
        const span = 2;

        if (frontIdx == null) {
          // No docx for the front page; keep the raw analyzedData name so it
          // surfaces in the missing-per-student summary later.
          if (!grouped.get(code).images.includes(frontRaw)) {
            grouped.get(code).images.push(frontRaw);
          }
          continue;
        }
        for (let i = 0; i < span; i++) {
          const siblingKey = sortedKeys[frontIdx + i];
          if (!siblingKey) break;
          const sibling = canonicalByKey.get(siblingKey) || siblingKey;
          if (!grouped.get(code).images.includes(sibling)) {
            grouped.get(code).images.push(sibling);
          }
        }
      }
      // Sort each student's images so the transcript reads in scan order:
      // front, back, next front, next back, etc.
      for (const s of grouped.values()) {
        s.images.sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));
      }

      // 4) Build the per-student payload, skipping students with no matches.
      const studentsPayload = [];
      const missingPerStudent = [];
      const students = Array.from(grouped.values()).sort((a, b) =>
        String(a.unique_code).localeCompare(String(b.unique_code), undefined, { numeric: true, sensitivity: 'base' })
      );
      for (const s of students) {
        const parts = [];
        const matchedImages = [];
        const missing = [];
        for (const img of s.images) {
          const text = textByKey.get(stripExtLower(img));
          if (text == null) {
            missing.push(img);
            continue;
          }
          parts.push(`--- ${img} ---\n${text}`);
          matchedImages.push(img);
        }
        if (missing.length > 0) missingPerStudent.push({ roll_name: s.roll_name, missing });
        if (parts.length === 0) continue;
        studentsPayload.push({
          student_name: s.roll_name,
          image_names: matchedImages,
          class: s.class || null,
          section: s.section || null,
          subject: s.subject || null,
          ocr_text: parts.join('\n\n')
        });
      }

      if (studentsPayload.length === 0) {
        showMessage('Local server returned docx files, but none matched the sorted students. Check that file names match the image names.', 'error');
        return;
      }

      // 5) Persist via the backend so rows survive a page refresh.
      setProgress(`Saving ${studentsPayload.length} student row(s)...`);
      const token = localStorage.getItem('authToken');
      const saveResp = await fetch(`${API_BASE_URL}/assess/results/from-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          folder_path: folderPath,
          source: 'check_more',
          students: studentsPayload
        })
      });
      const saveBody = await saveResp.json().catch(() => ({}));
      if (!saveResp.ok || !saveBody.success) {
        throw new Error(saveBody.message || `Backend rejected the save (HTTP ${saveResp.status})`);
      }

      await fetchAssessmentResultsLite();
      setSelectedResults([]);

      // Report exactly what the backend saved. Never fall back to
      // studentsPayload.length, that hides per-row insert failures (e.g. a
      // CHECK constraint violation) behind a fake success message.
      const savedCount = typeof saveBody.count === 'number' ? saveBody.count : 0;
      const skippedBackend = Array.isArray(saveBody.skipped) ? saveBody.skipped : [];

      const summaryBits = [];
      if (savedCount > 0) {
        summaryBits.push(`Saved transcripts for ${savedCount} student(s) from ${filesFromServer.length} docx file(s) in "${data.folder}".`);
      } else {
        summaryBits.push(`No rows were saved (backend returned count=0). Check the backend console for insert errors.`);
      }
      if (skippedBackend.length > 0) {
        const sample = skippedBackend.slice(0, 3).map(s => `${s.student}: ${s.reason}`).join('; ');
        summaryBits.push(`Backend skipped ${skippedBackend.length} row(s) — ${sample}${skippedBackend.length > 3 ? ', …' : ''}.`);
      }
      if (missingPerStudent.length > 0) {
        const sample = missingPerStudent.slice(0, 3).map(m => `${m.roll_name} (missing ${m.missing.length})`).join('; ');
        summaryBits.push(`Some images had no matching docx — e.g. ${sample}${missingPerStudent.length > 3 ? ', …' : ''}.`);
      }
      const toastType = savedCount === 0
        ? 'error'
        : (skippedBackend.length > 0 || missingPerStudent.length > 0 ? 'info' : 'success');
      showMessage(summaryBits.join(' '), toastType);
    } catch (err) {
      console.error('[GetText] Failed:', err);
      const hint = /failed to fetch|networkerror/i.test(String(err && err.message))
        ? ' Is the docx-server running on port 3100? (npm run docx-server)'
        : '';
      showMessage(`Urdu Step 1 failed: ${err.message || err}.${hint}`, 'error');
    } finally {
      setIsGettingText(false);
      setProgress('');
    }
  };

  const handleSort = (field) => {
    let direction = 'asc';
    if (sortConfig.column === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const sorted = [...assessmentResults].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];

      // Numeric sorting for marks
      if (field === 'marks') {
        // Handle ERROR or non-numeric marks
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        
        // Put ERROR values at the end
        if (isNaN(aNum) && isNaN(bNum)) return 0;
        if (isNaN(aNum)) return direction === 'asc' ? 1 : -1;
        if (isNaN(bNum)) return direction === 'asc' ? -1 : 1;
        
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // String comparison for other fields
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setAssessmentResults(sorted);
    setSortConfig({ column: field, direction });
  };

  const handleCellEdit = (resultId, field, currentValue) => {
    setEditingCell({ id: resultId, field, value: currentValue });
  };

  const handleCellChange = (value) => {
    setEditingCell(prev => ({ ...prev, value }));
  };

  const handleCellBlur = async () => {
    if (!editingCell) return;

    const { id, field, value } = editingCell;
    const originalResult = assessmentResults.find(r => r.id === id);
    
    // If value hasn't changed, just exit editing mode
    if (value === originalResult[field]) {
      setEditingCell(null);
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/assess/results/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          [field]: value
        })
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const stored = data && data.marks != null && data.marks !== '' ? data.marks : value;
        const nextValue = field === 'marks' ? stored : value;
        // Update local state
        setAssessmentResults(prev => prev.map(r =>
          r.id === id ? { ...r, [field]: nextValue } : r
        ));
        // Also sync the View-Comments modal snapshot if it's open on the same row.
        setViewingComment(prev =>
          prev && prev.id === id ? { ...prev, [field]: nextValue } : prev
        );
        showMessage('Cell updated successfully', 'success');
      } else {
        const data = await response.json();
        showMessage(data.message || 'Failed to update', 'error');
      }
    } catch (error) {
      console.error('Error updating cell:', error);
      showMessage('Failed to update: ' + error.message, 'error');
    }

    setEditingCell(null);
  };

  const handleCellKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const getStudentCount = () => {
    // Count unique students
    const uniqueCodes = new Set(analyzedData.map(item => item.unique_code));
    return uniqueCodes.size;
  };

  // Build a de-duplicated, roll-number-sorted list of students from analyzedData
  const getUniqueStudents = () => {
    const seen = new Map();
    analyzedData.forEach(item => {
      if (!item || !item.unique_code) return;
      if (!seen.has(item.unique_code)) {
        seen.set(item.unique_code, {
          unique_code: item.unique_code,
          roll_name: item.roll_name || item.student_name || item.unique_code,
          image_no: item.image_no || item.image_name || ''
        });
      }
    });
    const arr = Array.from(seen.values());
    arr.sort((a, b) => {
      const ra = a.unique_code || '';
      const rb = b.unique_code || '';
      return ra.localeCompare(rb, undefined, { numeric: true, sensitivity: 'base' });
    });
    return arr;
  };

  // Toggle a single student's selection in custom mode
  const toggleStudentCode = (code) => {
    setSelectedStudentCodes(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  // Select / deselect all students currently visible after the picker search filter
  const toggleSelectAllVisibleStudents = () => {
    const visible = getUniqueStudents().filter(s => {
      const q = studentPickerSearch.trim().toLowerCase();
      if (!q) return true;
      return (s.roll_name || '').toLowerCase().includes(q) || (s.unique_code || '').toLowerCase().includes(q);
    });
    const visibleCodes = visible.map(s => s.unique_code);
    const allSelected = visibleCodes.length > 0 && visibleCodes.every(c => selectedStudentCodes.includes(c));
    if (allSelected) {
      setSelectedStudentCodes(prev => prev.filter(c => !visibleCodes.includes(c)));
    } else {
      setSelectedStudentCodes(prev => Array.from(new Set([...prev, ...visibleCodes])));
    }
  };

  const getRangeStudentCount = () => {
    if (selectionMode === 'custom') {
      return selectedStudentCodes.length;
    }

    const totalStudents = getStudentCount();
    
    if (studentRange === 'All') {
      return totalStudents;
    }
    
    if (studentRange.endsWith('-All')) {
      const start = parseInt(studentRange.split('-')[0]);
      return Math.max(0, totalStudents - start + 1);
    }
    
    const [start, end] = studentRange.split('-').map(n => parseInt(n));
    return Math.min(end - start + 1, totalStudents - start + 1);
  };

  // Sorted students the current range (or custom pick) is supposed to cover,
  // in the same order the range count uses.
  const getStudentsInSelection = () => {
    const students = getUniqueStudents().filter(s => s.unique_code && s.unique_code !== '000');
    if (selectionMode === 'custom') {
      const picked = new Set(selectedStudentCodes);
      return students.filter(s => picked.has(s.unique_code));
    }
    if (studentRange === 'All') return students;
    if (String(studentRange).endsWith('-All')) {
      const start = parseInt(String(studentRange).split('-')[0], 10);
      return students.slice(Math.max(0, start - 1));
    }
    const [start, end] = String(studentRange).split('-').map(n => parseInt(n, 10));
    if (!Number.isFinite(start) || !Number.isFinite(end)) return students;
    return students.slice(Math.max(0, start - 1), end);
  };

  // Sorted students in the current selection who have no assessment row.
  // A row counts when its roll code matches, or when its image belongs to
  // that student.
  const getMissingResultStudents = () => {
    if (!assessmentResults.length || !analyzedData.length) return [];
    const norm = (c) => String(c || '').trim().toLowerCase();
    const present = new Set();
    const imageToCode = new Map();
    analyzedData.forEach(item => {
      if (!item || !item.unique_code || item.unique_code === '000') return;
      const img = item.image_no || item.image_name;
      if (img) imageToCode.set(String(img), norm(item.unique_code));
    });
    assessmentResults.forEach(r => {
      const code = (r && r.unique_code) || extractRollNumber(r && r.student_name);
      if (code) present.add(norm(code));
      const img = r && (r.image_name || r.image_no);
      if (img && imageToCode.has(String(img))) present.add(imageToCode.get(String(img)));
    });
    return getStudentsInSelection().filter(s => !present.has(norm(s.unique_code)));
  };

  // Thinking/revision phrases that indicate AI was reasoning in output
  const THINKING_PHRASES = [
    'Wait,', 'Wait ', 'Re-evaluating', 'Actually,', 'Actually ', 'However, looking at',
    'Note: As per strict', 'Corrected Marks', 'Revised Total', 'Adjusted to',
    'Let me reconsider', 'On second thought', 'I need to reconsider', 'Re-examining',
    'Upon reflection', 're-evaluating strictly'
  ];

  // Validate consistency of marks vs sub-scores in comments (3-way check)
  const validateConsistency = (marks, comments) => {
    const result = {
      isConsistent: true, statedTotal: null, calculatedTotal: null,
      headerStatedTotal: null,
      difference: 0, hasThinkingText: false, thinkingPhrase: '', details: '', subScores: []
    };
    if (!comments || !marks) return result;

    const statedTotal = parseFloat(marks);
    if (isNaN(statedTotal)) return result;
    result.statedTotal = statedTotal;

    // Extract header-stated total. Recognises both:
    //   - English: "Total marks: 23.1/25"
    //   - Urdu:    "مجموعی نمبر: 7.5/15"
    const headerTotalMatch = comments.match(/(?:[Tt]otal\s+marks?|مجموعی\s+نمبر)[:\s]+([\d.]+)\s*\/\s*[\d.]+/);
    if (headerTotalMatch) {
      result.headerStatedTotal = Math.round(parseFloat(headerTotalMatch[1]) * 100) / 100;
    }

    // === LEVEL 1: Numbers match (3-way) ===
    // Sub-scores live INSIDE structured marker blocks. Two formats exist:
    //
    //   NEW English  : 🔴(Q1: 11.1/14 | Q2: 6.3/9)              ← top-level question totals
    //                  - 🟢(Q1.i: 1.8/2, Q1.ii: 1.8/2, …)        ← sub-parts of Q1 (sum = 11.1)
    //                  - 🟢(Q2.a: 2.8/4, Q2.b: 3.5/5)            ← sub-parts of Q2 (sum = 6.3)
    //                  💠 Q1.i: 1.8/2 - Spelling …                ← detail rows
    //
    //   LEGACY/Urdu  : 🔸(Q1: 7.5/10, Q2: 0/5)                   ← header sub-scores
    //                  🔹 Q1: 7.5/10 - …                          ← detail rows
    //
    // The detail rows (💠 / 🔹) repeat per-question scores with feedback
    // prose. Counting them again would double the sum. We need to extract
    // ONLY the authoritative header. Priority:
    //   1) 🔴(...)   — new English: top-level totals.
    //   2) 🔸(...)   — legacy/Urdu header.
    //   3) 🟢(...)+  — new English without 🔴: concat ALL green groups
    //                  (their union covers every sub-part of every question).
    //   4) Text before the first 🔹/💠 marker — last-resort fallback.
    let headerArea;
    const redMatch = comments.match(/🔴\s*\(([\s\S]*?)\)/);
    if (redMatch) {
      headerArea = redMatch[1];
    } else {
      const sParenMatch = comments.match(/🔸\s*\(([\s\S]*?)\)/);
      if (sParenMatch) {
        headerArea = sParenMatch[1];
      } else {
        const greenMatches = [...comments.matchAll(/🟢\s*\(([\s\S]*?)\)/g)];
        if (greenMatches.length > 0) {
          headerArea = greenMatches.map(m => m[1]).join(' | ');
        } else {
          const beforeFirstDetail = comments.split(/🔹|💠/)[0];
          headerArea = beforeFirstDetail.substring(0, 1000);
        }
      }
    }

    // The Q prefix is REQUIRED so we don't accidentally match the row's
    // header (e.g. "Hassan: 64.5/80" or "Abdullah: 7.5/15") whose colon
    // would otherwise look like a sub-score separator. Sub-parts can be
    // Roman numerals (i, ii, iv, x) or letters (a, b, c) and may be
    // nested (e.g. Q1.a.i). Case-insensitive to also handle Q1.IV.
    const qScorePattern = /Q\.?[a-z0-9]+(?:\.[a-z0-9]+)*\s*[→>:]\s*([\d.]+)\/[\d.]+/gi;
    let match;
    const subScores = [];
    while ((match = qScorePattern.exec(headerArea)) !== null) {
      const score = parseFloat(match[1]);
      if (!isNaN(score)) subScores.push(score);
    }

    if (subScores.length === 0) return result; // Can't validate

    result.subScores = subScores;
    const calculatedTotal = subScores.reduce((sum, s) => sum + s, 0);
    result.calculatedTotal = Math.round(calculatedTotal * 100) / 100;
    result.difference = Math.round(Math.abs(result.statedTotal - result.calculatedTotal) * 100) / 100;

    // 3-way checks (tolerance 0.1)
    const marksVsSum    = result.difference <= 0.1;
    const headerVsMarks = result.headerStatedTotal === null ||
      Math.round(Math.abs(result.headerStatedTotal - result.statedTotal) * 100) / 100 <= 0.1;
    const headerVsSum   = result.headerStatedTotal === null ||
      Math.round(Math.abs(result.headerStatedTotal - result.calculatedTotal) * 100) / 100 <= 0.1;

    const numbersMatch = marksVsSum && headerVsMarks && headerVsSum;

    // === LEVEL 2: No thinking text ===
    for (const phrase of THINKING_PHRASES) {
      if (comments.includes(phrase)) {
        result.hasThinkingText = true;
        result.thinkingPhrase = phrase;
        break;
      }
    }

    // === Final verdict ===
    result.isConsistent = numbersMatch && !result.hasThinkingText;

    // Build details message
    if (!numbersMatch) {
      const parts = [];
      if (!headerVsSum && result.headerStatedTotal !== null)
        parts.push(`Header says ${result.headerStatedTotal} but sub-scores sum to ${result.calculatedTotal}`);
      if (!headerVsMarks && result.headerStatedTotal !== null)
        parts.push(`Header says ${result.headerStatedTotal} but marks column says ${result.statedTotal}`);
      if (!marksVsSum)
        parts.push(`${result.subScores.join('+')} = ${result.calculatedTotal}, but marks column says ${result.statedTotal}`);
      let detailMsg = parts.join(' | ');
      if (result.hasThinkingText) detailMsg += ` + thinking text: "${result.thinkingPhrase}"`;
      result.details = detailMsg;
    } else if (result.hasThinkingText) {
      result.details = `Thinking text detected: "${result.thinkingPhrase}"`;
    }

    return result;
  };

  // For duplicate groups, determine which entry is the "best" (most consistent)
  // Returns: bestIds (clear winners), badIds (should delete), examinerChoiceIds (user must pick),
  //          mismatchBestIds (best of bad options with number issues)
  const getDuplicateGroupInfo = () => {
    const groups = {};
    
    assessmentResults.forEach(result => {
      const rollNumber = extractRollNumber(result.student_name);
      if (rollNumber && duplicateRollNumbers.has(rollNumber)) {
        if (!groups[rollNumber]) groups[rollNumber] = [];
        const consistency = validateConsistency(result.marks, result.comments);
        groups[rollNumber].push({ ...result, consistency });
      }
    });

    const bestIds = new Set();            // ✅ Clear winner (green)
    const badIds = new Set();             // ⚠ Duplicate - Delete (orange)
    const examinerChoiceIds = new Set();  // 🔵 Examiner must choose (blue)
    const mismatchBestIds = new Map();    // ⚠ Best of bad options (orange with details)
    
    Object.values(groups).forEach(group => {
      const consistentEntries = group.filter(e => e.consistency.isConsistent);
      const inconsistentEntries = group.filter(e => !e.consistency.isConsistent);
      
      if (consistentEntries.length === 1) {
        // SCENARIO 1: Exactly 1 consistent → clear winner
        bestIds.add(consistentEntries[0].id);
        inconsistentEntries.forEach(e => badIds.add(e.id));
      } else if (consistentEntries.length > 1) {
        // SCENARIO 2: Multiple consistent → examiner must choose
        // (all have matching numbers and no thinking text)
        consistentEntries.forEach(e => examinerChoiceIds.add(e.id));
        inconsistentEntries.forEach(e => badIds.add(e.id));
      } else {
        // SCENARIO 3: None consistent → smallest mismatch is "best of bad"
        group.sort((a, b) => {
          // Prefer no-thinking-text entries first, then smallest difference
          if (a.consistency.hasThinkingText !== b.consistency.hasThinkingText) {
            return a.consistency.hasThinkingText ? 1 : -1;
          }
          return a.consistency.difference - b.consistency.difference;
        });
        const best = group[0];
        mismatchBestIds.set(best.id, best.consistency.details || `Mismatch: ${best.consistency.difference}`);
        group.slice(1).forEach(e => badIds.add(e.id));
      }
    });

    return { bestIds, badIds, examinerChoiceIds, mismatchBestIds };
  };

  // Extract roll number from student name (e.g., "(A01)Saher" -> "A01")
  const extractRollNumber = (studentName) => {
    if (!studentName) return null;
    const match = studentName.match(/\(([^)]+)\)/);
    return match ? match[1] : null;
  };

  // Detect when the AI accidentally emitted the "Total marks:" header more
  // than once — a sign the model was "thinking out loud" and produced a
  // recalculation instead of a single clean answer.
  const hasDuplicateHeaders = (comments) => {
    if (!comments) return false;
    // Match both English "Total marks:" and Urdu "مجموعی نمبر:" headers so
    // duplicate-header detection works for both mediums.
    const matches = String(comments).match(/(?:total\s+marks|مجموعی\s+نمبر)\s*:/gi);
    return !!(matches && matches.length > 1);
  };

  // Render the AI's BLOCK A markdown deduction table. Pure parser — splits
  // pipe-delimited rows, drops the `| --- |` separator, paints **bold** rows
  // (Qn Total / Grand Total) with a tinted background so the totals jump out.
  // Returns a React node (a <table>) or null when the input doesn't look
  // like a markdown table.
  const focusLinkedQuestion = (raw, owner, deduction) => {
    const id = normalizeQuestionId(raw);
    if (!id) return;
    const ownerKey = owner == null ? '' : String(owner);
    const rubric = checkRubricRef.current;
    const ocr = checkOcrRef.current;
    const open = previewImage && previewImage.result;
    const openKey = open ? String(open.image_name || open.id || '') : '';
    const sameStudent = !ownerKey || !openKey || ownerKey === openKey;
    const rubricSource = (rubric && rubric.value) || checkRubricDraft || autoRubricText || rubricText || '';
    const ocrSource = (ocr && ocr.value) || checkOcrDraft || (viewingComment && viewingComment.ocr_text) || '';
    const rubricSpan = rubricSource ? findQuestionSpan(rubricSource, id) : null;
    const ocrSpan = sameStudent && ocrSource ? findTranscriptSpan(ocrSource, id, deduction, rubricSource) : null;
    if (rubricSpan) setShowRubricPane(true);
    if (ocrSpan) setShowCommentTranscript(true);
    setLinkedQuestion({ id, owner: ownerKey, rubric: rubricSpan, ocr: ocrSpan, tick: Date.now() });
    if (ocr && ocrSpan) {
      revealTextareaSpan(ocr, ocrSpan.start, ocrSpan.end);
      if (previewImage && previewImage.images && previewImage.images.length) {
        const idx = transcriptPageIndex(ocr.value, ocrSpan, previewImage.images);
        if (idx >= 0 && idx !== previewImage.index) {
          setPreviewImage((prev) => prev ? { ...prev, index: idx } : prev);
        }
      }
    }
  };

  const renderMarkdownTable = (md, owner, onDeleteBelowRow) => {
    const text = String(md || '').trim();
    if (!text || !text.includes('|')) return null;
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.startsWith('|'));
    if (lines.length < 2) return null;
    const isSep = (line) => /^\|\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line);
    let header = null;
    const bodyRows = [];
    for (const line of lines) {
      if (isSep(line)) continue;
      if (!header) { header = splitRawMarkdownCells(line); continue; }
      bodyRows.push(splitMarkdownRowCells(line, header.length));
    }
    if (!header) return null;
    const isBoldRow = (cells) => cells.some(c => /^\*\*.+\*\*$/.test(c));
    const stripBold = (s) => String(s || '').replace(/\*\*/g, '').trim();
    // Display-only short header labels (the underlying markdown / table data
    // and every parser keep the full names — this only renames the rendered
    // <th> text). Matched case-insensitively, whitespace-collapsed.
    const HEADER_LABELS = {
      'question': 'Qs',
      'marks': 'T.M',
      'deduction': 'Mistakes',
      'deduction marks': '-ve',
      'obtained': 'Obt.M',
      'obtained marks': 'Obt.M'
    };
    const renameHeader = (h) => {
      const plain = stripBold(h);
      const key = plain.toLowerCase().replace(/\s+/g, ' ').trim();
      return HEADER_LABELS[key] || plain;
    };
    let seenGrandRow = false;
    const belowTotal = bodyRows.map((cells) => {
      const label = stripBold(cells[0] || '');
      if (/\bgrand\s*total\b/i.test(label)) {
        seenGrandRow = true;
        return false;
      }
      return seenGrandRow;
    });
    return (
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 13,
        fontFamily: 'inherit',
        border: '1px solid var(--border-color, #e2e8f0)'
      }}>
        <thead>
          <tr style={{ background: 'var(--surface-alt, #f1f5f9)' }}>
            {header.map((h, i) => (
              <th key={i} style={{
                padding: '8px 10px',
                textAlign: 'left',
                fontWeight: 700,
                color: 'var(--text-primary, #0f172a)',
                borderBottom: '2px solid var(--border-color, #cbd5e1)'
              }}>{renameHeader(h)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((cells, ri) => {
            const bold = isBoldRow(cells);
            const rowLabel = stripBold(cells[0]);
            const rowQuestionId = /total/i.test(rowLabel) ? null : normalizeQuestionId(rowLabel);
            const ownerKey = owner == null ? '' : String(owner);
            const linked = !!(rowQuestionId && linkedQuestion
              && linkedQuestion.id === rowQuestionId
              && linkedQuestion.owner === ownerKey);
            return (
              <tr
                key={ri}
                title={belowTotal[ri] ? 'This written label is not a rubric question, so this row is not in the total.' : undefined}
                style={{
                background: linked
                  ? 'rgba(245, 158, 11, 0.28)'
                  : belowTotal[ri]
                    ? 'rgba(220, 38, 38, 0.08)'
                    : bold ? 'rgba(139, 92, 246, 0.08)' : 'transparent',
                fontWeight: bold ? 700 : 400,
                outline: linked ? '2px solid #d97706' : 'none'
              }}>
                {cells.map((c, ci) => (
                  <td key={ci} style={{
                    padding: '6px 10px',
                    borderBottom: '1px solid var(--border-color, #e2e8f0)',
                    color: 'var(--text-secondary, #334155)',
                    verticalAlign: 'top',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {ci === 0 && belowTotal[ri] && typeof onDeleteBelowRow === 'function' && (
                      <button
                        type="button"
                        title="Remove this unmatched row"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteBelowRow(ri);
                        }}
                        style={{
                          marginRight: 6,
                          border: 'none',
                          background: 'transparent',
                          color: '#DC2626',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: 12,
                          lineHeight: 1
                        }}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    )}
                    {ci === 0 && rowQuestionId ? (
                      <button
                        type="button"
                        onClick={() => focusLinkedQuestion(rowQuestionId, owner, rowDeduction(header, cells))}
                        title="Highlight this question in the transcript and the rubric"
                        style={{
                          background: linked ? '#fde68a' : 'transparent',
                          border: linked ? '1px solid #d97706' : '1px solid transparent',
                          borderRadius: 6,
                          padding: '1px 6px',
                          font: 'inherit',
                          fontWeight: 700,
                          color: 'inherit',
                          cursor: 'pointer'
                        }}
                      >
                        {stripBold(c)}
                      </button>
                    ) : stripBold(c)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // Find duplicate roll numbers
  const getDuplicateRollNumbers = () => {
    const rollNumberCounts = {};
    
    assessmentResults.forEach(result => {
      const rollNumber = extractRollNumber(result.student_name);
      if (rollNumber) {
        rollNumberCounts[rollNumber] = (rollNumberCounts[rollNumber] || 0) + 1;
      }
    });
    
    // Return set of roll numbers that appear more than once
    return new Set(
      Object.entries(rollNumberCounts)
        .filter(([_, count]) => count > 1)
        .map(([rollNumber]) => rollNumber)
    );
  };

  // Check if a result has a duplicate roll number
  const hasDuplicateRollNumber = (result) => {
    const rollNumber = extractRollNumber(result.student_name);
    return rollNumber && duplicateRollNumbers.has(rollNumber);
  };

  // Check if a result has roll number 000/0000 (AI error roll)
  const hasZeroRollNumber = (result) => {
    const rollNumber = extractRollNumber(result.student_name);
    return rollNumber === '000' || rollNumber === '0000';
  };

  // Stuck / incomplete rows that look "ok" in the table but were never
  // actually graded — e.g. phase-1 placeholders left as
  // "OCR done — grading queued…" with empty class/marks, or success rows
  // with no marks and no verification table. Used for the Sorted Folder
  // "Ungraded rows" badge and the auto-check soft-error gate.
  const isUngradedAssessmentRow = (result) => {
    if (!result) return false;
    const status = String(result.status || '').toLowerCase();
    if (status === 'error') return false; // hard errors are counted separately
    const comments = String(result.comments || '');
    if (/grading queued|OCR done/i.test(comments)) return true;
    const marks = String(result.marks ?? '').trim();
    const hasMarks = marks !== '' && marks.toUpperCase() !== 'ERROR' && marks !== '-';
    if (hasMarks) return false;
    const hasClass = !!(result.class && String(result.class).trim() && result.class !== '-');
    const hasTable = !!(result.table_comments && String(result.table_comments).trim());
    // Success (or blank status) with no marks, no class, and no table = ungraded
    return !hasClass && !hasTable;
  };

  const ungradedRowCount = React.useMemo(
    () => (assessmentResults || []).filter(isUngradedAssessmentRow).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assessmentResults]
  );

  // Two-step progress is counted from saved rows, not from machine sends.
  // A student with a stored transcript counts toward OCR. A student with
  // saved marks counts toward grading, and also toward OCR.
  const twoStepProgress = React.useMemo(() => {
    const ocr = new Set();
    const grading = new Set();
    const keyOf = (result) => {
      const name = String(result?.student_name || '');
      const fromName = name.match(/\(([^)]+)\)/);
      const code = result?.unique_code || (fromName && fromName[1]) || result?.image_name || result?.id;
      return String(code || '').trim().toLowerCase();
    };
    const savedMarks = (result) => {
      const marks = String(result?.marks ?? '').trim();
      return marks !== '' && marks.toUpperCase() !== 'ERROR' && marks !== '-';
    };
    const transcript = (result) => {
      if (savedMarks(result)) return true;
      const status = String(result?.ocr_status || '').toLowerCase();
      const text = String(result?.ocr_text || '').trim();
      if (status === 'ok' && text) return true;
      return /OCR done/i.test(String(result?.comments || ''));
    };
    for (const result of assessmentResults || []) {
      const key = keyOf(result);
      if (!key) continue;
      if (transcript(result)) ocr.add(key);
      if (savedMarks(result)) grading.add(key);
    }
    const reported = Number(progressDetails?.total);
    const total = reported > 0 ? reported : (analyzedData?.length || 0);
    return { ocr: ocr.size, grading: grading.size, total };
  }, [assessmentResults, progressDetails, analyzedData]);

  // Students in the sorted list with no result row still need OCR.
  // Rows already in the table with no marks still need grading.
  const ocrLeftCount = getMissingResultStudents().length;
  const gradingLeftCount = ungradedRowCount;
  const resumeLabel = `Resume (${ocrLeftCount} OCR left, ${gradingLeftCount} grading left)`;

  // A paused class still has sheets to OCR or rows with no marks.
  // Resume continues those sheets and skips anyone already graded.
  const canResumeCheck = !isProcessing
    && !!folderPath
    && analyzedData.length > 0
    && (ocrLeftCount > 0 || gradingLeftCount > 0);

  // Get duplicate roll numbers — memoized so it recomputes only when the
  // results change, not on every single render.
  const duplicateRollNumbers = React.useMemo(
    () => getDuplicateRollNumbers(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assessmentResults]
  );

  // Get duplicate group consistency info
  const duplicateGroupInfo = React.useMemo(() => {
    if (duplicateRollNumbers.size === 0) return { bestIds: new Set(), badIds: new Set(), examinerChoiceIds: new Set(), mismatchBestIds: new Map() };
    return getDuplicateGroupInfo();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentResults, duplicateRollNumbers]);

  // Handler: auto-select all bad duplicate entries
  const handleSelectBadDuplicates = () => {
    const badIds = [...duplicateGroupInfo.badIds];
    if (badIds.length === 0) {
      showMessage('No inconsistent duplicates found', 'info');
      return;
    }
    setSelectedResults(badIds);
    showMessage(`Selected ${badIds.length} inconsistent duplicate(s). Close this modal and delete them.`, 'success');
  };

  // Sort results: duplicates first, then others
  const sortedAssessmentResults = React.useMemo(() => {
    const resultsWithZeroRoll = assessmentResults.filter(hasZeroRollNumber);
    const resultsWithDuplicates = assessmentResults.filter(
      r => !hasZeroRollNumber(r) && hasDuplicateRollNumber(r)
    );
    const resultsWithoutPriority = assessmentResults.filter(
      r => !hasZeroRollNumber(r) && !hasDuplicateRollNumber(r)
    );
    return [...resultsWithZeroRoll, ...resultsWithDuplicates, ...resultsWithoutPriority];
  }, [assessmentResults, duplicateRollNumbers]);

  // Per-row parsed metadata — the marks/comments consistency check, the
  // over-marked + duplicate-header flags, and the corrected table markdown —
  // computed ONCE per results change instead of re-parsed for every row on
  // every re-render. These parsers (validateConsistency, hasMarksExceeded,
  // hasDuplicateHeaders, correctTable) are pure functions of a single row's
  // fields, so memoizing on `sortedAssessmentResults` is safe and removes the
  // per-render / per-2s-poll re-parsing that made a 40-row table janky.
  const buildRowMeta = (result) => {
    const rowRepair = repairForResult(result);
    // Rebuild the teacher-facing one-line from BLOCK A when present. The
    // stored `comments` column is sometimes a stub ("🟣 Total marks → 5")
    // because a partial re-grade used to keep only the re-graded question
    // in the red header.
    const oneLineComments = (() => {
      if (!result.table_comments) return result.comments || '';
      try {
        const rebuilt = tableToOneLineComments({
          tableMd: result.table_comments,
          studentName: result.student_name,
          existingComments: result.comments || '',
          allowedIds: rowRepair.allowedIds,
          rubricMarks: rowRepair.rubricMarks,
          paperTotal: rowRepair.paperTotal
        });
        return rebuilt || result.comments || '';
      } catch (_) {
        return result.comments || '';
      }
    })();
    // The mismatch badge reads the same text the teacher sees. A hand-edited
    // comment stays as stored; otherwise the table rebuild is the comment.
    const shown = Number(result.comments_custom) === 1
      ? (result.comments || '')
      : (oneLineComments || result.comments || '');
    return {
      consistency: validateConsistency(result.marks, shown),
      isOverMarked: hasMarksExceeded(result.marks, shown),
      isDupHeader: hasDuplicateHeaders(shown),
      // Only parse the table when the row actually has one (mirrors the render
      // guard `result.table_comments ? … : …`); avoids running the parser on
      // every table-less row.
      correctedTableMd: result.table_comments
        ? keepLastTableBlock((correctTable(result.table_comments, rowRepair) || {}).md || result.table_comments)
        : '',
      oneLineComments
    };
  };
  const rowMetaById = React.useMemo(() => {
    const map = new Map();
    for (const result of sortedAssessmentResults) map.set(result.id, buildRowMeta(result));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedAssessmentResults, allowedQuestionIds, rubricQuestionMarks, rubricPaperTotal, folderRubricReady, folderPath]);
  // Fast memoized lookup, with a safe recompute fallback if a row isn't cached.
  const metaFor = (result) => rowMetaById.get(result.id) || buildRowMeta(result);

  // ─────────────────────────────────────────────────────────────────────
  // Auto-check engine. A browser-side loop (active only while this Checking
  // tab is open) that picks up folders whose sorted list is 'reviewed', has
  // an auto-check medium set, and has sat ≥2 min, then drives the whole
  // checking flow end-to-end: load → set medium → run → save table →
  // approve (per class/section) → flip status to 'copy_to_edupage'. Sort,
  // MCQ, and Check run independently — concurrent subjective checks are
  // still capped by LOCAL_AI_MAX_CONCURRENT_CHECKS.
  // ─────────────────────────────────────────────────────────────────────
  const aiStatus = useAiStatus(5000);
  const [hostOverride, setHostOverride] = useState({});
  // Set when an auto-check run switches both machines on. The next status
  // poll then follows the server, so a machine that did not answer shows off.
  const hostsFollowServerRef = useRef(false);
  React.useEffect(() => {
    const live = aiStatus?.dispatch?.health;
    if (!live) return;
    setHostOverride((prev) => {
      const next = { ...prev };
      let changed = false;
      let sawOverride = false;
      const follow = hostsFollowServerRef.current;
      for (const key of ['gpu', 'mac']) {
        if (!Object.prototype.hasOwnProperty.call(prev, key)) continue;
        sawOverride = true;
        if (typeof live[key] !== 'boolean') continue;
        if (follow || prev[key] === live[key]) {
          delete next[key];
          changed = true;
        }
      }
      if (follow && sawOverride) hostsFollowServerRef.current = false;
      return changed ? next : prev;
    });
  }, [aiStatus?.dispatch?.health]);
  const hostAvailable = (key) => {
    if (Object.prototype.hasOwnProperty.call(hostOverride, key)) return hostOverride[key];
    const live = aiStatus?.dispatch?.health?.[key];
    return typeof live === 'boolean' ? live : true;
  };
  const setHostAvailable = async (key, online) => {
    setHostOverride((prev) => ({ ...prev, [key]: online }));
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/ai-status/hosts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ host: key, online })
      });
      if (!res.ok) throw new Error('Could not update the machine');
    } catch (err) {
      setHostOverride((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      showMessage(err.message || 'Could not update the machine', 'error');
    }
  };
  // Seed host dropdowns from backend defaults once /api/ai-status loads.
  React.useEffect(() => {
    const hosts = aiStatus?.local?.hosts;
    if (!Array.isArray(hosts) || hosts.length === 0) return;
    const keys = new Set(hosts.map((h) => h.key));
    if (!ocrHost || !keys.has(ocrHost)) {
      const d = aiStatus.local.defaultOcrHost;
      if (d && keys.has(d)) setOcrHost(d);
      else if (keys.has('gpu')) setOcrHost('gpu');
      else setOcrHost(hosts[0].key);
    }
    if (!gradingHost || !keys.has(gradingHost)) {
      const d = aiStatus.local.defaultGradingHost;
      if (d && keys.has(d)) setGradingHost(d);
      else if (keys.has('mac')) setGradingHost('mac');
      else setGradingHost(hosts[Math.min(1, hosts.length - 1)].key);
    }
  }, [aiStatus?.local?.hosts, aiStatus?.local?.defaultOcrHost, aiStatus?.local?.defaultGradingHost]);
  // Auto-check is restricted to dedicated accounts (ai1–ai4). Each runs the
  // engine in its OWN browser; they coordinate through the backend — an atomic
  // folder-claim keeps them off the same class, and LOCAL_AI_MAX_CONCURRENT_CHECKS
  // caps how many may talk to LM Studio at once (others wait/retry). The account
  // list (AUTO_CHECK_USERS) lives at module scope so the manual step-reporting
  // helper can share it.
  const isAutoCheckUser = AUTO_CHECK_USERS.includes(currentUsernameFromStorage());
  // Default ON: auto-check runs whenever this Checking tab is open. The
  // toggle is a Stop/pause — an explicit 'false' the teacher set is honored,
  // but a fresh browser (no saved value) starts enabled.
  const [autoCheckEnabled, setAutoCheckEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem('autoCheckEnabled');
      return saved === null ? true : saved === 'true';
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('autoCheckEnabled', autoCheckEnabled ? 'true' : 'false'); } catch {}
  }, [autoCheckEnabled]);
  const [autoCheckStatus, setAutoCheckStatus] = useState('');
  const [autoCheckNotice, setAutoCheckNotice] = useState('');
  publishAutoCheckNoticeRef.current = (line) => {
    const text = String(line || '').trim().slice(0, 500);
    autoCheckNoticeRef.current = text;
    setAutoCheckNotice(text);
    if (text) setAutoCheckStatus(text);
  };
  clearAutoCheckNoticeRef.current = () => {
    autoCheckNoticeRef.current = '';
    setAutoCheckNotice('');
  };
  const [autoCheckBoard, setAutoCheckBoard] = useState(null);
  const [autoCheckSwitches, setAutoCheckSwitches] = useState({});
  const [autoCheckPresent, setAutoCheckPresent] = useState(null);
  const [switchBusy, setSwitchBusy] = useState('');
  // Ai accounts wait for the saved switch before the engine claims a folder,
  // so an examiner Stop is honored even if this browser last saved "on".
  const [switchReady, setSwitchReady] = useState(false);
  const appliedSwitchRef = useRef(undefined);
  const stopProcessingRef = useRef(null);
  stopProcessingRef.current = handleStopProcessing;
  const autoBusyRef = useRef(false);

  // Shared line: which class each ai account holds, and the folders a free
  // checker will claim, oldest review first. Examiners see the same line.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const here = isAutoCheckUser && !quickTranscript ? '?here=1' : '';
        const res = await fetch(`${API_BASE_URL}/analyze/auto-check/board${here}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok || cancelled) return;
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setAutoCheckBoard({
          checkers: Array.isArray(data.checkers) ? data.checkers : [],
          queue: Array.isArray(data.queue) ? data.queue : []
        });
        if (data.present && typeof data.present === 'object') setAutoCheckPresent(data.present);
        if (data.switches && typeof data.switches === 'object') {
          setAutoCheckSwitches(data.switches);
          if (isAutoCheckUser) {
            const me = currentUsernameFromStorage();
            const mine = data.switches[me];
            if (typeof mine === 'boolean' && appliedSwitchRef.current !== mine) {
              const prev = appliedSwitchRef.current;
              appliedSwitchRef.current = mine;
              setAutoCheckEnabled(mine);
              if (prev === true && mine === false && stopProcessingRef.current) {
                stopProcessingRef.current({ releaseAutoCheck: true });
              }
            }
          }
        }
        setSwitchReady(true);
      } catch { /* keep the last board */ }
    };
    load();
    const id = setInterval(load, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [API_BASE_URL, isAutoCheckUser, quickTranscript]);

  const toggleAutoCheckAccount = async (account, enabled) => {
    if (switchBusy) return;
    setSwitchBusy(account);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/analyze/auto-check/switches`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ account, enabled })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showMessage(data.message || `Could not update ${account}`, 'error');
        return;
      }
      if (data.switches) setAutoCheckSwitches(data.switches);
      showMessage(
        enabled
          ? `${account} auto-check is on. It runs while that Check tab is open.`
          : `${account} auto-check is off. Its current class stays with that account.`,
        'success'
      );
    } catch (err) {
      showMessage(err.message || `Could not update ${account}`, 'error');
    } finally {
      setSwitchBusy('');
    }
  };
  // folder_path → skipped for the rest of this browser session. A folder
  // whose checking errors mid-run is added here so the engine moves on to the
  // next folder instead of retrying it every tick. Cleared on page reload.
  const autoSkipRef = useRef(new Set());

  // Always-fresh mirror of the state + handlers the engine drives, so its
  // long-lived async job never closes over stale render values.
  const autoRefs = useRef({});
  autoRefs.current = {
    isProcessing, isGettingText, sessionId, analyzedData, assessmentResults,
    selectedResults, folderPath, folderRubricReady, subjectType, pipelineMode, rubricMetadata,
    beginFolderSwitch: () => {
      rubricFolderRef.current = '';
      folderRubricReadyRef.current = false;
      setFolderRubricReady(false);
      setAssessmentResults([]);
    },
    folderRubricReadyFor: (fp) => rubricFolderRef.current === fp && folderRubricReadyRef.current,
    studentLists, approveForm, approveLoading, aiStatus, ocrModel, gradingModel,
    ocrHost, gradingHost,
    handleMediumChange, handleStartAssessment, handleGetText, handleRecheckSelected,
    handleApprove, fetchAnalyzedData, fetchAssessmentResults, fetchRubricForFolder,
    setFolderPath, setSelectionMode, setStudentRange, setSelectedResults, setApproveForm,
    setOcrModel, setGradingModel, setOcrHost, setGradingHost,
    isUngradedAssessmentRow,
    armHosts: async () => {
      hostsFollowServerRef.current = true;
      await setHostAvailable('gpu', true);
      await setHostAvailable('mac', true);
    },
  };

  // Sort-tab "Re-grade" shortcut: once the saved table is imported and
  // medium + rubric are ready, select-all is already done — kick Check
  // Step 2 / Re-grade (grading_only) the same way the purple button does.
  useEffect(() => {
    if (!pendingAutoRegrade) return;
    if (!folderPath) return;
    if (pipelineMode !== 'two_step') return;
    if (subjectType === 'none') return;
    if (!(autoRubricText || autoPdfRubric || rubricText || pdfRubric)) return;

    const { title, ids, rows } = pendingAutoRegrade;
    if (!ids || ids.length === 0) {
      setPendingAutoRegrade(null);
      return;
    }

    if (isProcessing) {
      setPendingAutoRegrade(null);
      showMessage(`Loaded "${title}" and selected all ${ids.length} rows. Wait for the current check to finish, then click Re-grade.`, 'info');
      return;
    }

    setPendingAutoRegrade(null);
    (async () => {
      const kick = await autoRefs.current.handleRecheckSelected('grading_only', {
        skipConfirm: true,
        resultIds: ids,
        results: rows,
        folderPath,
        summaryKind: 'checked'
      });
      if (kick === 'busy') {
        showMessage(`AI is busy — "${title}" is loaded with all rows selected. Click "Check Step 2/Re-grade" when the AI is free.`, 'info');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoRegrade, folderPath, pipelineMode, subjectType, autoRubricText, autoPdfRubric, rubricText, pdfRubric, isProcessing]);

  useEffect(() => {
    if (quickTranscript) return;
    if (!autoCheckEnabled || !isAutoCheckUser || !switchReady) {
      if (!autoCheckNoticeRef.current) setAutoCheckStatus('');
      return;
    }
    let cancelled = false;

    const sleep = (ms) => new Promise(res => setTimeout(res, ms));
    // Step-by-step console logger so the EXACT point + reason of any auto-check
    // failure is visible in the browser console (timestamped, single prefix).
    // Open DevTools → Console and filter by "[AutoCheck]" to trace a run.
    const alog = (msg, data) => {
      const ts = new Date().toISOString().slice(11, 23);
      if (data !== undefined) console.log(`[AutoCheck ${ts}] ${msg}`, data);
      else console.log(`[AutoCheck ${ts}] ${msg}`);
    };
    // The step this job is CURRENTLY on ('run' | 'save' | 'scans' | 'approve' |
    // 'absent'), so the tick's catch can mark the right box red when a step
    // throws. Reset
    // per job by the '__start__' report at the top of runJob.
    let currentStep = null;
    // Claim guard for the in-flight job. The backend lock expires after
    // AUTO_CHECK_LOCK_MINUTES (default 60). Long English/Urdu grades often
    // run longer than that; without a heartbeat the lock dies mid-run and
    // another ai browser re-claims the same folder. `lost` is set only on an
    // explicit claimed:false — never on a network blip (those retry next beat).
    const claimGuard = { lost: false };
    // Persist step progress to the backend so it shows on everyone's Sort tab
    // (not just this ai-browser's console). Best-effort telemetry: a failed
    // report must NEVER break or fail the actual checking run.
    //   step '__start__' → reset the folder's boxes + record which ai user ran it
    //   step run/save/scans/approve/absent + status running/done/error → 1 box
    const reportStep = async (folderPath, step, status, error) => {
      try {
        const token = localStorage.getItem('authToken');
        await fetch(`${API_BASE_URL}/analyze/auto-check/step`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            folder_path: folderPath, step, status,
            error: error ? String(error).slice(0, 500) : undefined
          })
        });
      } catch (_) { /* best-effort — never break the run over a telemetry blip */ }
    };
    const lostClaimError = () => {
      const e = new Error('Lost the auto-check claim while job was running');
      e.aiBusy = true;
      return e;
    };
    const waitFor = async (pred, { timeout = 20 * 60 * 1000, interval = 700 } = {}) => {
      const start = Date.now();
      while (!cancelled) {
        if (pauseRequestedRef.current) throw new Error('paused by user');
        if (claimGuard.lost) throw lostClaimError();
        let ok = false;
        try { ok = pred(); } catch { ok = false; }
        if (ok) return true;
        if (Date.now() - start > timeout) throw new Error('Timed out waiting for a checking step to finish');
        await sleep(interval);
      }
      throw new Error('auto-check cancelled');
    };

    // Ready to start another folder in THIS tab. Other AI browsers may already
    // be grading — concurrent checks are allowed up to
    // LOCAL_AI_MAX_CONCURRENT_CHECKS (surfaced as check.max / check.active).
    // Sort, MCQ, and Check have independent mutex slots; LM Studio queues
    // their requests so all three pipelines may run at the same time.
    const localIdle = () => {
      const r = autoRefs.current;
      if (r.isProcessing || r.isGettingText || r.sessionId) return false;
      const s = r.aiStatus || {};
      const check = s.check || {};
      if (check.max > 0 && (check.active || 0) >= check.max) return false;
      return true;
    };

    // Urdu folders grade from typed-answer .docx files that Google Drive syncs
    // in alongside the scanned images. If checking starts before they've all
    // arrived, some answers come through blank. So gate on the local docx-server:
    // "ready" only when every image has its .docx (images === docx). Fail-open
    // — if the server is unreachable or can't resolve the folder locally, we
    // proceed rather than stall auto-check.
    const isUrduDocxReady = async (f) => {
      try {
        const DOCX_SERVER_URL = `http://${window.location.hostname}:3100`;
        const res = await fetch(`${DOCX_SERVER_URL}/docx-counts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ folders: [{
            id: f.folder_path, folder_name: f.title, class: f.class,
            section: f.section, subject: f.subject, test_date: f.test_date
          }] })
        });
        if (!res.ok) return true; // fail-open
        const data = await res.json().catch(() => ({}));
        const r0 = (data.results || [])[0];
        if (!r0 || r0.error != null) return true; // can't resolve locally → fail-open
        if (r0.ready) return true;
        alog(`tick: Urdu docx not ready for "${f.title}" (${r0.docx}/${r0.images} synced) — skipping until they match`);
        setAutoCheckStatus(`Waiting for typed-answer .docx to sync for "${f.title}" (${r0.docx}/${r0.images})…`);
        return false;
      } catch (_) {
        return true; // docx-server unreachable → fail-open
      }
    };

    // Oldest-first list of folders THIS browser could claim. Claimed folders
    // are already filtered out by the backend. Returns [] when nothing is ready
    // (Urdu folders without synced .docx are skipped, not blocking later ones).
    const fetchEligibleList = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/analyze/auto-check/eligible`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return [];
        const data = await res.json();
        const list = data.data || [];
        // Skip folders that already errored this session. Sort-tab "Retry
        // auto-check" clears auto_check_by — drop those from the skip list so
        // the same ai browser can retry without a full reload.
        const candidates = [];
        const pausedFolders = readPausedFolderSet();
        for (const f of list) {
          f.regrade = !!(f.regrade || f.auto_check_regrade_at);
          if (pausedFolders.has(f.folder_path)) continue;
          if (autoSkipRef.current.has(f.folder_path)) {
            if (!f.auto_check_by) autoSkipRef.current.delete(f.folder_path);
            else continue;
          }
          candidates.push(f);
        }
        const ready = [];
        for (const f of candidates) {
          // Re-grade uses the saved OCR/transcript table — no .docx wait.
          if (f.regrade || f.medium === 'english' || f.medium === 'urog') {
            ready.push(f);
            continue;
          }
          if (await isUrduDocxReady(f)) ready.push(f);
        }
        return ready;
      } catch { return []; }
    };

    // Atomically claim a folder before working on it so the OTHER checking
    // browser (ai1 / ai2 / ai3) can't grab the same class. The backend
    // does a conditional UPDATE: exactly one caller wins. Returns false when
    // we lost the race (the other browser already holds it) — we just skip and
    // let the next tick pick the next eligible folder.
    const claimFolder = async (folderPath) => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/analyze/auto-check/claim`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ folder_path: folderPath })
        });
        if (!res.ok) return false;
        const data = await res.json().catch(() => ({}));
        return !!data.claimed;
      } catch { return false; }
    };

    // Keep the claim alive for the whole job. The backend lock is soft
    // (default 60 min); long grades exceed that. Heartbeat renews well under
    // the TTL. Only an explicit claimed:false marks the claim lost — HTTP /
    // network blips are logged and retried on the next beat so a transient
    // glitch can't abort a multi-hour check.
    // Returns a stop() function; call it in finally before release.
    const CLAIM_HEARTBEAT_MS = 12 * 60 * 1000; // 12 min — ~5 renewals per 60-min lock
    const startClaimHeartbeat = (folderPath) => {
      claimGuard.lost = false;
      const id = setInterval(async () => {
        if (cancelled || claimGuard.lost) return;
        try {
          const token = localStorage.getItem('authToken');
          const res = await fetch(`${API_BASE_URL}/analyze/auto-check/claim`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ folder_path: folderPath })
          });
          if (!res.ok) {
            alog(`claim heartbeat: HTTP ${res.status} for ${folderPath} — will retry next beat`);
            return;
          }
          const data = await res.json().catch(() => ({}));
          if (!data.claimed) {
            alog(`claim heartbeat: LOST claim for ${folderPath} — another browser holds it`);
            claimGuard.lost = true;
          }
        } catch (e) {
          alog(`claim heartbeat: network blip for ${folderPath} — will retry next beat`, e && e.message);
        }
      }, CLAIM_HEARTBEAT_MS);
      return () => { clearInterval(id); };
    };

    // Clear our claim. Called on finish, failure, and AI-busy back-off so the
    // folder can be re-picked. Best-effort: the backend's claim auto-expiry is
    // the backstop if this never lands (e.g. browser closed mid-run).
    // Backend release is owner-scoped — wiping another ai user's claim is a no-op.
    const releaseFolder = async (folderPath, outcome, reason) => {
      try {
        const token = localStorage.getItem('authToken');
        await fetch(`${API_BASE_URL}/analyze/auto-check/release`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            folder_path: folderPath,
            outcome: outcome || 'failed',
            reason: reason ? String(reason).slice(0, 500) : ''
          })
        });
      } catch { /* the owner binding stays until a later release lands */ }
    };

    // Durably flag a folder whose auto-check FAILED or was interrupted (table
    // didn't load/save, incomplete result, status flip failed, run paused
    // mid-way). Flips its sorted-snapshot status → 'error', which shows a red
    // "Check Failed" badge in the Sort tab and drops the folder out of the
    // eligibility queue (it matches status = 'reviewed') so it isn't retried
    // forever. A human re-reviews the folder to re-arm auto-check. NOT called
    // for AI-busy or claim-lost — those are normal, retryable, not errors.
    const markFolderError = async (folderPath) => {
      // Flip the sorted-snapshot status → 'error'. This is what removes the
      // folder from the auto-check eligibility queue (which matches
      // status = 'reviewed'), so it MUST be reliable: a single dropped request
      // used to leave the folder 'reviewed', which let it be re-picked and
      // re-checked in a loop (the exact "it kept checking again" bug). Retry a
      // few times with backoff before giving up.
      for (let attempt = 1; attempt <= 4; attempt++) {
        try {
          const token = localStorage.getItem('authToken');
          const res = await fetch(`${API_BASE_URL}/analyze/snapshots/status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ folder_path: folderPath, status: 'error' })
          });
          if (res.ok) return true;
        } catch { /* fall through to retry */ }
        if (attempt < 4) await sleep(1500 * attempt);
      }
      return false;
    };

    const runJob = async (folder) => {
      const r = () => autoRefs.current;
      const fp = folder.folder_path;
      const resumeRun = ['paused', 'stopped', 'failed', 'running'].includes(String(folder.auto_check_run_state || ''));
      alog(`▶ START "${folder.title || folder.folder_path}" (${folder.folder_path}, medium=${folder.medium || '?'}, regrade=${!!folder.regrade}, resume=${resumeRun}, processed=${folder.auto_check_processed_count || 0}, last=${folder.auto_check_last_paper_id || '-'})`);
      try {
        await r().armHosts();
      } catch (e) {
        alog('Spc and Mac were not switched on', e && e.message);
      }
      // Reset this folder's step boxes and record which ai user is running it.
      // The "Run" box covers load → set medium → grade (steps 1–3): any failure
      // in that stretch marks Run red, and clicking it reloads the sorted list +
      // clears the table so the examiner can re-run.
      currentStep = 'run';
      await reportStep(fp, '__start__');
      await reportStep(fp, 'run', 'running');

      // 1. Load the folder into the Checking tab + select the whole list.
      //    First import the reviewed snapshot so the current (checking) user's
      //    working analyzed_papers = the reviewed list — same as clicking
      //    "Review" in the Sort tab. Then read it back via fetchAnalyzedData.
      setAutoCheckStatus(`Loading "${folder.title || folder.folder_path}"…`);
      r().beginFolderSwitch();
      r().setFolderPath(folder.folder_path);
      // Drop a poll that still holds the previous class. Keep results blocked
      // until this folder's own rubric is the one in memory.
      ++resultsFetchGenRef.current;
      blockResultsUntilSyncRef.current = true;
      try {
        try {
          const token = localStorage.getItem('authToken');
          await fetch(`${API_BASE_URL}/analyze/snapshots/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ folder_path: folder.folder_path })
          });
        } catch (e) { alog('Step 1: snapshot import failed (will try live data)', e.message); }
        await r().fetchAnalyzedData(folder.folder_path);
        // The folderPath effect loads the rubric only when the path changes.
        // Reloading the page reopens the latest class, so a regrade of that
        // same card must load the rubric here. Bump the generation first so
        // an older load cannot mark a different folder ready afterwards.
        rubricLoadGenRef.current += 1;
        await r().fetchRubricForFolder(folder.folder_path, { quiet: true });
        await waitFor(() => r().folderPath === folder.folder_path
          && (r().analyzedData || []).length > 0
          && !!r().rubricMetadata
          && r().folderRubricReadyFor(folder.folder_path), { timeout: 90000 });
      } finally {
        blockResultsUntilSyncRef.current = false;
      }
      alog(`Step 1 done: ${(r().analyzedData || []).length} sorted rows loaded, rubric=${!!r().rubricMetadata}`);
      r().setSelectionMode('range');
      r().setStudentRange('All');

      // 2. Pick the grading medium. Urdu-button folders whose subject reads
      //    as English grade with English-2; everything else with its own.
      const isUrOg = folder.medium === 'urog';
      const subjectText = `${folder.subject || ''} ${folder.title || ''}`;
      const subjectIsEnglish = /english/i.test(subjectText);
      const englishGrading = !isUrOg && (folder.medium === 'english' || subjectIsEnglish);
      r().handleMediumChange(englishGrading ? 'english_two_step' : 'urdu_two_step');
      await waitFor(() => r().pipelineMode === 'two_step'
        && r().subjectType === (englishGrading ? 'other' : 'urdu'));

      // Dropdowns and localStorage stay as the teacher left them. English
      // auto-check (fresh, Re-Auto, and regrade) forces OCR and grading to
      // ggml. Urdu and UrOG regrade force grading to ggml; a missing
      // transcript is read again with Gemini 3.8-flash. Fresh Urdu checks
      // keep their own models. Fresh UrOG reads with Gemini 3.8-flash and
      // grades with ggml.
      const ENGLISH_AUTO_MODEL = 'ggml';
      const URDU_REREAD_MODEL = 'gemini-3.8-flash';
      const UROG_OCR_MODEL = 'gemini-3.8-flash';
      const englishAutoModels = englishGrading
        ? {
            ocrModelOverride: ENGLISH_AUTO_MODEL,
            gradingModelOverride: ENGLISH_AUTO_MODEL,
            providerOverride: 'local',
            ocrProviderOverride: 'local'
          }
        : null;
      const urduRegradeModels = (!englishGrading && (folder.medium === 'urdu' || folder.medium === 'urog'))
        ? {
            ocrModelOverride: folder.medium === 'urog' ? UROG_OCR_MODEL : URDU_REREAD_MODEL,
            gradingModelOverride: ENGLISH_AUTO_MODEL,
            providerOverride: 'local',
            ocrProviderOverride: 'gemini'
          }
        : null;
      const ocrForLog = folder.regrade
        ? (urduRegradeModels ? urduRegradeModels.ocrModelOverride : (englishAutoModels ? ENGLISH_AUTO_MODEL : r().ocrModel))
        : (isUrOg ? UROG_OCR_MODEL : (englishGrading ? ENGLISH_AUTO_MODEL : r().ocrModel));
      const gradingForLog = folder.regrade
        ? ENGLISH_AUTO_MODEL
        : (isUrOg ? ENGLISH_AUTO_MODEL : (englishGrading ? ENGLISH_AUTO_MODEL : r().gradingModel));
      alog(`Step 2 done: medium=${englishGrading ? 'english_two_step' : 'urdu_two_step'}, ocr=${ocrForLog}, grading=${gradingForLog}, ocrHost=${r().ocrHost || '-'}, gradingHost=${r().gradingHost || '-'}, regrade=${!!folder.regrade}`);

      const runGradingOnlyOnLoadedTable = async (loadedRows) => {
        const WAIT_CAP_MS = 120 * 60 * 1000;
        const waitStart = Date.now();
        let attempt = 0;
        // Use the rows we just fetched — autoRefs.assessmentResults can still
        // be [] for a tick after setState, and waiting on that empty list
        // hung forever ("Loading saved table…") with 0 rows selected.
        const rows = (Array.isArray(loadedRows) && loadedRows.length)
          ? loadedRows
          : (r().assessmentResults || []);
        const allIds = rows.map(x => x && x.id).filter(id => id != null);
        if (allIds.length === 0) {
          throw new Error('No saved rows to re-grade');
        }
        r().setSelectedResults(allIds);
        for (;;) {
          if (cancelled) throw new Error('auto-check cancelled');
          if (claimGuard.lost) throw lostClaimError();

          setAutoCheckStatus(`Re-grading "${folder.title}" (Check Step 2, skipping OCR / Text Step 1)…`);
          const kick = await r().handleRecheckSelected('grading_only', {
            skipConfirm: true,
            resultIds: allIds,
            results: rows,
            folderPath: folder.folder_path,
            dispatchAuto: true,
            summaryKind: 'checked',
            ...((urduRegradeModels || englishAutoModels) || {})
          });
          if (kick !== 'busy') return kick;

          attempt++;
          if (Date.now() - waitStart > WAIT_CAP_MS) {
            throw new Error(`AI stayed busy for over ${Math.round(WAIT_CAP_MS / 60000)} min — giving up so the folder can be re-reviewed.`);
          }
          const stillMine = await claimFolder(folder.folder_path);
          if (!stillMine) {
            claimGuard.lost = true;
            throw lostClaimError();
          }
          const waitMs = 30000 + Math.floor(Math.random() * 15000);
          setAutoCheckStatus(`Waiting for the AI to free up to re-grade "${folder.title}" — retry #${attempt} in ${Math.round(waitMs / 1000)}s…`);
          alog(`regrade: AI busy — retrying in ${Math.round(waitMs / 1000)}s (attempt ${attempt})`);
          await sleep(waitMs);
        }
      };

      if (folder.regrade) {
        // Sort-tab Re-grade queue: keep the saved OCR/transcript table, skip
        // OCR / Text Step 1, then the rest of the pipeline (save → scans →
        // approve → absent) is unchanged below.
        setAutoCheckStatus(`Loading saved table to re-grade "${folder.title}" (skipping OCR / Text Step 1)…`);
        try {
          const token = localStorage.getItem('authToken');
          const imp = await fetch(`${API_BASE_URL}/assess/snapshots/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ folder_path: folder.folder_path, source: 'check_more' })
          });
          if (!imp.ok) {
            const data = await imp.json().catch(() => ({}));
            throw new Error(data.message || 'No saved result table to re-grade');
          }
        } catch (e) {
          throw new Error('Re-grade needs a saved table: ' + (e && e.message ? e.message : String(e)));
        }
        let loaded = [];
        for (let attempt = 1; attempt <= 4; attempt++) {
          loaded = (await r().fetchAssessmentResults()) || [];
          alog(`Re-grade: fetch saved table attempt ${attempt} → ${loaded.length} rows`);
          if (loaded.length > 0) break;
          if (attempt < 4) await sleep(800);
        }
        if (loaded.length === 0) {
          loaded = r().assessmentResults || [];
        }
        if (loaded.length === 0) {
          throw new Error('Saved table imported but no rows loaded for ' + folder.folder_path);
        }
        alog(`Re-grade: ${loaded.length} saved rows — grading only`);
        const kick = await runGradingOnlyOnLoadedTable(loaded);
        if (kick === 'busy') { const e = new Error('AI busy with another class'); e.aiBusy = true; throw e; }
        if (kick !== 'started' && kick !== 'completed') throw new Error('Re-grade failed to start');
        await sleep(1500);
        await waitFor(() => !r().isProcessing && !r().sessionId, { timeout: Infinity });
        alog('Step 3 done: re-grade finished (OCR / Text Step 1 skipped)');
      } else {
      // 2b. A new run clears the table and grades from the first paper.
      //     A paused, stopped, failed, or interrupted run keeps every paper
      //     that already has marks and continues from there.
      if (resumeRun) {
        setAutoCheckStatus(`Resuming "${folder.title}" from the last finished paper…`);
        alog(`Step 2b: resume — keeping finished papers, last=${folder.auto_check_last_paper_id || '-'}`);
      } else {
      setAutoCheckStatus(`Clearing previous results before "${folder.title}"…`);
      try {
        const token = localStorage.getItem('authToken');
        await fetch(`${API_BASE_URL}/assess/results?source=check_more&folder_path=${encodeURIComponent(fp)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        await r().fetchAssessmentResults();
        await sleep(600);
        alog('Step 2b done: cleared previous check_more results');
      } catch (e) { alog('Step 2b: clear previous results failed (continuing)', e.message); }
      }

      // 3. Run the checking flow.
      if (folder.medium === 'english' || folder.medium === 'urog') {
        const urOg = folder.medium === 'urog';
        alog(urOg ? 'Step 3: starting UrOG two-step check (Gemini 3.8-flash OCR, ggml grading)…' : 'Step 3: starting English two-step check…');
        setAutoCheckStatus(urOg
          ? `Checking "${folder.title}" (UrOG, Gemini 3.8-flash OCR, ggml grading)…`
          : `Checking "${folder.title}" (English, two-step)…`);
        const kick = await r().handleStartAssessment(urOg
          ? {
              dispatchAuto: true,
              resume: resumeRun,
              ocrModelOverride: UROG_OCR_MODEL,
              ocrProviderOverride: 'gemini',
              providerOverride: 'local',
              gradingModelOverride: ENGLISH_AUTO_MODEL
            }
          : { dispatchAuto: true, resume: resumeRun, ...(englishAutoModels || { ocrModelOverride: 'ggml', gradingModelOverride: 'ggml', providerOverride: 'local', ocrProviderOverride: 'local' }) });
        // 409 from the backend: the other browser grabbed LM Studio between our
        // idle-check and this kickoff. Bail out as a retryable "busy" so the
        // tick keeps this class on this account and tries again.
        if (kick === 'busy') { const e = new Error('AI busy with another class'); e.aiBusy = true; throw e; }
        if (kick === 'error') throw new Error('Check failed to start');
        await sleep(1500);
        await waitFor(() => !r().isProcessing && !r().sessionId, { timeout: Infinity });
        alog('Step 3 done: check finished');
      } else {
        // Urdu/typed flow: Text Step 1 (load docx) → Check Step 2 (re-grade).
        // Text loads ONCE from the docx-server (:3100 — no AI slot needed);
        // grading then competes for the shared LM Studio 'check' slot. To kill
        // the "keep reloading the text" churn when another class holds the AI,
        // we do NOT bail + restart the whole run on a grading 409. Instead we
        // keep the loaded text + hold the folder claim and retry ONLY the
        // grading step (backoff + jitter) until the slot frees — so the docx
        // text is read exactly once per folder.
        const urduUngradedIds = (rows) => (rows || []).filter((row) => {
          const marks = row && row.marks != null ? String(row.marks).trim() : '';
          return marks === '' || /^error$/i.test(marks);
        }).map((row) => row.id).filter((id) => id != null);
        let urduLoaded = false;
        if (resumeRun) {
          setAutoCheckStatus(`Resuming "${folder.title}" from the last finished paper…`);
          const loaded = (await r().fetchAssessmentResults()) || [];
          urduLoaded = loaded.length > 0;
          alog(`Step 3 (urdu): resume loaded ${loaded.length} row(s), ${urduUngradedIds(loaded).length} still ungraded`);
        }
        if (!urduLoaded) {
        setAutoCheckStatus(`Text Step 1 for "${folder.title}"…`);
        await r().handleGetText();
        await waitFor(() => !r().isGettingText);
        await waitFor(() => (r().assessmentResults || []).length > 0, { timeout: 120000 });
        alog(`Step 3 (urdu): text loaded (${(r().assessmentResults || []).length} rows) — grading, will wait for the AI slot WITHOUT reloading`);
        }

        // Safety net: if the AI stays busy this long the model is likely down —
        // give up so the folder is flagged for re-review instead of parking
        // forever. Well under normal backlogs; the claim is refreshed each wait.
        const URDU_WAIT_CAP_MS = 120 * 60 * 1000; // 2 h
        const waitStart = Date.now();
        let urduAttempt = 0;
        for (;;) {
          if (cancelled) throw new Error('auto-check cancelled');
          if (claimGuard.lost) throw lostClaimError();
          // (Re)select every current row before each grading attempt (the busy
          // path leaves the table intact, but re-selecting is race-proof).
          const allIds = resumeRun
            ? urduUngradedIds(r().assessmentResults)
            : (r().assessmentResults || []).map(x => x.id);
          if (allIds.length === 0) {
            alog('Step 3 (urdu): no ungraded rows left');
            break;
          }
          r().setSelectedResults(allIds);
          await waitFor(() => (r().selectedResults || []).length === allIds.length && allIds.length > 0);

          setAutoCheckStatus(`Grading "${folder.title}" (Step 2)…`);
          const kick = await r().handleRecheckSelected('grading_only', { skipConfirm: true, dispatchAuto: true, summaryKind: 'checked' });
          if (kick !== 'busy') break; // grading started/finished — proceed

          urduAttempt++;
          if (Date.now() - waitStart > URDU_WAIT_CAP_MS) {
            throw new Error(`AI stayed busy for over ${Math.round(URDU_WAIT_CAP_MS / 60000)} min — giving up so the folder can be re-reviewed.`);
          }
          // Refresh our claim so a long wait can't let the 60-min lock expire
          // and let another browser grab the folder (and reload its text). A
          // lost claim means another browser already took over → bail as a
          // retryable "busy" (no error, no reload here).
          const stillMine = await claimFolder(folder.folder_path);
          if (!stillMine) {
            claimGuard.lost = true;
            throw lostClaimError();
          }

          const waitMs = 30000 + Math.floor(Math.random() * 15000); // 30–45 s + jitter
          setAutoCheckStatus(`Waiting for the AI to free up to grade "${folder.title}" — text kept loaded, retry #${urduAttempt} in ${Math.round(waitMs / 1000)}s…`);
          alog(`Step 3 (urdu): AI busy — keeping text loaded, retrying grading in ${Math.round(waitMs / 1000)}s (attempt ${urduAttempt})`);
          await sleep(waitMs);
        }
        await sleep(1500);
        await waitFor(() => !r().isProcessing && !r().sessionId, { timeout: Infinity });
        alog('Step 3 done: check finished (urdu)');
      }
      } // end full-check (non-regrade) path
      await reportStep(fp, 'run', 'done');
      currentStep = 'save';
      await reportStep(fp, 'save', 'running');

      // 4. Refresh + save the table. This snapshot is the ONLY durable copy of
      //    the checked results: the NEXT folder's run (step 2b) wipes the live
      //    check_more table, and the Sort-tab "Auto-Checked" badge restores the
      //    Check tab from this snapshot. So a failed/short save must FAIL the
      //    job (the tick catch then marks the folder 'error' → red badge,
      //    re-review to retry) rather than be silently swallowed — otherwise
      //    the results are lost.
      //    Use fetchAssessmentResults' RETURN value (not the async state mirror)
      //    so we always save the full, current table — no stale-state race.
      //    But fetchAssessmentResults transiently returns [] when its GET (or
      //    one of the per-row auto-correct sub-calls it makes) blips, even
      //    though the check produced rows. Retry a few times before concluding
      //    the run produced nothing, so a network blip can't throw away a
      //    completed check (the rows are already safe in the DB).
      let rows = [];
      for (let attempt = 1; attempt <= 4; attempt++) {
        rows = (await r().fetchAssessmentResults()) || [];
        alog(`Step 4: fetch results attempt ${attempt} → ${rows.length} rows`);
        if (rows.length > 0) break;
        if (attempt < 4) {
          setAutoCheckStatus(`Loading results for "${folder.title}"… (retry ${attempt}/3)`);
          await sleep(1500);
        }
      }
      if (rows.length === 0) { alog('Step 4: ✖ no results after retries — aborting'); throw new Error('No results produced for ' + folder.folder_path); }
      // Always SAVE the table when there are rows — even if the check had
      // problems (OCR-failed rows, or fewer students checked than the sorted
      // list). Saving first means nothing is thrown away and the examiner can
      // open the folder to recheck the problem rows. The soft-error gate AFTER
      // the save then flags the folder 'error' (instead of approving).
      // Expected = unique student codes in the sorted list, excluding '000'
      // (invalid-roll sheets aren't checked), matching the backend's grouping.
      const expectedStudents = new Set(
        (r().analyzedData || []).map(a => a && a.unique_code).filter(c => c && c !== '000')
      ).size;
      alog(`Step 4: ${rows.length} rows (expected ${expectedStudents}) — building snapshot`);
      const snapshotBody = JSON.stringify({
        folder_path: folder.folder_path,
        rows: rows.map(x => ({
          student_name: x.student_name, image_name: x.image_name, class: x.class,
          section: x.section, subject: x.subject, marks: x.marks, comments: x.comments,
          table_comments: x.table_comments, status: x.status, source: x.source,
          ocr_text: x.ocr_text, ocr_status: x.ocr_status, ocr_unclear_ratio: x.ocr_unclear_ratio,
          ocr_model: x.ocr_model, grading_model: x.grading_model, pipeline: x.pipeline,
          grading_host: x.grading_host || null,
          ocr_image_note: x.ocr_image_note || null,
          ocr_prompt_tokens: x.ocr_prompt_tokens ?? null,
          ocr_history: x.ocr_history ?? null,
          table_history: x.table_history ?? null,
          ocr_completion_tokens: x.ocr_completion_tokens ?? null
        }))
      });
      setAutoCheckStatus(`Saving table for "${folder.title}" (${rows.length} rows)…`);
      alog(`Step 4: POST /assess/snapshots — ${rows.length} rows, ${(snapshotBody.length / 1024).toFixed(1)} KB payload`);
      let saveErr = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const token = localStorage.getItem('authToken');
          const res = await fetch(`${API_BASE_URL}/assess/snapshots`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: snapshotBody
          });
          // Read the body first so a non-2xx still surfaces the backend's
          // actual reason (e.g. a DB error message) instead of a bare status.
          const data = await res.json().catch(() => ({}));
          alog(`Step 4: save attempt ${attempt} → HTTP ${res.status}`, data);
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}${data && data.message ? ' — ' + data.message : ''}`);
          }
          if (data && data.saved != null && Number(data.saved) !== rows.length) {
            throw new Error(`saved ${data.saved}/${rows.length} rows`);
          }
          saveErr = null;
          alog('Step 4 done: snapshot saved ✔');
          break;
        } catch (e) {
          saveErr = e;
          alog(`Step 4: save attempt ${attempt} FAILED — ${e.message}`);
          if (attempt < 3) {
            setAutoCheckStatus(`Saving table for "${folder.title}"… (retry ${attempt}/3)`);
            await sleep(2000 * attempt);
          }
        }
      }
      if (saveErr) {
        alog('Step 4: ✖ ALL 3 save attempts failed — folder will be marked error', saveErr.message);
        throw new Error(`Checked but failed to save the ${rows.length}-row table: ${saveErr.message}. Folder marked "error" — re-review to retry.`);
      }
      await reportStep(fp, 'save', 'done');

      // 4b. Soft-error gate. The table is now SAVED. If the check had problems
      //     — some sheets' OCR/grading failed (error rows), stuck "OCR done —
      //     grading queued" placeholders (ungraded rows), or fewer students
      //     were checked than the sorted list has — flag the folder 'error'
      //     (do NOT auto-approve) so an examiner opens it and rechecks the
      //     problem rows. Unlike a hard failure, the saved table is kept as-is.
      const errorRows = rows.filter(x => x &&
        (String(x.status || '').toLowerCase() === 'error' || String(x.marks || '').toUpperCase() === 'ERROR'));
      // Placeholders / incomplete grades that still have status=success — the
      // classic "Table N/A" + "OCR done — grading queued…" rows. These used
      // to slip past this gate and get marked Auto-Checked.
      const ungradedRows = rows.filter(x => x && r().isUngradedAssessmentRow && r().isUngradedAssessmentRow(x));
      const incomplete = expectedStudents > 0 && rows.length < expectedStudents;
      if (incomplete || errorRows.length > 0 || ungradedRows.length > 0) {
        const reason = [
          incomplete ? `only ${rows.length}/${expectedStudents} students checked` : null,
          errorRows.length > 0 ? `${errorRows.length} row(s) had errors (missing images / OCR failed)` : null,
          ungradedRows.length > 0 ? `${ungradedRows.length} ungraded row(s) (OCR done but grading incomplete)` : null
        ].filter(Boolean).join('; ');
        alog(`Step 4b: ⚠ soft error (${reason}) — table SAVED, marking folder 'error' for recheck`);
        const flagged = await markFolderError(folder.folder_path).catch(() => false);
        alog(`Step 4b: marked "${folder.title || folder.folder_path}" status=error (flip ${flagged ? 'ok' : 'FAILED'})`);
        autoSkipRef.current.add(folder.folder_path);
        // Run + Save succeeded (table is SAVED); the check just couldn't be
        // auto-completed. Flag the Approve box so it's visibly ✗ with the
        // reason — clicking it (or the red "Check Failed" / "Ungraded rows"
        // badge) opens the folder in the Check tab with the saved table
        // loaded, so the examiner can recheck the problem rows and Approve.
        // We flag Approve (NOT the Run box) on purpose: clicking Run CLEARS
        // the table to re-grade, which would throw away the good results —
        // the whole point here is to keep them.
        const approveMsg = ungradedRows.length > 0 && errorRows.length === 0 && !incomplete
          ? `Ungraded rows — ${reason}. Table saved; open to regrade those rows, then Approve.`
          : `Error in rows — ${reason}. Table saved; open to recheck the error rows, then Approve.`;
        await reportStep(fp, 'approve', 'error', approveMsg);
        publishAutoCheckNoticeRef.current(`Checked "${folder.title}" with issues (${reason}) — table saved, marked Error for recheck.`);
        return; // table saved; skip auto-checked + approve
      }

      // 5. Mark the sorted-snapshot 'auto-checked' — a durable checkpoint that
      //    takes the folder out of the eligibility queue (which matches
      //    status = 'reviewed') the moment the result table is safely saved.
      //    Step 6 then auto-approves and moves it on to 'copy_to_edupage'. If
      //    Step 6 can't (no matching student list, or the approve POST fails),
      //    the folder rests here at 'auto-checked' for a human to approve — so
      //    nothing is lost either way.
      // Retry up to 3 times: this one-line request runs AFTER a long (30+ min)
      // check, so a transient blip ("Failed to fetch") here must not throw the
      // whole completed check away. Only after all 3 attempts fail do we throw,
      // and the tick catch marks the folder 'error' (re-review to retry). The
      // saved result snapshot from step 4 is preserved either way.
      setAutoCheckStatus(`Marking "${folder.title}" auto-checked…`);
      alog('Step 5: POST /analyze/snapshots/status → auto-checked');
      let flipErr = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const token = localStorage.getItem('authToken');
          const res = await fetch(`${API_BASE_URL}/analyze/snapshots/status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ folder_path: folder.folder_path, status: 'auto-checked' })
          });
          const data = await res.json().catch(() => ({}));
          alog(`Step 5: flip attempt ${attempt} → HTTP ${res.status}`, data);
          if (!res.ok) throw new Error(`HTTP ${res.status}${data && data.message ? ' — ' + data.message : ''}`);
          flipErr = null;
          break;
        } catch (e) {
          flipErr = e;
          alog(`Step 5: flip attempt ${attempt} FAILED — ${e.message}`);
          if (attempt < 3) {
            setAutoCheckStatus(`Marking "${folder.title}" auto-checked… (retry ${attempt}/3)`);
            await sleep(2000 * attempt); // 2s, then 4s backoff
          }
        }
      }
      if (flipErr) {
        alog('Step 5: ✖ flip failed after 3 tries', flipErr.message);
        throw new Error('Checked but failed to mark auto-checked after 3 tries: ' + (flipErr && flipErr.message ? flipErr.message : String(flipErr)));
      }
      alog(`Step 5 done: marked auto-checked`);

      currentStep = 'scans';
      await reportStep(fp, 'scans', 'running');
      // 5b. Save the checked table into the SCANS folder (checked_results) — the
      //     same durable, printable table the manual "Approve → Next — Save &
      //     Pick Student List" step writes. The auto flow never opens that
      //     picker, so without this the folder's "Saved Results" (and its Print
      //     Preview) stay EMPTY even though the check succeeded. Keyed by the
      //     folder's OWN rubric id (parsed from folder_path "rubrics/<id>"), so
      //     there is no same-name ambiguity — it always lands on this folder.
      //     Non-fatal: the assess snapshot from step 4 is the durable copy, so a
      //     blip here just leaves the scans folder without the printable table;
      //     it must not fail the job or downgrade a good check to 'error'.
      try {
        const cr_ridMatch = String(folder.folder_path || '').match(/rubrics\/(\d+)/);
        const cr_rubricId = cr_ridMatch ? Number(cr_ridMatch[1]) : (folder.id || null);
        const cr_meta = r().rubricMetadata || {};
        const cr_first = rows[0] || {};
        const cr_class = (cr_meta.class || cr_first.class || '').toString();
        const cr_section = (cr_meta.section || cr_first.section || '').toString();
        const cr_subject = (cr_meta.subject || cr_first.subject || '').toString();
        const cr_teachers = (cr_meta.created_by || '').toString();
        const cr_now = new Date();
        const cr_checkedDate = cr_meta.test_date
          ? new Date(cr_meta.test_date).toISOString().slice(0, 10)
          : cr_now.toISOString().slice(0, 10);
        const cr_checkedTime = cr_now.toTimeString().slice(0, 8);
        // Same per-row mapping as the manual finalize: prefer the table-view
        // comments, append an "[image] [N sheets]" footer for the print view.
        const cr_students = rows.map(rw => ({
          student_name: rw.student_name || '',
          checked_name: rw.student_name || '',
          first_name: rw.student_name || '.',
          marks: rw.marks ?? '',
          comments: commentsForScansFolder(rw),
          grading_host: rw.grading_host || null
        }));
        if (!cr_rubricId) {
          alog(`Step 5b: ✖ could not resolve rubric id from "${folder.folder_path}" — scans-folder table skipped`);
          await reportStep(fp, 'scans', 'error', `Could not resolve rubric id from "${folder.folder_path}"`);
        } else if (cr_students.length === 0) {
          alog('Step 5b: no rows to save — scans-folder table skipped');
          await reportStep(fp, 'scans', 'error', 'No rows to save to the scans folder');
        } else {
          setAutoCheckStatus(`Saving table to scans folder for "${folder.title}"…`);
          let cr_saved = false;
          let cr_lastErr = '';
          for (let attempt = 1; attempt <= 2 && !cr_saved; attempt++) {
            try {
              const token = localStorage.getItem('authToken');
              const res = await fetch(`${API_BASE_URL}/checked-results/finalize`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                  rubric_id: cr_rubricId,
                  metadata: {
                    class: cr_class, section: cr_section, subject: cr_subject,
                    teacherNames: cr_teachers, checkedDate: cr_checkedDate, checkedTime: cr_checkedTime,
                    teacher_names: cr_teachers, checked_date: cr_checkedDate, checked_time: cr_checkedTime
                  },
                  students: cr_students
                })
              });
              const data = await res.json().catch(() => ({}));
              if (!res.ok) throw new Error(`HTTP ${res.status}${data && (data.error || data.message) ? ' — ' + (data.error || data.message) : ''}`);
              cr_saved = true;
              alog(`Step 5b done: saved ${cr_students.length}-row table to scans folder (rubric ${cr_rubricId})`);
            } catch (e) {
              cr_lastErr = e && e.message ? e.message : String(e);
              alog(`Step 5b: save attempt ${attempt} FAILED — ${cr_lastErr}`);
              if (attempt < 2) await sleep(2000);
            }
          }
          if (cr_saved) await reportStep(fp, 'scans', 'done');
          else await reportStep(fp, 'scans', 'error', `Failed to save the scans-folder table${cr_lastErr ? ` — ${cr_lastErr}` : ''}`);
        }
      } catch (crErr) {
        alog(`Step 5b: ✖ scans-folder save errored (non-fatal) — ${crErr && crErr.message ? crErr.message : String(crErr)}`);
        await reportStep(fp, 'scans', 'error', crErr && crErr.message ? crErr.message : String(crErr));
      }

      // 6. Auto-approve into the student list. The check succeeded and the
      //    result snapshot is saved, so this is the "happy path" close-out:
      //    push the marks to the student list and flip the folder to
      //    'copy_to_edupage' (the approved state). We only auto-approve when an
      //    EXACT student-list match exists (class + section + subject) — the
      //    same match the manual approve form auto-selects. No match → leave
      //    the folder 'auto-checked' for a human to approve, rather than guess
      //    the wrong list.
      //
      //    Approve trouble does NOT fail the job: the check + save + the
      //    'auto-checked' flip all already succeeded, so any Step-6 problem
      //    (no matching list, approve POST fails, or this block throws) just
      //    leaves the folder at 'auto-checked' for a manual approve — it must
      //    NEVER downgrade a good check to 'error'. Hence the local try/catch.
      try {
        currentStep = 'approve';
        await reportStep(fp, 'approve', 'running');
        const meta = r().rubricMetadata || {};
        const lists = r().studentLists || [];
        const norm = (s) => String(s || '').trim().toLowerCase();
        // Resolve the student list by class + subject. Rubrics carry NO
        // `section` column (so meta.section is always ''), which is why the old
        // strict class+section+subject match never succeeded and nothing
        // auto-approved. Prefer an exact section match when the rubric actually
        // has one; otherwise adopt the UNIQUE class+subject list and use ITS
        // section. Comparison is trimmed + case-insensitive so "english-a" vs
        // "English-a" still matches.
        const byClassSubject = lists.filter(l =>
          norm(l.class) === norm(meta.class) && norm(l.subject) === norm(meta.subject)
        );
        let match = null;
        if (meta.section) {
          match = byClassSubject.find(l => norm(l.section) === norm(meta.section)) || null;
        }
        if (!match && byClassSubject.length === 1) {
          // Exactly one list for this class+subject — safe to auto-approve.
          match = byClassSubject[0];
        }
        if (!match) {
          const why = byClassSubject.length > 1
            ? `${byClassSubject.length} lists match class=${meta.class}/subject=${meta.subject} — ambiguous section, won't guess`
            : `no student list for class=${meta.class}/subject=${meta.subject}`;
          alog(`Step 6: ${why} — leaving auto-checked for manual Approve`);
          await reportStep(fp, 'approve', 'error', why);
          publishAutoCheckNoticeRef.current(`Checked "${folder.title}" — ${why}. Approve manually.`);
          alog(`✔ DONE "${folder.title || folder.folder_path}" — auto-checked (manual approve needed)`);
          return;
        }

        setAutoCheckStatus(`Approving "${folder.title}" → ${match.class}-${match.section} ${match.subject}…`);
        alog(`Step 6: auto-approving to ${match.class}-${match.section} ${match.subject}`);
        // Point the approve form at the matched list and select every row, then
        // wait (short timeout — pure state sync) for those to land in the ref
        // before calling handleApprove so it doesn't read stale state.
        r().setApproveForm({ class: match.class, section: match.section, subject: match.subject });
        const allIds = (r().assessmentResults || []).map(x => x.id).filter(x => x != null);
        r().setSelectedResults(allIds);
        await waitFor(() => {
          const f = r().approveForm || {};
          return f.class === match.class && f.section === match.section && f.subject === match.subject
            && (r().selectedResults || []).length === allIds.length && allIds.length > 0;
        }, { timeout: 15000 });

        const approved = await r().handleApprove({ auto: true });
        if (approved) {
          alog(`✔ DONE "${folder.title || folder.folder_path}" — approved → Copy to Edupage`);
          await reportStep(fp, 'approve', 'done');
          setAutoCheckStatus(`Approved "${folder.title}" → ${match.class}-${match.section} ${match.subject} (Copy to Edupage)`);

          // 7. Mark Absent — mirror the Students-tab "1. Mark Absent" button
          //    (handleDone). Approve just pushed marks to every student who had
          //    a paper; each OTHER student in the list is still blank (no scan =
          //    absent). Fill their empty first_name → '.', marks → '0',
          //    comments → 'Absent' so the list is submission-ready, exactly as
          //    clicking that button by hand would. Its own try/catch: a
          //    mark-absent hiccup must NEVER downgrade the good approve — the
          //    graded marks are already in the list regardless.
          currentStep = 'absent';
          try {
            await reportStep(fp, 'absent', 'running');
            setAutoCheckStatus(`Marking blank students absent in ${match.class}-${match.section} ${match.subject}…`);
            const token = localStorage.getItem('authToken');
            const listRes = await fetch(
              `${API_BASE_URL}/students?class=${encodeURIComponent(match.class)}&section=${encodeURIComponent(match.section)}&subject=${encodeURIComponent(match.subject)}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!listRes.ok) throw new Error(`Could not load the student list (HTTP ${listRes.status})`);
            const roster = await listRes.json();
            if (!Array.isArray(roster)) throw new Error('Unexpected student-list response');
            // Same fill rules as handleDone: keep any real value, else default.
            // '-' and blank/whitespace both count as empty.
            const filled = (v, dflt) => {
              const s = v == null ? '' : String(v);
              return (s.trim() !== '' && s !== '-') ? v : dflt;
            };
            let absentCount = 0;
            for (const student of roster) {
              const firstName = filled(student.first_name, '.');
              const marks = filled(student.marks, '0');
              const comments = filled(student.comments, 'Absent');
              // Only PUT students that actually change — same as the button.
              if (firstName === student.first_name && marks === student.marks && comments === student.comments) continue;
              const upRes = await fetch(`${API_BASE_URL}/students/${student.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ student_name: student.student_name, first_name: firstName, marks, comments })
              });
              if (!upRes.ok) {
                const t = await upRes.text().catch(() => '');
                throw new Error(`Failed to mark ${student.student_name || student.id} absent${t ? ` — ${t}` : ''}`);
              }
              absentCount++;
            }
            alog(`Step 7: marked ${absentCount} blank student(s) absent in ${match.class}-${match.section} ${match.subject}`);
            await reportStep(fp, 'absent', 'done');
            setAutoCheckStatus(`Approved + marked ${absentCount} absent — "${folder.title}" → ${match.class}-${match.section} ${match.subject} done`);
          } catch (absentErr) {
            const msg = absentErr && absentErr.message ? absentErr.message : String(absentErr);
            alog(`Step 7: ✖ mark-absent errored (${msg}) — approve stands, fill absentees manually`);
            await reportStep(fp, 'absent', 'error', msg);
            publishAutoCheckNoticeRef.current(`Approved "${folder.title}" — mark-absent failed: ${autoCheckFailureReason(absentErr, false)}. Click "1. Mark Absent" in Students.`);
          }
        } else {
          alog(`Step 6: ✖ approve returned false — folder left auto-checked for manual Approve`);
          await reportStep(fp, 'approve', 'error', 'Auto-approve failed (approve returned false) — approve manually');
          publishAutoCheckNoticeRef.current(`Checked "${folder.title}" — auto-approve failed. Approve manually.`);
        }
      } catch (approveErr) {
        alog(`Step 6: ✖ auto-approve errored (${approveErr && approveErr.message ? approveErr.message : String(approveErr)}) — folder left auto-checked`);
        const approveReason = autoCheckFailureReason(approveErr, false);
        await reportStep(fp, 'approve', 'error', approveReason);
        publishAutoCheckNoticeRef.current(`Checked "${folder.title}" — auto-approve error: ${approveReason}`);
      }
    };

    let lastCheckedTitle = '';
    const tick = async () => {
      if (cancelled || autoBusyRef.current) return;
      if (!localIdle()) {
        const check = (autoRefs.current.aiStatus || {}).check || {};
        if (!autoCheckNoticeRef.current && check.max > 0 && (check.active || 0) >= check.max) {
          setAutoCheckStatus(`Waiting for a check slot (${check.active}/${check.max} in use) — ai1–ai4 share ${check.max} concurrent LM Studio runs…`);
        }
        return;
      }
      const folders = await fetchEligibleList();
      if (cancelled || autoBusyRef.current || !localIdle()) return;
      if (!folders.length) {
        // No reviewed folders left to check. A stop or failure stays on this
        // line until the next class actually starts.
        if (autoCheckNoticeRef.current) {
          setAutoCheckStatus(autoCheckNoticeRef.current);
          return;
        }
        setAutoCheckStatus(lastCheckedTitle
          ? `Auto-check idle — no more reviewed folders waiting. Last finished "${lastCheckedTitle}". New reviewed folders are checked + approved automatically.`
          : 'Auto-check on — waiting for a reviewed folder. Reviewed folders are checked + approved automatically.');
        return;
      }
      // Walk oldest→newest and claim the first free folder. If ai1 already
      // took the oldest, ai2/ai3 immediately claim the next one in THIS tick
      // (no 20s wait) so all three can grade different classes at once.
      let folder = null;
      for (const f of folders) {
        if (cancelled || autoBusyRef.current) return;
        const claimed = await claimFolder(f.folder_path);
        if (claimed) {
          folder = f;
          break;
        }
        alog(`tick: claim lost for "${f.title || f.folder_path}" — trying next eligible folder`);
      }
      if (!folder) {
        if (!autoCheckNoticeRef.current) {
          setAutoCheckStatus('Auto-check on — other browsers hold the free folders; will retry…');
        }
        return;
      }
      // Engine was stopped between picking and claiming. Keep the class on
      // this account; do not hand it to another checker.
      if (cancelled) {
        publishAutoCheckNoticeRef.current(`Stopped "${folder.title || folder.folder_path}": Stopped by user.`);
        await releaseFolder(folder.folder_path, 'stopped', 'Stopped by user').catch(() => {});
        return;
      }
      alog(`tick: claimed "${folder.title || folder.folder_path}" (${folder.folder_path})`);
      autoBusyRef.current = true;
      // Renew the claim for the whole job so a grade longer than the 60-min
      // soft lock can't free the folder for ai2/ai3 mid-run.
      const stopHeartbeat = startClaimHeartbeat(folder.folder_path);
      let outcome = 'failed';
      let outcomeReason = '';
      try {
        // The engine loads or clears the table itself. A folderPath change
        // must not copy the saved table over a check that is about to start.
        suppressSavedTableSyncRef.current = true;
        clearAutoCheckNoticeRef.current();
        await runJob(folder);
        outcome = 'done';
        lastCheckedTitle = folder.title || folder.folder_path;
      } catch (e) {
        if (e && e.aiBusy) {
          // Stay on this account and retry. Do not offer the class to another account.
          outcome = 'keep';
          alog(`tick: AI busy / claim lost for "${folder.title || folder.folder_path}" — staying on this account`);
          setAutoCheckStatus(claimGuard.lost
            ? `Another browser took over "${folder.title || folder.folder_path}" — waiting to retry if still needed…`
            : `Waiting for an AI slot before checking "${folder.title || folder.folder_path}"…`);
        } else if (e && /paused by user/i.test(e.message || '')) {
          outcome = 'paused';
          outcomeReason = 'Paused by user';
          rememberPausedFolder(folder.folder_path);
          alog(`tick: paused "${folder.title || folder.folder_path}" — staying on this account`);
          publishAutoCheckNoticeRef.current(`${autoCheckOutcomeLine('paused', folder.title || folder.folder_path, outcomeReason)} Click Resume to continue this class.`);
        } else {
          const interrupted = !!(cancelled || (e && /cancelled/i.test(e.message || '')));
          outcome = interrupted ? 'stopped' : 'failed';
          outcomeReason = autoCheckFailureReason(e, interrupted);
          console.error('[AutoCheck] ✖ JOB FAILED:', e);
          alog(`✖ JOB ${interrupted ? 'INTERRUPTED' : 'FAILED'} "${folder.title || folder.folder_path}" — ${outcomeReason}`);
          if (currentStep) await reportStep(folder.folder_path, currentStep, 'error', outcomeReason);
          publishAutoCheckNoticeRef.current(`${autoCheckOutcomeLine(outcome, folder.title || folder.folder_path, outcomeReason)} This account will resume from the last finished paper.`);
        }
      } finally {
        suppressSavedTableSyncRef.current = false;
        stopHeartbeat();
        if (!claimGuard.lost) {
          await releaseFolder(folder.folder_path, outcome, outcomeReason).catch(() => {});
        } else {
          alog(`tick: skipping release for "${folder.folder_path}" — claim held by another browser`);
        }
        claimGuard.lost = false;
        autoBusyRef.current = false;
      }
    };

    const intervalId = setInterval(tick, 20000);
    tick();
    return () => { cancelled = true; clearInterval(intervalId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCheckEnabled, switchReady]);

  const selectedRubricIdForCheck = String(folderPath || '').match(/rubrics\/(\d+)/)?.[1] || null;
  const isUploadMismatchForCheck = !!(selectedRubricIdForCheck && uploadMismatchIds.has(selectedRubricIdForCheck));
  const uploadMismatchCheckMsg = isUploadMismatchForCheck
    ? (uploadMismatchErrors.get(selectedRubricIdForCheck)
      || 'Local folder and website image counts differ — sync with Upload on Sort Papers before checking.')
    : '';

  const oneLinePreviewText = (result, rowMeta) => (
    Number(result.comments_custom) === 1
      ? (result.comments || '')
      : ((rowMeta && rowMeta.oneLineComments) || result.comments || '')
  );
  // The badge, the one-line "Total marks", and the table Grand Total are one
  // number: the sum of the question rows. A saved marks cell that drifted
  // from that sum is shown and stored as the sum.
  const gradedObtainedMarks = (tableMd, result) => {
    if (!tableMd) return null;
    const grand = gradeFromTable(tableMd, repairForResult(result));
    if (!grand || !Number.isFinite(Number(grand.obtained))) return null;
    return String(Math.round(Number(grand.obtained) * 100) / 100);
  };
  const statedTotalMarks = (text) => {
    const match = String(text || '').match(/(?:Total marks|مجموعی نمبر)\s*[:：→]\s*([\d.]+)\s*\//i);
    if (!match) return null;
    const n = parseFloat(match[1]);
    return Number.isFinite(n) ? String(Math.round(n * 100) / 100) : null;
  };
  const marksSyncRef = useRef(new Set());
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const jobs = [];
    for (const result of assessmentResults) {
      if (!result.table_comments) continue;
      if (!rubricMatchesOpenFolder) continue;
      if (result.folder_path && result.folder_path !== folderPath) continue;
      const stub = !commentHasQuestions(result.comments);
      if (!stub) continue;
      const repaired = commentForResult(result);
      const comments = repaired ? repaired.comments : null;
      if (!comments) continue;
      const body = { comments };
      const key = `${result.id}:${comments}`;
      if (marksSyncRef.current.has(key)) continue;
      marksSyncRef.current.add(key);
      jobs.push({ id: result.id, body, key });
    }
    if (!jobs.length) return undefined;
    let cancelled = false;
    jobs.forEach(({ id, body, key }) => {
      fetch(`${API_BASE_URL}/assess/results/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      }).then((res) => {
        if (!res.ok || cancelled) return;
        setAssessmentResults((prev) => prev.map((r) => (r.id === id ? { ...r, ...body } : r)));
        setViewingComment((prev) => (prev && prev.id === id ? { ...prev, ...body } : prev));
      }).catch(() => {
        marksSyncRef.current.delete(key);
      });
    });
    return () => { cancelled = true; };
    // gradedObtainedMarks closes over the rubric maps, which are in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllComments, assessmentResults, allowedQuestionIds, rubricQuestionMarks, rubricPaperTotal, folderRubricReady, folderPath]);
  const tablePreviewSource = (result, rowMeta) => (
    result.table_comments
      ? ((rowMeta && rowMeta.correctedTableMd) || result.table_comments)
      : ''
  );
  const beginCommentEdit = (result, rowMeta) => {
    if (editingComment && editingComment.id !== result.id && commentDraft !== editingComment.original) {
      showMessage('Save or cancel the open comment first.', 'info');
      return;
    }
    const kind = allCommentsView === 'table' ? 'table' : 'oneline';
    const original = kind === 'table' ? tablePreviewSource(result, rowMeta) : oneLinePreviewText(result, rowMeta);
    setEditingComment({ id: result.id, kind, original });
    setCommentDraft(original);
    setCommentEditNote('');
  };
  const cancelCommentEdit = () => {
    setEditingComment(null);
    setCommentDraft('');
    setCommentEditNote('');
  };
  const saveCommentEdit = async () => {
    if (!editingComment || commentSaving || commentDraft === editingComment.original) return;
    setCommentSaving(true);
    setCommentEditNote('');
    try {
      const token = localStorage.getItem('authToken');
      const isTable = editingComment.kind === 'table';
      const row = assessmentResults.find((r) => r.id === editingComment.id);
      const editRepair = repairForResult(row);
      const savedTable = isTable
        ? keepLastTableBlock((correctTable(commentDraft, editRepair) || {}).md || commentDraft)
        : '';
      const nextMarks = isTable
        ? gradedObtainedMarks(savedTable, row)
        : (statedTotalMarks(commentDraft) || gradedObtainedMarks(row && row.table_comments, row));
      const res = await fetch(`${API_BASE_URL}/assess/results/${editingComment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(isTable
          ? { table_comments: savedTable, ...(nextMarks != null ? { marks: nextMarks } : {}) }
          : { comments: commentDraft, comments_custom: 1, ...(nextMarks != null ? { marks: nextMarks } : {}) })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Save failed (${res.status})`);
      const id = editingComment.id;
      const savedMarks = data.marks != null && data.marks !== '' ? data.marks : nextMarks;
      const apply = (r) => {
        if (r.id !== id) return r;
        return isTable
          ? { ...r, table_comments: savedTable, comments: typeof data.comments === 'string' ? data.comments : r.comments, comments_custom: 0, ...(savedMarks != null ? { marks: savedMarks } : {}), ...(data.table_history !== undefined ? { table_history: data.table_history } : {}) }
          : { ...r, comments: commentDraft, comments_custom: 1, ...(savedMarks != null ? { marks: savedMarks } : {}) };
      };
      setAssessmentResults((prev) => prev.map(apply));
      setViewingComment((prev) => (prev && prev.id === id ? apply(prev) : prev));
      showMessage(isTable ? 'Table saved — one-line comments regenerated' : 'Comment saved', 'success');
      setEditingComment(null);
      setCommentDraft('');
    } catch (err) {
      setCommentEditNote(err.message || 'Save failed');
    } finally {
      setCommentSaving(false);
    }
  };

  const toolBtn = (kind, disabled) => {
    const off = !!disabled;
    const base = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      height: 32,
      padding: '0 12px',
      borderRadius: 6,
      fontWeight: 600,
      fontSize: 13,
      lineHeight: 1,
      whiteSpace: 'nowrap',
      boxShadow: 'none',
      fontFamily: 'inherit',
      cursor: off ? 'not-allowed' : 'pointer'
    };
    if (off) {
      return { ...base, background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb' };
    }
    const solids = {
      primary: '#1e3a8a',
      success: '#047857',
      warning: '#b45309',
      violet: '#7c3aed',
      danger: '#b91c1c',
      info: '#0f4c81'
    };
    if (solids[kind]) {
      return { ...base, background: solids[kind], color: '#fff', border: `1px solid ${solids[kind]}` };
    }
    const accents = {
      outline: '#374151',
      'outline-violet': '#5b21b6',
      'outline-blue': '#1d4ed8',
      'outline-amber': '#b45309',
      'outline-teal': '#0f766e',
      'outline-red': '#b91c1c'
    };
    return {
      ...base,
      background: 'var(--card-bg, #fff)',
      color: accents[kind] || accents.outline,
      border: `1px solid ${kind === 'outline-red' ? '#fecaca' : 'var(--border-color, #d1d5db)'}`
    };
  };

  const metaChip = (tone) => {
    const map = {
      neutral: { bg: '#f3f4f6', fg: '#374151', bd: '#e5e7eb' },
      success: { bg: '#ecfdf5', fg: '#047857', bd: '#bbf7d0' },
      info: { bg: '#eff6ff', fg: '#1d4ed8', bd: '#bfdbfe' },
      warning: { bg: '#fffbeb', fg: '#b45309', bd: '#fde68a' },
      danger: { bg: '#fef2f2', fg: '#b91c1c', bd: '#fecaca' }
    };
    const toneStyle = map[tone] || map.neutral;
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      height: 28,
      padding: '0 10px',
      borderRadius: 6,
      background: toneStyle.bg,
      color: toneStyle.fg,
      border: `1px solid ${toneStyle.bd}`,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      lineHeight: 1
    };
  };

  const savedInfoTitle = assessSnapshotMeta
    ? `Last saved ${new Date(assessSnapshotMeta.saved_at).toLocaleString()} (${assessSnapshotMeta.record_count} rows) by ${assessSnapshotMeta.saved_by || 'unknown'}`
    : 'No saved table for this folder yet.';
  const BUTTON_CLICK_TASKS = {
    approve: [
      'Open the scans folder picker',
      'Save this table',
      'Clear the student list you pick, then write the selected rows',
      'Keep unmatched papers in Mistakes',
      'Mark the folder Copy to EduPage and open Students'
    ],
    save: [
      'Save marks and comments in this table',
      'Leave the print list unchanged',
      'Leave the EduPage list unchanged'
    ],
    update: [
      'Save this table',
      'Update the print list',
      'Update the EduPage student list'
    ]
  };
  const buttonHintCard = (kind) => (buttonHint !== kind ? null : (
    <div
      style={{
        position: 'absolute',
        top: 'calc(100% + 6px)',
        right: 0,
        zIndex: 40,
        width: 300,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.14)',
        padding: '10px 12px',
        textAlign: 'left'
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>{savedInfoTitle}</div>
      <div style={{ marginTop: 8, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b' }}>When clicked</div>
      <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {BUTTON_CLICK_TASKS[kind].map((line) => (
          <div key={line} style={{ fontSize: 12, color: '#334155', lineHeight: 1.35 }}>{line}</div>
        ))}
      </div>
    </div>
  ));

  const mediumButtons = (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap', width: '100%', minWidth: 0 }}
      title="English-2 / Urdu-2: two-step pipeline (vision model OCRs, then a separate model grades the transcript)."
    >
      <label style={{ fontSize: 10, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-secondary, #6b7280)', fontWeight: 600, flexShrink: 0 }}>Medium</label>
      {(() => {
        const opts = [
          { value: 'english_two_step', label: 'English-2', sub: 'two-step' },
          { value: 'urdu_two_step', label: 'Urdu-2', sub: 'two-step' }
        ];
        const unselected = mediumOption === 'none';
        return opts.map(opt => {
          const isSel = mediumOption === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleMediumChange(opt.value)}
              title={`${opt.label}, ${opt.sub}`}
              style={{
                height: 28,
                padding: '0 6px',
                border: isSel ? '1px solid #1e3a8a' : (unselected ? '1px solid #f59e0b' : '1px solid var(--border-color, #d1d5db)'),
                borderRadius: 6,
                background: isSel ? '#1e3a8a' : 'var(--card-bg, #fff)',
                color: isSel ? '#fff' : (unselected ? '#b45309' : 'var(--text-primary, #374151)'),
                fontWeight: 600,
                fontSize: 11,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '1 1 0',
                minWidth: 0,
                whiteSpace: 'nowrap'
              }}
            >
              {opt.label}
            </button>
          );
        });
      })()}
      {[
        { key: 'gpu', label: 'Spc' },
        { key: 'mac', label: 'Mac' }
      ].map((machine) => {
        const on = hostAvailable(machine.key);
        return (
          <button
            key={machine.key}
            type="button"
            onClick={() => setHostAvailable(machine.key, !on)}
            title={on
              ? `${machine.label} is on for OCR and grading. Click to skip it.`
              : `${machine.label} is off. Click to let the next job use it.`}
            style={{
              height: 28,
              padding: '0 6px',
              border: on ? '1px solid #047857' : '1px solid var(--border-color, #d1d5db)',
              borderRadius: 6,
              background: on ? '#047857' : 'var(--card-bg, #fff)',
              color: on ? '#fff' : 'var(--text-secondary, #6b7280)',
              fontWeight: 600,
              fontSize: 11,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '1 1 0',
              minWidth: 0,
              whiteSpace: 'nowrap'
            }}
          >
            {machine.label}
          </button>
        );
      })}
    </div>
  );

  const clearReplyDraft = () => {
    replyDiscardRef.current = true;
    if (replyRecorderRef.current && replyRecorderRef.current.state !== 'inactive') {
      try { replyRecorderRef.current.stop(); } catch (_) { /* ignore */ }
    }
    if (replyStreamRef.current) {
      replyStreamRef.current.getTracks().forEach((track) => track.stop());
      replyStreamRef.current = null;
    }
    replyRecorderRef.current = null;
    setReplyRecording(false);
    setReplyText('');
    setReplyFile(null);
    if (replyPreviewUrlRef.current) {
      URL.revokeObjectURL(replyPreviewUrlRef.current);
      replyPreviewUrlRef.current = '';
    }
    setReplyPreviewUrl('');
  };

  const startReplyRecording = async () => {
    if (!window.isSecureContext) {
      showMessage('Microphone recording needs HTTPS or localhost.', 'error');
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof MediaRecorder === 'undefined') {
      showMessage('This browser cannot record audio.', 'error');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      replyStreamRef.current = stream;
      replyChunksRef.current = [];
      let mimeType = '';
      if (typeof MediaRecorder.isTypeSupported === 'function') {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) mimeType = 'audio/webm;codecs=opus';
        else if (MediaRecorder.isTypeSupported('audio/webm')) mimeType = 'audio/webm';
        else if (MediaRecorder.isTypeSupported('audio/mp4')) mimeType = 'audio/mp4';
        else if (MediaRecorder.isTypeSupported('audio/ogg')) mimeType = 'audio/ogg';
      }
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      replyRecorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) replyChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blobType = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(replyChunksRef.current, { type: blobType });
        if (replyStreamRef.current) {
          replyStreamRef.current.getTracks().forEach((track) => track.stop());
          replyStreamRef.current = null;
        }
        replyRecorderRef.current = null;
        setReplyRecording(false);
        if (replyDiscardRef.current) {
          replyDiscardRef.current = false;
          return;
        }
        if (!blob.size) {
          showMessage('Recording was empty. Please try again.', 'error');
          return;
        }
        const ext = blobType.includes('mp4') ? 'm4a' : blobType.includes('ogg') ? 'ogg' : 'webm';
        const file = new File([blob], `reply.${ext}`, { type: blobType.split(';')[0] });
        if (replyPreviewUrlRef.current) URL.revokeObjectURL(replyPreviewUrlRef.current);
        const url = URL.createObjectURL(file);
        replyPreviewUrlRef.current = url;
        setReplyFile(file);
        setReplyPreviewUrl(url);
      };
      recorder.start(250);
      replyDiscardRef.current = false;
      setReplyRecording(true);
    } catch (err) {
      if (replyStreamRef.current) {
        replyStreamRef.current.getTracks().forEach((track) => track.stop());
        replyStreamRef.current = null;
      }
      setReplyRecording(false);
      showMessage((err && err.message) || 'Could not access the microphone.', 'error');
    }
  };

  const stopReplyRecording = () => {
    if (replyRecorderRef.current && replyRecorderRef.current.state !== 'inactive') {
      try { replyRecorderRef.current.stop(); } catch (_) { /* ignore */ }
    }
  };

  const finishRegrade = async (outcome) => {
    const voice = regradeVoice;
    const text = replyText.trim();
    const me = currentUsernameFromStorage();
    const own = !!(me && voice && voice.createdBy
      && me.toLowerCase() === String(voice.createdBy).toLowerCase());
    if (!voice || !voice.slot || replySending || replyRecording) return;
    if (outcome === 'rejected') {
      const ok = window.confirm(own
        ? `Remove Re-Grade ${voice.slot}? You requested it, so no reply is sent. The tag will be removed.`
        : `Reject Re-Grade ${voice.slot}? Your reply will be sent to the teacher and the tag will be removed.`);
      if (!ok) return;
    }
    if (!text && !replyFile && !(outcome === 'rejected' && own)) return;
    const folderId = String(voice.folderPath || '').match(/rubrics\/(\d+)/);
    if (!folderId) {
      showMessage('This re-grade is not tied to a folder.', 'error');
      return;
    }
    setReplyAction(outcome);
    setReplySending(true);
    try {
      const form = new FormData();
      form.append('text', text);
      form.append('outcome', outcome);
      if (replyFile) form.append('audio', replyFile);
      const token = localStorage.getItem('authToken');
      const res = await fetch(
        `${API_BASE_URL}/analyze/folders/${folderId[1]}/regrade-requests/${voice.slot}/reply`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: form
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.message || 'Could not send the reply');
      }
      try {
        await deleteRegradeRequest(voice.folderPath, voice.slot);
      } catch (err) {
        showMessage(
          data.self
            ? `The request was removed, but the tag stayed: ${err.message || 'Could not remove this re-grade'}`
            : `Reply sent, but the tag stayed: ${err.message || 'Could not remove this re-grade'}`,
          'error'
        );
        return;
      }
      setRegradeVoice(null);
      clearReplyDraft();
      const queue = regradeQueueRef.current;
      if (queue && Array.isArray(queue.requests)) {
        const requests = queue.requests.filter((item) => Number(item.slot) !== Number(voice.slot));
        if (!requests.length) {
          regradeQueueRef.current = null;
          setRegradeQueue(null);
        } else {
          const wasCurrent = Number((queue.requests[queue.index] || {}).slot) === Number(voice.slot);
          const keep = wasCurrent
            ? requests[Math.min(queue.index, requests.length - 1)]
            : queue.requests[queue.index];
          const index = Math.max(0, requests.findIndex((item) => Number(item.slot) === Number(keep && keep.slot)));
          const updated = { ...queue, requests, index };
          regradeQueueRef.current = updated;
          setRegradeQueue(updated);
          if (wasCurrent) applyQueuedRegrade(requests[index], updated);
        }
      }
      showMessage(
        data.self
          ? `Re-Grade ${voice.slot} removed. No reply was sent because you requested it.`
          : outcome === 'rejected'
            ? `Re-Grade ${voice.slot} rejected. The reply was sent and the tag was removed.`
            : `Re-Grade ${voice.slot} done. The reply was sent and the tag was removed.`,
        'success'
      );
    } catch (err) {
      showMessage(err.message || 'Could not send the reply', 'error');
    } finally {
      setReplySending(false);
      setReplyAction('');
    }
  };

  useEffect(() => () => {
    if (replyStreamRef.current) {
      replyStreamRef.current.getTracks().forEach((track) => track.stop());
      replyStreamRef.current = null;
    }
    if (replyPreviewUrlRef.current) {
      URL.revokeObjectURL(replyPreviewUrlRef.current);
      replyPreviewUrlRef.current = '';
    }
  }, []);

  const replyReady = !!(replyText.trim() || replyFile);
  const replyBusy = replySending || replyRecording;
  const ownRegrade = !!(
    regradeVoice
    && regradeVoice.createdBy
    && currentUsernameFromStorage()
    && currentUsernameFromStorage().toLowerCase() === String(regradeVoice.createdBy).toLowerCase()
  );

  const taskBusy = isProcessing || isGettingText || assessSnapshotBusy || approveLoading || quickOcrOpening;
  const taskPaused = !taskBusy && !!(progressDetails && (progressDetails.status === 'paused' || progressDetails.status === 'pausing'));
  const taskLabel = labelForRunningTask({
    paused: taskPaused,
    isProcessing,
    isGettingText,
    assessSnapshotBusy,
    approveLoading,
    quickOcrOpening,
    autoCheckStatus,
    progress
  });
  const taskDetail = taskTimer && taskTimer.endedAt
    ? (saveApproveResultTone(taskTimer.label) ? taskTimer.label : `${taskTimer.label} finished`)
    : (autoCheckStatus || progress || taskLabel);

  useEffect(() => {
    if (taskBusy) {
      setTaskTimer((prev) => {
        if (prev && !prev.endedAt) {
          if (saveApproveResultTone(prev.label) || prev.label === taskLabel) return prev;
          const next = { ...prev, label: taskLabel };
          writeTaskTimer(next);
          return next;
        }
        const next = { startedAt: Date.now(), endedAt: null, label: taskLabel, sessionId: null };
        writeTaskTimer(next);
        return next;
      });
      return;
    }
    if (taskPaused) {
      setTaskTimer((prev) => {
        if (!prev || prev.endedAt || prev.label === taskLabel) return prev;
        const next = { ...prev, label: taskLabel };
        writeTaskTimer(next);
        return next;
      });
      return;
    }
    setTaskTimer((prev) => {
      if (!prev || prev.endedAt) return prev;
      const next = { ...prev, endedAt: Date.now() };
      writeTaskTimer(next);
      return next;
    });
  }, [taskBusy, taskPaused, taskLabel]);

  // Same open as the folder-card Re-Grade chip: the first request is selected
  // and its students and questions are ticked.
  const openRegradeClass = async (folder) => {
    if (!folder || folder.id == null) return;
    const path = `rubrics/${folder.id}`;
    if (path === ((regradeVoice && regradeVoice.folderPath) || folderPath)) return;
    if (isProcessing) {
      showMessage('Stop the check that is running before loading another class.', 'error');
      return;
    }
    const requests = (Array.isArray(regradeByFolder && regradeByFolder[String(folder.id)])
      ? regradeByFolder[String(folder.id)]
      : []
    ).slice().sort((a, b) => Number(a.slot) - Number(b.slot));
    if (!requests.length) {
      showMessage('This class has no re-grade request.', 'error');
      return;
    }
    const first = requests[0];
    const token = localStorage.getItem('authToken');
    let audioUrl = '';
    let note = first.note || '';
    let students = first.students || [];
    let questions = first.questions || [];
    let createdBy = first.created_by || '';
    try {
      const res = await fetch(`${API_BASE_URL}/analyze/folders/${folder.id}/regrade-requests/${first.slot}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.request) {
        audioUrl = data.request.audio_url || '';
        note = data.request.note || note;
        students = data.request.students || students;
        questions = data.request.questions || questions;
        createdBy = data.request.created_by || createdBy;
      } else {
        showMessage((data && data.message) || 'Could not open this re-grade', 'error');
        return;
      }
    } catch (err) {
      showMessage(err.message || 'Could not open this re-grade', 'error');
      return;
    }
    const queue = requests.map((request, index) => ({
      slot: request.slot,
      students: index === 0 ? students : (request.students || []),
      questions: index === 0 ? questions : (request.questions || []),
      note: index === 0 ? note : (request.note || ''),
      createdBy: index === 0 ? createdBy : (request.created_by || ''),
      audioUrl: index === 0 ? audioUrl : ''
    }));
    setViewingOcr(null);
    setViewingComment(null);
    setShowAllComments(false);
    setSelectedResults([]);
    setShowApproveForm(false);
    navigate('/evaluate/check', {
      replace: true,
      state: {
        autoOpenFolder: {
          folder_path: path,
          title: folderDisplayTitle(folder) || folder.title || path,
          class: folder.class || '',
          subject: folder.subject || '',
          intent: 'regrade-request',
          regradeRequest: queue[0],
          regradeQueue: queue
        }
      }
    });
  };

  // Stay on Check and replace the open class with another folder's saved table.
  const switchToClass = (folder, options = {}) => {
    if (!folder || folder.id == null) return;
    const path = `rubrics/${folder.id}`;
    if (path === folderPath) return;
    if (isProcessing) {
      showMessage('Stop the check that is running before loading another class.', 'error');
      return;
    }
    if (!options.keepTranscript) setViewingOcr(null);
    if (!options.keepComment) setViewingComment(null);
    setShowAllComments(false);
    setSelectedResults([]);
    setShowApproveForm(false);
    setRegradeVoice(null);
    setPendingRegrade(null);
    setPendingAutoRegrade(null);
    setPendingOpenComment(null);
    const dateStr = folder.test_date
      ? new Date(folder.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
      : '';
    navigate('/evaluate/check', {
      replace: true,
      state: {
        autoOpenFolder: {
          folder_path: path,
          title: rubricNameWithDate(folder, dateStr) || folder.title || path,
          class: folder.class || '',
          subject: folder.subject || '',
          medium: folder.auto_check_medium || '',
          switchClass: true,
          openFirstComment: !!options.keepComment,
          openFirstTranscript: !!options.keepTranscript,
          openMissingOcr: !!options.openMissing,
          openThinkingOcr: !!options.openThinking
        }
      }
    });
  };

  const renderClassSwitch = (menuZ, onPick, compact = false) => (
    <Select
      aria-label="Other class"
      value={null}
      inputValue={classSwitchQuery}
      onInputChange={(val, meta) => {
        if (meta.action === 'input-change') setClassSwitchQuery(val);
        if (meta.action === 'menu-close' || meta.action === 'set-value') setClassSwitchQuery('');
      }}
      onChange={(option) => {
        const folder = scansFoldersForApprove.find((f) => String(f.id) === String(option && option.value));
        if (folder) (onPick || switchToClass)(folder);
        setClassSwitchQuery('');
      }}
      options={scansFoldersForApprove
        .filter((f) => `rubrics/${f.id}` !== folderPath)
        .map((f) => {
          const dateStr = f.test_date
            ? new Date(f.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
            : '';
          const classLabel = rubricNameWithDate(f, dateStr) || f.title || `Folder #${f.id}`;
          const assignment = String(f.assignment_name || '').trim();
          return {
            value: f.id,
            label: assignment ? `${classLabel} — ${assignment}` : classLabel
          };
        })}
      filterOption={(option, raw) => {
        const q = String(raw || '').trim().toLowerCase();
        if (!q) return true;
        return String(option.label || '').trim().toLowerCase().startsWith(q);
      }}
      placeholder="other"
      components={compact ? { DropdownIndicator: ThinClassChevron } : undefined}
      isSearchable
      openMenuOnFocus
      isDisabled={isProcessing || (typeof progress === 'string' && progress.startsWith('Loading'))}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPosition="fixed"
      noOptionsMessage={({ inputValue }) => (
        inputValue ? `No class starts with “${inputValue}”` : 'No other classes'
      )}
      styles={{
        container: (base) => ({ ...base, minWidth: compact ? 0 : base.minWidth }),
        control: (base, state) => ({
          ...base,
          minHeight: compact ? 22 : 32,
          height: compact ? 22 : 32,
          minWidth: compact ? 0 : base.minWidth,
          borderColor: state.isFocused ? '#1e3a8a' : 'var(--border-color, #d1d5db)',
          borderRadius: 6,
          background: 'var(--card-bg, #fff)',
          boxShadow: state.isFocused ? '0 0 0 3px rgba(30, 58, 138, 0.12)' : 'none',
          fontSize: compact ? 11 : 13,
          fontWeight: 500,
          cursor: 'text'
        }),
        valueContainer: (base) => ({ ...base, padding: compact ? '0 2px' : '0 8px' }),
        input: (base) => ({ ...base, margin: 0, padding: 0, color: 'var(--text-primary, #111827)', fontWeight: 500 }),
        placeholder: (base) => ({ ...base, color: 'var(--text-secondary, #6b7280)', fontWeight: 500, fontSize: compact ? 11 : 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
        indicatorsContainer: (base) => ({ ...base, height: compact ? 20 : 30, padding: 0 }),
        dropdownIndicator: (base) => ({ ...base, padding: compact ? '0 1px' : '0 6px', color: '#6b7280' }),
        indicatorSeparator: () => ({ display: 'none' }),
        menuPortal: (base) => ({ ...base, zIndex: menuZ }),
        menu: (base) => ({
          ...base,
          fontSize: 13,
          fontWeight: 500,
          zIndex: menuZ,
          borderRadius: 8,
          overflow: 'hidden',
          width: compact ? 'max-content' : base.width,
          minWidth: compact ? 240 : base.minWidth
        }),
        option: (base, state) => ({
          ...base,
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
          color: state.isFocused || state.isSelected ? '#fff' : 'var(--text-primary, #111827)',
          background: state.isFocused || state.isSelected ? '#1e3a8a' : 'var(--card-bg, #fff)'
        })
      }}
    />
  );

  const page = (
    <div className={quickTranscript
      ? `assessment-tab ${quickTranscript.mode === 'comment' ? 'quick-comment' : 'quick-transcript'}`
      : 'assessment-tab'}>
      {regradeVoice && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            padding: '8px 12px',
            marginBottom: 8,
            borderRadius: 8,
            border: '1.5px solid #7c3aed',
            background: '#f5f3ff'
          }}
        >
          {regradeQueue && regradeQueue.requests && regradeQueue.requests.length > 0 ? (
            <select
              aria-label="Re-grade request"
              value={String((regradeQueue.requests[regradeQueue.index] || {}).slot || regradeVoice.slot)}
              onChange={(event) => {
                const slot = Number(event.target.value);
                const index = regradeQueue.requests.findIndex((item) => Number(item.slot) === slot);
                if (index < 0) return;
                const updated = { ...regradeQueue, index };
                regradeQueueRef.current = updated;
                setRegradeQueue(updated);
                applyQueuedRegrade(regradeQueue.requests[index], updated);
              }}
              title="Choose which re-grade request is selected"
              style={{
                height: 32,
                border: '1px solid #c4b5fd',
                borderRadius: 6,
                padding: '0 8px',
                font: 'inherit',
                fontSize: 13,
                fontWeight: 700,
                color: '#5b21b6',
                background: '#fff'
              }}
            >
              {regradeQueue.requests.map((item) => (
                <option key={item.slot} value={String(item.slot)}>
                  {`Re-Grade ${item.slot}`}
                </option>
              ))}
            </select>
          ) : (
            <strong style={{ color: '#5b21b6', fontSize: 14 }}>
              Re-Grade {regradeVoice.slot}
            </strong>
          )}
          <span style={{ color: '#4c1d95', fontSize: 13 }}>{regradeVoice.title}</span>
          {regradeVoice.note ? (
            <span style={{
              flex: '1 1 220px',
              minWidth: 0,
              color: '#1e293b',
              fontSize: 13,
              lineHeight: 1.4
            }}
            >
              {regradeVoice.note}
            </span>
          ) : null}
          {regradeVoice.audioUrl ? (
            <audio controls src={regradeVoice.audioUrl} style={{ height: 34 }} />
          ) : regradeVoice.note ? null : (
            <span style={{ color: '#b91c1c', fontSize: 13 }}>Voice note could not be loaded.</span>
          )}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 280px', minWidth: 0 }}
            onClick={(event) => event.stopPropagation()}
          >
            <input
              type="text"
              value={replyText}
              onChange={(event) => setReplyText(event.target.value)}
              placeholder={regradeVoice.createdBy ? `Reply to ${regradeVoice.createdBy}` : 'Reply to the teacher'}
              aria-label="Reply to the teacher"
              maxLength={2000}
              style={{
                flex: 1,
                minWidth: 140,
                height: 32,
                border: '1px solid #c4b5fd',
                borderRadius: 6,
                padding: '0 8px',
                font: 'inherit',
                fontSize: 13,
                background: '#fff',
                color: '#1f2937'
              }}
            />
            <button
              type="button"
              onClick={replyRecording ? stopReplyRecording : startReplyRecording}
              disabled={replySending}
              style={{
                border: '1px solid #7c3aed',
                background: replyRecording ? '#7c3aed' : '#fff',
                color: replyRecording ? '#fff' : '#5b21b6',
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 13,
                fontWeight: 700,
                cursor: replySending ? 'wait' : 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {replyRecording ? 'Stop' : 'Voice'}
            </button>
            {replyPreviewUrl && (
              <audio controls src={replyPreviewUrl} style={{ height: 32, maxWidth: 180 }} />
            )}
            <button
              type="button"
              onClick={() => finishRegrade('done')}
              disabled={replyBusy || !replyReady}
              title="Send the reply and remove this re-grade tag"
              style={{
                border: 'none',
                background: '#5b21b6',
                color: '#fff',
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 13,
                fontWeight: 700,
                cursor: (replyBusy || !replyReady) ? 'default' : 'pointer',
                opacity: (replyBusy || !replyReady) ? 0.55 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              {replySending && replyAction === 'done' ? 'Sending…' : 'Done'}
            </button>
          </div>
          <button
            type="button"
            onClick={() => finishRegrade('rejected')}
            disabled={replyBusy || (!replyReady && !ownRegrade)}
            title={ownRegrade
              ? 'Remove this re-grade. No reply is sent because you requested it.'
              : 'Send the reply and remove this re-grade tag'}
            style={{
              marginLeft: 'auto',
              border: '1px solid #b91c1c',
              background: '#fff',
              color: '#b91c1c',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 13,
              fontWeight: 700,
              cursor: (replyBusy || (!replyReady && !ownRegrade)) ? 'default' : 'pointer',
              opacity: (replyBusy || (!replyReady && !ownRegrade)) ? 0.55 : 1
            }}
          >
            {replySending && replyAction === 'rejected' ? 'Sending…' : 'Reject'}
          </button>
        </div>
      )}
      {regradeVoice && regradeClassList.length > 0 && (() => {
        const pageCount = Math.ceil(regradeClassList.length / 2);
        const start = (regradeFolderPage % pageCount) * 2;
        const shown = regradeClassList.slice(start, start + 2);
        const openFolder = (regradeVoice.folderPath || folderPath || '');
        const tone = { border: '#7C3AED', bg: '#F5F3FF', text: '#5B21B6', fill: '#7C3AED', nextBorder: '#6D28D9' };
        return (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
            margin: '0 0 8px',
            minWidth: 0
          }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: tone.text, flex: '0 0 auto' }}>
              Re-grade
            </span>
            {shown.map((item) => {
              const current = `rubrics/${item.id}` === openFolder;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => openRegradeClass(item.folder)}
                  title={current ? `${item.label} is open` : `Open ${item.label}`}
                  style={{
                    maxWidth: 280,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    padding: '3px 8px',
                    borderRadius: 6,
                    border: `1px solid ${tone.border}`,
                    background: current ? tone.fill : tone.bg,
                    color: current ? 'white' : tone.text,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: current ? 'default' : 'pointer'
                  }}
                >
                  {item.label}
                </button>
              );
            })}
            {pageCount > 1 && (
              <button
                type="button"
                onClick={() => setRegradeFolderPage((page) => (page + 1) % pageCount)}
                title="Show the next classes for regrade"
                style={{
                  flex: '0 0 auto',
                  padding: '3px 8px',
                  borderRadius: 6,
                  border: `1px solid ${tone.nextBorder}`,
                  background: 'white',
                  color: tone.text,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Next
              </button>
            )}
          </div>
        );
      })()}
      {/* Auto-check engine control. Only the two dedicated accounts
          (ai1 / ai2 / ai3) see this and run the engine — hidden for
          everyone else so they can't double-process folders. The two run in
          parallel on separate browsers (a backend folder-claim keeps them off
          the same class; the backend AI-slot keeps only one on LM Studio at a
          time). When ON, this tab auto-checks Reviewed folders (with a medium
          set) ~2 min after review and, on a clean check with a matching student
          list, auto-approves them into that list (status → 'copy_to_edupage').
          Folders with no matching list rest at 'auto-checked' for a manual
          Approve; a failed/interrupted check is flagged 'error'. */}
      {isAutoCheckUser && (
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          padding: '6px 10px', marginBottom: 8, borderRadius: 8,
          border: `1.5px solid ${autoCheckEnabled ? '#2563eb' : 'var(--border-color, #e0e0e0)'}`,
          background: autoCheckEnabled ? 'rgba(37,99,235,0.06)' : 'var(--card-bg, #fafafa)'
        }}
      >
        <button
          onClick={async () => {
            const next = !autoCheckEnabled;
            const me = currentUsernameFromStorage();
            try {
              const token = localStorage.getItem('authToken');
              const res = await fetch(`${API_BASE_URL}/analyze/auto-check/switches`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ account: me, enabled: next })
              });
              const data = await res.json().catch(() => ({}));
              if (!res.ok) {
                showMessage(data.message || 'Could not update auto-check', 'error');
                return;
              }
              if (data.switches) setAutoCheckSwitches(data.switches);
            } catch (err) {
              showMessage(err.message || 'Could not update auto-check', 'error');
              return;
            }
            appliedSwitchRef.current = next;
            setAutoCheckEnabled(next);
            if (!next) handleStopProcessing({ releaseAutoCheck: true });
          }}
          title={autoCheckEnabled
            ? 'Auto-check is ON. Stop cancels any in-progress check for this account so it does not keep locking the table.'
            : 'Turn ON automatic checking. Keep this Checking tab open while it runs.'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: '0.85rem', color: 'white',
            background: autoCheckEnabled
              ? 'linear-gradient(135deg, #ef4444, #b91c1c)'
              : 'linear-gradient(135deg, #2563eb, #1d4ed8)'
          }}
        >
          <i className={`fa-solid ${autoCheckEnabled ? 'fa-stop' : 'fa-robot'}`}></i>
          {autoCheckEnabled ? 'Stop Auto-Check' : 'Start Auto-Check'}
        </button>
        {isProcessing && (
          <button
            type="button"
            onClick={handlePauseProcessing}
            disabled={isPausing}
            title="Stop after the sheet already with the model. Saved rows stay. Resume continues from the next sheet that has no marks."
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '7px 14px', borderRadius: 8, border: 'none',
              cursor: isPausing ? 'wait' : 'pointer',
              fontWeight: 700, fontSize: '0.85rem', color: 'white',
              background: 'linear-gradient(135deg, #d97706, #b45309)',
              opacity: isPausing ? 0.75 : 1
            }}
          >
            <i className="fa-solid fa-pause"></i>
            {isPausing ? 'Pausing…' : 'Pause'}
          </button>
        )}
        {canResumeCheck && (
          <button
            type="button"
            onClick={() => {
              if (!requireMediumOrPrompt()) return;
              handleStartAssessment({ resume: true });
            }}
            disabled={isProcessing || !folderPath || analyzedData.length === 0 || isUploadMismatchForCheck}
            title="Continue this class. Students with no result row are OCR'd. Rows with no marks are graded. Already graded sheets are skipped."
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontWeight: 700, fontSize: '0.85rem', color: 'white',
              background: 'linear-gradient(135deg, #d97706, #b45309)'
            }}
          >
            <i className="fa-solid fa-play"></i>
            {resumeLabel}
          </button>
        )}
        <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {autoCheckEnabled ? (
            <>
              <span style={{
                width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                background: autoBusyRef.current ? '#f59e0b' : '#10b981',
                boxShadow: autoBusyRef.current ? '0 0 6px #f59e0b' : '0 0 6px #10b981'
              }} />
              <span title={autoCheckStatus || undefined} style={{ whiteSpace: 'normal' }}>
                {autoCheckStatus || 'Watching for reviewed folders…'}
              </span>
            </>
          ) : (
            <span title={autoCheckNotice || undefined} style={{ fontStyle: autoCheckNotice ? 'normal' : 'italic', whiteSpace: 'normal' }}>
              {autoCheckNotice || 'Auto-check is off.'}
            </span>
          )}
        </div>
        <div
          style={{
            marginLeft: 'auto',
            flex: '1 1 420px',
            minWidth: 280,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            alignItems: 'flex-end'
          }}
        >
          <AutoCheckStatusPanel
            dispatch={aiStatus?.dispatch}
            hostOn={hostAvailable}
            board={autoCheckBoard}
            present={autoCheckPresent}
          />
        </div>
      </div>
      )}

      <FailureNotice
        notice={latePaperFailure}
        onClose={() => setLatePaperFailure(null)}
        zIndex={13000}
      />

      {message.show && (
        <div className={`message message-${message.type}`} style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <span>{message.text}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {message.canStopActive && (
              <button
                type="button"
                onClick={() => handleStopProcessing({ releaseAutoCheck: true })}
                style={{
                  padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                  fontWeight: 700, fontSize: '0.78rem', color: 'white',
                  background: 'linear-gradient(135deg, #ef4444, #b91c1c)'
                }}
              >
                Stop that check
              </button>
            )}
            <button
              type="button"
              onClick={() => setMessage({ show: false, text: '', type: '' })}
              aria-label="Dismiss"
              title="Dismiss"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: '1.1rem', lineHeight: 1, color: 'inherit', opacity: 0.7, padding: '0 4px'
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {regradeHistoryOpen && (
        <div
          onClick={() => setRegradeHistoryOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10100,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="regrade-history-title"
            onClick={(event) => event.stopPropagation()}
            style={{
              background: '#fff',
              color: '#1f2937',
              borderRadius: 16,
              width: 'min(720px, calc(100vw - 48px))',
              maxHeight: 'calc(100vh - 48px)',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              overflow: 'hidden'
            }}
          >
            <div style={{ padding: '18px 20px 12px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 id="regrade-history-title" style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#14532d' }}>
                Re-Grading History
              </h2>
              <div style={{ marginTop: 6, fontSize: 13, color: '#6b7280' }}>
                Saved summaries for checked and rechecked classes.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                {[
                  { id: 'all', label: `All (${regradeHistory.length})` },
                  { id: 'folder', label: `This class (${regradeHistory.filter((item) => item.folderPath && item.folderPath === folderPath).length})` }
                ].map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    onClick={() => setRegradeHistoryScope(chip.id)}
                    disabled={chip.id === 'folder' && !folderPath}
                    style={{
                      border: '1px solid #bbf7d0',
                      background: regradeHistoryScope === chip.id ? '#14532d' : '#f0fdf4',
                      color: regradeHistoryScope === chip.id ? '#fff' : '#14532d',
                      borderRadius: 999,
                      padding: '6px 12px',
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: chip.id === 'folder' && !folderPath ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ overflow: 'auto', padding: '8px 12px 12px' }}>
              {regradeHistoryLoading && regradeHistory.length === 0 && (
                <div style={{ padding: 16, color: '#6b7280' }}>Loading saved summaries…</div>
              )}
              {!regradeHistoryLoading && regradeHistory.filter((item) => regradeHistoryScope !== 'folder' || item.folderPath === folderPath).length === 0 && (
                <div style={{ padding: 16, color: '#6b7280' }}>
                  No saved re-grading summaries yet. One is kept each time a class is checked or rechecked.
                </div>
              )}
              {regradeHistory
                .filter((item) => regradeHistoryScope !== 'folder' || item.folderPath === folderPath)
                .map((item) => {
                  const title = formatSummaryClassLine(item) || item.folderPath || 'Re-grading';
                  const kindColor = item.kind === 'checked' ? '#1e3a8a' : '#5b21b6';
                  const kindBg = item.kind === 'checked' ? '#dbeafe' : '#ede9fe';
                  return (
                    <button
                      key={item.id || `${item.folderPath}-${item.createdAt}`}
                      type="button"
                      onClick={() => openSavedRegradeSummary(item)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 12,
                        padding: '12px 14px',
                        marginTop: 8,
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 800, color: '#111827' }}>{title}</span>
                        <span style={{
                          background: kindBg,
                          color: kindColor,
                          borderRadius: 999,
                          padding: '2px 8px',
                          fontSize: 12,
                          fontWeight: 800
                        }}>
                          {regradeKindLabel(item.kind)}
                        </span>
                      </div>
                      <div style={{ marginTop: 4, fontSize: 13, color: '#6b7280' }}>
                        {item.rows.length} student{item.rows.length === 1 ? '' : 's'}
                        {item.assignmentName ? ` · ${item.assignmentName}` : ''}
                        {formatSavedAt(item.createdAt) ? ` · ${formatSavedAt(item.createdAt)}` : ''}
                        {item.createdBy ? ` · ${item.createdBy}` : ''}
                      </div>
                    </button>
                  );
                })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 20px 16px', borderTop: '1px solid #e5e7eb' }}>
              <button type="button" onClick={() => setRegradeHistoryOpen(false)} className="modal-close-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {regradeSummaryOpen && regradeSummary && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10100,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="regrade-summary-title"
            style={{
              background: '#fff',
              color: '#1f2937',
              borderRadius: 16,
              width: 'min(920px, calc(100vw - 48px))',
              maxHeight: 'calc(100vh - 48px)',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              overflow: 'hidden'
            }}
          >
            <div ref={regradeCardRef} style={{ background: '#fff', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '12px 20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                <div style={{ minWidth: 0 }}>
                  <h2 id="regrade-summary-title" style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#14532d' }}>
                    Re-Grading Summary
                  </h2>
                  {(() => {
                    const ownLine = formatSummaryClassLine(regradeSummary);
                    const liveLine = (!ownLine && rubricMetadata && (!regradeSummary.folderPath || regradeSummary.folderPath === folderPath))
                      ? formatSummaryClassLine({
                        className: rubricMetadata.class,
                        subject: rubricMetadata.subject,
                        testDate: rubricMetadata.test_date,
                        paper_type: rubricMetadata.paper_type
                      })
                      : '';
                    const line = ownLine || liveLine;
                    if (!line) return null;
                    return (
                      <div style={{ marginTop: 4, fontSize: 15, fontWeight: 700, color: '#166534' }}>
                        {line}
                      </div>
                    );
                  })()}
                  <div style={{ marginTop: 4, fontSize: 13, color: '#6b7280' }}>
                    {regradeSummary.kind ? `${regradeKindLabel(regradeSummary.kind)} · ` : ''}
                    {regradeSummary.rows.length} student{regradeSummary.rows.length === 1 ? '' : 's'}
                    {formatSavedAt(regradeSummary.createdAt) ? ` · ${formatSavedAt(regradeSummary.createdAt)}` : ''}
                  </div>
                  {regradeSaveError && (
                    <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>{regradeSaveError}</div>
                  )}
                </div>
                <div data-skip-copy="1" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={copyRegradeSummaryText}
                    title="Copy the student name and change columns"
                    style={{
                      background: '#fff',
                      color: '#14532d',
                      border: '1px solid #86efac',
                      borderRadius: 8,
                      padding: '8px 16px',
                      fontWeight: 800,
                      fontSize: 14,
                      cursor: 'pointer'
                    }}
                  >
                    Copy Text
                  </button>
                  <button
                    type="button"
                    onClick={copyRegradeSummaryImage}
                    disabled={regradeCopying}
                    style={{
                      background: regradeCopying ? '#d1d5db' : 'linear-gradient(135deg, #059669, #047857)',
                      color: regradeCopying ? '#6b7280' : '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 16px',
                      fontWeight: 800,
                      fontSize: 14,
                      cursor: regradeCopying ? 'wait' : 'pointer'
                    }}
                  >
                    {regradeCopying ? 'Copying…' : 'Copy as Image'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRegradeSummaryOpen(false)}
                    className="modal-close-btn"
                  >
                    Close
                  </button>
                </div>
              </div>
              <div data-regrade-body="1" style={{ overflow: 'auto', padding: '12px 20px 16px', flex: 1, minHeight: 0 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: '#f0fdf4', color: '#14532d' }}>
                      {REGRADE_SORT_COLUMNS.map((col) => (
                        <th
                          key={col.column}
                          onClick={() => setRegradeSort((current) => {
                            const firstDirection = col.column === 'name' ? 'asc' : 'desc';
                            if (!current || current.column !== col.column) {
                              return { column: col.column, direction: firstDirection };
                            }
                            return {
                              column: col.column,
                              direction: current.direction === 'asc' ? 'desc' : 'asc'
                            };
                          })}
                          title={col.title}
                          style={{
                            textAlign: col.column === 'name' ? 'left' : 'center',
                            padding: '8px 10px',
                            borderBottom: '1px solid #bbf7d0',
                            fontWeight: 800,
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                        >
                          {col.label}
                          <span style={{ marginLeft: 6, fontSize: 12, opacity: 0.7 }}>
                            {regradeSort && regradeSort.column === col.column
                              ? (regradeSort.direction === 'asc' ? '▲' : '▼')
                              : '⇅'}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...regradeSummary.rows].sort((a, b) => compareRegradeRows(a, b, regradeSort)).map((row, index) => {
                      const changeColor = row.delta == null ? '#6b7280' : row.delta > 0 ? '#15803d' : row.delta < 0 ? '#b91c1c' : '#4b5563';
                      return (
                        <tr key={`${row.name}-${index}`} style={{ background: index % 2 ? '#f9fafb' : '#fff' }}>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', fontWeight: 700 }}>
                            {row.name}
                          </td>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', textAlign: 'center', color: changeColor, fontWeight: 800 }}>
                            <span>
                              New ({formatMarkNumber(row.newMarks)}) - Old ({formatMarkNumber(row.oldMarks)}) = {formatMarkDelta(row.delta)}
                            </span>
                          </td>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                            <MarkWithMachine marks={row.oldMarks} host={row.oldGradingHost} />
                          </td>
                          <td style={{ padding: '8px 10px', borderBottom: '1px solid #e5e7eb', textAlign: 'center', fontWeight: 700 }}>
                            <MarkWithMachine marks={row.newMarks} host={row.newGradingHost || row.gradingHost} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {regradeCopyNotice && (
            <div
              role="status"
              style={{
                position: 'fixed',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10200,
                background: regradeCopyNotice === 'Summary image copied to clipboard!' ? '#14532d' : '#991b1b',
                color: '#fff',
                borderRadius: 999,
                padding: '8px 16px',
                fontWeight: 700,
                fontSize: 14,
                boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
              }}
            >
              {regradeCopyNotice}
            </div>
          )}
        </div>
      )}

      {/* Styled in-app confirm dialog (replaces native window.confirm for
          destructive actions like delete-selected and delete-all). */}
      <ConfirmDialog
        confirm={confirmDialog}
        onConfirm={handleConfirmYes}
        onCancel={handleConfirmNo}
      />

      {/* Pre-approve Select Scans Folder modal. Opened by the Approve
          button; after the user picks a folder we close this modal and
          open the existing approve form (student-list picker). The picked
          folder id is later used in handleApprove() for /checked-results/
          finalize so the list is saved into that scans folder in one shot. */}
      {showScanFolderPicker && (
        <div
          onClick={() => { setShowScanFolderPicker(false); }}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--panel-bg, white)',
              padding: '24px 28px',
              borderRadius: 12,
              maxWidth: 520,
              width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }}
          >
            <h3 style={{ margin: '0 0 6px 0', color: 'var(--text-primary, #111827)' }}>
              <i className="fa-solid fa-folder-open" style={{ color: '#10b981', marginRight: 8 }}></i>
              Save to Scans Folder
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '0.85rem', color: 'var(--text-secondary, #6b7280)' }}>
              Pick the scans folder this checked list should be saved into. Next step: pick the student list to send the comments to.
            </p>
            <Select
              value={selectedScanFolderForApprove
                ? (() => {
                    const f = scansFoldersForApprove.find(x => String(x.id) === String(selectedScanFolderForApprove));
                    return f ? { value: f.id, label: f.title || `Folder #${f.id}` } : null;
                  })()
                : null}
              inputValue={scanFolderSearchInput}
              onInputChange={(val, action) => {
                if (action.action === 'input-change') setScanFolderSearchInput(val);
                if (action.action === 'set-value' || action.action === 'menu-close') {
                  if (selectedScanFolderForApprove) setScanFolderSearchInput('');
                }
              }}
              onChange={(option) => {
                setSelectedScanFolderForApprove(option ? option.value : null);
                if (option) setScanFolderSearchInput('');
              }}
              options={scansFoldersForApprove.map(folder => {
                const dateStr = folder.test_date
                  ? new Date(folder.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                  : '';
                return {
                  value: folder.id,
                  label: `${folder.title || `Folder #${folder.id}`}${dateStr ? ' — ' + dateStr : ''} (${folder.scan_count || 0} images)`
                };
              })}
              placeholder="Search or select a scans folder..."
              menuIsOpen={scanFolderSearchInput.length > 0 && !selectedScanFolderForApprove ? true : undefined}
              isClearable
              isSearchable
              noOptionsMessage={() => 'No folders found'}
              styles={{
                control: (base) => ({ ...base, padding: 4, fontSize: 14, borderRadius: 8, marginBottom: 18, minHeight: 42 }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowScanFolderPicker(false);
                  setSelectedScanFolderForApprove(null);
                  setScanFolderSearchInput('');
                }}
                style={{
                  padding: '10px 20px', background: 'var(--surface, #f3f4f6)',
                  color: 'var(--text-primary, #111827)', border: '2px solid var(--border, #ddd)',
                  borderRadius: 8, cursor: 'pointer', fontSize: 14
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!selectedScanFolderForApprove || scanFolderSavePending) return;
                  setScanFolderSavePending(true);
                  try {
                    const token = localStorage.getItem('authToken');
                    const folder = scansFoldersForApprove.find(f => String(f.id) === String(selectedScanFolderForApprove));
                    const today = new Date();
                    const todayDateStr = today.toISOString().slice(0, 10);
                    const todayTimeStr = today.toTimeString().slice(0, 8);
                    const folderClass = folder?.class || rubricMetadata?.class || '';
                    const folderSection = folder?.section || rubricMetadata?.section || '';
                    const folderSubject = folder?.subject || rubricMetadata?.subject || '';
                    const teacherNames = folder?.created_by || rubricMetadata?.created_by || '';
                    const checkedDate = folder?.test_date
                      ? new Date(folder.test_date).toISOString().slice(0, 10)
                      : (rubricMetadata?.test_date
                          ? new Date(rubricMetadata.test_date).toISOString().slice(0, 10)
                          : todayDateStr);

                    // The scans folder gets the TABLE-VIEW comments
                    // (markdown Verification Table with Question / Marks /
                    // Deduction / Deduction Marks / Obtained columns) so it
                    // can be printed as a proper table. Falls back to the
                    // one-line text when a row has no table_comments (e.g.
                    // a hand-edited row that lost its table). We always
                    // append an [image] [N sheets] footer so the print view
                    // can show how many sheets each student had — the
                    // ScansFolderView header reads this from the parsed
                    // "after" text of the markdown.
                    const studentsForFolder = (assessmentResults || []).map(r => ({
                      student_name: r.student_name || '',
                      checked_name: r.student_name || '',
                      first_name:   r.student_name || '.',
          marks:        r.marks ?? '',
          comments: commentsForScansFolder(r),
          grading_host: r.grading_host || null
        }));

                    const finalizeRes = await fetch(`${API_BASE_URL}/checked-results/finalize`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                      body: JSON.stringify({
                        rubric_id: selectedScanFolderForApprove,
                        metadata: {
                          class: folderClass,
                          section: folderSection,
                          subject: folderSubject,
                          teacherNames,
                          checkedDate,
                          checkedTime: todayTimeStr,
                          // Snake-case mirrors for any downstream consumer.
                          teacher_names: teacherNames,
                          checked_date: checkedDate,
                          checked_time: todayTimeStr
                        },
                        students: studentsForFolder
                      })
                    });
                    if (!finalizeRes.ok) {
                      const errBody = await finalizeRes.json().catch(() => ({}));
                      const reason = errBody.error || errBody.message || `HTTP ${finalizeRes.status}`;
                      console.error('[Approve→folder save] non-ok:', finalizeRes.status, errBody);
                      showMessage(`Failed to save to scans folder: ${reason}`, 'error');
                      setScanFolderSavePending(false);
                      return;
                    }
                    showMessage(`Saved ${studentsForFolder.length} row(s) to scans folder.`, 'success');
                    // Manual save-to-scans-folder — fill the Sort-tab "Scans"
                    // box (backfills Run + Save) for the CHECKED folder, not
                    // the picked destination folder.
                    {
                      const fpForSteps = (assessmentResults.find(r => r.folder_path) || {}).folder_path || folderPath;
                      reportManualCheckStep(fpForSteps, 'scans', 'done');
                    }
                    // Folder save succeeded — close folder modal, open the
                    // student-list picker with everything pre-selected.
                    setShowScanFolderPicker(false);
                    setSelectedResults(assessmentResults.map(r => r.id));
                    setShowApproveForm(true);
                  } catch (err) {
                    console.error('[Approve→folder save] error:', err);
                    showMessage('Failed to save to scans folder: ' + err.message, 'error');
                  } finally {
                    setScanFolderSavePending(false);
                  }
                }}
                disabled={!selectedScanFolderForApprove || scanFolderSavePending}
                style={{
                  padding: '10px 20px',
                  background: (!selectedScanFolderForApprove || scanFolderSavePending) ? '#9ca3af' : 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'white', border: 'none', borderRadius: 8,
                  cursor: (!selectedScanFolderForApprove || scanFolderSavePending) ? 'not-allowed' : 'pointer',
                  fontSize: 14, fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 8
                }}
              >
                {scanFolderSavePending ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Saving to folder…
                  </>
                ) : (
                  <>Next — Save & Pick Student List</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info dialog (Medium not selected, etc.) — blocks until OK is clicked */}
      {infoDialog && (
        <div
          onClick={() => setInfoDialog(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 12000,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '16px',
              padding: '28px 28px 22px', maxWidth: '460px', width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              textAlign: 'center'
            }}
          >
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
              border: '2px solid #F59E0B',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <i className="fa-solid fa-circle-exclamation" style={{ fontSize: '24px', color: '#D97706' }}></i>
            </div>
            <h3 style={{ margin: '0 0 10px', fontSize: '18px', fontWeight: 700, color: '#1F2937' }}>
              {infoDialog.title}
            </h3>
            <p style={{ margin: '0 0 22px', fontSize: '14px', color: '#4B5563', lineHeight: 1.55 }}>
              {infoDialog.message}
            </p>
            <button
              onClick={() => setInfoDialog(null)}
              autoFocus
              style={{
                padding: '10px 32px', borderRadius: '8px', border: 'none',
                background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                color: 'white', fontWeight: 700, fontSize: '14px',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(59,130,246,0.35)'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Two-column Checking layout: controls on the left (35%), assessment
          results on the right (65%). When the Approve form is open, the
          right column takes full width and the left column hides — the
          teacher is focused on approving, no controls needed. */}
      <div
        className="checking-split-layout"
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
          marginTop: 0,
          flexWrap: 'wrap'
        }}
      >
        {/* ── LEFT COLUMN ─────────────────────────────────────────── */}
        {!showApproveForm && (
        <div
          className="checking-left-col"
          style={controlsCollapsed ? {
            flex: '0 0 36px',
            maxWidth: 36,
            minWidth: 36,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'sticky',
            top: 60
          } : {
            flex: '0 0 24%',
            maxWidth: '24%',
            minWidth: 280,
            display: 'flex',
            flexDirection: 'column',
            gap: 10
          }}
        >
        <button
          type="button"
          onClick={() => setControlsCollapsed((collapsed) => {
            const next = !collapsed;
            localStorage.setItem('checkControlsCollapsed', String(next));
            return next;
          })}
          title={controlsCollapsed ? 'Expand the check controls' : 'Collapse the check controls'}
          style={{
            alignSelf: controlsCollapsed ? 'center' : 'flex-end',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            height: 28,
            width: controlsCollapsed ? 28 : 'auto',
            padding: controlsCollapsed ? 0 : '0 10px',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            background: '#fff',
            color: '#475569',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            flexShrink: 0
          }}
        >
          <i className={`fa-solid ${controlsCollapsed ? 'fa-angles-right' : 'fa-angles-left'}`}></i>
          {!controlsCollapsed && 'Collapse'}
        </button>
      {!controlsCollapsed && (<>
      {!showApproveForm && (
      <div className="assessment-controls">
        {/* Rubric Status */}
        <div className="rubric-status">
          {rubricLoading && !rubricVisible ? (
            <div className="status-inactive" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
              <span>Loading rubric…</span>
              {mediumButtons}
            </div>
          ) : rubricVisible && (rubricMetadata || autoRubricText || autoPdfRubric || rubricText || pdfRubric) ? (
            <>
              <div
                className="status-active"
                data-edu-card="1"
                style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', minWidth: 0, boxSizing: 'border-box' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap', width: '100%', minWidth: 0, overflow: 'hidden' }}>
                <span style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, color: 'var(--text-primary, #111827)', fontSize: 11, letterSpacing: '-0.01em' }}>
                  {rubricMetadata && (rubricMetadata.class || rubricMetadata.subject || rubricMetadata.test_date)
                    ? rubricNameWithDate(
                        rubricMetadata,
                        rubricMetadata.test_date
                          ? new Date(rubricMetadata.test_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                          : ''
                      )
                    : (autoRubricText || autoPdfRubric ? 'Rubric Auto-Loaded' : 'Active Rubric Loaded')}
                </span>
                {rubricMetadata?.assignment_name && (
                  <span
                    title={`Assignment: ${rubricMetadata.assignment_name}`}
                    style={{
                      flex: '0 1 auto',
                      minWidth: 0,
                      maxWidth: '38%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      height: 22,
                      lineHeight: '20px',
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#1D4ED8',
                      background: 'var(--card-bg, #fff)',
                      padding: '0 8px',
                      borderRadius: 6,
                      border: '1px solid #BFDBFE'
                    }}
                  >
                    {rubricMetadata.assignment_name}
                  </span>
                )}
                {!quickTranscript && (
                  <div
                    style={{ flex: '0 0 46px', width: 46, minWidth: 46, marginLeft: 'auto' }}
                    title={isProcessing
                      ? 'Stop the check that is running before loading another class'
                      : 'Type the start of a class, such as 8, to list only those classes'}
                  >
                    {renderClassSwitch(10000, null, true)}
                  </div>
                )}
                </div>
                {mediumButtons}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {rubricMetadata?.creator_email && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      height: 26,
                      fontSize: 12, fontWeight: 500,
                      color: 'var(--text-primary, #374151)',
                      background: 'var(--card-bg, #fff)',
                      padding: '0 8px',
                      borderRadius: 6,
                      border: '1px solid var(--border-color, #e5e7eb)',
                      whiteSpace: 'nowrap', flexShrink: 0
                    }}>
                      <i className="fa-solid fa-user-tie" style={{ fontSize: 10, color: '#6b7280' }}></i>
                      {rubricMetadata.creator_email}
                      <button
                        title="Copy email"
                        onClick={() => {
                          copyToClipboard(rubricMetadata.creator_email);
                          const btn = document.getElementById('copy-email-btn');
                          if (btn) { btn.className = 'fa-solid fa-check'; setTimeout(() => { btn.className = 'fa-solid fa-copy'; }, 1500); }
                        }}
                        style={{
                          background: 'transparent', border: 'none', padding: 0,
                          cursor: 'pointer', display: 'flex', alignItems: 'center', marginLeft: '2px'
                        }}
                      >
                        <i id="copy-email-btn" className="fa-solid fa-copy" style={{ fontSize: 10, color: '#6b7280' }}></i>
                      </button>
                    </span>
                  )}
                  {rubricMetadata?.creator_email && (
                    <button
                      type="button"
                      onClick={loginToEduPageForCard}
                      title={eduBridgeReady
                        ? `Log into EduPage as ${rubricMetadata.creator_email} and open this test`
                        : `Log into EduPage as ${rubricMetadata.creator_email} — needs the Login Edupage extension installed & updated`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        height: 26,
                        padding: '0 8px',
                        fontSize: 12,
                        fontWeight: 600,
                        border: '1px solid var(--border-color, #d1d5db)',
                        borderRadius: 6,
                        background: 'var(--card-bg, #fff)',
                        color: 'var(--text-primary, #374151)',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      <i className="fa-solid fa-right-to-bracket"></i>
                      Login
                    </button>
                  )}
                  {/* Show / Hide preview toggle. Hidden by default \u2014 the
                      header line already tells the user which rubric is
                      loaded; full marking guidelines are one click away. */}
                  {((autoRubricText || rubricText) || ((autoPdfRubric || pdfRubric) && (autoPdfRubric || pdfRubric).text)) && (
                    <button
                      type="button"
                      onClick={showRubricPreview}
                      title={rubricPreviewExpanded ? 'Hide rubric preview' : 'Show rubric preview'}
                      aria-label={rubricPreviewExpanded ? 'Hide rubric preview' : 'Show rubric preview'}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: rubricPreviewExpanded ? '#1e3a8a' : 'var(--card-bg, #fff)',
                        color: rubricPreviewExpanded ? '#fff' : '#374151',
                        border: rubricPreviewExpanded ? '1px solid #1e3a8a' : '1px solid var(--border-color, #d1d5db)',
                        borderRadius: 6,
                        width: 26, height: 26,
                        padding: 0,
                        fontSize: 12,
                        cursor: 'pointer',
                        flexShrink: 0
                      }}
                    >
                      <i className={`fa-solid ${rubricPreviewExpanded ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  )}
                </div>
              </div>
              {/* Rubric Preview \u2014 collapsed by default. Toggle via the
                  Show/Hide button above. Only the preview blocks are
                  hidden; the title row + email pill stay visible so users
                  always see WHICH rubric is loaded. */}
              {rubricPreviewExpanded && (autoRubricText || rubricText) && (
                <div className="rubric-preview">
                  <strong>Preview:</strong>
                  <div className="preview-text">
                    {stripQuestionPaperText(autoRubricText || rubricText).split('\n').map((line, index) => (
                      <div key={index}>{line ? <MatrixText text={line} /> : '\u00A0'}</div>
                    ))}
                  </div>
                </div>
              )}
              {rubricPreviewExpanded && (autoPdfRubric || pdfRubric) && (autoPdfRubric || pdfRubric).text && (
                <div className="rubric-preview">
                  <strong>PDF Text Preview:</strong>
                  <div className="preview-text">
                    {(autoPdfRubric || pdfRubric).text.split('\n').map((line, index) => (
                      <div key={index}>{line ? <MatrixText text={line} /> : '\u00A0'}</div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="status-inactive" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
              {folderPath ? (
                <button
                  type="button"
                  onClick={showRubricPreview}
                  disabled={rubricLoading}
                  title="Load this folder's rubric"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 10px',
                    border: '1px solid #93C5FD',
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.75)',
                    color: '#1E40AF',
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: rubricLoading ? 'default' : 'pointer'
                  }}
                >
                  <i className="fa-solid fa-book-open"></i>
                  {rubricLoading ? 'Loading rubric…' : 'Show rubric'}
                </button>
              ) : (
                <span>⚠️ No Rubric Loaded - Please upload a rubric from the Rubrics tab</span>
              )}
              {mediumButtons}
            </div>
          )}
        </div>

        {/* Unified header card: Sorted folder + counts on the left, Model +
            Medium dropdowns on the right (mirrors the Check 1 Sheet layout).
            Compact spacing — fits the 30% left column without bulky padding. */}
        <div style={{
          background: 'var(--card-bg, #fff)',
          border: '1px solid var(--border-color, #e5e7eb)',
          borderRadius: 8,
          padding: '10px 12px',
          marginTop: 8,
          marginBottom: 8,
          boxShadow: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 6,
              background: '#eff6ff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#1e3a8a', flexShrink: 0, fontSize: 12
            }}>
              <i className="fa-solid fa-folder-open"></i>
            </div>
            <div style={{ minWidth: 0, lineHeight: 1.3, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary, #111827)' }}>
                  Sorted Folder
                  {analyzedData.length > 0 && (
                    <span style={{ fontWeight: 500, color: 'var(--text-secondary)', marginLeft: 6 }}>
                      · Sorted <strong style={{ color: 'var(--text-primary)' }}>{getStudentCount()}</strong>
                      {' · In range '}<strong style={{ color: 'var(--text-primary)' }}>{getRangeStudentCount()}</strong>
                    </span>
                  )}
                </div>
                {ungradedRowCount > 0 && (
                  <div
                    title="Some rows finished OCR but never got marks (e.g. “OCR done — grading queued…” / Table N/A). Select them and regrade, then Approve."
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      background: '#fffbeb',
                      color: '#b45309',
                      border: '1px solid #fde68a',
                      borderRadius: 6,
                      height: 24,
                      padding: '0 8px',
                      fontSize: 11,
                      fontWeight: 600,
                      lineHeight: 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 10 }}></i>
                    Ungraded rows · {ungradedRowCount}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {folderPath || 'No Sorted folder found'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, width: '100%', flexWrap: 'nowrap' }}>
            {subjectType !== 'none' && (
              <>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>Size</label>
                <select
                  value={imageSize}
                  onChange={(e) => setImageSize(e.target.value === 'full' ? 'full' : 'resized')}
                  title="Resized fits each page inside 1600 pixels. Full size sends the original photo. If the model rejects a full-size page, that page is read again from the resized image and the fallback is saved on the student."
                  style={{
                    padding: '4px 6px',
                    border: '1px solid var(--border-color, #ccc)',
                    borderRadius: 6,
                    background: 'var(--select-bg, #fff)',
                    color: 'var(--text-primary)',
                    fontSize: 12,
                    width: 92,
                    flex: '0 0 auto'
                  }}
                >
                  <option value="resized">Resized</option>
                  <option value="full">Full size</option>
                </select>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>Send</label>
                <select
                  value={ocrSend}
                  onChange={(e) => setOcrSend(e.target.value === 'all' ? 'all' : 'per_page')}
                  title="All pages sends every page of a student in one call, including a local model. One page per call sends each page separately and joins the transcripts. Auto-check always uses All pages."
                  style={{
                    padding: '4px 6px',
                    border: '1px solid var(--border-color, #ccc)',
                    borderRadius: 6,
                    background: 'var(--select-bg, #fff)',
                    color: 'var(--text-primary)',
                    fontSize: 12,
                    width: 96,
                    flex: '0 0 auto'
                  }}
                >
                  <option value="all">All pages</option>
                  <option value="per_page">One page per call</option>
                </select>
              </>
            )}
          </div>

          {/* Two-step pipeline: OCR first, grading second. Each dropdown
              lists Gemini and the local models. A gemini-* choice calls
              Gemini. A local choice uses Spc and the Mac. */}
          {pipelineMode === 'two_step' && subjectType !== 'none' && (() => {
            const providerOptions = (
              <>
                <optgroup label="Gemini">
                  {GEMINI_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </optgroup>
                <optgroup label="Local">
                  {LOCAL_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </optgroup>
              </>
            );

            // OCR + Grading sit on one row (each takes 50%); the row wraps
            // gracefully on very narrow screens.
            const labelStyle = { display: 'block', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-secondary, #6b7280)', fontWeight: 600, marginBottom: 4 };
            const selectStyle = {
              width: '100%',
              height: 32,
              padding: '0 8px',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: 6,
              background: 'var(--select-bg, #fff)',
              color: 'var(--text-primary, #111827)',
              fontSize: 13,
              minWidth: 0
            };
            const cellStyle = { minWidth: 0 };
            const runSel = progressDetails && progressDetails.runSelection;
            const showRun = !!(runSel && runSel.ocrModel);
            const runLive = showRun && (
              isProcessing
              || progressDetails.status === 'processing'
              || progressDetails.status === 'started'
              || progressDetails.status === 'pausing'
              || progressDetails.status === 'paused'
            );
            const labelOf = (value) => {
              const hit = GEMINI_MODELS.find((m) => m.value === value)
                || LOCAL_MODELS.find((m) => m.value === value);
              return hit ? hit.label : (value || '');
            };
            const ocrModelShown = runLive ? (runSel.ocrModel || '') : ocrModel;
            const gradeModelShown = runLive ? (runSel.gradingModel || '') : gradingModel;
            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '10px 12px',
                  marginTop: 8,
                  background: 'var(--surface, #f9fafb)',
                  border: '1px solid var(--border-color, #e5e7eb)',
                  borderRadius: 8
                }}
              >
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 8,
                  alignItems: 'stretch',
                  width: '100%'
                }}>
                  <div style={cellStyle}>
                        <label style={labelStyle}>OCR Model</label>
                        <select
                          value={ocrModelShown}
                          onChange={(e) => { if (!runLive) setOcrModel(e.target.value); }}
                          disabled={runLive}
                          title={runLive
                            ? `This run transcribes with ${labelOf(ocrModelShown)}`
                            : 'Step 1 — model that transcribes handwriting verbatim. A vision model is required for image OCR.'}
                          style={selectStyle}
                        >
                          {runLive
                            ? <option value={ocrModelShown}>{labelOf(ocrModelShown)}</option>
                            : providerOptions}
                        </select>
                      </div>
                      <div style={cellStyle}>
                        <label style={labelStyle}>Grading Model</label>
                        <select
                          value={gradeModelShown}
                          onChange={(e) => { if (!runLive) setGradingModel(e.target.value); }}
                          disabled={runLive}
                          title={runLive
                            ? `This run grades with ${labelOf(gradeModelShown)}`
                            : 'Step 2 — model that grades the OCR transcript against the rubric. Text-only is sufficient.'}
                          style={selectStyle}
                        >
                          {runLive
                            ? <option value={gradeModelShown}>{labelOf(gradeModelShown)}</option>
                            : providerOptions}
                        </select>
                      </div>

                </div>
              </div>
            );
          })()}

          {checkUsesLocal && pipelineMode === 'two_step' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
              marginTop: 8,
              padding: '8px 10px',
              border: '1px solid var(--border-color, #e5e7eb)',
              borderRadius: 8,
              background: 'var(--surface, #f9fafb)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--text-primary, #111827)'
            }}>
              <span style={{ fontWeight: 600, color: 'var(--text-secondary, #6b7280)', fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase' }}>This class</span>
              {[
                ...(progressDetails?.runSelection?.ocrHost === 'gemini' || Number(progressDetails?.hostTasks?.gemini?.ocr) > 0
                  ? [['Gemini', progressDetails?.hostTasks?.gemini]]
                  : []),
                ['Spc', progressDetails?.hostTasks?.server],
                ['Mac', progressDetails?.hostTasks?.mac]
              ].map(([name, counts], index) => (
                <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {index > 0 && (
                    <span style={{ width: 1, height: 14, background: 'var(--border-color, #e5e7eb)', marginRight: 6 }} />
                  )}
                  <span>{name}</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-secondary)', fontSize: 12 }}>
                    OCR <strong style={{ color: 'var(--text-primary, #111827)' }}>{Number(counts?.ocr) || 0}</strong>
                  </span>
                  <span style={{ fontWeight: 600, color: 'var(--text-secondary)', fontSize: 12 }}>
                    Grading <strong style={{ color: 'var(--text-primary, #111827)' }}>{Number(counts?.grading) || 0}</strong>
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>

        {progress && (
          <div className="detailed-progress-container">
            {/* Main Progress Message */}
            <div className="progress-header">
              <h3>
                Assessment In Progress
              </h3>
              {assessmentResults.length === 0 && (
                <TaskElapsedChip
                  startedAt={taskTimer && taskTimer.startedAt}
                  endedAt={taskTimer && taskTimer.endedAt}
                  label={taskTimer && taskTimer.label}
                  detail={taskDetail}
                />
              )}
              <p className="progress-main-message">{progress}</p>
            </div>

            {progressDetails && progressDetails.status === 'processing' && (
              <div className="progress-details-expanded">
                {pipelineMode === 'two_step' ? (
                  <div className="progress-section">
                    {(() => {
                      const serverMode = progressDetails.recheckMode;
                      const fromServer = serverMode === 'ocr_only' || serverMode === 'grading_only' || serverMode === 'full'
                        ? serverMode
                        : '';
                      const statusText = `${autoCheckStatus || ''} ${progress || ''} ${progressDetails.message || ''}`;
                      const fromStatus = /re[-\s]?grad|skipping OCR/i.test(statusText)
                        ? 'grading_only'
                        : (/ocr[-\s]?only/i.test(statusText) ? 'ocr_only' : '');
                      // The job says which step it is running. A re-grade draws
                      // only Grading, an OCR-only run draws only OCR, and a
                      // full check draws both.
                      const runMode = fromServer || fromStatus || activeRunMode || 'full';
                      return [
                        {
                          key: 'ocr',
                          label: 'OCR',
                          caption: 'Transcript finished',
                          done: twoStepProgress.ocr,
                          color: '#6D28D9',
                          fill: 'linear-gradient(135deg, #8B5CF6, #6D28D9)'
                        },
                        {
                          key: 'grading',
                          label: 'Grading',
                          caption: 'Marks saved',
                          done: twoStepProgress.grading,
                          color: '#0F766E',
                          fill: 'linear-gradient(135deg, #14B8A6, #0F766E)'
                        }
                      ].filter((step) => {
                        if (runMode === 'grading_only') return step.key === 'grading';
                        if (runMode === 'ocr_only') return step.key === 'ocr';
                        return true;
                      });
                    })().map((step, index) => {
                      const total = twoStepProgress.total || 0;
                      const pct = total > 0 ? Math.min(100, Math.round((step.done / total) * 100)) : 0;
                      return (
                        <div key={step.label} style={{ marginTop: index === 0 ? 0 : 12 }}>
                          <div className="progress-section-header">
                            <span className="progress-label" style={{ color: step.color }}>
                              {step.label}
                              <span style={{ marginLeft: 8, fontWeight: 600, color: 'var(--text-secondary)', fontSize: 12 }}>
                                {step.caption}
                              </span>
                            </span>
                            <span className="progress-percentage" style={{ color: step.color }}>{pct}%</span>
                          </div>
                          <div className="progress-bar-main">
                            <div
                              className="progress-bar-fill-main"
                              style={{ width: `${pct}%`, background: step.fill }}
                            />
                          </div>
                          <p className="progress-stats-main">
                            {step.done} / {total} students
                          </p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="progress-section">
                    <div className="progress-section-header">
                      <span className="progress-label">Overall Progress</span>
                      <span className="progress-percentage">
                        {Math.round((progressDetails.completed / progressDetails.total) * 100)}%
                      </span>
                    </div>
                    <div className="progress-bar-main">
                      <div
                        className="progress-bar-fill-main"
                        style={{ width: `${(progressDetails.completed / progressDetails.total) * 100}%` }}
                      />
                    </div>
                    <p className="progress-stats-main">
                      {progressDetails.completed} / {progressDetails.total} students completed
                    </p>
                  </div>
                )}

                {/* Current Student Info */}
                {progressDetails.currentStudent && (
                  <div className="progress-section current-student-section">
                    <div className="current-student-header">
                      <span className="student-icon"><i className="fa-solid fa-user"></i></span>
                      <span className="student-name">{progressDetails.currentStudent}</span>
                    </div>
                    
                    {/* Current Phase Badge */}
                    {progressDetails.currentPhase && (
                      <div className="phase-badge-container">
                        <span className={`phase-badge phase-${progressDetails.currentPhase}`}>
                          {progressDetails.currentPhase === 'preparing' && (<><i className="fa-solid fa-gear"></i> Preparing</>)}
                          {progressDetails.currentPhase === 'downloading' && (<><i className="fa-solid fa-download"></i> Downloading Images</>)}
                          {progressDetails.currentPhase === 'validating' && (<><i className="fa-solid fa-shield-halved"></i> Validating</>)}
                          {progressDetails.currentPhase === 'ai_processing' && (<><i className="fa-solid fa-microchip"></i> AI Processing</>)}
                        </span>
                      </div>
                    )}

                    {/* Image Download Progress */}
                    {progressDetails.currentImages && progressDetails.currentImages.length > 0 && (
                      <div className="images-progress">
                        <div className="images-header">
                          <span><i className="fa-solid fa-images"></i> Images</span>
                          <span className="images-count">
                            {progressDetails.downloadedImages?.length || 0} / {progressDetails.currentImages.length}
                          </span>
                        </div>
                        <div className="images-grid">
                          {progressDetails.currentImages.map((img, idx) => {
                            // Check if this specific image at this index was downloaded
                            // Backend tracks as "imageName#index" to handle duplicates
                            const indexedName = `${img}#${idx}`;
                            const isDownloaded = progressDetails.downloadedImages?.some(
                              d => d === indexedName || d === img
                            );
                            const isFailed = progressDetails.failedImages?.some(f => f.name === img);
                            const isMissing = progressDetails.missingImages?.includes(img);
                            return (
                              <div 
                                key={idx} 
                                className={`image-item ${
                                  isMissing ? 'image-missing' : 
                                  isFailed ? 'image-failed' : 
                                  isDownloaded ? 'image-downloaded' : 'image-pending'
                                }`}
                                title={img}
                              >
                                {isMissing && <i className="fa-solid fa-circle-xmark"></i>}
                                {isFailed && <i className="fa-solid fa-triangle-exclamation"></i>}
                                {isDownloaded && <i className="fa-solid fa-check"></i>}
                                {!isMissing && !isFailed && !isDownloaded && <i className="fa-solid fa-hourglass-half"></i>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Validation Status */}
                    {progressDetails.validationStatus && (
                      <div className={`validation-status validation-${progressDetails.validationStatus}`}>
                        {progressDetails.validationStatus === 'passed' && (
                          <><span className="validation-icon"><i className="fa-solid fa-circle-check"></i></span> Validation Passed</>
                        )}
                        {progressDetails.validationStatus === 'failed' && (
                          <><span className="validation-icon"><i className="fa-solid fa-circle-xmark"></i></span> Validation Failed - Images Missing</>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Warnings Section */}
                {progressDetails.warnings && progressDetails.warnings.length > 0 && (
                  <div className="warnings-section">
                    <div className="warnings-header">
                      <span><i className="fa-solid fa-triangle-exclamation"></i> Warnings ({progressDetails.warnings.length})</span>
                    </div>
                    <div className="warnings-list">
                      {progressDetails.warnings.slice(-3).map((warning, idx) => (
                        <div key={idx} className="warning-item">
                          {warning.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Statistics Summary */}
                {progressDetails.stats && (
                  <div className="stats-grid">
                    <div className="stat-box">
                      <span className="stat-value">{progressDetails.stats.downloadedCount}</span>
                      <span className="stat-label">Downloaded</span>
                    </div>
                    <div className="stat-box stat-box-error">
                      <span className="stat-value">{progressDetails.stats.failedCount}</span>
                      <span className="stat-label">Failed</span>
                    </div>
                    <div className="stat-box stat-box-warning">
                      <span className="stat-value">{progressDetails.stats.missingCount}</span>
                      <span className="stat-label">Missing</span>
                    </div>
                    <div className="stat-box stat-box-skipped">
                      <span className="stat-value">{progressDetails.stats.skippedCount}</span>
                      <span className="stat-label">Skipped</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Selection Mode toggle: Range or Custom */}
        <div className="control-row" style={{ alignItems: 'center' }}>
          <div className="control-group">
            <label>Selection Mode</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap', width: '100%', minWidth: 0 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', flex: '1 1 auto', minWidth: 0, background: 'var(--surface, #f3f4f6)', padding: 2, borderRadius: 8, gap: 2, border: '1px solid var(--border-color, #e5e7eb)' }}>
              <button
                type="button"
                onClick={() => setSelectionMode('range')}
                style={{
                  height: 28,
                  padding: '0 6px',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 11,
                  whiteSpace: 'nowrap',
                  flex: '1 1 0',
                  minWidth: 0,
                  background: selectionMode === 'range' ? 'var(--card-bg, #fff)' : 'transparent',
                  color: selectionMode === 'range' ? 'var(--text-primary, #111827)' : 'var(--text-secondary, #6b7280)',
                  boxShadow: selectionMode === 'range' ? '0 1px 2px rgba(17,24,39,0.08)' : 'none'
                }}
              >
                Student Range
              </button>
              <button
                type="button"
                onClick={() => setSelectionMode('custom')}
                style={{
                  height: 28,
                  padding: '0 6px',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 11,
                  whiteSpace: 'nowrap',
                  flex: '1 1 0',
                  minWidth: 0,
                  background: selectionMode === 'custom' ? 'var(--card-bg, #fff)' : 'transparent',
                  color: selectionMode === 'custom' ? 'var(--text-primary, #111827)' : 'var(--text-secondary, #6b7280)',
                  boxShadow: selectionMode === 'custom' ? '0 1px 2px rgba(17,24,39,0.08)' : 'none'
                }}
              >
                Pick Students
                {selectionMode === 'custom' && selectedStudentCodes.length > 0 && (
                  <span style={{
                    marginLeft: '6px',
                    padding: '2px 8px',
                    background: 'rgba(255,255,255,0.25)',
                    borderRadius: '10px',
                    fontSize: '0.75rem',
                    fontWeight: 700
                  }}>{selectedStudentCodes.length}</span>
                )}
              </button>
            </div>
            <button
              type="button"
              onClick={openRegradeHistory}
              title="Open saved re-grading summaries"
              style={{ ...toolBtn('outline'), flex: '0 1 auto', minWidth: 0, height: 28, padding: '0 6px', fontSize: 11 }}
            >
              History{regradeHistory.length ? ` (${regradeHistory.length})` : ''}
            </button>
            <select
              value={studentRange}
              aria-label="Student range"
              onChange={(e) => {
                setStudentRange(e.target.value);
                setSelectionMode('range');
              }}
              style={{
                flex: '0 0 88px',
                width: 88,
                height: 28,
                padding: '0 4px',
                border: '1px solid var(--border-color, #d1d5db)',
                borderRadius: 6,
                background: 'var(--select-bg, #fff)',
                color: 'var(--text-primary, #111827)',
                fontSize: 11,
                fontWeight: 600
              }}
            >
              <option value="1-2">1-2</option>
              <option value="3-10">3-10</option>
              <option value="3-20">3-20</option>
              <option value="3-All">3-All</option>
              <option value="11-20">11-20</option>
              <option value="21-30">21-30</option>
              <option value="31-40">31-40</option>
              <option value="41-50">41-50</option>
              <option value="21-40">21-40</option>
              <option value="41-60">41-60</option>
              <option value="61-80">61-80</option>
              <option value="All">All</option>
            </select>
            </div>
          </div>
        </div>

        {selectionMode === 'custom' && (
        <div className="control-row">
            <div className="control-group" style={{ flex: 1, minWidth: '260px' }}>
              <label>
                Pick Students ({selectedStudentCodes.length} of {getUniqueStudents().length} selected)
              </label>
              {getUniqueStudents().length === 0 ? (
                <div style={{
                  padding: '12px',
                  border: '1px dashed var(--border, #d1d5db)',
                  borderRadius: '8px',
                  color: 'var(--text-muted, #6b7280)',
                  fontSize: '0.85rem'
                }}>
                  No sorted students available. Make sure a sorted folder is selected.
                </div>
              ) : (
                <div style={{
                  border: '1px solid var(--border, #d1d5db)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: 'var(--select-bg, white)'
                }}>
                  <div style={{ padding: '8px', display: 'flex', gap: '8px', alignItems: 'center', borderBottom: '1px solid var(--border, #e5e7eb)', background: 'var(--surface, #f9fafb)' }}>
                    <input
                      type="text"
                      placeholder="Search by roll number or name..."
                      value={studentPickerSearch}
                      onChange={(e) => setStudentPickerSearch(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        border: '1px solid var(--border, #d1d5db)',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        backgroundColor: 'var(--select-bg, white)',
                        color: 'var(--text-primary)'
                      }}
                    />
                    <button
                      type="button"
                      onClick={toggleSelectAllVisibleStudents}
                      style={{
                        padding: '6px 12px',
                        border: 'none',
                        borderRadius: '6px',
                        background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}
                      title="Toggle selection for all students matching the search"
                    >
                      Select All
                    </button>
                    {selectedStudentCodes.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedStudentCodes([])}
                        style={{
                          padding: '6px 12px',
                          border: 'none',
                          borderRadius: '6px',
                          background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div style={{ maxHeight: '320px', overflowY: 'auto', padding: '8px' }}>
                    {(() => {
                      const filtered = getUniqueStudents().filter(s => {
                        const q = studentPickerSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (s.roll_name || '').toLowerCase().includes(q) || (s.unique_code || '').toLowerCase().includes(q);
                      });
                      if (filtered.length === 0) {
                        return (
                          <div style={{ padding: '12px', textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-muted, #6b7280)' }}>
                            No students match "{studentPickerSearch}"
                          </div>
                        );
                      }
                      return (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                          gap: '6px'
                        }}>
                          {filtered.map(s => {
                            const checked = selectedStudentCodes.includes(s.unique_code);
                            return (
                              <label
                                key={s.unique_code}
                                title={`${s.unique_code} — ${s.roll_name}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '8px 10px',
                                  border: checked
                                    ? '2px solid #3B82F6'
                                    : '1px solid var(--border, #e5e7eb)',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  background: checked ? 'var(--select-blue-bg, #EFF6FF)' : 'var(--select-bg, #fff)',
                                  fontSize: '12px',
                                  minWidth: 0,
                                  transition: 'all 0.15s ease'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleStudentCode(s.unique_code)}
                                  style={{ cursor: 'pointer', accentColor: '#3B82F6', flexShrink: 0, width: '16px', height: '16px', margin: 0, padding: 0 }}
                                />
                                <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                  <span style={{
                                    fontFamily: 'monospace',
                                    fontWeight: 700,
                                    color: 'var(--text-primary, #111827)',
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    lineHeight: 1.3
                                  }}>{s.unique_code || '—'}</span>
                                  <span style={{
                                    fontSize: '11px',
                                    color: 'var(--text-secondary, #6b7280)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    lineHeight: 1.3
                                  }}>{s.roll_name || s.unique_code || '—'}</span>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
        </div>
        )}

        <div className="control-row action-buttons" style={{ flexWrap: 'nowrap', gap: 4 }}>
          {canResumeCheck && (
            <button
              type="button"
              onClick={() => {
                if (!requireMediumOrPrompt()) return;
                handleStartAssessment({ resume: true });
              }}
              disabled={isProcessing || !folderPath || analyzedData.length === 0 || isUploadMismatchForCheck}
              title="Continue this class. Students with no result row are OCR'd. Rows with no marks are graded. Already graded sheets are skipped."
              className="start-assessment-btn"
              style={{ ...toolBtn('warning', isProcessing || !folderPath || analyzedData.length === 0 || isUploadMismatchForCheck), flex: '1 1 0', minWidth: 0, padding: '0 4px', fontSize: 11 }}
            >
              {resumeLabel}
            </button>
          )}
          <button
            onClick={() => {
              // Medium check FIRST — no other modal pops up until a medium
              // is chosen.
              if (!requireMediumOrPrompt()) return;

              // Jump straight into the run (no Edupage login reminder).
              const proceed = () => {
                handleStartAssessment();
              };

              // If there are existing assessment results already in the table,
              // ask the teacher whether to wipe them before starting the new
              // check. "Yes, delete" = clear DB + state, then proceed.
              // "No, keep" = leave existing rows alone and proceed anyway —
              // the new run's results will be appended below them.
              if (assessmentResults.length > 0) {
                showConfirm(
                  `There are ${assessmentResults.length} existing assessment result(s) in the table. Delete them before starting this new check?`,
                  async () => {
                    try {
                      const token = localStorage.getItem('authToken');
                      const response = await fetch(checkMoreResultsUrl(), {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await response.json().catch(() => ({}));
                      if (response.ok) {
                        showMessage(`Cleared ${data.deleted_count || assessmentResults.length} previous result(s).`, 'success');
                        setAssessmentResults([]);
                        setSelectedResults([]);
                      } else {
                        showMessage(data.message || 'Failed to clear previous results.', 'error');
                      }
                    } catch (e) {
                      showMessage('Failed to clear previous results: ' + e.message, 'error');
                    }
                    proceed();
                  },
                  {
                    confirmLabel: 'Yes, delete',
                    cancelLabel: 'No, keep',
                    onCancel: proceed
                  }
                );
                return;
              }

              proceed();
            }}
            disabled={isProcessing || !folderPath || analyzedData.length === 0 || isUploadMismatchForCheck}
            title={isUploadMismatchForCheck ? uploadMismatchCheckMsg : undefined}
            className="start-assessment-btn"
            style={{ ...toolBtn('primary', isProcessing || !folderPath || analyzedData.length === 0 || isUploadMismatchForCheck), flex: '1 1 0', minWidth: 0, padding: '0 4px', fontSize: 11 }}
          >
            {isProcessing ? 'Processing...' : isUploadMismatchForCheck ? 'Local ≠ Web' : 'Start Eng Checking'}
          </button>

          {/* OCR-only run — only useful for two-step pipelines (English-2 /
              Urdu-2) where step 1 (OCR) and step 2 (grading) can run
              independently. Single-call mediums hide this button because
              there's nothing to skip. After this finishes, the teacher
              reviews the captured transcripts and clicks "Re-grade" on the
              result rows to score them. */}
          {pipelineMode === 'two_step' && (
            <button
              onClick={() => {
                // Medium check FIRST — even though this button only renders
                // when pipelineMode === 'two_step', a user could conceivably
                // hit it from a stale render or keyboard navigation.
                if (!requireMediumOrPrompt()) return;
                // Build a count message that's accurate for whichever mode
                // (range vs custom). The teacher should know exactly how
                // many students this OCR pass will hit before paying for it.
                const target = selectionMode === 'custom'
                  ? selectedStudentCodes.length
                  : analyzedData.length;
                if (target === 0) {
                  showMessage(selectionMode === 'custom'
                    ? 'Pick at least one student before starting OCR.'
                    : 'No analyzed students to OCR yet.', 'error');
                  return;
                }
                showConfirm(
                  `Run OCR only on ${target} student(s)? This will transcribe each answer sheet but will NOT assign marks. After it finishes you can review the transcripts and click Re-grade on the result rows to score them.`,
                  () => handleStartAssessment({ ocrOnlyRun: true }),
                  { confirmLabel: 'Start OCR', cancelLabel: 'Cancel' }
                );
              }}
              disabled={isProcessing || !folderPath || analyzedData.length === 0 || isUploadMismatchForCheck}
              title={isUploadMismatchForCheck
                ? uploadMismatchCheckMsg
                : 'Run only the OCR step (step 1). Marks/comments are left empty until you click Re-grade on the result rows.'}
              style={{ ...toolBtn('success', isProcessing || !folderPath || analyzedData.length === 0 || isUploadMismatchForCheck), flex: '1 1 0', minWidth: 0, padding: '0 4px', fontSize: 11 }}
            >
              {isProcessing ? 'Processing...' : isUploadMismatchForCheck ? 'Local ≠ Web' : 'Start Eng OCR'}
            </button>
          )}
          
          {/* Get Text — load typed answers via the local-server. It resolves
              <SCANS_BASE_PATH>/<Month>/<Day>/<class folder>/ from the active
              rubric's test_date + class/section/subject, reads every .docx
              via mammoth, and we save one row per student. No folder picker. */}
          <button
            type="button"
            onClick={() => {
              // Mirror the Start Eng Checking flow: if there are existing
              // assessment rows in the table, ask the teacher whether to wipe
              // them first. "Yes, delete" = DELETE /assess/results then run
              // Text Step 1. "No, keep" = run Text Step 1 anyway (new rows
              // append below the old ones).
              if (assessmentResults.length > 0) {
                showConfirm(
                  `There are ${assessmentResults.length} existing assessment result(s) in the table. Delete them before starting Text Step 1?`,
                  async () => {
                    try {
                      const token = localStorage.getItem('authToken');
                      const response = await fetch(checkMoreResultsUrl(), {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const data = await response.json().catch(() => ({}));
                      if (response.ok) {
                        showMessage(`Cleared ${data.deleted_count || assessmentResults.length} previous result(s).`, 'success');
                        setAssessmentResults([]);
                        setSelectedResults([]);
                      } else {
                        showMessage(data.message || 'Failed to clear previous results.', 'error');
                      }
                    } catch (e) {
                      showMessage('Failed to clear previous results: ' + e.message, 'error');
                    }
                    handleGetText();
                  },
                  {
                    confirmLabel: 'Yes, delete',
                    cancelLabel: 'No, keep',
                    onCancel: () => handleGetText()
                  }
                );
                return;
              }
              handleGetText();
            }}
            disabled={isProcessing || isGettingText || analyzedData.length === 0}
            title={analyzedData.length === 0
              ? 'Run Sort first so students are grouped.'
              : 'Load typed answers from the matching local folder (resolved automatically from the active rubric).'}
            style={{ ...toolBtn('outline-violet', isProcessing || isGettingText || analyzedData.length === 0), flex: '1 1 0', minWidth: 0, padding: '0 4px', fontSize: 11 }}
          >
            {isGettingText ? 'Reading...' : 'Text Step 1'}
          </button>

          {isProcessing && (
            <button
              type="button"
              onClick={handlePauseProcessing}
              disabled={isPausing}
              title="Stop after the sheet already with the model. Saved rows stay. Resume continues from the next sheet that has no marks."
              style={{ ...toolBtn('warning', isPausing), cursor: isPausing ? 'wait' : 'pointer', opacity: isPausing ? 0.75 : 1 }}
            >
              {isPausing ? 'Pausing…' : 'Pause'}
            </button>
          )}

          {isProcessing && (
            <button
              onClick={handleStopProcessing}
              className="stop-btn"
              style={toolBtn('danger')}
            >
              Stop Checking
            </button>
          )}
        </div>

      </div>
      )}

      {analyzedData.length === 0 && folderPath && !showApproveForm && (
        <div className="warning-box">
          <p><i className="fa-solid fa-circle-exclamation"></i> No sorted data found for this folder. Please run sorting in the Sort tab first.</p>
        </div>
      )}

      {/* Skipped Students Section */}
      {progressDetails && progressDetails.skippedStudents && progressDetails.skippedStudents.length > 0 && (
        <div className="skipped-students-section">
          <div className="skipped-header">
            <h3><i className="fa-solid fa-user-slash"></i> Skipped Students ({progressDetails.skippedStudents.length})</h3>
            <p>The following students were skipped due to missing or failed images:</p>
          </div>
          <div className="skipped-students-list">
            {progressDetails.skippedStudents.map((student, idx) => (
              <div key={idx} className="skipped-student-card">
                <div className="skipped-student-header">
                  <span className="student-code">{student.uniqueCode}</span>
                  <span className="student-name-text">{student.rollName}</span>
                </div>
                <div className="skipped-reason">
                  <strong>Reason:</strong> {student.reason}
                </div>
                <div className="skipped-details">
                  <span>Expected: {student.expectedImages} images</span>
                  <span>Collected: {student.collectedImages} images</span>
                </div>
                {student.missingImages && student.missingImages.length > 0 && (
                  <div className="skipped-missing-images">
                    <strong>Missing Images:</strong>
                    <div className="missing-images-list">
                      {student.missingImages.map((img, imgIdx) => (
                        <span key={imgIdx} className="missing-image-badge">{img}</span>
                      ))}
                    </div>
                  </div>
                )}
                {student.failedImages && student.failedImages.length > 0 && (
                  <div className="skipped-failed-images">
                    <strong>Failed Images:</strong>
                    <div className="failed-images-list">
                      {student.failedImages.map((img, imgIdx) => (
                        <span key={imgIdx} className="failed-image-badge" title={img.error}>
                          {img.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(() => {
        const missingStudents = getMissingResultStudents();
        if (missingStudents.length === 0) return null;
        return (
          <div
            style={{
              marginTop: 8,
              background: '#fff7ed',
              border: '1px solid #fdba74',
              borderRadius: 10,
              padding: '10px 12px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <i className="fa-solid fa-user-slash" style={{ color: '#c2410c', fontSize: 13 }}></i>
              <strong style={{ color: '#9a3412', fontSize: 13 }}>
                Missing from results ({missingStudents.length})
              </strong>
            </div>
            <p style={{ margin: '0 0 8px', fontSize: 12, color: '#9a3412', lineHeight: 1.35 }}>
              These students are in the sorted list, but they have no row in Assessment Results.
            </p>
            <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {missingStudents.map(s => {
                const name = s.roll_name || '';
                const alreadyCoded = name.includes(`(${s.unique_code})`);
                const label = alreadyCoded ? name : `(${s.unique_code}) ${name}`.trim();
                return (
                  <div
                    key={s.unique_code}
                    style={{
                      background: 'white',
                      border: '1px solid #fed7aa',
                      borderRadius: 6,
                      padding: '4px 8px',
                      fontSize: 12,
                      color: '#7c2d12',
                      fontWeight: 600
                    }}
                  >
                    {label}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      </>)}
        </div>
        )}
        {/* ── RIGHT COLUMN ─────────────────────────────────────────── */}
        <div
          className="checking-right-col"
          style={{
            flex: showApproveForm ? '1 1 100%' : '1 1 0',
            minWidth: 320,
            display: 'flex',
            flexDirection: 'column',
            gap: 16
          }}
        >
        {!isAutoCheckUser && !assessmentResults.length && !showApproveForm && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <AutoCheckStatusPanel
              dispatch={aiStatus?.dispatch}
              hostOn={hostAvailable}
              board={autoCheckBoard}
              present={autoCheckPresent}
              switches={autoCheckSwitches}
              onToggleAccount={toggleAutoCheckAccount}
              switchBusy={switchBusy}
            />
          </div>
        )}
        {/* Empty-state placeholder when no results have arrived yet. */}
        {!assessmentResults.length && !showApproveForm && (
          <div
            style={{
              textAlign: 'center',
              padding: '48px 24px',
              background: 'rgba(0,0,0,0.02)',
              border: '1px dashed var(--border-color, #cbd5e1)',
              borderRadius: 12,
              color: 'var(--text-secondary, #94a3b8)'
            }}
          >
            <i
              className="fa-solid fa-clipboard-check"
              style={{ fontSize: 32, marginBottom: 12, color: '#3B82F6', opacity: 0.7 }}
            ></i>
            <h3 style={{ margin: '0 0 6px', fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              No assessment results yet
            </h3>
            <p style={{ margin: 0, fontSize: 13 }}>
              Configure the checks on the left, then click <strong>Start Eng Checking</strong> to begin.
            </p>
          </div>
        )}

      {/* Assessment Results Table */}
      {assessmentResults.length > 0 && (
        <div className="results-section" style={{ marginTop: 0 }}>
          {/* Timestamp Header */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            padding: '10px 12px',
            background: 'var(--card-bg, #fff)',
            borderBottom: '1px solid var(--border-color, #e5e7eb)'
          }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--text-primary, #111827)' }}>
              Results
              <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: 'var(--text-secondary, #6b7280)' }}>{assessmentResults.length}</span>
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginLeft: 'auto' }}>
              <TaskElapsedChip
                startedAt={taskTimer && taskTimer.startedAt}
                endedAt={taskTimer && taskTimer.endedAt}
                label={taskTimer && taskTimer.label}
                detail={taskDetail}
              />
              {(() => {
                const latestForFolder = regradeHistory.find((item) => item.folderPath && item.folderPath === folderPath);
                const visible = (regradeSummary && (!regradeSummary.folderPath || regradeSummary.folderPath === folderPath))
                  ? regradeSummary
                  : latestForFolder;
                if (!visible) return null;
                return (
                  <button
                    type="button"
                    onClick={() => openSavedRegradeSummary(visible)}
                    title="Open the re-grading summary to review and copy it"
                    style={toolBtn('success')}
                  >
                    Re-Grading Summary
                  </button>
                );
              })()}
              {(() => {
                const saved = stepTimesByFolder[folderPath];
                if (!saved || (saved.ocrMs == null && saved.gradingMs == null)) return null;
                const ocrLabel = formatStepDuration(saved.ocrMs) || '—';
                const gradingLabel = formatStepDuration(saved.gradingMs) || '—';
                const pill = (label, value) => (
                  <div style={metaChip('success')}>
                    <span style={{ fontWeight: 500 }}>{label}</span>
                    <span>{value}</span>
                  </div>
                );
                return (
                  <>
                    {pill('OCR', ocrLabel)}
                    {pill('Grading', gradingLabel)}
                  </>
                );
              })()}
            {assessmentResults.length > 0 && assessmentResults[0].created_at && (() => {
              const saved = stepTimesByFolder[folderPath] || {};
              const ocrClock = formatPktClock(saved.ocrEndedAt);
              const gradingClock = formatPktClock(saved.gradingEndedAt);
              if (ocrClock || gradingClock) {
                const clockChip = (label, clock) => (
                  <div style={metaChip('success')}>
                    <i className="fa-solid fa-clock"></i>
                    <span style={{ fontWeight: 500 }}>{label}</span>
                    <span>{clock || (isProcessing ? 'running' : '—')}</span>
                  </div>
                );
                return (
                  <>
                    {clockChip('OCR', ocrClock)}
                    {clockChip('Grading', gradingClock)}
                  </>
                );
              }
              return (
                <div style={metaChip('success')}>
                  <i className="fa-solid fa-clock"></i>
                  <span>
                    Last Assessed: {new Date(assessmentResults[0].created_at).toLocaleString('en-PK', {
                      timeZone: 'Asia/Karachi',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </span>
                </div>
              );
            })()}
            </div>
          </div>
          
          {/* Action Buttons Row */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border-color, #f3f4f6)' }}>
            {!isAutoCheckUser && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
                <AutoCheckStatusPanel
                  dispatch={aiStatus?.dispatch}
                  hostOn={hostAvailable}
                  board={autoCheckBoard}
                  present={autoCheckPresent}
                  switches={autoCheckSwitches}
                  onToggleAccount={toggleAutoCheckAccount}
                  switchBusy={switchBusy}
                />
              </div>
            )}
            {/* Approve, the view buttons, and the saved-table button sit on
                the same row as re-grade and delete. The class-and-date name
                already shows on the left card. */}
              {pipelineMode === 'two_step' && rubricQuestions.length > 0 && (
                <div style={{ width: '100%', flexBasis: '100%', marginTop: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-secondary, #6b7280)' }}>Re-grade questions</span>
                    <button
                      type="button"
                      onClick={() => setSelectedQuestionIds(rubricQuestions.map(q => q.id))}
                      style={{ border: 'none', background: 'transparent', color: '#1e3a8a', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedQuestionIds([])}
                      style={{ border: 'none', background: 'transparent', color: '#6B7280', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                    >
                      Clear
                    </button>
                    <span style={{ fontSize: 12, color: '#6B7280' }}>
                      {partialQuestionLabels.length
                        ? 'Only the ticked questions are re-graded. The rest keep their marks.'
                        : 'None ticked — Re-grade covers the whole paper.'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {rubricQuestions.map(q => {
                      const on = selectedQuestionIds.includes(q.id);
                      return (
                        <button
                          key={q.id}
                          type="button"
                          onClick={() => setSelectedQuestionIds(prev => (
                            prev.includes(q.id) ? prev.filter(id => id !== q.id) : [...prev, q.id]
                          ))}
                          style={{
                            height: 26,
                            padding: '0 8px',
                            borderRadius: 6,
                            border: on ? '1px solid #1e3a8a' : '1px solid var(--border-color, #d1d5db)',
                            background: on ? '#eff6ff' : 'var(--card-bg, #fff)',
                            color: on ? '#1e3a8a' : '#374151',
                            fontWeight: 600,
                            fontSize: 12,
                            cursor: 'pointer'
                          }}
                        >
                          {q.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {duplicateRollNumbers.size > 0 && (
                <div style={{ ...metaChip('danger'), height: 32, flexShrink: 0 }}>
                  {duplicateRollNumbers.size} Duplicate Roll Number{duplicateRollNumbers.size > 1 ? 's' : ''}
                </div>
              )}
              {pipelineMode === 'two_step' ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    onClick={() => handleRecheckSelected('grading_only', {
                      questionIds: partialQuestionLabels.length ? selectedQuestionIds : null
                    })}
                    disabled={selectedResults.length === 0}
                    title={partialQuestionLabels.length
                      ? `Re-grade only ${partialQuestionLabels.join(', ')} from the saved transcript. Other questions keep their marks.`
                      : 'Re-run only the grading step using the saved OCR transcript. Tick questions above to re-grade just those.'}
                    style={toolBtn('primary', selectedResults.length === 0)}
                  >
                    {partialQuestionLabels.length > 4
                      ? `Re-grade ${partialQuestionLabels.length} questions (${selectedResults.length})`
                      : partialQuestionLabels.length
                        ? `Re-grade ${partialQuestionLabels.join(', ')} (${selectedResults.length})`
                        : `Check Step 2/Re-grade (${selectedResults.length})`}
                  </button>
                  <button
                    onClick={() => handleRecheckSelected('ocr_only')}
                    disabled={selectedResults.length === 0}
                    title="Re-run ONLY the OCR step. Keeps the existing marks and comments — useful when the transcript was wrong but you don't want to pay to re-grade."
                    style={toolBtn('primary', selectedResults.length === 0)}
                  >
                    Re-OCR ({selectedResults.length})
                  </button>
                  <button
                    onClick={() => handleRecheckSelected('full')}
                    disabled={selectedResults.length === 0}
                    title="Re-run BOTH steps: re-OCR the image, then re-grade. Use this when OCR was bad."
                    style={toolBtn('primary', selectedResults.length === 0)}
                  >
                    Full Re-check ({selectedResults.length})
                  </button>
                  <button
                    onClick={() => {
                      const ocrPages = {};
                      selectedMissingRows.forEach((row) => {
                        const pages = parseMissingPages(row.ocr_missing_pages);
                        if (row.image_name && pages.length) ocrPages[row.image_name] = pages;
                      });
                      handleRecheckSelected('ocr_only', {
                        resultIds: selectedMissingRows.map((row) => row.id),
                        ocrPages
                      });
                    }}
                    disabled={selectedMissingRows.length === 0}
                    title="Send only the image numbers on the ocr missing tag. Pages already in the transcript stay."
                    style={toolBtn('outline-amber', selectedMissingRows.length === 0)}
                  >
                    Re-OCR missing ({selectedMissingRows.length})
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleRecheckSelected('full')}
                  disabled={selectedResults.length === 0}
                  style={toolBtn('warning', selectedResults.length === 0)}
                >
                  Recheck ({selectedResults.length})
                </button>
              )}
              <button
                onClick={() => setShowAllComments(true)}
                style={{ ...toolBtn('outline'), flexShrink: 0, padding: '0 10px', fontSize: 12 }}
              >
                View All Comments
              </button>
              <button
                onClick={() => setShowAllOcr(true)}
                title="Open every saved transcript"
                style={{ ...toolBtn('outline'), flexShrink: 0, padding: '0 10px', fontSize: 12 }}
              >
                View All OCR
              </button>
              <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <div
                style={{ position: 'relative' }}
                onMouseEnter={() => setButtonHint('save')}
                onMouseLeave={() => setButtonHint((current) => (current === 'save' ? null : current))}
              >
              <button
                type="button"
                onClick={() => handleSaveAssessmentSnapshot({ tableOnly: true })}
                disabled={assessSnapshotBusy || assessmentResults.length === 0}
                style={{ ...toolBtn(tableNeedsSave ? 'primary' : 'outline-blue', assessSnapshotBusy || assessmentResults.length === 0), flexShrink: 0, padding: '0 10px', fontSize: 12 }}
              >
                {assessSnapshotBusy && snapshotSaveKind === 'table' ? 'Saving…' : 'Save'}
              </button>
              {buttonHintCard('save')}
              </div>
              <div
                style={{ position: 'relative' }}
                onMouseEnter={() => setButtonHint('update')}
                onMouseLeave={() => setButtonHint((current) => (current === 'update' ? null : current))}
              >
              <button
                type="button"
                onClick={handleSaveAssessmentSnapshot}
                disabled={assessSnapshotBusy}
                style={{ ...toolBtn(tableNeedsSave ? 'violet' : 'outline-amber', assessSnapshotBusy), flexShrink: 0, padding: '0 10px', fontSize: 12 }}
              >
                {assessSnapshotBusy && snapshotSaveKind === 'copies'
                  ? 'Saving…'
                  : assessSnapshotMeta
                    ? `Update all Tables (${assessSnapshotMeta.record_count})`
                    : 'Save Table to Folder'}
              </button>
              {buttonHintCard('update')}
              </div>
              </div>
              <div
                style={{ position: 'relative' }}
                onMouseEnter={() => setButtonHint('approve')}
                onMouseLeave={() => setButtonHint((current) => (current === 'approve' ? null : current))}
              >
              <button
                type="button"
                onClick={openApprovePicker}
                style={{ ...toolBtn(approveNeedsClick ? 'success' : 'outline'), flexShrink: 0, padding: '0 10px', fontSize: 12 }}
              >
                Approve
              </button>
              {buttonHintCard('approve')}
              </div>
              {/* One smart Delete button. With a selection it deletes the
                  selected rows after a confirm; with no selection it falls
                  back to deleting EVERY row after a confirm — both flows
                  pop their own native window.confirm so the teacher can
                  cancel before anything is destroyed. The label and tint
                  flip to make the active mode obvious. */}
              <button
                onClick={() => {
                  if (selectedResults.length > 0) {
                    handleDeleteSelected();
                  } else if (assessmentResults.length > 0) {
                    handleDeleteAll();
                  }
                }}
                disabled={assessmentResults.length === 0}
                title={selectedResults.length > 0
                  ? `Delete the ${selectedResults.length} selected row(s)`
                  : (assessmentResults.length > 0
                      ? `No rows selected — clicking will delete ALL ${assessmentResults.length} row(s)`
                      : 'No rows to delete')}
                style={selectedResults.length > 0
                  ? toolBtn('danger', assessmentResults.length === 0)
                  : toolBtn('outline-red', assessmentResults.length === 0)}
              >
                {selectedResults.length > 0
                  ? `Delete Selected (${selectedResults.length})`
                  : 'Delete All'}
              </button>
            </div>
          </div>

          {/* Approve Form */}
          {showApproveForm && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(5, 150, 105, 0.05))',
              borderLeft: '4px solid #10b981',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '24px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
                    Approve Selected Results
                    {rubricMetadata && (
                      <span style={{ fontWeight: '600', marginLeft: '10px', fontSize: '0.85rem', color: '#10B981' }}>
                        — {rubricNameWithDate(
                          rubricMetadata,
                          rubricMetadata.test_date
                            ? new Date(rubricMetadata.test_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : ''
                        )}
                      </span>
                    )}
                  </h3>
              </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '250px' }}>
                  <Select
                    value={selectedStudentList}
                    inputValue={approveSearchInput}
                    onInputChange={(val, action) => {
                      if (action.action === 'input-change') {
                        setApproveSearchInput(val);
                      }
                      if (action.action === 'set-value' || action.action === 'menu-close') {
                        if (selectedStudentList) setApproveSearchInput('');
                      }
                    }}
                    onChange={(selected) => {
                      setSelectedStudentList(selected);
                      setApproveSearchInput('');
                      if (selected) {
                        setApproveForm({ class: selected.classVal, section: selected.section, subject: selected.subject });
                      } else {
                        setApproveForm({ class: '', section: '', subject: '' });
                      }
                    }}
                    options={getStudentListOptions()}
                    isSearchable
                    isClearable
                    placeholder="Search or select a student list..."
                    noOptionsMessage={() => 'No student lists found'}
                    menuIsOpen={approveSearchInput.length > 0 && !selectedStudentList ? true : undefined}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: 8,
                        borderColor: 'var(--border-color, #ddd)',
                        fontSize: '0.95rem',
                        minHeight: 42,
                      }),
                      menu: (base) => ({ ...base, zIndex: 10000 }),
                    }}
                  />
                </div>
                <button 
                  onClick={() => handleApprove()}
                  disabled={approveLoading || selectedResults.length === 0}
                  style={{
                    padding: '12px 24px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    borderRadius: '8px',
                    border: 'none',
                    background: (approveLoading || selectedResults.length === 0) ? '#94a3b8' : 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    cursor: (approveLoading || selectedResults.length === 0) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {approveLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Approving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check"></i>
                      Submit ({selectedResults.length})
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
          
          <div className="results-table-container">
            <table className="results-table">
              <thead>
                <tr>
                  <th>
                    <input 
                      type="checkbox" 
                      checked={selectedResults.length === assessmentResults.length && assessmentResults.length > 0}
                      onChange={handleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th
                    onClick={() => handleSort('student_name')}
                    style={{ cursor: 'pointer', userSelect: 'none', minWidth: 220, width: '20%' }}
                  >
                    Name
                    <span style={{ marginLeft: '6px', fontSize: '12px', opacity: 0.6 }}>
                      {sortConfig.column === 'student_name' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                    <div style={{ marginTop: 2, fontSize: 11, fontWeight: 500, letterSpacing: 0, textTransform: 'none', opacity: 0.75 }}>{GRADING_MACHINE_LEGEND}</div>
                  </th>
                  <th
                    onClick={() => handleSort('image_name')}
                    style={{ cursor: 'pointer', userSelect: 'none', width: 110, whiteSpace: 'nowrap' }}
                  >
                    Image
                    <span style={{ marginLeft: '6px', fontSize: '12px', opacity: 0.6 }}>
                      {sortConfig.column === 'image_name' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort('class')}
                    title="Class"
                    style={{ cursor: 'pointer', userSelect: 'none', width: 42, whiteSpace: 'nowrap' }}
                  >
                    Cl
                    <span style={{ marginLeft: '4px', fontSize: '12px', opacity: 0.6 }}>
                      {sortConfig.column === 'class' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort('section')}
                    title="Section"
                    style={{ cursor: 'pointer', userSelect: 'none', width: 50, whiteSpace: 'nowrap' }}
                  >
                    Sec
                    <span style={{ marginLeft: '4px', fontSize: '12px', opacity: 0.6 }}>
                      {sortConfig.column === 'section' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </th>
                  <th onClick={() => handleSort('subject')} style={{ cursor: 'pointer', userSelect: 'none', width: 90, whiteSpace: 'nowrap' }}>
                    Subject
                    <span style={{ marginLeft: '6px', fontSize: '12px', opacity: 0.6 }}>
                      {sortConfig.column === 'subject' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </th>
                  <th onClick={() => handleSort('marks')} style={{ cursor: 'pointer', userSelect: 'none', width: 80, whiteSpace: 'nowrap' }}>
                    Marks
                    <span style={{ marginLeft: '6px', fontSize: '12px', opacity: 0.6 }}>
                      {sortConfig.column === 'marks' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </th>
                  {/* OCR column — only meaningful for two-step runs
                      (English-2 / Urdu-2). Kept visible always so rows from a
                      previous two-step run still surface their transcript.
                      Cell renders "—" for single-call rows that have no
                      ocr_text. */}
                  <th style={{ userSelect: 'none', textAlign: 'center', width: 110 }}>
                    <i className="fa-solid fa-language" style={{ marginRight: 4, color: '#7C3AED' }}></i>
                    OCR
                  </th>
                  <th onClick={() => handleSort('comments')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Comments
                    <span style={{ marginLeft: '6px', fontSize: '12px', opacity: 0.6 }}>
                      {sortConfig.column === 'comments' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                    </span>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedAssessmentResults.map((result, rowIndex) => {
                  // Memoized per-row parse (consistency + over-marked + dup-header
                  // + corrected table). Avoids re-parsing every row on each render.
                  const rowMeta = metaFor(result);
                  const isDuplicate = hasDuplicateRollNumber(result);
                  const isSelected = selectedResults.includes(result.id);
                  const isError = result.status === 'error';
                  const isOverMarked = rowMeta.isOverMarked;
                  const isDupHeader = rowMeta.isDupHeader;
                  // OCR step 1 failed — either too many (unclear) words, or
                  // backend marked the row as failed. Step 2 was skipped, so
                  // the row has no marks; we want it to read as a hard error.
                  const isOcrFailed = result.ocr_status === 'unclear' || result.ocr_status === 'failed';
                  const rollCodeMatch = result.student_name?.match(/\(([^)]+)\)/);
                  const rollCode = rollCodeMatch ? rollCodeMatch[1] : '';
                  const hasInvalidRoll = !/^[A-Za-z]\d{2}$/.test(rollCode);
                  const ocrNoiseNotes = noiseNotes(result.ocr_text, result.ocr_chatter_ok);
                  const suspectedChatterOnly = ocrNoiseNotes.length > 0 && ocrNoiseNotes.every(isSuspectedNote);
                  const unexpectedBlanks = result.ocr_text != null
                    ? unexpectedBlankPages(result.ocr_text)
                    : String(result.unexpected_blank_pages || '').split(',').map((s) => s.trim()).filter(Boolean);
                  const unmatchedLabels = listUnmatchedQuestionLabels(
                    (rowMeta && rowMeta.correctedTableMd) || result.table_comments,
                    repairForResult(result).allowedIds
                  );

                  // Priority: selected > over-marked > OCR-failed > duplicate-header > invalid roll > duplicate-roll > error
                  // Stripped "/N" suffix marks (`_marksStripped`) are flagged
                  // ONLY by the scissors badge next to the marks cell — no row
                  // tint, since stripping is a low-severity AI quirk that
                  // shouldn't shout louder than over-max or duplicate issues.
                  // Use CSS variables for warning-row backgrounds so dark
                  // theme can swap the light pastels for translucent tints
                  // that don't blow out the light text. Light-theme fallbacks
                  // keep the original colours.
                  let backgroundColor = 'transparent';
                  let borderLeft = 'none';
                  if (isSelected) {
                    backgroundColor = 'var(--row-selected-bg, #E0F2FE)';
                    borderLeft = 'none';
                  } else if (isOverMarked) {
                    backgroundColor = 'var(--row-warn-red-bg, #FEE2E2)';
                    borderLeft = '4px solid #DC2626';
                  } else if (isOcrFailed) {
                    backgroundColor = 'var(--row-warn-red-bg, #FEE2E2)';
                    borderLeft = '4px solid #DC2626';
                  } else if (isDupHeader) {
                    backgroundColor = 'var(--row-warn-amber-bg, #FEF3C7)';
                    borderLeft = '4px solid #D97706';
                  } else if (hasInvalidRoll) {
                    backgroundColor = 'var(--row-warn-yellow-bg, #FEF9C3)';
                  } else if (isDuplicate) {
                    backgroundColor = 'var(--row-warn-red-bg, #FEE2E2)';
                    borderLeft = '4px solid #DC2626';
                  } else if (isError) {
                    backgroundColor = 'var(--row-warn-amber-bg, #FEF3C7)';
                  } else if (unexpectedBlanks.length) {
                    backgroundColor = 'var(--row-warn-red-bg, #FEE2E2)';
                    borderLeft = '4px solid #DC2626';
                  }
                  
                  return (
                  <tr
                    key={result.id}
                    onClick={(e) => {
                      // Single-click anywhere on the row toggles selection,
                      // EXCEPT when the click landed on a form control or
                      // button (those have their own handlers — we don't
                      // want to double-fire) or while a cell on this row is
                      // being edited (clicking inside the edit input must
                      // not deselect the row).
                      const tag = (e.target.tagName || '').toLowerCase();
                      if (['input', 'button', 'select', 'textarea', 'a', 'label'].includes(tag)) return;
                      if (editingCell && editingCell.id === result.id) return;
                      handleSelectResult(result.id, rowIndex, e.shiftKey);
                    }}
                    style={{
                      background: backgroundColor,
                      borderLeft,
                      cursor: 'pointer'
                    }}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedResults.includes(result.id)}
                        // We drive selection from onClick because it carries
                        // shiftKey reliably across browsers; onChange does not.
                        // preventDefault + an empty onChange keeps React from
                        // either firing a duplicate handler or warning about
                        // a missing change handler on a controlled input.
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectResult(result.id, rowIndex, e.shiftKey);
                          e.preventDefault();
                        }}
                        onChange={() => {}}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td 
                      onDoubleClick={() => handleCellEdit(result.id, 'student_name', result.student_name)}
                      style={{ cursor: 'pointer' }}
                      title="Double-click to edit"
                    >
                      {editingCell?.id === result.id && editingCell?.field === 'student_name' ? (
                        <input
                          type="text"
                          value={editingCell.value}
                          onChange={(e) => handleCellChange(e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={handleCellKeyDown}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '2px solid #3B82F6',
                            borderRadius: '4px',
                            fontSize: 'inherit',
                            fontFamily: 'inherit'
                          }}
                        />
                      ) : (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                            <span>{result.student_name}</span>
                            {unmatchedLabels.length ? (
                              <span
                                title={unmatchedLabels.join(', ')}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '1px 6px',
                                  borderRadius: 6,
                                  background: 'rgba(220, 38, 38, 0.1)',
                                  color: '#991B1B',
                                  border: '1px solid #DC2626',
                                  fontSize: 10,
                                  fontWeight: 800,
                                  lineHeight: 1.35,
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                              >
                                Unmatched {unmatchedLabels.length === 1 ? unmatchedLabels[0] : `(${unmatchedLabels.length})`}
                              </span>
                            ) : null}
                            {ocrNoiseNotes.length ? (
                              <span
                                title={ocrNoiseNotes.join(', ')}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '1px 6px',
                                  borderRadius: 6,
                                  background: 'rgba(220, 38, 38, 0.1)',
                                  color: '#991B1B',
                                  border: '1px solid #DC2626',
                                  fontSize: 10,
                                  fontWeight: 800,
                                  lineHeight: 1.35,
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                              >
                                {suspectedChatterOnly ? 'Suspected chatter' : 'Bad OCR'}
                              </span>
                            ) : null}
                            {unexpectedBlanks.length ? (
                              <span
                                title={`Unexpected blank page (${unexpectedBlanks.join(', ')})`}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '1px 6px',
                                  borderRadius: 6,
                                  background: 'rgba(220, 38, 38, 0.1)',
                                  color: '#991B1B',
                                  border: '1px solid #DC2626',
                                  fontSize: 10,
                                  fontWeight: 800,
                                  lineHeight: 1.35,
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                              >
                                Unexpected blank page
                              </span>
                            ) : null}
                            {result.ocr_missing_pages ? (
                              <span
                                title={formatMissingImageNumbers(result.ocr_missing_pages)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '1px 6px',
                                  borderRadius: 999,
                                  background: '#fff7ed',
                                  color: '#c2410c',
                                  border: '1px solid #fdba74',
                                  fontSize: 10,
                                  fontWeight: 700,
                                  letterSpacing: 0.2,
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                              >
                                ocr missing
                              </span>
                            ) : null}
                          </span>
                          <GradingMachineBadge host={result.grading_host} />
                        </span>
                      )}
                    </td>
                    <td 
                      onDoubleClick={() => handleCellEdit(result.id, 'image_name', result.image_name)}
                      style={{ cursor: 'pointer' }}
                      title="Double-click to edit"
                    >
                      {editingCell?.id === result.id && editingCell?.field === 'image_name' ? (
                        <input
                          type="text"
                          value={editingCell.value}
                          onChange={(e) => handleCellChange(e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={handleCellKeyDown}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '2px solid #3B82F6',
                            borderRadius: '4px',
                            fontSize: 'inherit',
                            fontFamily: 'inherit'
                          }}
                        />
                      ) : (
                        result.image_name
                      )}
                    </td>
                    <td 
                      onDoubleClick={() => handleCellEdit(result.id, 'class', result.class || '-')}
                      style={{ cursor: 'pointer' }}
                      title="Double-click to edit"
                    >
                      {editingCell?.id === result.id && editingCell?.field === 'class' ? (
                        <input
                          type="text"
                          value={editingCell.value}
                          onChange={(e) => handleCellChange(e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={handleCellKeyDown}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '2px solid #3B82F6',
                            borderRadius: '4px',
                            fontSize: 'inherit',
                            fontFamily: 'inherit'
                          }}
                        />
                      ) : (
                        result.class || '-'
                      )}
                    </td>
                    <td 
                      onDoubleClick={() => handleCellEdit(result.id, 'section', result.section || '-')}
                      style={{ cursor: 'pointer' }}
                      title="Double-click to edit"
                    >
                      {editingCell?.id === result.id && editingCell?.field === 'section' ? (
                        <input
                          type="text"
                          value={editingCell.value}
                          onChange={(e) => handleCellChange(e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={handleCellKeyDown}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '2px solid #3B82F6',
                            borderRadius: '4px',
                            fontSize: 'inherit',
                            fontFamily: 'inherit'
                          }}
                        />
                      ) : (
                        result.section || '-'
                      )}
                    </td>
                    <td 
                      onDoubleClick={() => handleCellEdit(result.id, 'subject', result.subject || '-')}
                      style={{ cursor: 'pointer' }}
                      title="Double-click to edit"
                    >
                      {editingCell?.id === result.id && editingCell?.field === 'subject' ? (
                        <input
                          type="text"
                          value={editingCell.value}
                          onChange={(e) => handleCellChange(e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={handleCellKeyDown}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '2px solid #3B82F6',
                            borderRadius: '4px',
                            fontSize: 'inherit',
                            fontFamily: 'inherit'
                          }}
                        />
                      ) : (
                        result.subject || '-'
                      )}
                    </td>
                    <td
                      onDoubleClick={() => handleCellEdit(result.id, 'marks', result.marks)}
                      style={{
                        fontWeight: '600',
                        // Highlight the cell red when the obtained marks exceed
                        // the total (isOverMarked) so an over-max score stands out.
                        color: isOverMarked ? '#B91C1C' : result._autoCorrect ? '#8B5CF6' : result.status === 'error' ? '#DC2626' : '#059669',
                        background: isOverMarked ? 'rgba(220, 38, 38, 0.18)' : undefined,
                        boxShadow: isOverMarked ? 'inset 0 0 0 2px rgba(220, 38, 38, 0.55)' : undefined,
                        cursor: 'pointer'
                      }}
                      title={isOverMarked ? 'Marks exceed the total — please review' : 'Double-click to edit'}
                    >
                      {editingCell?.id === result.id && editingCell?.field === 'marks' ? (
                        <input
                          type="text"
                          value={editingCell.value}
                          onChange={(e) => handleCellChange(e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={handleCellKeyDown}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '2px solid #3B82F6',
                            borderRadius: '4px',
                            fontSize: 'inherit',
                            fontFamily: 'inherit',
                            fontWeight: '600'
                          }}
                        />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {result.marks}
                          {isOverMarked && (
                            <i
                              className="fa-solid fa-arrow-up"
                              style={{ color: '#DC2626', fontSize: '12px', cursor: 'default', fontWeight: 'bold' }}
                              title="Obtained marks exceed maximum marks — please review"
                            />
                          )}
                          {isDupHeader && (
                            <i
                              className="fa-solid fa-rotate-right"
                              style={{ color: '#D97706', fontSize: '12px', cursor: 'default', fontWeight: 'bold' }}
                              title="AI emitted the &quot;Total marks:&quot; header more than once — please review"
                            />
                          )}
                          {result._marksStripped && (
                            <i
                              className="fa-solid fa-scissors"
                              style={{ color: '#DB2777', fontSize: '12px', cursor: 'default', fontWeight: 'bold' }}
                              title={`AI returned marks as "${result._marksStripped.from}". Auto-stripped the trailing "/${result._marksStripped.total}" — please verify.`}
                            />
                          )}
                          {result._autoCorrect && (
                            <i className="fa-solid fa-wand-magic-sparkles"
                               style={{ color: '#8B5CF6', fontSize: '11px', cursor: 'default' }}
                               title={`Auto-corrected from ${result._autoCorrect.from}\nReason: ${result._autoCorrect.details}`} />
                          )}
                          {!result._autoCorrect && (() => {
                            const c = rowMeta.consistency;
                            if (c.statedTotal === null || c.subScores.length === 0) return null;
                            return c.isConsistent ? (
                              <i className="fa-solid fa-circle-check" style={{ color: '#10B981', fontSize: '12px' }} title={`Consistent: ${c.subScores.join('+')}=${c.calculatedTotal}`} />
                            ) : (
                              <i className="fa-solid fa-triangle-exclamation" style={{ color: '#F59E0B', fontSize: '12px' }} title={`${c.details || `Mismatch: ${c.subScores.join('+')}=${c.calculatedTotal}, total=${c.statedTotal}`} Reload the Check tab for this folder to remove this sign.`} />
                            );
                          })()}
                          {/* Copy marks + comments — lives in the Marks column
                              so it sits right next to the value it copies. */}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCopyResult(result); }}
                            onDoubleClick={(e) => e.stopPropagation()}
                            title="Copy marks & comments (paste into Excel / EduPage)"
                            style={{
                              background: '#059669',
                              color: 'white',
                              border: 'none',
                              padding: '3px 7px',
                              borderRadius: '5px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              marginLeft: '2px'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#047857'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#059669'}
                          >
                            <i className="fa-solid fa-copy"></i>
                          </button>
                        </div>
                      )}
                    </td>
                    {/* OCR cell. Three states:
                          - has ocr_text + ocr_status === 'ok': purple "View" button
                          - ocr_status === 'unclear' or 'failed': red triangle, click opens modal
                          - no ocr data (English row, or two-step not yet run): em dash */}
                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      {(() => {
                        const ocrText = result.ocr_text || '';
                        const ocrStatus = result.ocr_status || (ocrText ? 'ok' : null);
                        const imageNote = result.ocr_image_note || '';
                        const fellBack = imageNote.includes('rejected');
                        if (!ocrText && !ocrStatus && !imageNote) {
                          return <span style={{ color: 'var(--text-muted, #9CA3AF)', fontSize: 12 }}>—</span>;
                        }
                        if (!ocrText && !ocrStatus) {
                          return (
                            <button
                              onClick={() => setViewingOcr(result)}
                              title={imageNote}
                              style={{
                                background: fellBack ? 'rgba(245, 158, 11, 0.15)' : 'rgba(16, 185, 129, 0.12)',
                                color: fellBack ? '#92400E' : '#065F46',
                                border: fellBack ? '1px solid #FCD34D' : '1px solid #6EE7B7',
                                borderRadius: 6,
                                padding: '4px 8px',
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: 'pointer'
                              }}
                            >
                              {fellBack ? 'Fallback' : 'Images'}
                            </button>
                          );
                        }
                        const failed = ocrStatus === 'unclear' || ocrStatus === 'failed';
                        return (
                          <button
                            onClick={() => {
                              pendingOcrPageRef.current = failed ? firstProblemPage(result.comments, result.ocr_text) : 0;
                              setViewingOcr(result);
                            }}
                            title={
                              (failed
                                ? `OCR ${ocrStatus} (${result.ocr_unclear_ratio != null
                                    ? Math.round(result.ocr_unclear_ratio * 100) + '% unclear'
                                    : 'transcript unreadable'}). Click to view / edit.`
                                : 'View / edit OCR transcript')
                              + (imageNote ? `\n${imageNote}` : '')
                            }
                            style={{
                              background: failed ? 'rgba(220, 38, 38, 0.12)' : 'rgba(124, 58, 237, 0.12)',
                              color: failed ? '#DC2626' : '#7C3AED',
                              border: failed ? '1px solid #FCA5A5' : '1px solid rgba(124, 58, 237, 0.3)',
                              borderRadius: 6,
                              padding: '4px 10px',
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 5
                            }}
                          >
                            <i className={`fa-solid ${failed ? 'fa-triangle-exclamation' : 'fa-file-lines'}`}></i>
                            {failed
                              ? (ocrStatus === 'failed' ? 'Failed' : 'Unclear')
                              : 'View'}
                            {fellBack && (
                              <i className="fa-solid fa-image" title={imageNote} style={{ marginLeft: 4 }}></i>
                            )}
                          </button>
                        );
                      })()}
                    </td>
                    <td
                      onDoubleClick={() => handleCellEdit(result.id, 'comments', result.comments)}
                      style={{ 
                        maxWidth: '300px', 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        cursor: 'pointer'
                      }}
                      title="Double-click to edit"
                    >
                      {editingCell?.id === result.id && editingCell?.field === 'comments' ? (
                        <input
                          type="text"
                          value={editingCell.value}
                          onChange={(e) => handleCellChange(e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyDown={handleCellKeyDown}
                          autoFocus
                          style={{
                            width: '100%',
                            padding: '4px',
                            border: '2px solid #3B82F6',
                            borderRadius: '4px',
                            fontSize: 'inherit',
                            fontFamily: 'inherit'
                          }}
                        />
                      ) : (
                        <>
                          {/* Yellow pill flagged when the AI skipped the
                              verification table (BLOCK A). Teacher sees the
                              one-line comments only — no internal sum check
                              to compare against. */}
                          {result.status === 'success' && !result.table_comments && (
                            <span
                              title="AI skipped the verification table — open the View Comments modal to inspect the marks."
                              style={{
                                display: 'inline-block',
                                background: '#fef3c7',
                                color: '#92400e',
                                border: '1px solid #fcd34d',
                                borderRadius: 999,
                                padding: '1px 8px',
                                fontSize: 10,
                                fontWeight: 700,
                                marginRight: 6,
                                verticalAlign: 'middle'
                              }}
                            >
                              ⚠ Table N/A
                            </span>
                          )}
                          {rowMeta.oneLineComments || result.comments}
                        </>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleViewComments(result)}
                          title="View full comments"
                          style={{
                            background: '#3B82F6',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#2563EB'}
                          onMouseLeave={(e) => e.target.style.background = '#3B82F6'}
                        >
                          <i className="fa-solid fa-eye"></i>
                        </button>
                        <button
                          onClick={() => handlePreviewImage(result)}
                          disabled={previewLoading === result.image_name}
                          title="View scan image"
                          style={{
                            background: '#7C3AED',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <i className="fa-solid fa-image"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteResult(result.id)}
                          title="Delete result"
                          style={{
                            background: '#EF4444',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#DC2626'}
                          onMouseLeave={(e) => e.target.style.background = '#EF4444'}
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </div>{/* /checking-right-col */}
      </div>{/* /checking-split-layout */}

      {/* View All Comments Modal */}
      {showAllComments && (
        <div
          className="modal-overlay"
          onClick={() => { setShowAllComments(false); setShowRubricPane(false); }}
          // When the image preview is also open, anchor this modal to
          // the LEFT half of the viewport so the image preview can sit
          // on the right half side-by-side. justifyContent override
          // bypasses the centered-flex default from the .modal-overlay
          // CSS class. paddingRight reserves the right half for the
          // image preview to avoid the modal-content overflowing into it.
          //
          // zIndex bumped to 10000 — the FastGrader app header uses
          // z-index 1000 (same as the default .modal-overlay), and
          // header-wins-stacking-order means the header was bleeding
          // through the top of the modal in split view. 10000 keeps
          // it just below the image preview's 10001 so the image stays
          // on top within the right half.
          //
          // Split-view layouts (right space reserved via paddingRight):
          //   image + OCR (3-pane)            → 60vw  (image 30 + OCR 30)
          //   rubric only (2-pane)            → 42vw
          //   image + OCR + rubric (4-pane)   → 72vw  (image 24 + OCR 24 + rubric 24)
          // The image preview, OCR pane and rubric pane are independent fixed
          // panels; they can all be open at once, so comments shrinks to fit.
          style={(() => {
            const imgOpen = !!(previewImage && previewImage.images && previewImage.images.length > 0);
            const layout = commentSideLayout({ image: imgOpen, transcript: imgOpen, rubric: showRubricPane, wide: false });
            if (!layout || !layout.paddingRight) return undefined;
            return { justifyContent: 'flex-start', paddingRight: layout.paddingRight, background: 'rgba(0,0,0,0.5)', zIndex: 10000 };
          })()}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={(() => {
              const imgOpen = !!(previewImage && previewImage.images && previewImage.images.length > 0);
              const layout = commentSideLayout({ image: imgOpen, transcript: imgOpen, rubric: showRubricPane, wide: false });
              if (!layout || !layout.paddingRight) return { maxWidth: '1100px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' };
              return { maxWidth: 'none', width: layout.commentsWidth, margin: '20px 0 20px 16px', maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column' };
            })()}
          >
            <div className="modal-header">
              <div>
                <h3 className="modal-title">All Comments ({sortedAssessmentResults.length})</h3>
                <p className="modal-subtitle">
                  {allCommentsView === 'table'
                    ? 'Showing verification tables for all assessment results.'
                    : 'Showing one-line comments for all assessment results.'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {/* Rubrics toggle — sits before the One-line | Tables toggle.
                    Opens the loaded marking rubric in a side-pane on the right
                    (closeable via its own cross button). Only useful once a
                    rubric is actually loaded, so it's hidden otherwise. */}
                {(folderPath || autoRubricText || rubricText || (autoPdfRubric || pdfRubric)?.text) && (
                  <button
                    onClick={toggleRubricPane}
                    title={showRubricPane ? 'Hide rubric' : 'Show rubric'}
                    style={{
                      padding: '7px 14px',
                      border: showRubricPane ? 'none' : '1px solid var(--border-color, #ddd)',
                      borderRadius: 8,
                      background: showRubricPane
                        ? 'linear-gradient(135deg, #3B82F6, #2563EB)'
                        : 'var(--select-bg, white)',
                      color: showRubricPane ? 'white' : 'var(--text-primary)',
                      cursor: 'pointer',
                      fontSize: 13,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <i className="fa-solid fa-book-open"></i>
                    Rubrics
                  </button>
                )}
                {/* View toggle: One-line | Tables. Defaults to one-line so
                    the modal opens to its previous behaviour. */}
                <div style={{
                  display: 'inline-flex',
                  border: '1px solid var(--border-color, #ddd)',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: 'var(--select-bg, white)'
                }}>
                  {[
                    { v: 'oneline', label: 'One-line', icon: 'fa-align-left' },
                    { v: 'table',   label: 'Tables',   icon: 'fa-table-cells' }
                  ].map(opt => (
                    <button
                      key={opt.v}
                      onClick={() => setAllCommentsView(opt.v)}
                      style={{
                        padding: '7px 14px',
                        border: 'none',
                        background: allCommentsView === opt.v
                          ? 'linear-gradient(135deg, #8B5CF6, #6D28D9)'
                          : 'transparent',
                        color: allCommentsView === opt.v ? 'white' : 'var(--text-primary)',
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <i className={`fa-solid ${opt.icon}`}></i>
                      {opt.label}
                    </button>
                  ))}
                </div>
                {duplicateGroupInfo.badIds.size > 0 && (
                  <button
                    onClick={handleSelectBadDuplicates}
                    style={{
                      background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <i className="fa-solid fa-filter-circle-xmark"></i>
                    Select Bad Duplicates ({duplicateGroupInfo.badIds.size})
                  </button>
                )}
                <button
                  onClick={() => { setShowAllComments(false); setShowRubricPane(false); }}
                  className="modal-close-btn"
                >
                  <i className="fa-solid fa-times"></i> Close
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 0' }}>
              {sortedAssessmentResults.map((result, idx) => {
                const rowMeta = metaFor(result);
                const consistency = rowMeta.consistency;
                const isDuplicate = hasDuplicateRollNumber(result);
                const isBest = duplicateGroupInfo.bestIds.has(result.id);
                const isBad = duplicateGroupInfo.badIds.has(result.id);
                const isExaminerChoice = duplicateGroupInfo.examinerChoiceIds.has(result.id);
                const isMismatchBest = duplicateGroupInfo.mismatchBestIds.has(result.id);
                
                // Determine border color based on consistency + duplicate status
                let borderLeftColor = result.status === 'error' ? '#EF4444' : '#10B981';
                let bgColor = result.status === 'error' ? 'rgba(239, 68, 68, 0.06)' : 'var(--surface, #F9FAFB)';
                if (isDuplicate && isBest) {
                  borderLeftColor = '#10B981';
                  bgColor = 'rgba(16, 185, 129, 0.06)';
                } else if (isDuplicate && isExaminerChoice) {
                  borderLeftColor = '#3B82F6';
                  bgColor = 'rgba(59, 130, 246, 0.06)';
                } else if (isDuplicate && isMismatchBest) {
                  borderLeftColor = '#F59E0B';
                  bgColor = 'rgba(245, 158, 11, 0.08)';
                } else if (isDuplicate && isBad) {
                  borderLeftColor = '#F59E0B';
                  bgColor = 'rgba(245, 158, 11, 0.04)';
                } else if (isDuplicate) {
                  borderLeftColor = '#DC2626';
                  bgColor = 'rgba(220, 38, 38, 0.06)';
                }

                return (
                <div
                  key={result.id}
                  style={{
                    padding: '14px 16px',
                    marginBottom: '10px',
                    background: bgColor,
                    borderRadius: '10px',
                    border: `1px solid ${isDuplicate ? (isBad ? 'rgba(245, 158, 11, 0.3)' : isBest ? 'rgba(16, 185, 129, 0.3)' : 'rgba(220, 38, 38, 0.2)') : result.status === 'error' ? 'rgba(239, 68, 68, 0.2)' : 'var(--border, #E5E7EB)'}`,
                    borderLeft: `4px solid ${borderLeftColor}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{
                        background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                        color: 'white',
                        width: '26px',
                        height: '26px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        flexShrink: 0
                      }}>{idx + 1}</span>
                      <span style={{ fontWeight: '700', fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                        {result.student_name}
                      </span>
                      {/* Image filename chip — clickable, opens the
                          scanned-image preview modal. Mirrors the
                          equivalent chip in the View All OCR modal so
                          both views feel the same. */}
                      {result.image_name && (
                        <button
                          type="button"
                          onClick={() => handlePreviewImage(result)}
                          disabled={previewLoading === result.image_name}
                          title="Click to view the scanned image"
                          style={{
                            background: 'rgba(124, 58, 237, 0.10)',
                            color: '#6D28D9',
                            border: '1px solid rgba(124, 58, 237, 0.35)',
                            borderRadius: 6,
                            padding: '2px 8px',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            cursor: previewLoading === result.image_name ? 'wait' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                        >
                          <i className={`fa-solid ${previewLoading === result.image_name ? 'fa-spinner fa-spin' : 'fa-image'}`}></i>
                          {result.image_name}
                        </button>
                      )}
                      {/* Duplicate + Consistency badges */}
                      {isDuplicate && isBest && (
                        <span style={{
                          background: 'linear-gradient(135deg, #10B981, #059669)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '700',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <i className="fa-solid fa-check"></i> Recommended
                        </span>
                      )}
                      {isDuplicate && isExaminerChoice && (
                        <span style={{
                          background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '700',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <i className="fa-solid fa-user-check"></i> Examiner: Choose one
                        </span>
                      )}
                      {isDuplicate && isMismatchBest && (
                        <span style={{
                          background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '700',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <i className="fa-solid fa-triangle-exclamation"></i> Best available (has issues)
                        </span>
                      )}
                      {isDuplicate && isBad && (
                        <span style={{
                          background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.7rem',
                          fontWeight: '700',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <i className="fa-solid fa-triangle-exclamation"></i> Duplicate - Delete
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    {!(editingComment && editingComment.id === result.id) && (
                      <button
                        type="button"
                        onClick={() => beginCommentEdit(result, rowMeta)}
                        title={allCommentsView === 'table' ? 'Edit this verification table' : 'Edit this comment'}
                        style={{
                          background: 'transparent',
                          color: '#6D28D9',
                          border: '1px solid rgba(109, 40, 217, 0.45)',
                          borderRadius: 6,
                          padding: '2px 8px',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                      >
                        Edit
                      </button>
                    )}
                    <span style={{
                      fontWeight: '700',
                      fontSize: '0.95rem',
                      color: result._autoCorrect ? '#8B5CF6' : result.status === 'error' ? '#DC2626' : '#059669',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      {gradedObtainedMarks(result.table_comments, result) || result.marks}
                      {result._autoCorrect && (
                        <span style={{
                          background: 'rgba(139, 92, 246, 0.12)',
                          color: '#8B5CF6',
                          padding: '1px 6px',
                          borderRadius: '8px',
                          fontSize: '0.68rem',
                          fontWeight: '700',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px'
                        }}
                          title={`AI stated ${result._autoCorrect.from} — auto-corrected to ${result.marks}\nReason: ${result._autoCorrect.details}`}
                        >
                          <i className="fa-solid fa-wand-magic-sparkles"></i> AC
                        </span>
                      )}
                    </span>
                    </div>
                  </div>
                  {/* Auto-correct detail hint */}
                  {result._autoCorrect && (
                    <div style={{
                      fontSize: '0.74rem',
                      color: '#8B5CF6',
                      background: 'rgba(139, 92, 246, 0.07)',
                      border: '1px solid rgba(139, 92, 246, 0.2)',
                      borderRadius: '6px',
                      padding: '4px 8px',
                      marginBottom: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <i className="fa-solid fa-wand-magic-sparkles"></i>
                      <span>Auto-corrected: AI stated <strong>{result._autoCorrect.from}</strong> → corrected to <strong>{result.marks}</strong> &nbsp;|&nbsp; {result._autoCorrect.details}</span>
                    </div>
                  )}
                  {/* Consistency badge — hidden when auto-correct bar already covers the detail */}
                  {consistency.statedTotal !== null && consistency.subScores.length > 0 && !result._autoCorrect && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginBottom: '6px',
                      fontSize: '0.78rem',
                      fontWeight: '600'
                    }}>
                      {consistency.isConsistent ? (
                        <span style={{
                          color: '#059669',
                          background: 'rgba(16, 185, 129, 0.1)',
                          padding: '2px 8px',
                          borderRadius: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <i className="fa-solid fa-circle-check"></i>
                          Consistent ({consistency.subScores.join(' + ')} = {consistency.calculatedTotal})
                        </span>
                      ) : (
                        <span style={{
                          color: '#D97706',
                          background: 'rgba(245, 158, 11, 0.1)',
                          padding: '2px 8px',
                          borderRadius: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <i className="fa-solid fa-triangle-exclamation"></i>
                          Mismatch: {consistency.details || `${consistency.subScores.join('+')}=${consistency.calculatedTotal}, total says ${consistency.statedTotal}`}
                        </span>
                      )}
                    </div>
                  )}
                  {editingComment && editingComment.id === result.id ? (
                    <div>
                      <textarea
                        dir="auto"
                        value={commentDraft}
                        onChange={(e) => { setCommentDraft(e.target.value); setCommentEditNote(''); }}
                        onKeyDown={(e) => {
                          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                            e.preventDefault();
                            saveCommentEdit();
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            e.stopPropagation();
                            cancelCommentEdit();
                          }
                        }}
                        spellCheck={false}
                        style={{
                          width: '100%',
                          minHeight: 140,
                          resize: 'vertical',
                          boxSizing: 'border-box',
                          padding: '10px 12px',
                          border: '1px solid var(--border, #E5E7EB)',
                          borderRadius: 6,
                          font: 'inherit',
                          fontSize: '0.9rem',
                          lineHeight: 1.55,
                          color: 'var(--text-primary)',
                          background: 'var(--card-bg, #fff)'
                        }}
                      />
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={saveCommentEdit}
                          disabled={commentSaving || commentDraft === editingComment.original}
                          style={{
                            background: commentSaving || commentDraft === editingComment.original ? '#e5e7eb' : '#059669',
                            color: commentSaving || commentDraft === editingComment.original ? '#6b7280' : 'white',
                            border: 'none',
                            borderRadius: 6,
                            padding: '4px 12px',
                            fontWeight: 700,
                            fontSize: 12,
                            cursor: commentSaving || commentDraft === editingComment.original ? 'default' : 'pointer'
                          }}
                        >
                          {commentSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={cancelCommentEdit}
                          disabled={commentSaving}
                          style={{
                            background: 'transparent',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border, #E5E7EB)',
                            borderRadius: 6,
                            padding: '4px 12px',
                            fontWeight: 700,
                            fontSize: 12,
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                        <span style={{ fontSize: 12, color: commentEditNote ? '#b91c1c' : 'var(--text-secondary, #6b7280)' }}>
                          {commentEditNote || (editingComment.kind === 'table'
                            ? 'Saving rebuilds the one-line comment. The total mark becomes the table sum.'
                            : 'Saving sets the total mark from Total marks in this comment.')}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {allCommentsView === 'table' && (
                        result.table_comments ? (
                          <div style={{
                            padding: 6,
                            background: 'var(--surface, #f8fafc)',
                            borderRadius: 6,
                            border: '1px solid var(--border-color, #e2e8f0)',
                            overflow: 'auto'
                          }}>
                            {renderMarkdownTable(rowMeta.correctedTableMd, result.image_name || result.id) || (
                              <div style={{ fontSize: 12, color: '#92400e' }}>Table preview unavailable.</div>
                            )}
                          </div>
                        ) : (
                          <div style={{
                            fontSize: 12,
                            color: '#92400e',
                            background: '#fef3c7',
                            border: '1px solid #fcd34d',
                            padding: '6px 10px',
                            borderRadius: 6
                          }}>
                            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />
                            No verification table on file for this row.
                          </div>
                        )
                      )}
                      {allCommentsView === 'oneline' && (
                        <p style={{
                          margin: 0,
                          fontSize: '0.9rem',
                          lineHeight: '1.6',
                          color: 'var(--text-secondary, #4B5563)',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {oneLinePreviewText(result, rowMeta)
                            ? renderCommentsWithOverMaxHighlight(oneLinePreviewText(result, rowMeta))
                            : 'No comments'}
                        </p>
                      )}
                    </>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* View All OCR Modal — lists every result's stored ocr_text +
          ocr_status so the teacher can scan transcripts in one place.
          Rows without a transcript (legacy single-call runs) show a
          muted "(no transcript)" stub. */}
      {showAllOcr && (
        <div
          className="modal-overlay"
          onClick={() => setShowAllOcr(false)}
          // Same split-view behavior as the View All Comments modal —
          // anchor to LEFT half when the image preview is open, so
          // OCR transcript + scanned image can be reviewed together.
          // zIndex 10000 to sit above the FastGrader header (same fix
          // as the Comments overlay above — see that comment for the
          // stacking-order rationale).
          style={(previewImage && previewImage.images && previewImage.images.length > 0) ? {
            justifyContent: 'flex-start',
            paddingRight: '50vw',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 10000
          } : undefined}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={(previewImage && previewImage.images && previewImage.images.length > 0) ? {
              maxWidth: 'none',
              width: 'calc(50vw - 24px)',
              margin: '20px 0 20px 16px',
              maxHeight: 'calc(100vh - 40px)',
              display: 'flex',
              flexDirection: 'column'
            } : { maxWidth: '1100px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="modal-header">
              <div>
                <h3 className="modal-title">All OCR Transcripts ({sortedAssessmentResults.length})</h3>
                <p className="modal-subtitle">
                  Stored OCR text for every assessment result. Rows without a
                  transcript came from a single-call run that didn't
                  persist OCR separately.
                </p>
              </div>
              <button
                className="btn-close"
                onClick={() => setShowAllOcr(false)}
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', flex: 1, padding: '12px 16px' }}>
              {sortedAssessmentResults.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                  No assessment results to show.
                </p>
              ) : sortedAssessmentResults.map((result, idx) => {
                const ocrText = (result.ocr_text || '').trim();
                const ocrStatus = (result.ocr_status || '').toLowerCase();
                const hasText = !!ocrText;
                // Status pill — green ok, amber unclear, red failed,
                // grey "(no transcript)" for legacy rows. Tooltip
                // includes the unclear ratio when available.
                const statusMeta = ocrStatus === 'ok'
                  ? { bg: 'linear-gradient(135deg, #10B981, #059669)', label: 'OCR OK' }
                  : ocrStatus === 'unclear'
                    ? { bg: 'linear-gradient(135deg, #F59E0B, #D97706)', label: 'OCR unclear' }
                    : ocrStatus === 'failed'
                      ? { bg: 'linear-gradient(135deg, #EF4444, #B91C1C)', label: 'OCR failed' }
                      : { bg: 'linear-gradient(135deg, #94A3B8, #64748B)', label: 'no transcript' };
                const ratioPct = typeof result.ocr_unclear_ratio === 'number'
                  ? `${Math.round(result.ocr_unclear_ratio * 100)}% unclear`
                  : null;
                return (
                  <div
                    key={result.id}
                    style={{
                      padding: '14px 16px',
                      marginBottom: '10px',
                      background: hasText ? 'rgba(59,130,246,0.04)' : 'rgba(148,163,184,0.06)',
                      borderRadius: '10px',
                      border: '1px solid var(--border, #E5E7EB)',
                      borderLeft: `4px solid ${hasText ? '#3B82F6' : '#94A3B8'}`
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '8px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span style={{
                          background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                          color: 'white',
                          width: '26px',
                          height: '26px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          flexShrink: 0
                        }}>{idx + 1}</span>
                        <span style={{ fontWeight: '700', fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                          {result.student_name}
                        </span>
                        {result.image_name && (
                          <button
                            type="button"
                            onClick={() => handlePreviewImage(result)}
                            disabled={previewLoading === result.image_name}
                            title="Click to view the scanned image"
                            style={{
                              background: 'rgba(124, 58, 237, 0.10)',
                              color: '#6D28D9',
                              border: '1px solid rgba(124, 58, 237, 0.35)',
                              borderRadius: 6,
                              padding: '2px 8px',
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              cursor: previewLoading === result.image_name ? 'wait' : 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4
                            }}
                          >
                            <i className={`fa-solid ${previewLoading === result.image_name ? 'fa-spinner fa-spin' : 'fa-image'}`}></i>
                            {result.image_name}
                          </button>
                        )}
                        <span
                          title={ratioPct || statusMeta.label}
                          style={{
                            background: statusMeta.bg,
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.7rem',
                            fontWeight: '700'
                          }}
                        >
                          {statusMeta.label}{ratioPct ? ` · ${ratioPct}` : ''}
                        </span>
                      </div>
                    </div>
                    {result.ocr_image_note && (
                      <p style={{
                        margin: '0 0 8px',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        whiteSpace: 'pre-wrap',
                        color: String(result.ocr_image_note).includes('rejected') ? '#92400E' : '#065F46'
                      }}>
                        {result.ocr_image_note}
                      </p>
                    )}
                    {hasText ? (
                      <pre style={{
                        margin: 0,
                        padding: '10px 12px',
                        background: 'var(--surface, #F8FAFC)',
                        border: '1px solid var(--border, #E5E7EB)',
                        borderRadius: '6px',
                        fontSize: '0.82rem',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
                        color: 'var(--text-primary)',
                        maxHeight: '260px',
                        overflow: 'auto'
                      }}>
                        {ocrText}
                      </pre>
                    ) : (
                      <p style={{ margin: 0, fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                        (no transcript stored for this row)
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && previewImage.images && previewImage.images.length > 0 && (() => {
        const total = previewImage.images.length;
        const current = previewImage.images[previewImage.index];
        const atStart = previewImage.index <= 0;
        const atEnd = previewImage.index >= total - 1;
        const goPrev = () => !atStart && setPreviewImage(p => ({ ...p, index: p.index - 1 }));
        const goNext = () => !atEnd && setPreviewImage(p => ({ ...p, index: p.index + 1 }));
        // Split-view: when either of the "View All" modals is open we
        // anchor the image to the RIGHT half so comments/OCR + scan
        // can be reviewed together. Otherwise full-screen as before.
        const splitView = !!(showAllComments || viewingComment || showAllOcr);
        // 3-pane when comments are open beside the scan: comments
        // (40vw) | image (middle 30vw) | OCR (right 30vw). The View All OCR
        // modal stays on the original 2-pane 50/50 split.
        const threePane = splitView && (showAllComments || !!viewingComment);
        // 4-pane: All Comments + image + OCR + rubric all open. The image
        // shrinks to the 28vw–52vw band, leaving OCR (24vw) and rubric (24vw)
        // stacked on the right.
        const fourPane = threePane && showRubricPane;
        const besideComments = !!(showAllComments || viewingComment);
        const singleComment = !!viewingComment && !showAllComments;
        const transcriptOn = besideComments && (singleComment ? showCommentTranscript : true);
        const wideGrades = singleComment && tableRunsForView(viewingComment).length > 1;
        const wideLayout = besideComments
          ? commentSideLayout({ image: true, transcript: transcriptOn, rubric: showRubricPane, wide: wideGrades })
          : null;
        return (
          <div
            className="quick-comment-pane"
            onClick={() => setPreviewImage(null)}
            style={splitView ? {
              position: 'fixed', top: 'var(--header-height, 52px)',
              left: (wideLayout && wideLayout.imageLeft) || (fourPane ? '28vw' : threePane ? '40vw' : '50vw'),
              right: (wideLayout && wideLayout.imageRight) || (fourPane ? '48vw' : threePane ? '30vw' : 0),
              bottom: 0,
              background: 'rgba(0,0,0,0.92)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 10001, flexDirection: 'column', gap: '12px',
              padding: '16px'
            } : {
              position: 'fixed', top: 'var(--header-height, 52px)', left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.88)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 9999, flexDirection: 'column', gap: '12px'
            }}
          >
            <div onClick={e => e.stopPropagation()} style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ position: 'relative' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); setPreviewImage(null); }}
                  // In split view the inner image container is centred
                  // in the right-half overlay, which can push the
                  // close button up behind the FastGrader app header.
                  // Pin it to the viewport's top-right (just below the
                  // header) when split, fall back to the corner-of-
                  // image position when full-screen.
                  style={splitView ? {
                    position: 'fixed',
                    top: 'calc(var(--header-height, 52px) + 16px)',
                    // In 3-pane mode the OCR transcript occupies the right
                    // 30vw, so pin the close button just inside the image
                    // pane (left of the OCR column) instead of at right:16.
                    right: (wideLayout && wideLayout.closeRight) || (fourPane ? 'calc(48vw + 16px)' : threePane ? 'calc(30vw + 16px)' : 16),
                    background: '#EF4444', color: 'white', border: '2px solid white',
                    borderRadius: '50%', width: 38, height: 38,
                    cursor: 'pointer', fontWeight: '700', fontSize: 20, lineHeight: '34px',
                    zIndex: 10002,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                  } : {
                    position: 'absolute', top: -14, right: -14,
                    background: '#EF4444', color: 'white', border: 'none',
                    borderRadius: '50%', width: 32, height: 32,
                    cursor: 'pointer', fontWeight: '700', fontSize: 18, lineHeight: '30px',
                    zIndex: 1
                  }}
                >×</button>
                <p style={{ color: 'white', textAlign: 'center', marginBottom: 8, fontSize: 13 }}>
                  {current.name}
                  {total > 1 && (
                    <span style={{ marginLeft: 10, opacity: 0.75 }}>
                      ({previewImage.index + 1} / {total})
                    </span>
                  )}
                </p>
                <ZoomableImage url={current.url} alt={current.name} width={(wideLayout && wideLayout.imagePic) || (fourPane ? 'calc(24vw - 10px)' : threePane ? 'calc(30vw - 20px)' : splitView ? 'calc(50vw - 40px)' : '90vw')} height={splitView ? 'calc(100vh - var(--header-height, 52px) - 44px)' : 'calc(100vh - var(--header-height, 52px) - 48px)'} />
                {/* Nav arrows overlay the image edges (lightbox-style) so they
                    no longer steal horizontal space — the scan can use the
                    full pane width and stays larger / more zoomable. */}
                {total > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); goPrev(); }}
                      disabled={atStart}
                      title="Previous image"
                      style={{
                        position: 'absolute', top: '50%', left: 10, transform: 'translateY(-50%)',
                        background: atStart ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.55)',
                        color: atStart ? 'rgba(255,255,255,0.4)' : 'white',
                        border: '1px solid rgba(255,255,255,0.45)', borderRadius: '50%',
                        width: 40, height: 40, fontSize: 18, fontWeight: 700,
                        cursor: atStart ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 5, backdropFilter: 'blur(2px)'
                      }}
                    >
                      <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); goNext(); }}
                      disabled={atEnd}
                      title="Next image"
                      style={{
                        position: 'absolute', top: '50%', right: 10, transform: 'translateY(-50%)',
                        background: atEnd ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.55)',
                        color: atEnd ? 'rgba(255,255,255,0.4)' : 'white',
                        border: '1px solid rgba(255,255,255,0.45)', borderRadius: '50%',
                        width: 40, height: 40, fontSize: 18, fontWeight: 700,
                        cursor: atEnd ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 5, backdropFilter: 'blur(2px)'
                      }}
                    >
                      <i className="fa-solid fa-chevron-right"></i>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Rubrics side-pane — opened from the "Rubrics" button in the All
          Comments header. Pinned to the right 42vw, it shows the loaded
          marking rubric (plain-text and/or PDF-extracted text) so the teacher
          can read the guide next to the comments. Has its own cross button to
          close just this pane (leaving the comments modal open). */}
      {(showAllComments || viewingComment) && showRubricPane && (() => {
        const rubricBody = autoRubricText || rubricText || '';
        const pdfBody = (autoPdfRubric || pdfRubric)?.text || '';
        // Always rightmost. Narrows to 24vw when the image preview is also
        // open (4-pane: comments | image | OCR | rubric); 42vw on its own.
        const imgOpen = !!(previewImage && previewImage.images && previewImage.images.length > 0);
        const singleComment = !!viewingComment && !showAllComments;
        const wideLayout = commentSideLayout({
          image: imgOpen,
          transcript: singleComment ? showCommentTranscript : imgOpen,
          rubric: true,
          wide: singleComment && tableRunsForView(viewingComment).length > 1
        });
        return (
          <div
            className="quick-comment-pane"
            style={{
              position: 'fixed', top: 'var(--header-height, 52px)', right: 0, bottom: 0, width: (wideLayout && wideLayout.rubricWidth) || (imgOpen ? '24vw' : '42vw'),
              background: 'var(--card-bg, #ffffff)',
              borderLeft: '1px solid var(--border, #E5E7EB)',
              boxShadow: '-4px 0 16px rgba(0,0,0,0.18)',
              zIndex: 10001,
              display: 'flex', flexDirection: 'column'
            }}
          >
            <div style={{
              padding: '14px 16px',
              borderBottom: '1px solid var(--border, #E5E7EB)',
              display: 'flex', alignItems: 'center', gap: 10,
              flexShrink: 0
            }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', flex: 1, minWidth: 0 }}>
                <i className="fa-solid fa-book-open" style={{ marginRight: 6, color: '#2563EB' }}></i>
                Rubric
                {rubricMetadata && (rubricMetadata.class || rubricMetadata.subject) && (
                  <span style={{ marginLeft: 8, fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    {rubricNameWithDate(rubricMetadata)}
                  </span>
                )}
              </h3>
              <button
                onClick={reloadCheckRubric}
                disabled={checkRubricReloading || checkRubricSaving}
                title="Reload the saved rubric and replace this pane"
                style={{
                  padding: '4px 10px',
                  background: checkRubricReloading ? '#e5e7eb' : '#2563EB',
                  color: checkRubricReloading ? '#6b7280' : 'white',
                  border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12,
                  cursor: checkRubricReloading || checkRubricSaving ? 'default' : 'pointer',
                  flexShrink: 0
                }}
              >
                {checkRubricReloading ? 'Reloading…' : 'Reload'}
              </button>
              <button
                onClick={saveCheckRubric}
                disabled={checkRubricReloading || checkRubricSaving || checkRubricDraft === stripQuestionPaperText(autoRubricText || rubricText || '')}
                title="Save this folder's rubric. Ctrl+Enter"
                style={{
                  padding: '4px 10px',
                  background: checkRubricReloading || checkRubricDraft === stripQuestionPaperText(autoRubricText || rubricText || '') ? '#e5e7eb' : '#059669',
                  color: checkRubricReloading || checkRubricDraft === stripQuestionPaperText(autoRubricText || rubricText || '') ? '#6b7280' : 'white',
                  border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12,
                  cursor: checkRubricReloading || checkRubricSaving || checkRubricDraft === stripQuestionPaperText(autoRubricText || rubricText || '') ? 'default' : 'pointer',
                  flexShrink: 0
                }}
              >
                {checkRubricSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => setShowRubricPane(false)}
                title="Close rubric"
                aria-label="Close rubric"
                style={{
                  background: '#EF4444', color: 'white', border: 'none',
                  borderRadius: '50%', width: 30, height: 30,
                  cursor: 'pointer', fontSize: 16, lineHeight: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <MarkedTextarea
              textareaRef={checkRubricRef}
              mark={linkedQuestion?.rubric || null}
              scrollTick={linkedQuestion?.tick || 0}
              dir="auto"
              value={checkRubricDraft}
              placeholder="No rubric text saved for this folder yet."
              onChange={(e) => { setCheckRubricDraft(e.target.value); setCheckRubricNote(''); }}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  saveCheckRubric();
                }
              }}
              spellCheck={false}
              style={{
                flex: 1, width: '100%', minHeight: 0, resize: 'none',
                border: 'none', outline: 'none', padding: '14px 18px',
                font: 'inherit', fontSize: '0.86rem', lineHeight: 1.55,
                background: 'transparent', color: 'var(--text-primary)'
              }}
            />
            {pdfBody && pdfBody.trim() !== (rubricBody || '').trim() && (
              <div style={{ flexShrink: 0, maxHeight: '30%', overflowY: 'auto', padding: '8px 18px 0', borderTop: '1px solid var(--border, #E5E7EB)' }}>
                <div style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 6 }}>
                  PDF Text
                </div>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.86rem', lineHeight: 1.55, color: 'var(--text-primary)' }}>
                  {pdfBody}
                </div>
              </div>
            )}
            <div style={{ flexShrink: 0, padding: '4px 18px 8px', fontSize: 12, minHeight: 22, color: !checkRubricNote ? 'var(--text-secondary, #6b7280)' : (checkRubricNote === 'Saved' || checkRubricNote === 'Reloaded') ? '#059669' : '#b91c1c' }}>
              {checkRubricNote === 'Saved'
                ? 'Saved for this folder'
                : checkRubricNote === 'Reloaded'
                  ? 'Reloaded from the saved rubric'
                  : checkRubricNote || 'Edits apply to every student in this folder'}
            </div>
          </div>
        );
      })()}

      {/* OCR side-pane — the third column of the All Comments 3-pane layout.
          Pinned to the right 30vw next to the image preview, it shows the
          stored OCR transcript for the student whose image is being viewed
          (the same ocr_text/ocr_status the "View All OCR" modal renders).
          Only mounts when the image preview was opened from All Comments. */}
      {(() => {
        const singleComment = !!viewingComment && !showAllComments;
        const row = singleComment
          ? (showCommentTranscript ? viewingComment : null)
          : (showAllComments && previewImage && previewImage.images && previewImage.images.length > 0 && previewImage.result
            ? previewImage.result
            : null);
        if (!row) return null;
        const r = row;
        const ocrStatus = (r.ocr_status || '').toLowerCase();
        const statusMeta = ocrStatus === 'ok'
          ? { bg: 'linear-gradient(135deg, #10B981, #059669)', label: 'OCR OK' }
          : ocrStatus === 'unclear'
            ? { bg: 'linear-gradient(135deg, #F59E0B, #D97706)', label: 'OCR unclear' }
            : ocrStatus === 'failed'
              ? { bg: 'linear-gradient(135deg, #EF4444, #B91C1C)', label: 'OCR failed' }
              : { bg: 'linear-gradient(135deg, #94A3B8, #64748B)', label: 'no transcript' };
        const ratioPct = typeof r.ocr_unclear_ratio === 'number'
          ? `${Math.round(r.ocr_unclear_ratio * 100)}% unclear`
          : null;
        const imgOpen = !!(previewImage && previewImage.images && previewImage.images.length > 0);
        const rubricOpen = showRubricPane;
        const wideLayout = commentSideLayout({
          image: imgOpen,
          transcript: true,
          rubric: rubricOpen,
          wide: singleComment && tableRunsForView(viewingComment).length > 1
        });
        return (
          <div
            className="quick-comment-pane"
            style={{
              position: 'fixed', top: 'var(--header-height, 52px)', right: wideLayout ? wideLayout.ocrRight : (rubricOpen ? '24vw' : 0), bottom: 0,
              width: wideLayout ? wideLayout.ocrWidth : (rubricOpen ? '24vw' : '30vw'),
              background: 'var(--card-bg, #ffffff)',
              borderLeft: '1px solid var(--border, #E5E7EB)',
              boxShadow: '-4px 0 16px rgba(0,0,0,0.18)',
              zIndex: 10001,
              display: 'flex', flexDirection: 'column'
            }}
          >
            <div style={{
              padding: '14px 16px',
              borderBottom: '1px solid var(--border, #E5E7EB)',
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              flexShrink: 0
            }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                <i className="fa-solid fa-file-lines" style={{ marginRight: 6, color: '#6D28D9' }}></i>
                OCR — {r.student_name}
              </h3>
              <span
                title={ratioPct || statusMeta.label}
                style={{ background: statusMeta.bg, color: 'white', padding: '2px 8px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 700 }}
              >
                {statusMeta.label}{ratioPct ? ` · ${ratioPct}` : ''}
              </span>
              <button
                onClick={saveCheckOcr}
                disabled={checkOcrSaving || checkOcrDraft === (r.ocr_text || '')}
                title="Save this student's OCR transcript. Ctrl+Enter"
                style={{
                  marginLeft: 'auto',
                  padding: '4px 10px',
                  background: checkOcrDraft === (r.ocr_text || '') ? '#e5e7eb' : '#7c3aed',
                  color: checkOcrDraft === (r.ocr_text || '') ? '#6b7280' : 'white',
                  border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12,
                  cursor: checkOcrSaving || checkOcrDraft === (r.ocr_text || '') ? 'default' : 'pointer'
                }}
              >
                {checkOcrSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
            <MarkedTextarea
              textareaRef={checkOcrRef}
              mark={linkedQuestion?.ocr || null}
              scrollTick={linkedQuestion?.tick || 0}
              dir="auto"
              value={checkOcrDraft}
              placeholder="No OCR transcript saved for this student yet."
              onChange={(e) => { setCheckOcrDraft(e.target.value); setCheckOcrNote(''); }}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  saveCheckOcr();
                }
              }}
              spellCheck={false}
              style={{
                flex: 1, width: '100%', minHeight: 0, resize: 'none',
                border: 'none', outline: 'none', padding: '12px 16px',
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
                fontSize: '0.82rem', lineHeight: 1.5,
                background: 'transparent', color: 'var(--text-primary)'
              }}
            />
            <div style={{ flexShrink: 0, padding: '4px 16px 8px', fontSize: 12, minHeight: 22, color: !checkOcrNote ? 'var(--text-secondary, #6b7280)' : checkOcrNote === 'Saved' ? '#059669' : '#b91c1c' }}>
              {checkOcrNote && checkOcrNote !== 'Saved' ? checkOcrNote : (checkOcrNote === 'Saved' ? 'Saved for this student' : 'Edits apply only to this student')}
            </div>
          </div>
        );
      })()}

      {/* View Comments Modal */}
      {viewingComment && (
        <div
          className="modal-overlay quick-comment-pane"
          onClick={closeViewComments}
          style={(() => {
            const imgOpen = !!(previewImage && previewImage.images && previewImage.images.length > 0);
            const wideLayout = commentSideLayout({
              image: imgOpen,
              transcript: showCommentTranscript,
              rubric: showRubricPane,
              wide: tableRunsForView(viewingComment).length > 1
            });
            return {
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              padding: 'var(--header-height, 52px) 0 0 0',
              paddingRight: (wideLayout && wideLayout.paddingRight) || 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 10000
            };
          })()}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              width: '100%',
              maxWidth: 'none',
              height: '100%',
              maxHeight: 'none',
              margin: 0,
              borderRadius: 0,
              boxSizing: 'border-box'
            }}
          >
            {/* Header row: class loader, then the student name, then Class /
                Section / Subject / Marks chips + Close. The image filename
                stays under the student name. Image numbers and the
                auto-correct note sit on the row under this one. */}
            <div
              className="modal-header"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'nowrap',
                marginBottom: 6,
                flexShrink: 0
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0, flex: '0 1 auto' }}>
                <div
                  style={{ width: 240, flex: '0 0 auto' }}
                  title={isProcessing
                    ? 'Stop the check that is running before loading another class'
                    : 'Type the start of a class, such as 10, to list only those classes'}
                >
                  {renderClassSwitch(11000, (folder) => switchToClass(folder, { keepComment: true }))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    {commentClassmates.length > 1 ? (
                      <select
                        className="modal-title"
                        value={String(viewingComment.id)}
                        onChange={(e) => {
                          const next = commentClassmates.find((row) => String(row.id) === e.target.value);
                          if (next) switchViewingComment(next);
                        }}
                        title="Open another student from this class"
                        aria-label="Student in this class"
                        style={{
                          margin: 0,
                          fontSize: '0.95rem',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          maxWidth: 280,
                          padding: '2px 6px',
                          border: '1px solid var(--border-color, #d1d5db)',
                          borderRadius: 6,
                          background: 'var(--select-bg, white)',
                          color: 'var(--text-primary)',
                          cursor: 'pointer'
                        }}
                      >
                        {commentClassmates.map((row) => (
                          <option key={row.id} value={String(row.id)}>
                            {row.student_name || `Student ${row.id}`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <h3 className="modal-title" style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                        {viewingComment.student_name}
                      </h3>
                    )}
                    <select
                      aria-label="Late papers or Re-grade"
                      value={showingRegradeList ? 'regrade' : 'late'}
                      onChange={(e) => setPaperListMode(e.target.value === 'regrade' ? 'regrade' : 'late')}
                      title="Late papers lists students still missing from the checked table. Re-grade lists students named on re-grade requests."
                      style={{
                        margin: 0,
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        padding: '2px 6px',
                        border: `1px solid ${commentListTone.border}`,
                        borderRadius: 6,
                        background: commentListTone.bg,
                        color: commentListTone.text,
                        cursor: 'pointer',
                        flex: '0 0 auto'
                      }}
                    >
                      <option value="late">Late papers</option>
                      <option value="regrade">Re-grade</option>
                    </select>
                    <select
                      aria-label={showingRegradeList ? 'Re-grade students' : 'Late papers'}
                      value=""
                      disabled={commentListStudents == null || commentListStudents.length === 0 || !!commentListOpening}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (!value) return;
                        if (showingRegradeList) openRegradeStudent(value);
                        else openLatePaper(value);
                      }}
                      title={showingRegradeList
                        ? "Students named on this class's re-grade requests. One name per student."
                        : 'Reviewed students who are not in this assessment table. One name per roll, even when the paper has several sheets.'}
                      style={{
                        margin: 0,
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        maxWidth: 240,
                        padding: '2px 6px',
                        border: `1px solid ${commentListTone.border}`,
                        borderRadius: 6,
                        background: commentListTone.bg,
                        color: commentListTone.text,
                        cursor: commentListStudents && commentListStudents.length && !commentListOpening ? 'pointer' : 'default'
                      }}
                    >
                      <option value="">
                        {commentListStudents == null
                          ? (showingRegradeList ? 'Re-grade…' : 'Late papers…')
                          : commentListOpening
                            ? 'Opening…'
                            : commentListStudents.length
                              ? `${showingRegradeList ? 'Re-grade' : 'Late papers'} (${commentListStudents.length})`
                              : (showingRegradeList ? 'No re-grade students' : 'No late papers')}
                      </option>
                      {(commentListStudents || []).map((student) => (
                        <option key={showingRegradeList ? student.key : student.roll} value={showingRegradeList ? student.key : student.roll}>
                          {student.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="modal-subtitle" style={{ margin: 0, fontSize: '0.72rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    Image: {viewingComment.image_name}
                  </p>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  gap: 4,
                  alignItems: 'stretch',
                  flex: '0 0 auto'
                }}
              >
                {[
                  { label: 'Class',   value: viewingComment.class || '-' },
                  { label: 'Section', value: viewingComment.section || '-' },
                  { label: 'Subject', value: viewingComment.subject || '-' }
                ].map(({ label, value }) => (
                  <div
                    key={label}
                    className="modal-info-card"
                    style={{
                      padding: '2px 8px',
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0
                    }}
                  >
                    <span className="modal-info-label" style={{ margin: 0, fontSize: '0.62rem', lineHeight: 1.2 }}>{label}</span>
                    <span className="modal-info-value" style={{ margin: 0, fontSize: '0.78rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>{value}</span>
                  </div>
                ))}
                <div
                  className={`modal-marks-card ${viewingComment._autoCorrect ? 'modal-marks-autocorrect' : viewingComment.status === 'error' ? 'modal-marks-error' : 'modal-marks-success'}`}
                  style={{
                    padding: '2px 8px',
                    minWidth: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0
                  }}
                >
                  <span className="modal-info-label" style={{ margin: 0, fontSize: '0.62rem', lineHeight: 1.2 }}>Marks</span>
                  <span
                    className={`modal-marks-value ${viewingComment._autoCorrect ? 'marks-autocorrect' : viewingComment.status === 'error' ? 'marks-error' : 'marks-success'}`}
                    style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.2 }}
                  >
                    {(() => {
                      const stored = parseFloat(viewingComment.marks);
                      const grand = viewingComment.table_comments
                        ? gradeFromTable(viewingComment.table_comments, repairForResult(viewingComment))
                        : null;
                      if (grand && !isNaN(grand.obtained) && (isNaN(stored) || Math.abs(stored - grand.obtained) > 0.05)) {
                        return String(Math.round(grand.obtained * 100) / 100);
                      }
                      return viewingComment.marks;
                    })()}
                  </span>
                </div>
              </div>

              {(() => {
                const imageOpen = !!(previewImage && previewImage.images && previewImage.images.length > 0);
                const sidesOpen = imageOpen || showCommentTranscript || showRubricPane;
                if (!sidesOpen) return null;
                return (
                  <button
                    type="button"
                    onClick={() => {
                      setPreviewImage(null);
                      setShowCommentTranscript(false);
                      setShowRubricPane(false);
                    }}
                    title="Hide the scan, the transcript, and the rubric"
                    style={{
                      padding: '4px 10px',
                      border: '1px solid #D1D5DB',
                      borderRadius: 8,
                      background: 'var(--select-bg, white)',
                      color: 'var(--text-primary, #374151)',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      whiteSpace: 'nowrap',
                      marginLeft: 'auto',
                      flex: '0 0 auto'
                    }}
                  >
                    <i className="fa-solid fa-eye-slash"></i>
                    Hide all
                  </button>
                );
              })()}
              <button
                type="button"
                onClick={() => setShowCommentTranscript((on) => !on)}
                title={showCommentTranscript ? 'Hide transcript' : 'Show transcript'}
                style={{
                  padding: '4px 10px',
                  border: showCommentTranscript ? 'none' : '1px solid var(--border-color, #ddd)',
                  borderRadius: 8,
                  background: showCommentTranscript
                    ? 'linear-gradient(135deg, #7C3AED, #6D28D9)'
                    : 'var(--select-bg, white)',
                  color: showCommentTranscript ? 'white' : 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap',
                  marginLeft: (previewImage && previewImage.images && previewImage.images.length > 0) || showCommentTranscript || showRubricPane ? undefined : 'auto',
                  flex: '0 0 auto'
                }}
              >
                <i className="fa-solid fa-file-lines"></i>
                Transcript
              </button>
              {(folderPath || autoRubricText || rubricText || (autoPdfRubric || pdfRubric)?.text) && (
                <button
                  onClick={toggleRubricPane}
                  title={showRubricPane ? 'Hide rubric' : 'Show rubric'}
                  style={{
                    padding: '4px 10px',
                    border: showRubricPane ? 'none' : '1px solid var(--border-color, #ddd)',
                    borderRadius: 8,
                    background: showRubricPane
                      ? 'linear-gradient(135deg, #3B82F6, #2563EB)'
                      : 'var(--select-bg, white)',
                    color: showRubricPane ? 'white' : 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    whiteSpace: 'nowrap',
                    flex: '0 0 auto'
                  }}
                >
                  <i className="fa-solid fa-book-open"></i>
                  Rubrics
                </button>
              )}
              <button
                onClick={closeViewComments}
                className="modal-close-btn"
                style={{ padding: '4px 10px', fontSize: 12, flex: '0 0 auto' }}
              >
                <i className="fa-solid fa-times"></i> Close
              </button>
            </div>

            {commentListClasses.length > 0 && (() => {
              const pageCount = Math.ceil(commentListClasses.length / 2);
              const start = (commentListPage % pageCount) * 2;
              const shown = commentListClasses.slice(start, start + 2);
              return (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  flexShrink: 0,
                  margin: '0 0 8px',
                  minWidth: 0
                }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: commentListTone.text, flex: '0 0 auto' }}>
                    {showingRegradeList ? 'Re-grade' : 'Late papers'}
                  </span>
                  {shown.map((item) => {
                    const current = `rubrics/${item.id}` === folderPath;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => switchToClass(item.folder, { keepComment: true })}
                        title={current ? `${item.label} is open` : `Open ${item.label}`}
                        style={{
                          maxWidth: 280,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          padding: '3px 8px',
                          borderRadius: 6,
                          border: `1px solid ${commentListTone.border}`,
                          background: current ? commentListTone.fill : commentListTone.bg,
                          color: current ? 'white' : commentListTone.text,
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: current ? 'default' : 'pointer'
                        }}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                  {pageCount > 1 && (
                    <button
                      type="button"
                      onClick={() => setCommentListPage((page) => (page + 1) % pageCount)}
                      title={showingRegradeList
                        ? 'Show the next classes with re-grade students'
                        : 'Show the next classes with late papers'}
                      style={{
                        flex: '0 0 auto',
                        padding: '3px 8px',
                        borderRadius: 6,
                        border: `1px solid ${commentListTone.nextBorder}`,
                        background: 'white',
                        color: commentListTone.text,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      Next
                    </button>
                  )}
                </div>
              );
            })()}

            {(commentPages.length > 0 || viewingComment._autoCorrect) && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'nowrap',
                flexShrink: 0,
                margin: '0 0 8px',
                minWidth: 0
              }}>
                {viewingComment._autoCorrect && (
                  <div style={{
                    fontSize: '0.78rem',
                    color: '#8B5CF6',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    flex: '0 0 auto',
                    whiteSpace: 'nowrap'
                  }}>
                    <i className="fa-solid fa-wand-magic-sparkles"></i>
                    Auto-corrected from <strong style={{ margin: '0 2px' }}>{viewingComment._autoCorrect.from}</strong> — {viewingComment._autoCorrect.details}
                  </div>
                )}
                {commentPages.length > 0 && (() => {
                  const allPagesPicked = commentPages.every((_, index) => commentPickedPages.includes(index + 1));
                  return (
                    <button
                      type="button"
                      onClick={() => setCommentPickedPages(
                        allPagesPicked ? [] : commentPages.map((_, index) => index + 1)
                      )}
                      title={allPagesPicked ? 'Clear every image on this paper' : 'Tick every image on this paper'}
                      aria-label={allPagesPicked ? 'Clear all images' : 'Select all images'}
                      style={{
                        flex: '0 0 auto',
                        padding: '3px 8px',
                        borderRadius: 6,
                        border: '1px solid #7C3AED',
                        background: '#F5F3FF',
                        color: '#5B21B6',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {allPagesPicked ? 'Clear all' : 'Select all'}
                    </button>
                  );
                })()}
                {commentPages.length > 0 && (
                  <CommentPageChips
                    pages={commentPages}
                    previewImage={previewImage}
                    pickedPages={commentPickedPages}
                    onToggle={(number) => {
                      setCommentPickedPages((prev) => (
                        prev.includes(number) ? prev.filter((n) => n !== number) : [...prev, number].sort((a, b) => a - b)
                      ));
                    }}
                    onOpen={openCommentPage}
                  />
                )}
              </div>
            )}

            {(() => {
              const picked = commentRegradeQuestions.filter((q) => commentRegradeIds.includes(q.id));
              const hasTranscript = !!(viewingComment.ocr_text && String(viewingComment.ocr_text).trim());
              const canRegrade = !!(viewingComment.id && viewingComment.image_name && hasTranscript && !isProcessing);
              const canOcr = !!(viewingComment.id && viewingComment.image_name && commentPickedPages.length && !isProcessing);
              const mediumMissing = subjectType === 'none';
              const ocrBusy = isProcessing && activeRunMode === 'ocr_only';
              const gradeBusy = isProcessing && activeRunMode !== 'ocr_only';
              const regradeLabel = picked.length > 3
                ? `Re-grade ${picked.length} questions`
                : picked.length
                  ? `Re-grade ${picked.map((q) => q.label).join(', ')}`
                  : 'Re-grade';
              const subjectOverride = () => (
                subjectType !== 'none' ? { subjectTypeOverride: subjectType } : {}
              );
              const modelOptions = (value) => (
                <>
                  {value
                    && !GEMINI_MODELS.some((m) => m.value === value)
                    && !LOCAL_MODELS.some((m) => m.value === value) && (
                    <option value={value}>{value}</option>
                  )}
                  <optgroup label="Gemini">
                    {GEMINI_MODELS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Local">
                    {LOCAL_MODELS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </optgroup>
                </>
              );
              const startRegrade = () => {
                if (!requireMediumOrPrompt()) return;
                if (!canRegrade) return;
                handleRecheckSelected('grading_only', {
                  resultIds: [viewingComment.id],
                  results: [viewingComment],
                  folderPath: viewingComment.folder_path || folderPath,
                  questionIds: picked.length ? picked.map((q) => q.id) : null,
                  gradingModelOverride: gradingModel,
                  forceTwoStep: true,
                  ...subjectOverride()
                });
              };
              const startOcr = () => {
                if (!requireMediumOrPrompt()) return;
                if (!canOcr) return;
                const pages = commentPickedPages.slice();
                handleRecheckSelected('ocr_only', {
                  resultIds: [viewingComment.id],
                  results: [viewingComment],
                  folderPath: viewingComment.folder_path || folderPath,
                  ocrPages: { [viewingComment.image_name]: pages },
                  ocrModelOverride: ocrModel,
                  forceTwoStep: true,
                  ...subjectOverride()
                });
              };
              return (
                <div className="comment-regrade-bar">
                  <div className="comment-regrade-questions">
                    <span className="comment-regrade-label">Questions</span>
                    {commentRegradeQuestions.length > 0 && (
                      <>
                        <button
                          type="button"
                          className="comment-regrade-link"
                          onClick={() => setCommentRegradeIds(commentRegradeQuestions.map((q) => q.id))}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className="comment-regrade-link"
                          onClick={() => setCommentRegradeIds([])}
                        >
                          Clear
                        </button>
                        {commentRegradeQuestions.map((q) => {
                          const on = commentRegradeIds.includes(q.id);
                          return (
                            <button
                              key={q.id}
                              type="button"
                              className={on ? 'comment-regrade-chip on' : 'comment-regrade-chip'}
                              onClick={() => setCommentRegradeIds((prev) => (
                                prev.includes(q.id) ? prev.filter((id) => id !== q.id) : [...prev, q.id]
                              ))}
                            >
                              {q.label}
                            </button>
                          );
                        })}
                      </>
                    )}
                    <span className="comment-regrade-hint">
                      {picked.length
                        ? 'Only the ticked questions are re-graded. The rest keep their marks.'
                        : 'None ticked — the whole paper is re-graded.'}
                    </span>
                  </div>
                  <div className="comment-action-stack">
                    <div className="comment-action-models">
                      <label className="comment-regrade-model">
                        <span>Size</span>
                        <select
                          value={imageSize === 'full' ? 'full' : 'resized'}
                          onChange={(e) => setImageSize(e.target.value === 'full' ? 'full' : 'resized')}
                          aria-label="Image size"
                          title="Resized fits each page inside 1600 pixels. Full size sends the original photo. OCR uses this size."
                        >
                          <option value="resized">Resized</option>
                          <option value="full">Full size</option>
                        </select>
                      </label>
                      <label className="comment-regrade-model">
                        <span>OCR model</span>
                        <select
                          value={ocrModel}
                          onChange={(e) => setOcrModel(e.target.value)}
                          aria-label="OCR model"
                          title="Model that reads the ticked pages. The same choice is kept on the Check page."
                        >
                          {modelOptions(ocrModel)}
                        </select>
                      </label>
                    </div>
                    <button
                      type="button"
                      className="comment-ocr-go"
                      onClick={startOcr}
                      disabled={mediumMissing ? isProcessing : !canOcr}
                      title={mediumMissing
                        ? 'Select English-2 or Urdu-2 first'
                        : commentPickedPages.length
                          ? `Re-read the ticked pages with ${ocrChoiceLabel(ocrModel)}. The rest of the transcript stays.`
                          : 'Tick the pages to send for OCR'}
                    >
                      <i className={`fa-solid ${ocrBusy ? 'fa-spinner fa-spin' : 'fa-eye'}`}></i>
                      {ocrBusy ? 'Reading…' : `OCR (${commentPickedPages.length})`}
                    </button>
                    <div className="comment-action-grade">
                      <div className="comment-medium-buttons">
                        {[
                          { value: 'english_two_step', label: 'English-2', color: '#2563EB' },
                          { value: 'urdu_two_step', label: 'Urdu-2', color: '#7C3AED' }
                        ].map((opt) => {
                          const on = mediumOption === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => handleMediumChange(opt.value)}
                              title={opt.value === 'urdu_two_step'
                                ? 'Read and grade this paper as Urdu'
                                : 'Read and grade this paper as English'}
                              style={{
                                border: on ? `2px solid ${opt.color}` : '1px solid #D1D5DB',
                                background: on ? opt.color : '#fff',
                                color: on ? '#fff' : '#374151'
                              }}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      <label className="comment-regrade-model">
                        <span>Grading model</span>
                        <select
                          value={gradingModel}
                          onChange={(e) => setGradingModel(e.target.value)}
                          aria-label="Grading model"
                          title="Model that re-grades this transcript"
                        >
                          {modelOptions(gradingModel)}
                        </select>
                      </label>
                    </div>
                    <button
                      type="button"
                      className="comment-regrade-go"
                      onClick={startRegrade}
                      disabled={mediumMissing ? isProcessing : !canRegrade}
                      title={mediumMissing
                        ? 'Select English-2 or Urdu-2 first'
                        : hasTranscript
                          ? `Re-grade this student with ${ocrChoiceLabel(gradingModel)}`
                          : 'This student has no saved transcript to re-grade'}
                    >
                      <i className={`fa-solid ${gradeBusy ? 'fa-spinner fa-spin' : 'fa-pen-to-square'}`}></i>
                      {gradeBusy ? 'Re-grading…' : regradeLabel}
                    </button>
                    {isProcessing && progress ? (
                      <span className="comment-regrade-hint comment-action-progress">{progress}</span>
                    ) : null}
                  </div>
                </div>
              );
            })()}

            <div
              className="modal-body"
              // The re-grade bar above stays put. This body fills the
              // rest. The verification controls and grade titles stay
              // at the top of the card; the question rows scroll inside it.
              style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
              <ModalCommentsEditor
                viewingComment={viewingComment}
                allowedQuestionIds={repairForResult(viewingComment).allowedIds}
                rubricQuestionMarks={repairForResult(viewingComment).rubricMarks}
                paperTotal={repairForResult(viewingComment).paperTotal}
                renderHighlights={renderCommentsWithOverMaxHighlight}
                renderMarkdownTable={renderMarkdownTable}
                apiBaseUrl={API_BASE_URL}
                onSaved={(newValue, marks) => {
                  // Sync the parent table row + this modal's snapshot.
                  setAssessmentResults(prev => prev.map(r =>
                    r.id === viewingComment.id ? { ...r, comments: newValue, comments_custom: 1, ...(marks != null ? { marks } : {}) } : r
                  ));
                  setViewingComment(prev => prev ? { ...prev, comments: newValue, comments_custom: 1, ...(marks != null ? { marks } : {}) } : prev);
                  showMessage('Comment saved', 'success');
                }}
                onTableSaved={(newTableMd, regeneratedComments, marks, tableHistory) => {
                  // Server regenerates the one-line `comments` from the new
                  // table. Both fields land in state so the row stays in sync.
                  const historyPatch = tableHistory !== undefined ? { table_history: tableHistory } : {};
                  const hadAside = thinkingPhrasesIn(`${viewingComment.table_comments || ''}\n${viewingComment.comments || ''}`).length > 0;
                  const stillAside = thinkingPhrasesIn(`${newTableMd || ''}\n${regeneratedComments || ''}`).length > 0;
                  setAssessmentResults(prev => prev.map(r =>
                    r.id === viewingComment.id
                      ? { ...r, table_comments: newTableMd, comments: regeneratedComments, comments_custom: 0, ...(marks != null ? { marks } : {}), ...historyPatch }
                      : r
                  ));
                  setViewingComment(prev => prev
                    ? { ...prev, table_comments: newTableMd, comments: regeneratedComments, comments_custom: 0, ...(marks != null ? { marks } : {}), ...historyPatch }
                    : prev
                  );
                  if (hadAside && !stillAside) noteThinkingAccepted();
                  showMessage('Table saved — one-line comments regenerated', 'success');
                }}
                onError={(msg) => showMessage(msg, 'error')}
                linkedQuestion={linkedQuestion}
                onFocusQuestion={focusLinkedQuestion}
                onLogin={rubricMetadata?.creator_email ? loginToEduPageForCard : null}
                loginTitle={rubricMetadata?.creator_email
                  ? (eduBridgeReady
                    ? `Log into EduPage as ${rubricMetadata.creator_email} and open this test`
                    : `Log into EduPage as ${rubricMetadata.creator_email} — needs the Login Edupage extension installed & updated`)
                  : ''}
                onCopyMarks={(tableMd, comments) => handleCopyResult({
                  ...viewingComment,
                  table_comments: tableMd || viewingComment.table_comments,
                  comments: comments != null ? comments : viewingComment.comments
                })}
                copySignature={(tableMd, comments) => {
                  const packed = commentForResult({
                    ...viewingComment,
                    table_comments: tableMd || viewingComment.table_comments,
                    comments: comments != null ? comments : viewingComment.comments
                  });
                  return `${packed.marks}\t${packed.comments}`;
                }}
                onRefresh={refreshOpenCheck}
                refreshing={checkRefreshing}
                reloadToken={checkReloadToken}
                onPublishStudent={publishOpenStudent}
                onHighlightsSaved={(tableHistory) => {
                  const historyPatch = tableHistory !== undefined ? { table_history: tableHistory } : {};
                  setAssessmentResults(prev => prev.map(r => (
                    r.id === viewingComment.id ? { ...r, ...historyPatch } : r
                  )));
                  setViewingComment(prev => (prev ? { ...prev, ...historyPatch } : prev));
                }}
              />
            </div>
          </div>
        </div>
      )}

      {quickOcrOpening && (
        <div
          className="quick-ocr-opening"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            background: 'rgba(255,255,255,0.94)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            color: '#1F2937'
          }}
        >
          {quickTranscript && quickTranscript.mode === 'comment' ? 'Opening comments…' : 'Opening transcript…'}
        </div>
      )}
      {/* OCR Transcript Modal — editable. Step-1 output for Urdu Medium runs. */}
      {viewingOcr && (
        <div
          className="modal-overlay ocr-transcript-modal"
          onClick={() => {
            if (ocrDragMovedRef.current) return;
            closeViewingOcr();
          }}
        >
          <div
            ref={ocrFrameRef}
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={(() => {
              const frame = ocrGeomRef.current || defaultOcrFrame();
              ocrGeomRef.current = frame;
              return { left: frame.x, top: frame.y, width: frame.w, height: frame.h };
            })()}
          >
            {OCR_RESIZE_EDGES.map((edge) => (
              <div
                key={edge}
                className={`ocr-resize-handle ocr-resize-${edge}`}
                onPointerDown={beginOcrGesture('resize', edge)}
                title={edge === 'se' ? 'Drag to resize' : undefined}
              />
            ))}
            <div
              className="modal-header ocr-check-header"
              onPointerDown={beginOcrGesture('drag')}
              onDoubleClick={restoreOcrFrame}
            >
              <div className="ocr-check-toolbar" data-ocr-control>
                <span className="ocr-drag-grip" title="Drag to move. Double-click to fill the screen.">
                  <i className="fa-solid fa-up-down-left-right" aria-hidden="true"></i>
                </span>
                <div
                  style={{ width: 220, flex: '0 0 auto' }}
                  data-ocr-control
                  title={isProcessing
                    ? 'Stop the check that is running before loading another class'
                    : 'Type the start of a class, such as 10, to list only those classes'}
                >
                  {renderClassSwitch(11000, (folder) => switchToClass(folder, { keepTranscript: true }))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: '0 1 auto' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    {ocrClassmates.length > 1 ? (
                      <select
                        className="modal-title"
                        value={String(viewingOcr.id)}
                        onChange={(e) => {
                          const next = ocrClassmates.find((row) => String(row.id) === e.target.value);
                          if (next) switchViewingOcr(next);
                        }}
                        title="Open another student from this class"
                        aria-label="Student in this class"
                        style={{
                          margin: 0,
                          fontSize: '0.95rem',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          maxWidth: 240,
                          padding: '2px 6px',
                          border: '1px solid var(--border-color, #d1d5db)',
                          borderRadius: 6,
                          background: 'var(--select-bg, white)',
                          color: 'var(--text-primary)',
                          cursor: 'pointer'
                        }}
                      >
                        {ocrClassmates.map((row) => (
                          <option key={row.id} value={String(row.id)}>
                            {row.student_name || `Student ${row.id}`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <h3 className="modal-title" style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                        {viewingOcr.student_name}
                      </h3>
                    )}
                    <select
                      aria-label="Thinking in marks, unexpected blank page, or OCR missing"
                      value={showingThinkingMarks ? 'thinking' : showingMissingOcr ? 'missing' : 'blank'}
                      onChange={(e) => {
                        const next = e.target.value;
                        setOcrListMode(next === 'thinking' ? 'thinking' : next === 'missing' ? 'missing' : 'blank');
                      }}
                      title="Thinking in marks lists students whose saved marks still contain the grader's aside. Unexpected blank page lists a blank page before the last page. OCR missing lists a scan that was not read."
                      style={{
                        margin: 0,
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        padding: '2px 6px',
                        border: `1px solid ${ocrFlagTone.border}`,
                        borderRadius: 6,
                        background: ocrFlagTone.bg,
                        color: ocrFlagTone.text,
                        cursor: 'pointer',
                        flex: '0 0 auto'
                      }}
                    >
                      <option value="thinking">Thinking in marks</option>
                      <option value="blank">Unexpected blank page</option>
                      <option value="missing">OCR missing</option>
                    </select>
                    <select
                      aria-label={ocrFlagLabel}
                      value=""
                      disabled={ocrFlagStudents.length === 0 || !!transcriptStudentOpening}
                      onChange={(e) => {
                        const name = e.target.value;
                        if (!name) return;
                        const student = ocrFlagStudents.find((item) => item.name === name);
                        const pages = student && student.pages;
                        openTranscriptByName(name, Array.isArray(pages) ? pages[0] : '');
                      }}
                      title={showingThinkingMarks
                        ? "Students whose saved marks still contain the grader's aside. One name per student."
                        : showingMissingOcr
                          ? 'Students whose scan was not read. One name per student.'
                          : 'Students whose transcript has an unexpected blank page. One name per student.'}
                      style={{
                        margin: 0,
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        maxWidth: 360,
                        padding: '2px 6px',
                        border: `1px solid ${ocrFlagTone.border}`,
                        borderRadius: 6,
                        background: ocrFlagTone.bg,
                        color: ocrFlagTone.text,
                        cursor: ocrFlagStudents.length && !transcriptStudentOpening ? 'pointer' : 'default'
                      }}
                    >
                      <option value="">
                        {transcriptStudentOpening
                          ? 'Opening…'
                          : ocrFlagStudents.length
                            ? `${ocrFlagLabel} (${ocrFlagStudents.length})`
                            : ocrFlagEmpty}
                      </option>
                      {ocrFlagStudents.map((student) => {
                        const pageLabel = showingMissingOcr ? formatMissingImageNumbers(student.pages) : '';
                        const phraseLabel = showingThinkingMarks && Array.isArray(student.phrases)
                          ? student.phrases.join(', ')
                          : '';
                        const extra = phraseLabel || pageLabel;
                        return (
                          <option key={student.name} value={student.name}>
                            {extra ? `${student.name} — ${extra}` : student.name}
                          </option>
                        );
                      })}
                    </select>
                    {Array.isArray(quickCursor && quickCursor.queue) && quickCursor.queue.length > 0 && (
                      <span style={{ color: '#6B7280', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>
                        {Math.max(1, quickCursor.queue.findIndex((student) => student && student.name === quickCursor.name) + 1)}
                        {' of '}
                        {quickCursor.queue.length}
                      </span>
                    )}
                  </div>
                  <p className="modal-subtitle" style={{ margin: 0, fontSize: '0.72rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    Image: {viewingOcr.image_name}
                  </p>
                </div>
                <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 4, alignItems: 'stretch', flex: '0 0 auto' }}>
                  {[
                    { label: 'Class', value: viewingOcr.class || '-' },
                    { label: 'Section', value: viewingOcr.section || '-' },
                    { label: 'Subject', value: viewingOcr.subject || '-' }
                  ].map(({ label, value }) => (
                    <div
                      key={label}
                      className="modal-info-card"
                      style={{ padding: '2px 8px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0 }}
                    >
                      <span className="modal-info-label" style={{ margin: 0, fontSize: '0.62rem', lineHeight: 1.2 }}>{label}</span>
                      <span className="modal-info-value" style={{ margin: 0, fontSize: '0.78rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>{value}</span>
                    </div>
                  ))}
                  <div
                    className={`modal-marks-card ${viewingOcr._autoCorrect ? 'modal-marks-autocorrect' : viewingOcr.status === 'error' ? 'modal-marks-error' : 'modal-marks-success'}`}
                    style={{ padding: '2px 8px', minWidth: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 0 }}
                  >
                    <span className="modal-info-label" style={{ margin: 0, fontSize: '0.62rem', lineHeight: 1.2 }}>Marks</span>
                    <span
                      className={`modal-marks-value ${viewingOcr._autoCorrect ? 'marks-autocorrect' : viewingOcr.status === 'error' ? 'marks-error' : 'marks-success'}`}
                      style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.2 }}
                    >
                      {(() => {
                        const stored = parseFloat(viewingOcr.marks);
                        const grand = viewingOcr.table_comments
                          ? gradeFromTable(viewingOcr.table_comments, repairForResult(viewingOcr))
                          : null;
                        if (grand && !isNaN(grand.obtained) && (isNaN(stored) || Math.abs(stored - grand.obtained) > 0.05)) {
                          return String(Math.round(grand.obtained * 100) / 100);
                        }
                        return viewingOcr.marks;
                      })()}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowOcrVerificationTable((on) => !on)}
                  title={showOcrVerificationTable ? 'Hide verification table' : 'Show verification table'}
                  style={{
                    padding: '4px 10px',
                    border: showOcrVerificationTable ? 'none' : '1px solid var(--border-color, #ddd)',
                    borderRadius: 8,
                    background: showOcrVerificationTable
                      ? 'linear-gradient(135deg, #059669, #047857)'
                      : 'var(--select-bg, white)',
                    color: showOcrVerificationTable ? 'white' : 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    whiteSpace: 'nowrap',
                    marginLeft: 'auto',
                    flex: '0 0 auto'
                  }}
                >
                  <i className="fa-solid fa-table-cells"></i>
                  {showOcrVerificationTable ? 'Hide table' : 'Show table'}
                </button>
                {(showOcrTranscriptPane || showOcrRubricPane || (ocrSideImage && ocrSideImage.name)) && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowOcrTranscriptPane(false);
                      setShowOcrRubricPane(false);
                      closeOcrPageImage();
                    }}
                    title="Hide the scan, the transcript, and the rubric"
                    style={{
                      padding: '4px 10px',
                      border: '1px solid #D1D5DB',
                      borderRadius: 8,
                      background: 'var(--select-bg, white)',
                      color: 'var(--text-primary, #374151)',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      whiteSpace: 'nowrap',
                      flex: '0 0 auto'
                    }}
                  >
                    <i className="fa-solid fa-eye-slash"></i>
                    Hide all
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowOcrTranscriptPane((on) => !on)}
                  title={showOcrTranscriptPane ? 'Hide transcript' : 'Show transcript'}
                  style={{
                    padding: '4px 10px',
                    border: showOcrTranscriptPane ? 'none' : '1px solid var(--border-color, #ddd)',
                    borderRadius: 8,
                    background: showOcrTranscriptPane
                      ? 'linear-gradient(135deg, #7C3AED, #6D28D9)'
                      : 'var(--select-bg, white)',
                    color: showOcrTranscriptPane ? 'white' : 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    whiteSpace: 'nowrap',
                    flex: '0 0 auto'
                  }}
                >
                  <i className="fa-solid fa-file-lines"></i>
                  Transcript
                </button>
                {(folderPath || viewingOcr.folder_path || autoRubricText || rubricText || (autoPdfRubric || pdfRubric)?.text) && (
                  <button
                    type="button"
                    onClick={toggleOcrRubricPane}
                    title={showOcrRubricPane ? 'Hide rubric' : 'Show rubric'}
                    style={{
                      padding: '4px 10px',
                      border: showOcrRubricPane ? 'none' : '1px solid var(--border-color, #ddd)',
                      borderRadius: 8,
                      background: showOcrRubricPane
                        ? 'linear-gradient(135deg, #3B82F6, #2563EB)'
                        : 'var(--select-bg, white)',
                      color: showOcrRubricPane ? 'white' : 'var(--text-primary)',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      whiteSpace: 'nowrap',
                      flex: '0 0 auto'
                    }}
                  >
                    <i className="fa-solid fa-book-open"></i>
                    Rubrics
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeViewingOcr}
                  className="modal-close-btn"
                  style={{ padding: '4px 10px', fontSize: 12, flex: '0 0 auto' }}
                >
                  <i className="fa-solid fa-times"></i> Close
                </button>
              </div>

              {ocrFlagClasses.length > 0 && (() => {
                const pageCount = Math.ceil(ocrFlagClasses.length / 2);
                const start = (ocrFlagPage % pageCount) * 2;
                const shown = ocrFlagClasses.slice(start, start + 2);
                const openFolder = (viewingOcr.folder_path || folderPath || '');
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }} data-ocr-control>
                    <span style={{ fontSize: 12, fontWeight: 800, color: ocrFlagTone.text, flex: '0 0 auto' }}>
                      {ocrFlagLabel}
                    </span>
                    {shown.map((item) => {
                      const current = `rubrics/${item.id}` === openFolder;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => switchToClass(item.folder, {
                            keepTranscript: true,
                            openMissing: showingMissingOcr,
                            openThinking: showingThinkingMarks
                          })}
                          title={current ? `${item.label} is open` : `Open ${item.label}`}
                          style={{
                            maxWidth: 280,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            padding: '3px 8px',
                            borderRadius: 6,
                            border: `1px solid ${ocrFlagTone.border}`,
                            background: current ? ocrFlagTone.fill : ocrFlagTone.bg,
                            color: current ? 'white' : ocrFlagTone.text,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: current ? 'default' : 'pointer'
                          }}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                    {pageCount > 1 && (
                      <button
                        type="button"
                        onClick={() => setOcrFlagPage((page) => (page + 1) % pageCount)}
                        title={showingThinkingMarks
                          ? 'Show the next classes with thinking in marks'
                          : showingMissingOcr
                            ? 'Show the next classes with OCR missing'
                            : 'Show the next classes with an unexpected blank page'}
                        style={{
                          flex: '0 0 auto',
                          padding: '3px 8px',
                          borderRadius: 6,
                          border: `1px solid ${ocrFlagTone.nextBorder}`,
                          background: 'white',
                          color: ocrFlagTone.text,
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        Next
                      </button>
                    )}
                  </div>
                );
              })()}

              {ocrPages.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }} data-ocr-control>
                  {(() => {
                    const storedMissing = openStudentMissingPages();
                    const orphanMissing = storedMissing.filter((n) => n < 1 || n > ocrPages.length);
                    if (!orphanMissing.length) return null;
                    const orphanLabel = formatMissingImageNumbers(orphanMissing);
                    return (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                        padding: '6px 8px',
                        borderRadius: 8,
                        border: '1px solid #FECACA',
                        background: '#FEF2F2',
                        color: '#DC2626',
                        fontSize: 12,
                        fontWeight: 700
                      }}>
                        <span>No chip for the {orphanLabel}, so click Clear OCR missing.</span>
                        <button
                          type="button"
                          onClick={clearOrphanOcrMissing}
                          disabled={clearingOcrMissing}
                          style={{
                            flex: '0 0 auto',
                            padding: '3px 8px',
                            borderRadius: 6,
                            border: '1px solid #B91C1C',
                            background: '#DC2626',
                            color: 'white',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: clearingOcrMissing ? 'wait' : 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {clearingOcrMissing ? 'Saving…' : 'Clear OCR missing'}
                        </button>
                      </div>
                    );
                  })()}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, overflowX: 'auto' }}>
                  {(() => {
                    const allPagesPicked = ocrPages.every((_, index) => pickedOcrPages.includes(index + 1));
                    return (
                      <button
                        type="button"
                        onClick={() => setPickedOcrPages(
                          allPagesPicked ? [] : ocrPages.map((_, index) => index + 1)
                        )}
                        title={allPagesPicked ? 'Clear every image on this paper' : 'Tick every image on this paper'}
                        aria-label={allPagesPicked ? 'Clear all images' : 'Select all images'}
                        style={{
                          flex: '0 0 auto',
                          padding: '3px 8px',
                          borderRadius: 6,
                          border: '1px solid #7C3AED',
                          background: '#F5F3FF',
                          color: '#5B21B6',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {allPagesPicked ? 'Clear all' : 'Select all'}
                      </button>
                    );
                  })()}
                  <div className="ocr-page-chips">
                    <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 4, alignItems: 'center' }}>
                      {ocrPages.map((name, index) => {
                        const number = index + 1;
                        const on = pickedOcrPages.includes(number);
                        const viewing = !!(ocrSideImage && ocrSideImage.page === number);
                        const blankMarked = blankChipPages.includes(number);
                        return (
                          <span
                            key={`${name}-${number}`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '5px 8px 5px 6px',
                              borderRadius: 999,
                              border: blankMarked ? '1px solid #DC2626' : (on ? '1px solid #C2410C' : '1px solid #D1D5DB'),
                              background: on ? '#FFF7ED' : (blankMarked ? '#FEF2F2' : '#fff'),
                              color: on ? '#C2410C' : (blankMarked ? '#991B1B' : '#374151'),
                              boxShadow: viewing ? '0 0 0 2px #2563EB' : 'none'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              title={`Send image ${number} for OCR`}
                              aria-label={`Send image ${number} for OCR`}
                              onChange={() => {
                                setPickedOcrPages((prev) => (
                                  prev.includes(number) ? prev.filter((n) => n !== number) : [...prev, number].sort((a, b) => a - b)
                                ));
                              }}
                              style={{ margin: 0, accentColor: '#C2410C', cursor: 'pointer' }}
                            />
                            <button
                              type="button"
                              title={blankMarked
                                ? (viewing ? `Unexpected blank page ${number}. Close image ${number}` : `Unexpected blank page ${number}: ${name}`)
                                : (viewing ? `Close image ${number}` : `Open image ${number}: ${name}`)}
                              onClick={() => {
                                setShowOcrTranscriptPane(true);
                                openOcrPageImage(number);
                              }}
                              style={{
                                padding: '2px 2px',
                                border: 'none',
                                background: 'transparent',
                                color: 'inherit',
                                fontWeight: 700,
                                fontSize: 11,
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                                lineHeight: 1.4
                              }}
                            >
                              {number}
                              <span style={{ fontWeight: 500, color: '#6B7280', marginLeft: 3 }}>{imageNumberLabel(name)}</span>
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
                </div>
              )}
            </div>

            {(() => {
              const picked = ocrRegradeQuestions.filter((q) => ocrRegradeIds.includes(q.id));
              const hasTranscript = !!(viewingOcr.ocr_text && String(viewingOcr.ocr_text).trim());
              const canRegrade = !!(viewingOcr.id && viewingOcr.image_name && hasTranscript && !isProcessing);
              const canOcr = !!(viewingOcr.id && viewingOcr.image_name && pickedOcrPages.length && !isProcessing);
              const mediumMissing = subjectType === 'none';
              const ocrBusy = isProcessing && activeRunMode === 'ocr_only';
              const gradeBusy = isProcessing && activeRunMode !== 'ocr_only';
              const regradeLabel = picked.length > 3
                ? `Re-grade ${picked.length} questions`
                : picked.length
                  ? `Re-grade ${picked.map((q) => q.label).join(', ')}`
                  : 'Re-grade';
              const subjectOverride = () => (
                subjectType !== 'none' ? { subjectTypeOverride: subjectType } : {}
              );
              const modelOptions = (value) => (
                <>
                  {value
                    && !GEMINI_MODELS.some((m) => m.value === value)
                    && !LOCAL_MODELS.some((m) => m.value === value) && (
                    <option value={value}>{value}</option>
                  )}
                  <optgroup label="Gemini">
                    {GEMINI_MODELS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Local">
                    {LOCAL_MODELS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </optgroup>
                </>
              );
              const startRegrade = () => {
                if (!requireMediumOrPrompt()) return;
                if (!canRegrade) return;
                handleRecheckSelected('grading_only', {
                  resultIds: [viewingOcr.id],
                  results: [viewingOcr],
                  folderPath: viewingOcr.folder_path || folderPath,
                  questionIds: picked.length ? picked.map((q) => q.id) : null,
                  gradingModelOverride: gradingModel,
                  forceTwoStep: true,
                  ...subjectOverride()
                });
              };
              const startOcr = () => {
                if (!requireMediumOrPrompt()) return;
                if (!canOcr) return;
                const pages = pickedOcrPages.slice();
                handleRecheckSelected('ocr_only', {
                  resultIds: [viewingOcr.id],
                  results: [viewingOcr],
                  folderPath: viewingOcr.folder_path || folderPath,
                  ocrPages: { [viewingOcr.image_name]: pages },
                  ocrModelOverride: ocrModel,
                  forceTwoStep: true,
                  ...subjectOverride()
                });
              };
              return (
                <div className="comment-regrade-bar" data-ocr-control>
                  <div className="comment-regrade-questions">
                    <span className="comment-regrade-label">Questions</span>
                    {ocrRegradeQuestions.length > 0 && (
                      <>
                        <button
                          type="button"
                          className="comment-regrade-link"
                          onClick={() => setOcrRegradeIds(ocrRegradeQuestions.map((q) => q.id))}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className="comment-regrade-link"
                          onClick={() => setOcrRegradeIds([])}
                        >
                          Clear
                        </button>
                        {ocrRegradeQuestions.map((q) => {
                          const on = ocrRegradeIds.includes(q.id);
                          return (
                            <button
                              key={q.id}
                              type="button"
                              className={on ? 'comment-regrade-chip on' : 'comment-regrade-chip'}
                              onClick={() => setOcrRegradeIds((prev) => (
                                prev.includes(q.id) ? prev.filter((id) => id !== q.id) : [...prev, q.id]
                              ))}
                            >
                              {q.label}
                            </button>
                          );
                        })}
                      </>
                    )}
                    <span className="comment-regrade-hint">
                      {picked.length
                        ? 'Only the ticked questions are re-graded. The rest keep their marks.'
                        : 'None ticked — the whole paper is re-graded.'}
                    </span>
                  </div>
                  <div className="comment-action-stack">
                    <div className="comment-action-models">
                      <label className="comment-regrade-model">
                        <span>Size</span>
                        <select
                          value={imageSize === 'full' ? 'full' : 'resized'}
                          onChange={(e) => setImageSize(e.target.value === 'full' ? 'full' : 'resized')}
                          aria-label="Image size"
                          title="Resized fits each page inside 1600 pixels. Full size sends the original photo. OCR uses this size."
                        >
                          <option value="resized">Resized</option>
                          <option value="full">Full size</option>
                        </select>
                      </label>
                      <label className="comment-regrade-model">
                        <span>OCR model</span>
                        <select
                          value={ocrModel}
                          onChange={(e) => setOcrModel(e.target.value)}
                          aria-label="OCR model"
                          title="Model that reads the ticked pages. The same choice is kept on the Check page."
                        >
                          {modelOptions(ocrModel)}
                        </select>
                      </label>
                    </div>
                    <button
                      type="button"
                      className="comment-ocr-go"
                      onClick={startOcr}
                      disabled={mediumMissing ? isProcessing : !canOcr}
                      title={mediumMissing
                        ? 'Select English-2 or Urdu-2 first'
                        : pickedOcrPages.length
                          ? `Re-read the ticked pages with ${ocrChoiceLabel(ocrModel)}. The rest of the transcript stays.`
                          : 'Tick the pages to send for OCR'}
                    >
                      <i className={`fa-solid ${ocrBusy ? 'fa-spinner fa-spin' : 'fa-eye'}`}></i>
                      {ocrBusy ? 'Reading…' : `OCR (${pickedOcrPages.length})`}
                    </button>
                    <div className="comment-action-grade">
                      <div className="comment-medium-buttons">
                        {[
                          { value: 'english_two_step', label: 'English-2', color: '#2563EB' },
                          { value: 'urdu_two_step', label: 'Urdu-2', color: '#7C3AED' }
                        ].map((opt) => {
                          const on = mediumOption === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => handleMediumChange(opt.value)}
                              title={opt.value === 'urdu_two_step'
                                ? 'Read and grade this paper as Urdu'
                                : 'Read and grade this paper as English'}
                              style={{
                                border: on ? `2px solid ${opt.color}` : '1px solid #D1D5DB',
                                background: on ? opt.color : '#fff',
                                color: on ? '#fff' : '#374151'
                              }}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      <label className="comment-regrade-model">
                        <span>Grading model</span>
                        <select
                          value={gradingModel}
                          onChange={(e) => setGradingModel(e.target.value)}
                          aria-label="Grading model"
                          title="Model that re-grades this transcript"
                        >
                          {modelOptions(gradingModel)}
                        </select>
                      </label>
                    </div>
                    <button
                      type="button"
                      className="comment-regrade-go"
                      onClick={startRegrade}
                      disabled={mediumMissing ? isProcessing : !canRegrade}
                      title={mediumMissing
                        ? 'Select English-2 or Urdu-2 first'
                        : hasTranscript
                          ? `Re-grade this student with ${ocrChoiceLabel(gradingModel)}`
                          : 'This student has no saved transcript to re-grade'}
                    >
                      <i className={`fa-solid ${gradeBusy ? 'fa-spinner fa-spin' : 'fa-pen-to-square'}`}></i>
                      {gradeBusy ? 'Re-grading…' : regradeLabel}
                    </button>
                    {isProcessing && progress ? (
                      <span className="comment-regrade-hint comment-action-progress">{progress}</span>
                    ) : null}
                  </div>
                </div>
              );
            })()}

            <div className="modal-body" style={{ flexDirection: 'row', gap: showOcrVerificationTable ? 8 : 0 }}>
              <div style={{ flex: showOcrVerificationTable ? '1 1 0' : '0 0 0', minWidth: showOcrVerificationTable ? 280 : 0, width: showOcrVerificationTable ? undefined : 0, minHeight: 0, display: showOcrVerificationTable ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}>
                <ModalCommentsEditor
                  viewingComment={viewingOcr}
                  allowedQuestionIds={repairForResult(viewingOcr).allowedIds}
                  rubricQuestionMarks={repairForResult(viewingOcr).rubricMarks}
                  paperTotal={repairForResult(viewingOcr).paperTotal}
                  renderHighlights={renderCommentsWithOverMaxHighlight}
                  renderMarkdownTable={renderMarkdownTable}
                  apiBaseUrl={API_BASE_URL}
                  onSaved={(newValue, marks) => {
                    setAssessmentResults(prev => prev.map(r =>
                      r.id === viewingOcr.id ? { ...r, comments: newValue, comments_custom: 1, ...(marks != null ? { marks } : {}) } : r
                    ));
                    setViewingOcr(prev => prev ? { ...prev, comments: newValue, comments_custom: 1, ...(marks != null ? { marks } : {}) } : prev);
                    showMessage('Comment saved', 'success');
                  }}
                  onTableSaved={(newTableMd, regeneratedComments, marks, tableHistory) => {
                    const historyPatch = tableHistory !== undefined ? { table_history: tableHistory } : {};
                    const hadAside = thinkingPhrasesIn(`${viewingOcr.table_comments || ''}\n${viewingOcr.comments || ''}`).length > 0;
                    const stillAside = thinkingPhrasesIn(`${newTableMd || ''}\n${regeneratedComments || ''}`).length > 0;
                    setAssessmentResults(prev => prev.map(r =>
                      r.id === viewingOcr.id
                        ? { ...r, table_comments: newTableMd, comments: regeneratedComments, comments_custom: 0, ...(marks != null ? { marks } : {}), ...historyPatch }
                        : r
                    ));
                    setViewingOcr(prev => prev
                      ? { ...prev, table_comments: newTableMd, comments: regeneratedComments, comments_custom: 0, ...(marks != null ? { marks } : {}), ...historyPatch }
                      : prev
                    );
                    if (hadAside && !stillAside) noteThinkingAccepted();
                    showMessage('Table saved — one-line comments regenerated', 'success');
                  }}
                  onError={(msg) => showMessage(msg, 'error')}
                  focusAside={showingThinkingMarks}
                  linkedQuestion={linkedQuestion}
                  onFocusQuestion={focusLinkedQuestion}
                  onLogin={rubricMetadata?.creator_email ? loginToEduPageForCard : null}
                  loginTitle={rubricMetadata?.creator_email
                    ? (eduBridgeReady
                      ? `Log into EduPage as ${rubricMetadata.creator_email} and open this test`
                      : `Log into EduPage as ${rubricMetadata.creator_email} — needs the Login Edupage extension installed & updated`)
                    : ''}
                  onCopyMarks={(tableMd, comments) => handleCopyResult({
                    ...viewingOcr,
                    table_comments: tableMd || viewingOcr.table_comments,
                    comments: comments != null ? comments : viewingOcr.comments
                  })}
                  copySignature={(tableMd, comments) => {
                    const packed = commentForResult({
                      ...viewingOcr,
                      table_comments: tableMd || viewingOcr.table_comments,
                      comments: comments != null ? comments : viewingOcr.comments
                    });
                    return `${packed.marks}\t${packed.comments}`;
                  }}
                  onRefresh={refreshOpenTranscript}
                  refreshing={checkRefreshing}
                  reloadToken={ocrCheckReloadToken}
                  onPublishStudent={(patch) => publishOpenStudent(patch, viewingOcr)}
                  onHighlightsSaved={(tableHistory) => {
                    const historyPatch = tableHistory !== undefined ? { table_history: tableHistory } : {};
                    setAssessmentResults(prev => prev.map(r => (
                      r.id === viewingOcr.id ? { ...r, ...historyPatch } : r
                    )));
                    setViewingOcr(prev => (prev ? { ...prev, ...historyPatch } : prev));
                  }}
                />
              </div>
              {showOcrTranscriptPane && (
              <div style={{ flex: '1.15 1 0', minWidth: 280, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {(() => {
                const status = viewingOcr.ocr_status || (viewingOcr.ocr_text ? 'ok' : 'missing');
                const ratio = viewingOcr.ocr_unclear_ratio;
                if (status === 'unclear' || status === 'failed') {
                  return (
                    <div style={{
                      background: 'rgba(220, 38, 38, 0.08)',
                      border: '1px solid #FCA5A5',
                      borderRadius: 8,
                      padding: '10px 14px',
                      marginBottom: 12,
                      color: '#991B1B',
                      fontSize: 13,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <i className="fa-solid fa-triangle-exclamation"></i>
                      OCR {status === 'failed' ? 'failed' : 'unclear'} — grading was skipped.
                      {typeof ratio === 'number' && ratio > 0 && (
                        <span style={{ fontWeight: 500 }}>
                          ({Math.round(ratio * 100)}% of words unreadable, threshold {Math.round(OCR_UNCLEAR_THRESHOLD * 100)}%)
                        </span>
                      )}
                      <span style={{ marginLeft: 'auto', fontWeight: 500 }}>
                        Edit the transcript and save to fix it manually, then "Re-grade" the row.
                      </span>
                    </div>
                  );
                }
                if (status === 'missing') {
                  return (
                    <div style={{
                      background: 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid #FCD34D',
                      borderRadius: 8,
                      padding: '10px 14px',
                      marginBottom: 12,
                      color: '#92400E',
                      fontSize: 13,
                      fontWeight: 600
                    }}>
                      <i className="fa-solid fa-circle-info" style={{ marginRight: 8 }}></i>
                      No OCR transcript — this row was processed before the two-step pipeline ran. Use "Full Re-check" to generate one.
                    </div>
                  );
                }
                return null;
              })()}

              {(() => {
                const runs = ocrRunsForView(viewingOcr);
                const focusedImageName = focusedOcrPage ? (ocrPages[focusedOcrPage - 1] || '') : '';
                const several = runs.length > 1;
                const latestText = ocrCompareText != null ? ocrCompareText : (runs[runs.length - 1] ? runs[runs.length - 1].text : '');
                const showDiff = several && ocrDiffOn;
                const hasManualMarks = runs.some((run) => run.marks && run.marks.length);
                const saveRunMarks = async (body) => {
                  if (!viewingOcr || ocrMarkSaving) return;
                  setOcrMarkSaving(true);
                  try {
                    const token = localStorage.getItem('authToken');
                    const res = await fetch(`${API_BASE_URL}/assess/results/${viewingOcr.id}`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({ ocr_run_marks: body })
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
                    const patch = { ocr_history: data.ocr_history ?? null };
                    setAssessmentResults(prev => prev.map(r => (
                      r.id === viewingOcr.id ? { ...r, ...patch } : r
                    )));
                    setViewingOcr(prev => (prev ? { ...prev, ...patch } : prev));
                    setOcrSelection(null);
                    showMessage(data.message || 'Highlight saved', 'success');
                  } catch (err) {
                    showMessage('Highlight failed: ' + err.message, 'error');
                  } finally {
                    setOcrMarkSaving(false);
                  }
                };
                const applySelection = (color) => {
                  if (!ocrSelection || ocrMarkSaving) return;
                  if (ocrSelection.dirty) {
                    showMessage('Save the transcript first, then highlight the text.', 'error');
                    return;
                  }
                  const run = runs[ocrSelection.index];
                  if (!run) return;
                  const marks = mergeOcrMarks(run.text, run.marks, { ...ocrSelection, color });
                  saveRunMarks({ index: ocrSelection.index, marks });
                };
                const clearManualMarks = () => {
                  if (ocrMarkSaving) return;
                  if (!window.confirm('Clear the colored highlights? The transcripts stay as they are.')) return;
                  saveRunMarks({ clear_all: true });
                };
                const removeRun = async (index) => {
                  if (!viewingOcr || ocrRunDeleting) return;
                  const current = ocrRunsForView(viewingOcr);
                  const target = current[index];
                  if (!target) return;
                  const message = current.length === 1
                    ? 'Delete this transcript? The student\'s OCR will be cleared.'
                    : target.latest
                      ? 'Delete the latest OCR? Grading will use the previous run.'
                      : 'Delete this OCR run?';
                  if (!window.confirm(message)) return;
                  setOcrRunDeleting(true);
                  try {
                    const token = localStorage.getItem('authToken');
                    const res = await fetch(`${API_BASE_URL}/assess/results/${viewingOcr.id}`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({ ocr_remove_index: index })
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
                    const patch = {
                      ocr_text: data.ocr_text ?? '',
                      ocr_history: data.ocr_history ?? null,
                      ocr_model: data.ocr_model ?? '',
                      ocr_status: data.ocr_status ?? viewingOcr.ocr_status,
                      ocr_unclear_ratio: data.ocr_unclear_ratio ?? null,
                      ocr_prompt_tokens: data.ocr_prompt_tokens ?? null,
                      ocr_completion_tokens: data.ocr_completion_tokens ?? null
                    };
                    setAssessmentResults(prev => prev.map(r => (
                      r.id === viewingOcr.id ? { ...r, ...patch } : r
                    )));
                    setViewingOcr(prev => (prev ? { ...prev, ...patch } : prev));
                    showMessage(data.message || 'OCR run deleted', 'success');
                  } catch (err) {
                    showMessage('Delete failed: ' + err.message, 'error');
                  } finally {
                    setOcrRunDeleting(false);
                  }
                };
                return (
                  <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginBottom: 2, flexWrap: 'nowrap', flex: '0 0 auto' }}>
                      {viewingOcr.ocr_image_note && (
                        <div style={{
                          marginRight: 'auto',
                          background: String(viewingOcr.ocr_image_note).includes('rejected')
                            ? 'rgba(245, 158, 11, 0.12)'
                            : 'rgba(16, 185, 129, 0.08)',
                          border: String(viewingOcr.ocr_image_note).includes('rejected')
                            ? '1px solid #FCD34D'
                            : '1px solid #6EE7B7',
                          borderRadius: 6,
                          padding: '2px 8px',
                          color: String(viewingOcr.ocr_image_note).includes('rejected') ? '#92400E' : '#065F46',
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}>
                          <i className="fa-solid fa-image" style={{ marginRight: 6 }}></i>
                          {viewingOcr.ocr_image_note}
                        </div>
                      )}
                      {[
                        ['blue', 'Blue', '#BFDBFE', '#1E3A8A', '#93C5FD'],
                        ['red', 'Red', '#FECACA', '#991B1B', '#FCA5A5'],
                        ['green', 'Green', '#BBF7D0', '#166534', '#86EFAC']
                      ].map(([color, label, bg, fg, border]) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => applySelection(color)}
                          disabled={!ocrSelection || ocrMarkSaving}
                          title={`Highlight the selection in ${label.toLowerCase()}`}
                          style={{
                            background: (!ocrSelection || ocrMarkSaving) ? '#E2E8F0' : bg,
                            color: (!ocrSelection || ocrMarkSaving) ? '#64748B' : fg,
                            border: `1px solid ${border}`,
                            borderRadius: 6,
                            padding: '2px 8px',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: (!ocrSelection || ocrMarkSaving) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {label}
                        </button>
                      ))}
                      {hasManualMarks && (
                        <button
                          type="button"
                          onClick={clearManualMarks}
                          disabled={ocrMarkSaving}
                          style={{
                            background: 'white',
                            color: '#1E3A8A',
                            border: '1px solid #93C5FD',
                            borderRadius: 6,
                            padding: '2px 8px',
                            fontSize: 12,
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            cursor: ocrMarkSaving ? 'not-allowed' : 'pointer'
                          }}
                        >
                          Clear highlights
                        </button>
                      )}
                      {several && (
                        <button
                          type="button"
                          onClick={() => setOcrDiffOn((on) => !on)}
                          style={{
                            background: showDiff ? '#FDE68A' : 'white',
                            color: '#92400E',
                            border: '1px solid #FCD34D',
                            borderRadius: 6,
                            padding: '2px 8px',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <i className="fa-solid fa-highlighter" />
                          {showDiff ? 'Hide differences' : 'Highlight differences'}
                        </button>
                      )}
                    </div>
                  <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', gap: 10, alignItems: 'stretch', overflowX: 'auto', overflowY: 'hidden' }}>
                    {ocrSideImage && (
                      <div style={{ flex: '0 0 42%', minWidth: 300, maxWidth: 720, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flex: '0 0 auto' }}>
                          <button
                            type="button"
                            title="Previous image"
                            disabled={ocrSideImage.page <= 1}
                            onClick={() => openOcrPageImage(ocrSideImage.page - 1)}
                            style={{
                              border: '1px solid #D1D5DB',
                              background: '#fff',
                              borderRadius: 6,
                              width: 26,
                              height: 26,
                              cursor: ocrSideImage.page <= 1 ? 'default' : 'pointer',
                              fontWeight: 700,
                              color: ocrSideImage.page <= 1 ? '#D1D5DB' : '#374151'
                            }}
                          >
                            ‹
                          </button>
                          <p className="modal-info-label" style={{ margin: 0, flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            Image {ocrSideImage.page} · {imageNumberLabel(ocrSideImage.name)}
                          </p>
                          <button
                            type="button"
                            title="Next image"
                            disabled={ocrSideImage.page >= ocrPages.length}
                            onClick={() => openOcrPageImage(ocrSideImage.page + 1)}
                            style={{
                              border: '1px solid #D1D5DB',
                              background: '#fff',
                              borderRadius: 6,
                              width: 26,
                              height: 26,
                              cursor: ocrSideImage.page >= ocrPages.length ? 'default' : 'pointer',
                              fontWeight: 700,
                              color: ocrSideImage.page >= ocrPages.length ? '#D1D5DB' : '#374151'
                            }}
                          >
                            ›
                          </button>
                          {markPageActualBlank(viewingOcr.ocr_text, ocrSideImage.page, ocrSideImage.name) != null && !blankQueueAdvanceLabel(quickCursor) && (
                            <button
                              type="button"
                              onClick={markOpenPageActualBlank}
                              disabled={markingActualBlank}
                              title="Change this page from blank page to actual blank. The unexpected blank error is removed."
                              style={{
                                border: '1px solid #15803D',
                                background: markingActualBlank ? '#DCFCE7' : '#166534',
                                color: markingActualBlank ? '#166534' : '#fff',
                                borderRadius: 6,
                                padding: '4px 8px',
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: markingActualBlank ? 'wait' : 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {markingActualBlank ? 'Saving…' : 'Actual blank'}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={closeOcrPageImage}
                            style={{
                              border: '1px solid #D1D5DB',
                              background: '#fff',
                              color: '#374151',
                              borderRadius: 6,
                              padding: '4px 8px',
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: 'pointer'
                            }}
                          >
                            Close image
                          </button>
                        </div>
                        <div style={{ flex: '1 1 auto', minHeight: 0, height: 0, position: 'relative' }}>
                          {ocrSideImage.url ? (
                            <div style={{ position: 'absolute', inset: 0 }}>
                              <AnnotatableImage
                                url={ocrSideImage.url}
                                alt={ocrSideImage.name}
                                width="100%"
                                height="100%"
                                availableTools={['hand', 'pen', 'eraser']}
                                inkColors={OCR_WHITE_PEN}
                                defaultColor="#ffffff"
                                defaultStrokeWidth={28}
                                maxStroke={80}
                                exportScale={1}
                                exportMime="image/jpeg"
                                exportQuality={0.92}
                                requireMarks
                                saveButtonLabel={ocrImageSaving ? 'Saving' : 'Save'}
                                saveTitle="Save the white-pen edit onto this scan"
                                onSaveForRecheck={saveOcrPageImage}
                              />
                            </div>
                          ) : (
                            <div style={{
                              position: 'absolute',
                              inset: 0,
                              borderRadius: 8,
                              background: '#F8FAFC',
                              border: '1px solid #E5E7EB',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 16,
                              textAlign: 'center',
                              color: ocrSideImageError ? '#991B1B' : '#6B7280',
                              fontSize: 13,
                              fontWeight: 600
                            }}>
                              {ocrSideImageLoading ? 'Opening image…' : (ocrSideImageError || 'Opening image…')}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {runs.filter((run) => !run.latest).map((run, index) => (
                      <OcrRunColumn
                        key={`${run.at || 'older'}-${index}`}
                        run={run}
                        index={index}
                        total={runs.length}
                        highlight={showDiff}
                        mismatchMask={showDiff ? wordMismatch(run.text, latestText) : null}
                        deleting={ocrRunDeleting}
                        onDelete={() => removeRun(index)}
                        onSelect={setOcrSelection}
                        onClearPage={() => setFocusedOcrPage(null)}
                        pageNumber={focusedOcrPage}
                        imageName={focusedImageName}
                      />
                    ))}
                    <div style={{ flex: '1 1 0', minWidth: several ? 320 : 0, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <ModalOcrEditor
                viewingOcr={viewingOcr}
                apiBaseUrl={API_BASE_URL}
                heading={(() => {
                  const latest = runs[runs.length - 1] || {};
                  const ocrModelShown = stageModelLabel(latest.model, latest.host) || viewingOcr.ocr_model;
                  const ocrWhen = formatRunWhen(latest.at);
                  if (!ocrModelShown && !ocrWhen) return '';
                  return <StageStamp label="OCR" model={ocrModelShown} when={ocrWhen} />;
                })()}
                highlight={showDiff}
                mismatchMask={showDiff ? combinedMismatch(latestText, runs.filter((run) => !run.latest).map((run) => run.text)) : null}
                deleting={ocrRunDeleting}
                marks={runs[runs.length - 1] ? runs[runs.length - 1].marks : []}
                runIndex={runs.length - 1}
                onSelect={setOcrSelection}
                onClearPage={() => setFocusedOcrPage(null)}
                onDelete={() => removeRun(runs.length - 1)}
                onDraftChange={setOcrCompareText}
                pageNumber={focusedOcrPage}
                imageName={focusedImageName}
                onBeginAdvance={() => { blankAdvancingRef.current = true; }}
                onCancelAdvance={() => { blankAdvancingRef.current = false; }}
                onAdvance={Array.isArray(quickCursor && quickCursor.queue) && quickCursor.queue.length ? advanceBlankQueue : null}
                advanceLabel={blankQueueAdvanceLabel(quickCursor)}
                blankAction={ocrSideImage && blankQueueAdvanceLabel(quickCursor) && markPageActualBlank(viewingOcr.ocr_text, ocrSideImage.page, ocrSideImage.name) != null ? {
                  onClick: markOpenPageActualBlank,
                  busy: markingActualBlank
                } : null}
                onAcceptChatter={acceptOpenChatter}
                onRerunOcr={() => {
                  const page = ocrSideImage ? ocrSideImage.page : focusedOcrPage;
                  if (!page || !viewingOcr.image_name) return;
                  if (!requireMediumOrPrompt()) return;
                  handleRecheckSelected('ocr_only', {
                    resultIds: [viewingOcr.id],
                    results: [viewingOcr],
                    ocrPages: { [viewingOcr.image_name]: [page] },
                    ocrModelOverride: ocrModel,
                    forceTwoStep: true,
                    ...(subjectType !== 'none' ? { subjectTypeOverride: subjectType } : {})
                  });
                }}
                onSaved={(newValue) => {
                  if (blankAdvancingRef.current) return;
                  // Sync table row + modal snapshot. Editing also clears the
                  // 'unclear'/'failed' status — the user has manually fixed it,
                  // so the row is now considered an OK transcript ready for
                  // re-grading. The edit stays on the latest run.
                  const history = ocrHistoryAfterEdit(viewingOcr.ocr_history, newValue);
                  setAssessmentResults(prev => prev.map(r =>
                    r.id === viewingOcr.id
                      ? { ...r, ocr_text: newValue, ocr_history: history, ocr_status: 'ok', ocr_unclear_ratio: null }
                      : r
                  ));
                  setViewingOcr(prev => prev
                    ? { ...prev, ocr_text: newValue, ocr_history: history, ocr_status: 'ok', ocr_unclear_ratio: null }
                    : prev
                  );
                  showMessage('OCR transcript saved', 'success');
                }}
                onError={(msg) => showMessage(msg, 'error')}
              />
                    </div>
                  </div>
                  </div>
                );
              })()}
              </div>
              )}
              {showOcrRubricPane && (() => {
                const rubricBody = autoRubricText || rubricText || '';
                const pdfBody = (autoPdfRubric || pdfRubric)?.text || '';
                const savedText = stripQuestionPaperText(rubricBody);
                return (
                  <div style={{
                    flex: '0 0 300px',
                    minWidth: 240,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: '1px solid var(--border, #E5E7EB)',
                    background: 'var(--card-bg, #fff)'
                  }}>
                    <div style={{
                      padding: '8px 10px',
                      borderBottom: '1px solid var(--border, #E5E7EB)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexShrink: 0
                    }}>
                      <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, flex: 1, minWidth: 0 }}>
                        <i className="fa-solid fa-book-open" style={{ marginRight: 6, color: '#2563EB' }}></i>
                        Rubric
                      </h3>
                      <button
                        type="button"
                        onClick={reloadCheckRubric}
                        disabled={checkRubricReloading || checkRubricSaving}
                        title="Reload the saved rubric and replace this pane"
                        style={{
                          padding: '4px 10px',
                          background: checkRubricReloading ? '#e5e7eb' : '#2563EB',
                          color: checkRubricReloading ? '#6b7280' : 'white',
                          border: 'none',
                          borderRadius: 6,
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: checkRubricReloading || checkRubricSaving ? 'default' : 'pointer'
                        }}
                      >
                        {checkRubricReloading ? 'Reloading…' : 'Reload'}
                      </button>
                      <button
                        type="button"
                        onClick={saveCheckRubric}
                        disabled={checkRubricReloading || checkRubricSaving || checkRubricDraft === savedText}
                        title="Save this folder's rubric. Ctrl+Enter"
                        style={{
                          padding: '4px 10px',
                          background: checkRubricReloading || checkRubricDraft === savedText ? '#e5e7eb' : '#059669',
                          color: checkRubricReloading || checkRubricDraft === savedText ? '#6b7280' : 'white',
                          border: 'none',
                          borderRadius: 6,
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: checkRubricReloading || checkRubricSaving || checkRubricDraft === savedText ? 'default' : 'pointer'
                        }}
                      >
                        {checkRubricSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowOcrRubricPane(false)}
                        title="Close rubric"
                        aria-label="Close rubric"
                        style={{
                          background: '#EF4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: 28,
                          height: 28,
                          cursor: 'pointer',
                          fontSize: 14,
                          lineHeight: 1
                        }}
                      >
                        <i className="fa-solid fa-xmark"></i>
                      </button>
                    </div>
                    <MarkedTextarea
                      textareaRef={checkRubricRef}
                      mark={linkedQuestion?.rubric || null}
                      scrollTick={linkedQuestion?.tick || 0}
                      dir="auto"
                      value={checkRubricDraft}
                      placeholder="No rubric text saved for this folder yet."
                      onChange={(e) => { setCheckRubricDraft(e.target.value); setCheckRubricNote(''); }}
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                          e.preventDefault();
                          saveCheckRubric();
                        }
                      }}
                      spellCheck={false}
                      style={{
                        flex: 1,
                        width: '100%',
                        minHeight: 0,
                        resize: 'none',
                        border: 'none',
                        outline: 'none',
                        padding: '10px 12px',
                        font: 'inherit',
                        fontSize: '0.86rem',
                        lineHeight: 1.55,
                        background: 'transparent',
                        color: 'var(--text-primary)'
                      }}
                    />
                    {pdfBody && pdfBody.trim() !== (rubricBody || '').trim() && (
                      <div style={{ flexShrink: 0, maxHeight: '30%', overflowY: 'auto', padding: '8px 12px 0', borderTop: '1px solid var(--border, #E5E7EB)' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 6 }}>
                          PDF Text
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.86rem', lineHeight: 1.55, color: 'var(--text-primary)' }}>
                          {pdfBody}
                        </div>
                      </div>
                    )}
                    <div style={{ flexShrink: 0, padding: '4px 12px 8px', fontSize: 12, minHeight: 22, color: !checkRubricNote ? 'var(--text-secondary, #6b7280)' : (checkRubricNote === 'Saved' || checkRubricNote === 'Reloaded') ? '#059669' : '#b91c1c' }}>
                      {checkRubricNote === 'Saved'
                        ? 'Saved for this folder'
                        : checkRubricNote === 'Reloaded'
                          ? 'Reloaded from the saved rubric'
                          : checkRubricNote || 'Edits apply to every student in this folder'}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
  return quickTranscript ? createPortal(page, document.body) : page;
}

// Always-editable comments editor for the View Comments modal.
// Self-contained: keeps its own draft state so it doesn't fight with the
// table-cell editing flow above. Save button and blur both persist via PUT.
//
// Tabs:
//   • "Table" — renders the AI's BLOCK A markdown deduction table and lets
//     the teacher edit the raw markdown. On save, the server regenerates
//     the one-line `comments` from the new table.
//   • "One-line" — the legacy single-string editor.
// The Table tab is the default when the row has table_comments. When it's
// missing (AI skipped verification) the One-line tab is the default and a
// warning chip is shown.
function dropMarkdownTableBodyRow(md, bodyIndex) {
  const isSep = (line) => /^\|\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(String(line || '').trim());
  const lines = String(md || '').split('\n');
  let headerSeen = false;
  let body = -1;
  const kept = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('|') || isSep(trimmed)) {
      kept.push(line);
      continue;
    }
    if (!headerSeen) {
      headerSeen = true;
      kept.push(line);
      continue;
    }
    body += 1;
    if (body === bodyIndex) continue;
    kept.push(line);
  }
  return kept.join('\n');
}

function stripGradeBold(value) {
  return String(value || '').replace(/\*\*/g, '').trim();
}

function gradeHeaderLabel(value) {
  const labels = {
    question: 'Qs',
    marks: 'T.M',
    deduction: 'Mistakes',
    'deduction summary': 'Mistakes',
    'deduction marks': '-ve',
    obtained: 'Obt.M',
    'obtained marks': 'Obt.M'
  };
  const plain = stripGradeBold(value);
  const key = plain.toLowerCase().replace(/\s+/g, ' ').trim();
  return labels[key] || plain;
}

function gradeColumnTemplate(header) {
  return header.map((cell) => {
    const key = stripGradeBold(cell).toLowerCase().replace(/\s+/g, ' ').trim();
    if (key === 'deduction' || key === 'deduction summary' || key === 'mistakes') return 'minmax(88px, 2.2fr)';
    if (key === 'question') return 'minmax(68px, 0.9fr)';
    return 'minmax(46px, 0.7fr)';
  }).join(' ');
}

function gradeRowKey(cells, seen) {
  const label = stripGradeBold(cells[0] || '');
  const question = /total/i.test(label) ? '' : (normalizeQuestionId(label) || '');
  let key = question ? `q:${question}` : (label ? `l:${label.toLowerCase().replace(/\s+/g, ' ')}` : 'i:blank');
  if (seen.has(key)) {
    let n = 2;
    while (seen.has(`${key}#${n}`)) n += 1;
    key = `${key}#${n}`;
  }
  seen.add(key);
  return key;
}

function parseGradeTable(md) {
  const text = String(md || '').trim();
  if (!text || !text.includes('|')) return null;
  const lines = text.split('\n').map((line) => line.trim()).filter((line) => line.startsWith('|'));
  if (lines.length < 2) return null;
  const isSep = (line) => /^\|\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line);
  let header = null;
  const rows = [];
  const seen = new Set();
  let seenGrand = false;
  for (const line of lines) {
    if (isSep(line)) continue;
    if (!header) {
      header = splitRawMarkdownCells(line);
      continue;
    }
    const cells = splitMarkdownRowCells(line, header.length);
    const label = stripGradeBold(cells[0] || '');
    const belowTotal = seenGrand;
    if (/\bgrand\s*total\b/i.test(label)) seenGrand = true;
    rows.push({
      key: gradeRowKey(cells, seen),
      cells,
      index: rows.length,
      bold: cells.some((cell) => /^\*\*.+\*\*$/.test(cell)),
      belowTotal,
      label
    });
  }
  if (!header || !rows.length) return null;
  return { header, rows };
}

function alignedRowKeys(parsed) {
  const keys = [];
  const seen = new Set();
  const push = (key) => {
    if (!key || seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  };
  const latest = parsed[parsed.length - 1];
  if (latest) latest.rows.forEach((row) => push(row.key));
  parsed.forEach((table) => table.rows.forEach((row) => push(row.key)));
  return keys;
}

function mergeCellHighlights(highlights, next) {
  const row = next.row;
  const col = Number(next.col);
  const mine = [];
  const rest = [];
  (highlights || []).forEach((mark) => {
    if (mark.row === row && Number(mark.col) === col) mine.push(mark);
    else rest.push(mark);
  });
  const merged = mergeOcrMarks(next.cellText, mine, next);
  return rest.concat(merged.map((mark) => ({
    start: mark.start,
    end: mark.end,
    text: mark.text,
    color: mark.color,
    row,
    col
  })));
}

function PaintedWords({ text, mask, marks, onMouseUp, aside }) {
  const ref = React.useRef(null);
  const source = String(text || '');
  const segments = paintSegments(source, mask, marks, aside);
  let asideMarked = false;
  return (
    <span
      ref={ref}
      onMouseUp={(event) => {
        event.stopPropagation();
        if (onMouseUp) onMouseUp(selectionInBox(ref.current));
      }}
      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', userSelect: 'text' }}
    >
      {(segments.length ? segments : [{ text: source, yellow: false, color: '' }]).map((segment, index) => {
        const fill = { blue: '#BFDBFE', red: '#FECACA', green: '#BBF7D0' };
        const line = { blue: '#2563EB', red: '#DC2626', green: '#15803D' };
        const asideHere = segment.selected && !asideMarked;
        if (asideHere) asideMarked = true;
        if (!segment.yellow && !segment.color && !segment.selected) return <span key={index}>{segment.text}</span>;
        return (
          <mark
            key={index}
            data-aside={asideHere ? '1' : undefined}
            title={segment.selected ? "Grader's aside" : undefined}
            style={{
              background: segment.selected ? '#FDE68A' : (segment.color && !segment.yellow ? fill[segment.color] : '#FDE68A'),
              boxShadow: segment.selected ? 'inset 0 -2px 0 #B45309' : (segment.color ? `inset 0 -2px 0 ${line[segment.color]}` : 'none'),
              color: 'inherit',
              borderRadius: 3,
              padding: '0 1px'
            }}
          >
            {segment.text}
          </mark>
        );
      })}
    </span>
  );
}

function GradeCompareCell({ row, header, column, showDiff, latest, referenceRows, linkedQuestion, onFocusQuestion, onDeleteBelowRow, onSelect }) {
  if (!row) {
    return <div style={{ border: '1px solid var(--border-color, #e2e8f0)', borderTop: 'none', background: '#fafafa', minHeight: 32 }} />;
  }
  const rowQuestionId = /total/i.test(row.label) ? null : normalizeQuestionId(row.label);
  const linked = !!(rowQuestionId && linkedQuestion && linkedQuestion.id === rowQuestionId
    && (linkedQuestion.owner == null || linkedQuestion.owner === '' || linkedQuestion.owner === column.owner));
  return (
    <div
      title={row.belowTotal ? 'This written label is not a rubric question, so this row is not in the total.' : undefined}
      style={{
        display: 'grid',
        gridTemplateColumns: gradeColumnTemplate(header),
        height: '100%',
        border: '1px solid var(--border-color, #e2e8f0)',
        borderTop: 'none',
        background: linked
          ? 'rgba(245, 158, 11, 0.28)'
          : row.belowTotal
            ? 'rgba(220, 38, 38, 0.08)'
            : row.bold ? 'rgba(139, 92, 246, 0.08)' : 'transparent',
        fontWeight: row.bold ? 700 : 400,
        outline: linked ? '2px solid #d97706' : 'none',
        boxSizing: 'border-box'
      }}
    >
      {row.cells.map((cell, col) => {
        const plain = stripGradeBold(cell);
        const marks = (column.highlights || [])
          .filter((mark) => mark.row === row.key && Number(mark.col) === col)
          .map((mark) => ({ start: mark.start, end: mark.end, text: mark.text, color: mark.color }));
        let mask = null;
        if (showDiff && plain) {
          const refs = (referenceRows || []).map((table) => {
            const match = table && table.rows.find((item) => item.key === row.key);
            return match ? stripGradeBold(match.cells[col] || '') : '';
          });
          mask = latest ? combinedMismatch(plain, refs) : wordMismatch(plain, refs[0] || '');
        }
        const aside = latest ? asidePhraseRanges(plain) : null;
        const painted = (
          <PaintedWords
            text={plain}
            mask={mask}
            marks={marks}
            aside={aside}
            onMouseUp={(range) => {
              if (!onSelect) return;
              if (!range) {
                onSelect(null);
                return;
              }
              onSelect({
                index: column.index,
                row: row.key,
                col,
                start: range.start,
                end: range.end,
                text: range.text,
                cellText: plain
              });
            }}
          />
        );
        return (
          <div key={col} style={{ padding: '6px 10px', verticalAlign: 'top', minWidth: 0 }}>
            {col === 0 && row.belowTotal && latest && typeof onDeleteBelowRow === 'function' && (
              <button
                type="button"
                title="Remove this unmatched row"
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteBelowRow(row.index);
                }}
                style={{ marginRight: 6, border: 'none', background: 'transparent', color: '#DC2626', cursor: 'pointer', padding: 0, fontSize: 12 }}
              >
                <i className="fa-solid fa-trash"></i>
              </button>
            )}
            {col === 0 && rowQuestionId && onFocusQuestion ? (
              <button
                type="button"
                onClick={() => {
                  const sel = window.getSelection();
                  if (sel && !sel.isCollapsed) return;
                  onFocusQuestion(rowQuestionId, column.owner, rowDeduction(header, row.cells));
                }}
                title="Highlight this question in the transcript and the rubric"
                style={{
                  background: linked ? '#fde68a' : 'transparent',
                  border: linked ? '1px solid #d97706' : '1px solid transparent',
                  borderRadius: 6,
                  padding: '1px 6px',
                  font: 'inherit',
                  fontWeight: 700,
                  color: 'inherit',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                {painted}
              </button>
            ) : painted}
          </div>
        );
      })}
    </div>
  );
}

function GradeCompareView({ columns, showDiff, linkedQuestion, onFocusQuestion, onDeleteBelowRow, onSelect, fallback, part = 'all' }) {
  const parsed = columns.map((col) => parseGradeTable(col.md));
  const showStamps = part !== 'body' && columns.some((col) => col.stamp);
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${Math.max(columns.length, 1)}, minmax(280px, 1fr))`,
    columnGap: 12,
    alignItems: 'stretch'
  };
  const stampRow = showStamps
    ? columns.map((col) => <div key={`stamp-${col.index}`}>{col.stamp}</div>)
    : null;
  if (part === 'stamps') {
    if (!stampRow) return null;
    return <div style={gridStyle}>{stampRow}</div>;
  }
  if (!columns.length || parsed.some((table) => !table)) {
    if (typeof fallback !== 'function') return null;
    return (
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', overflowX: part === 'all' ? 'auto' : 'visible' }}>
        {columns.map((col) => (
          <div key={col.index} style={{ flex: '1 1 320px', minWidth: 280 }}>
            {part !== 'body' && col.stamp}
            {fallback(col.md, col.owner, col.latest ? onDeleteBelowRow : null)}
          </div>
        ))}
      </div>
    );
  }
  const keys = alignedRowKeys(parsed);
  const tableGrid = (
    <div style={gridStyle}>
      {part === 'all' && stampRow}
      {parsed.map((table, index) => (
        <div
          key={`head-${index}`}
          style={{
            display: 'grid',
            gridTemplateColumns: gradeColumnTemplate(table.header),
            background: 'var(--surface-alt, #f1f5f9)',
            border: '1px solid var(--border-color, #e2e8f0)',
            borderRadius: '8px 8px 0 0',
            height: '100%',
            boxSizing: 'border-box'
          }}
        >
          {table.header.map((cell, ci) => (
            <div key={ci} style={{ padding: '8px 10px', fontWeight: 700, fontSize: 13, color: 'var(--text-primary, #0f172a)' }}>
              {gradeHeaderLabel(cell)}
            </div>
          ))}
        </div>
      ))}
      {keys.map((key) => parsed.map((table, index) => (
        <GradeCompareCell
          key={`${key}-${index}`}
          row={table.rows.find((item) => item.key === key) || null}
          header={table.header}
          column={columns[index]}
          showDiff={showDiff}
          latest={index === parsed.length - 1}
          referenceRows={index === parsed.length - 1 ? parsed.slice(0, -1) : [parsed[parsed.length - 1]]}
          linkedQuestion={linkedQuestion}
          onFocusQuestion={onFocusQuestion}
          onDeleteBelowRow={onDeleteBelowRow}
          onSelect={onSelect}
        />
      )))}
    </div>
  );
  if (part === 'body') return <div style={{ paddingBottom: 8 }}>{tableGrid}</div>;
  return <div style={{ overflowX: 'auto', paddingBottom: 8 }}>{tableGrid}</div>;
}

export function ModalCommentsEditor({ viewingComment, allowedQuestionIds, rubricQuestionMarks, paperTotal = null, renderHighlights, renderMarkdownTable, apiBaseUrl, onSaved, onTableSaved, onHighlightsSaved, onError, linkedQuestion = null, onFocusQuestion = null, onLogin = null, loginTitle = '', onCopyMarks = null, copySignature = null, onRefresh = null, refreshing = false, reloadToken = 0, onPublishStudent = null, focusAside = false }) {
  const hasTable = !!(viewingComment && viewingComment.table_comments);
  const [activeTab, setActiveTab] = React.useState(hasTable ? 'table' : 'oneline');

  const [draft, setDraft] = React.useState(viewingComment?.comments || '');
  const [tableDraft, setTableDraft] = React.useState(viewingComment?.table_comments || '');
  const [saving, setSaving] = React.useState(false);
  const [publishing, setPublishing] = React.useState(false);
  const publishingRef = React.useRef(false);
  const [tableSelection, setTableSelection] = React.useState(null);
  const [tableDiffOn, setTableDiffOn] = React.useState(false);
  const [highlightSaving, setHighlightSaving] = React.useState(false);
  const [deleteConfirm, setDeleteConfirm] = React.useState({ show: false, text: '', bodyIndex: null, confirmLabel: 'Save' });
  const lastLoadedIdRef = React.useRef(viewingComment?.id);
  const lastLoadedTableRef = React.useRef(viewingComment?.table_comments || '');
  const lastLoadedCommentsRef = React.useRef(viewingComment?.comments || '');
  const rejectedTableRef = React.useRef('');
  const rejectedCommentsRef = React.useRef('');
  const textareaRef = React.useRef(null);
  const tableTextareaRef = React.useRef(null);
  const asideBoxRef = React.useRef(null);
  const asideSpanRef = React.useRef(null);
  const [asideSpan, setAsideSpan] = React.useState(() => (
    focusAside ? firstAsidePhraseRange(viewingComment?.table_comments || '') : null
  ));
  const gradeScrollRef = React.useRef(null);
  const gradeColumnsScrollRef = React.useRef(null);
  const gradeStampScrollRef = React.useRef(null);
  const [gradeGutter, setGradeGutter] = React.useState(0);
  const [copiedSignature, setCopiedSignature] = React.useState('');

  // Memoized Table-tab preview markdown — recompute the corrected/de-duplicated
  // table only when the draft text actually changes, not on every render. This
  // used to re-parse the whole table on EVERY keystroke in the editor.
  const tableDraftPreviewMd = React.useMemo(
    () => keepLastTableBlock((correctTable(tableDraft, { allowedIds: allowedQuestionIds, rubricMarks: rubricQuestionMarks, paperTotal }) || {}).md || tableDraft),
    [tableDraft, allowedQuestionIds, rubricQuestionMarks, paperTotal]
  );

  // Auto-grow textareas so the full content is visible without inner scroll.
  React.useLayoutEffect(() => {
    const el = activeTab === 'oneline' ? textareaRef.current : tableTextareaRef.current;
    [el, asideBoxRef.current].forEach((node) => {
      if (!node) return;
      node.style.height = 'auto';
      node.style.height = `${node.scrollHeight}px`;
    });
  }, [draft, tableDraft, activeTab, asideSpan]);

  // When the modal switches to a different student, refresh both drafts and
  // re-pick the default tab.
  React.useEffect(() => {
    if (!viewingComment || String(viewingComment.id) === String(lastLoadedIdRef.current)) return;
    const incomingTable = viewingComment.table_comments || '';
    const incomingComments = viewingComment.comments || '';
    if (
      (rejectedTableRef.current && incomingTable === rejectedTableRef.current)
      || (rejectedCommentsRef.current && incomingComments === rejectedCommentsRef.current)
    ) {
      lastLoadedIdRef.current = viewingComment.id;
      return;
    }
    rejectedTableRef.current = '';
    rejectedCommentsRef.current = '';
    lastLoadedIdRef.current = viewingComment.id;
    lastLoadedCommentsRef.current = incomingComments;
    lastLoadedTableRef.current = incomingTable;
    setDraft(incomingComments);
    setTableDraft(incomingTable);
    setAsideSpan(focusAside ? firstAsidePhraseRange(incomingTable) : null);
    setActiveTab(incomingTable ? 'table' : 'oneline');
    setTableSelection(null);
    setTableDiffOn(false);
  }, [viewingComment]);

  // A finished re-grade writes a new table. Apply it when the teacher has
  // not typed over the copy that was on screen.
  React.useEffect(() => {
    if (!viewingComment || String(viewingComment.id) !== String(lastLoadedIdRef.current)) return;
    const incoming = viewingComment.comments || '';
    if (incoming === lastLoadedCommentsRef.current) return;
    if (rejectedCommentsRef.current && incoming === rejectedCommentsRef.current) return;
    setDraft((prev) => (prev === lastLoadedCommentsRef.current ? incoming : prev));
    lastLoadedCommentsRef.current = incoming;
  }, [viewingComment?.comments, viewingComment?.id]);
  React.useEffect(() => {
    if (!viewingComment || String(viewingComment.id) !== String(lastLoadedIdRef.current)) return;
    const incoming = viewingComment.table_comments || '';
    if (incoming === lastLoadedTableRef.current) return;
    if (rejectedTableRef.current && incoming === rejectedTableRef.current) return;
    setTableDraft((prev) => (prev === lastLoadedTableRef.current ? incoming : prev));
    lastLoadedTableRef.current = incoming;
  }, [viewingComment?.table_comments, viewingComment?.id]);

  // A Refresh click reloads the saved grade and replaces the on-screen copy.
  const lastReloadRef = React.useRef(0);
  React.useEffect(() => {
    if (!reloadToken || reloadToken === lastReloadRef.current) return;
    lastReloadRef.current = reloadToken;
    if (!viewingComment) return;
    const incomingTable = viewingComment.table_comments || '';
    const incomingComments = viewingComment.comments || '';
    if (
      (rejectedTableRef.current && incomingTable === rejectedTableRef.current)
      || (rejectedCommentsRef.current && incomingComments === rejectedCommentsRef.current)
    ) return;
    lastLoadedIdRef.current = viewingComment.id;
    lastLoadedCommentsRef.current = incomingComments;
    lastLoadedTableRef.current = incomingTable;
    setDraft(incomingComments);
    setTableDraft(incomingTable);
    setAsideSpan(focusAside ? firstAsidePhraseRange(incomingTable) : null);
    setTableSelection(null);
  }, [reloadToken, viewingComment]);

  React.useEffect(() => {
    setCopiedSignature('');
  }, [viewingComment && viewingComment.id]);

  const original = viewingComment?.comments || '';
  const tableOriginal = viewingComment?.table_comments || '';
  const dirty = activeTab === 'oneline' ? draft !== original : tableDraft !== tableOriginal;

  const persistTable = async (nextMd) => {
    if (saving || !viewingComment?.id) return false;
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      let savedTable = nextMd;
      try {
        savedTable = keepLastTableBlock((correctTable(nextMd, { allowedIds: allowedQuestionIds, rubricMarks: rubricQuestionMarks, paperTotal }) || {}).md || nextMd);
      } catch (_) {
        savedTable = nextMd;
      }
      const grand = gradeFromTable(savedTable, { allowedIds: allowedQuestionIds, rubricMarks: rubricQuestionMarks, paperTotal });
      const nextMarks = grand && Number.isFinite(Number(grand.obtained))
        ? String(Math.round(Number(grand.obtained) * 100) / 100)
        : null;
      const res = await fetch(`${apiBaseUrl}/assess/results/${viewingComment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          table_comments: savedTable,
          ...(nextMarks != null ? { marks: nextMarks } : {})
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      const savedMarks = data.marks != null && data.marks !== '' ? data.marks : nextMarks;
      const echoed = typeof data.table_comments === 'string' ? data.table_comments : savedTable;
      const shownTable = echoed;
      const regenerated = (data && typeof data.comments === 'string') ? data.comments : '';
      const shownComments = regenerated;
      lastLoadedTableRef.current = shownTable;
      setTableDraft(shownTable);
      if (shownComments) {
        lastLoadedCommentsRef.current = shownComments;
        setDraft(shownComments);
      }
      if (onTableSaved) onTableSaved(shownTable, shownComments, savedMarks, data.table_history);
      return true;
    } catch (err) {
      if (onError) onError('Save failed: ' + err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const thinkingMarked = asidePhraseRanges(tableDraft).length > 0 || asidePhraseRanges(draft).length > 0;

  const replaceThinking = async () => {
    if (saving || publishing || !viewingComment?.id) return;
    const nextTable = replaceAsidePhrases(tableDraft);
    const nextComments = replaceAsidePhrases(draft);
    if (nextTable === tableDraft && nextComments === draft) return;
    if (nextTable !== tableDraft) {
      rejectedTableRef.current = tableDraft;
      lastLoadedTableRef.current = nextTable;
      setTableDraft(nextTable);
    }
    if (nextComments !== draft) {
      rejectedCommentsRef.current = draft;
      lastLoadedCommentsRef.current = nextComments;
      setDraft(nextComments);
    }
    setAsideSpan(null);
    if (String(tableDraft || '').trim() && nextTable !== tableDraft) {
      await persistTable(nextTable);
      return;
    }
    if (nextComments === draft) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${apiBaseUrl}/assess/results/${viewingComment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ comments: nextComments, comments_custom: 1 })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      lastLoadedCommentsRef.current = nextComments;
      setDraft(nextComments);
      if (onSaved) onSaved(nextComments, data.marks != null && data.marks !== '' ? data.marks : null);
    } catch (err) {
      if (onError) onError('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  asideSpanRef.current = asideSpan;
  const onAsideChange = (event) => {
    const span = asideSpanRef.current;
    if (!span) return;
    const next = event.target.value;
    setTableDraft((current) => current.slice(0, span.start) + next + current.slice(span.end));
    setAsideSpan({ start: span.start, end: span.start + next.length });
  };

  const asidePlacedRef = React.useRef('');
  React.useLayoutEffect(() => {
    if (!focusAside || !viewingComment) return undefined;
    const placeKey = `${viewingComment.id}:${reloadToken}`;
    if (asidePlacedRef.current === placeKey) return undefined;
    const loadedTable = viewingComment.table_comments || '';
    const loadedComments = viewingComment.comments || '';
    if (tableDraft !== loadedTable || draft !== loadedComments) return undefined;
    const tableRange = firstAsidePhraseRange(tableDraft);
    const commentRange = firstAsidePhraseRange(draft);
    if (tableRange && activeTab !== 'table') {
      setActiveTab('table');
      return undefined;
    }
    if (!tableRange && commentRange && activeTab !== 'oneline') {
      setActiveTab('oneline');
      return undefined;
    }
    const range = tableRange || commentRange;
    if (!range) {
      asidePlacedRef.current = placeKey;
      return undefined;
    }
    const root = gradeScrollRef.current;
    const area = tableRange ? asideBoxRef.current : textareaRef.current;
    if (!area) return undefined;
    asidePlacedRef.current = placeKey;
    area.focus({ preventScroll: true });
    const selectEnd = tableRange ? area.value.length : range.end;
    const selectStart = tableRange ? 0 : range.start;
    area.setSelectionRange(selectStart, selectEnd);
    if (root) {
      const top = tableRange
        ? area.getBoundingClientRect().top
        : (() => {
          const style = window.getComputedStyle(area);
          const lineHeight = parseFloat(style.lineHeight) || 21;
          const linesBefore = draft.slice(0, range.start).split('\n').length - 1;
          return area.getBoundingClientRect().top + (parseFloat(style.paddingTop) || 0) + (linesBefore * lineHeight);
        })();
      root.scrollTop += top - root.getBoundingClientRect().top - 8;
    }
    return undefined;
  }, [focusAside, viewingComment, reloadToken, tableDraft, draft, activeTab]);

  const deleteBelowTotalRow = (bodyIndex) => {
    if (saving) return;
    setDeleteConfirm({
      show: true,
      text: 'Remove this unmatched row and save the table?',
      bodyIndex,
      confirmLabel: 'Save'
    });
  };

  const confirmDeleteRow = () => {
    const bodyIndex = deleteConfirm.bodyIndex;
    setDeleteConfirm({ show: false, text: '', bodyIndex: null, confirmLabel: 'Save' });
    if (bodyIndex == null || saving) return;
    const nextMd = dropMarkdownTableBodyRow(tableDraftPreviewMd, bodyIndex);
    setTableDraft(nextMd);
    persistTable(nextMd);
  };

  const cancelDeleteRow = () => {
    setDeleteConfirm({ show: false, text: '', bodyIndex: null, confirmLabel: 'Save' });
  };

  const commitDraft = async () => {
    const token = localStorage.getItem('authToken');
    const savedTable = activeTab === 'table'
      ? keepLastTableBlock((correctTable(tableDraft, { allowedIds: allowedQuestionIds, rubricMarks: rubricQuestionMarks, paperTotal }) || {}).md || tableDraft)
      : (viewingComment?.table_comments || '');
    const nextMarks = activeTab === 'table'
      ? (() => {
        const grand = gradeFromTable(savedTable, { allowedIds: allowedQuestionIds, rubricMarks: rubricQuestionMarks, paperTotal });
        if (!grand || !Number.isFinite(Number(grand.obtained))) return null;
        return String(Math.round(Number(grand.obtained) * 100) / 100);
      })()
      : (() => {
        const match = String(draft || '').match(/(?:Total marks|مجموعی نمبر)\s*[:：→]\s*([\d.]+)\s*\//i);
        if (!match) return null;
        const n = parseFloat(match[1]);
        return Number.isFinite(n) ? String(Math.round(n * 100) / 100) : null;
      })();
    const body = activeTab === 'oneline'
      ? { comments: draft, comments_custom: 1, ...(nextMarks != null ? { marks: nextMarks } : {}) }
      : { table_comments: savedTable, ...(nextMarks != null ? { marks: nextMarks } : {}) };
    const res = await fetch(`${apiBaseUrl}/assess/results/${viewingComment.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
    const savedMarks = data.marks != null && data.marks !== '' ? data.marks : nextMarks;
    if (activeTab === 'oneline') {
      return {
        table: viewingComment?.table_comments || '',
        comments: draft,
        marks: savedMarks,
        comments_custom: 1,
        tableHistory: data.table_history
      };
    }
    const regenerated = (data && typeof data.comments === 'string') ? data.comments : '';
    const alignedTable = (data && typeof data.table_comments === 'string' && data.table_comments)
      ? data.table_comments
      : savedTable;
    return {
      table: alignedTable,
      comments: regenerated || draft,
      marks: savedMarks,
      comments_custom: 0,
      tableHistory: data.table_history
    };
  };

  const applyCommitted = (saved) => {
    if (activeTab === 'oneline') {
      if (onSaved) onSaved(saved.comments, saved.marks);
      return;
    }
    if (saved.table) {
      lastLoadedTableRef.current = saved.table;
      setTableDraft(saved.table);
    }
    if (saved.comments) {
      lastLoadedCommentsRef.current = saved.comments;
      setDraft(saved.comments);
    }
    if (onTableSaved) onTableSaved(saved.table, saved.comments, saved.marks, saved.tableHistory);
  };

  const save = async () => {
    if (!dirty || saving || publishing) return;
    setSaving(true);
    try {
      const saved = await commitDraft();
      applyCommitted(saved);
    } catch (err) {
      if (onError) onError('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const publishStudent = async () => {
    if (publishingRef.current || saving || !onPublishStudent || !viewingComment) return;
    publishingRef.current = true;
    setPublishing(true);
    try {
      let patch = {
        table_comments: viewingComment.table_comments || '',
        comments: viewingComment.comments || '',
        marks: viewingComment.marks,
        comments_custom: viewingComment.comments_custom ? 1 : 0
      };
      if (dirty) {
        const saved = await commitDraft();
        applyCommitted(saved);
        patch = {
          table_comments: saved.table,
          comments: saved.comments,
          marks: saved.marks != null ? saved.marks : patch.marks,
          comments_custom: saved.comments_custom
        };
      }
      await onPublishStudent(patch);
    } catch (err) {
      if (onError) onError('Update failed: ' + (err.message || 'could not update'));
    } finally {
      publishingRef.current = false;
      setPublishing(false);
    }
  };

  const resetCurrent = () => {
    if (activeTab === 'oneline') setDraft(original);
    else setTableDraft(tableOriginal);
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      resetCurrent();
    }
  };

  const tabBtnStyle = (active) => ({
    background: active ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' : 'transparent',
    color: active ? 'white' : 'var(--text-secondary, #475569)',
    border: active ? 'none' : '1px solid var(--border-color, #cbd5e1)',
    borderRadius: 8,
    padding: '5px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer'
  });

  const gradeRuns = tableRunsForView(viewingComment);
  const olderGrades = gradeRuns.filter((run) => !run.latest);
  const severalGrades = olderGrades.length > 0;
  const latestGrade = gradeRuns[gradeRuns.length - 1];
  const gradeWhen = formatRunWhen((latestGrade && latestGrade.at) || viewingComment?.created_at);
  const hasTableHighlights = gradeRuns.some((run) => run.highlights && run.highlights.length);
  const showTableDiff = severalGrades && tableDiffOn;
  const saveTableHighlights = async (body) => {
    if (highlightSaving || !viewingComment?.id) return;
    setHighlightSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${apiBaseUrl}/assess/results/${viewingComment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ table_run_highlights: body })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      if (onHighlightsSaved) onHighlightsSaved(data.table_history);
      setTableSelection(null);
    } catch (err) {
      if (onError) onError('Highlight failed: ' + err.message);
    } finally {
      setHighlightSaving(false);
    }
  };
  const applyTableColor = (color) => {
    if (!tableSelection || highlightSaving) return;
    if (tableDraft !== tableOriginal) {
      if (onError) onError('Save the table first, then highlight the text.');
      return;
    }
    const run = gradeRuns[tableSelection.index];
    if (!run) return;
    const highlights = mergeCellHighlights(run.highlights, { ...tableSelection, color });
    saveTableHighlights({ index: tableSelection.index, highlights });
  };
  const clearTableHighlights = () => {
    if (highlightSaving) return;
    if (!window.confirm('Clear the colored highlights? The tables stay as they are.')) return;
    saveTableHighlights({ clear_all: true });
  };
  const gradeStamp = (run, index) => (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
      <StageStamp
        label={run.latest ? `Grade ${index + 1} · current` : `Grade ${index + 1}`}
        model={stageModelLabel(run.model, run.host)}
        when={formatRunWhen(run.at)}
      />
      {run.marks ? (
        <span style={{ fontWeight: 800, color: '#166534', whiteSpace: 'nowrap' }}>{run.marks}</span>
      ) : null}
    </div>
  );
  const gradeColumns = [];
  if ((tableDraft || '').trim() || severalGrades) {
    olderGrades.forEach((run, index) => {
      const previewMd = keepLastTableBlock((correctTable(run.table, { allowedIds: allowedQuestionIds, rubricMarks: rubricQuestionMarks, paperTotal }) || {}).md || run.table);
      gradeColumns.push({
        md: previewMd,
        highlights: run.highlights || [],
        index,
        latest: false,
        owner: `${viewingComment?.image_name || viewingComment?.id || 'grade'}-${index}`,
        stamp: gradeStamp(run, index)
      });
    });
    if ((tableDraft || '').trim()) {
      const latestRun = gradeRuns[gradeRuns.length - 1];
      gradeColumns.push({
        md: tableDraftPreviewMd,
        highlights: (latestRun && latestRun.highlights) || [],
        index: Math.max(0, gradeRuns.length - 1),
        latest: true,
        owner: String(viewingComment?.image_name || viewingComment?.id || ''),
        stamp: severalGrades && latestRun ? gradeStamp(latestRun, gradeRuns.length - 1) : null
      });
    }
  }

  React.useLayoutEffect(() => {
    const el = gradeScrollRef.current;
    if (!el) return undefined;
    const measure = () => setGradeGutter(Math.max(0, el.offsetWidth - el.clientWidth));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab, severalGrades]);

  const syncGradeScroll = (from, toRef) => {
    const to = toRef.current;
    if (!to || to.scrollLeft === from.scrollLeft) return;
    to.scrollLeft = from.scrollLeft;
  };

  return (
    <>
    <div className="modal-comments-card" style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'nowrap', flex: '0 0 auto' }}>
          <p className="modal-info-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>
            {activeTab === 'table'
              ? (severalGrades ? `Verification Tables (${gradeRuns.length})` : 'Verification Table (editable)')
              : 'Comments (editable)'}
          </p>
          <div style={{ display: 'flex', gap: 6, flex: '0 0 auto' }}>
            <button onClick={() => setActiveTab('table')} style={tabBtnStyle(activeTab === 'table')}>
              <i className="fa-solid fa-table-cells" style={{ marginRight: 4 }} />
              Table
            </button>
            <button onClick={() => setActiveTab('oneline')} style={tabBtnStyle(activeTab === 'oneline')}>
              <i className="fa-solid fa-align-left" style={{ marginRight: 4 }} />
              One-line
            </button>
          </div>
          {activeTab === 'table' && gradeWhen && (
            <span
              title="When this sheet was graded"
              style={{ fontSize: 12, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}
            >
              Graded {gradeWhen}
            </span>
          )}
          {!hasTable && (
            <span
              title="AI skipped the verification table for this row."
              style={{
                fontSize: 11,
                color: '#92400e',
                background: '#fef3c7',
                border: '1px solid #fcd34d',
                padding: '2px 8px',
                borderRadius: 999,
                fontWeight: 700,
                whiteSpace: 'nowrap'
              }}
            >
              ⚠ Verification skipped
            </span>
          )}
          {dirty && (
            <span style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 999, fontWeight: 600, whiteSpace: 'nowrap' }}>
              Unsaved changes
            </span>
          )}
          <button
            onClick={save}
            disabled={!dirty || saving || publishing}
            style={{
              background: (!dirty || saving || publishing) ? '#cbd5e1' : 'linear-gradient(135deg, #10b981, #059669)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 700,
              cursor: (!dirty || saving || publishing) ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: (!dirty || saving || publishing) ? 'none' : '0 2px 6px rgba(16,185,129,0.35)',
              flex: '0 0 auto'
            }}
          >
            <i className={saving ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-check'} />
            {saving ? 'Saving' : 'Save'}
          </button>
          {dirty && (
            <button
              onClick={resetCurrent}
              disabled={saving || publishing}
              style={{
                background: 'white',
                color: '#475569',
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                flex: '0 0 auto'
              }}
            >
              Reset
            </button>
          )}
          {activeTab === 'table' && (
            <>
              {[
                ['blue', 'Blue', '#BFDBFE', '#1E3A8A', '#93C5FD'],
                ['red', 'Red', '#FECACA', '#991B1B', '#FCA5A5'],
                ['green', 'Green', '#BBF7D0', '#166534', '#86EFAC']
              ].map(([color, label, bg, fg, border]) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => applyTableColor(color)}
                  disabled={!tableSelection || highlightSaving}
                  title={`Highlight the selection in ${label.toLowerCase()}`}
                  style={{
                    background: (!tableSelection || highlightSaving) ? '#E2E8F0' : bg,
                    color: (!tableSelection || highlightSaving) ? '#64748B' : fg,
                    border: `1px solid ${border}`,
                    borderRadius: 6,
                    padding: '2px 8px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: (!tableSelection || highlightSaving) ? 'not-allowed' : 'pointer',
                    flex: '0 0 auto'
                  }}
                >
                  {label}
                </button>
              ))}
              {hasTableHighlights && (
                <button
                  type="button"
                  onClick={clearTableHighlights}
                  disabled={highlightSaving}
                  style={{
                    background: 'white',
                    color: '#1E3A8A',
                    border: '1px solid #93C5FD',
                    borderRadius: 6,
                    padding: '2px 8px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: highlightSaving ? 'not-allowed' : 'pointer',
                    flex: '0 0 auto',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Clear highlights
                </button>
              )}
              {severalGrades && (
                <button
                  type="button"
                  onClick={() => setTableDiffOn((on) => !on)}
                  style={{
                    background: showTableDiff ? '#FDE68A' : 'white',
                    color: '#92400E',
                    border: '1px solid #FCD34D',
                    borderRadius: 6,
                    padding: '2px 8px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    flex: '0 0 auto',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <i className="fa-solid fa-highlighter" />
                  {showTableDiff ? 'Hide differences' : 'Highlight differences'}
                </button>
              )}
              {thinkingMarked && (
                <button
                  type="button"
                  onClick={replaceThinking}
                  disabled={saving || publishing}
                  title="Replace the highlighted thinking words with ..."
                  style={{
                    background: (saving || publishing) ? '#E2E8F0' : '#FEF3C7',
                    color: '#92400E',
                    border: '1px solid #D97706',
                    borderRadius: 6,
                    padding: '2px 8px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: (saving || publishing) ? 'not-allowed' : 'pointer',
                    flex: '0 0 auto',
                    whiteSpace: 'nowrap'
                  }}
                >
                  RepThink
                </button>
              )}
            </>
          )}
          {(onRefresh || onLogin || onCopyMarks || onPublishStudent) && (
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, flex: '0 0 auto' }}>
              {onRefresh && (
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={refreshing}
                  title="Reload this student's saved marks, comments, and verification table"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '4px 10px',
                    fontSize: 12,
                    fontWeight: 700,
                    border: '1px solid #cbd5e1',
                    borderRadius: 6,
                    background: refreshing ? '#f1f5f9' : 'white',
                    color: '#334155',
                    cursor: refreshing ? 'default' : 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <i className={refreshing ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-rotate-right'}></i>
                  Refresh
                </button>
              )}
              {onLogin && (
                <button
                  type="button"
                  onClick={onLogin}
                  title={loginTitle || 'Log into EduPage as this paper’s teacher'}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '4px 10px',
                    fontSize: 12,
                    fontWeight: 700,
                    border: '1px solid #059669',
                    borderRadius: 6,
                    background: 'white',
                    color: '#059669',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <i className="fa-solid fa-right-to-bracket"></i>
                  Login
                </button>
              )}
              {onCopyMarks && (() => {
                const signature = copySignature ? copySignature(tableDraft, draft) : '';
                const copied = !!(copiedSignature && signature && copiedSignature === signature);
                return (
                  <button
                    type="button"
                    onClick={async () => {
                      const payload = await onCopyMarks(tableDraft, draft);
                      setCopiedSignature(payload || '');
                    }}
                    title={copied
                      ? 'Copied. Paste into Excel / EduPage'
                      : 'Copy marks & comments (paste into Excel / EduPage)'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '4px 10px',
                      fontSize: 12,
                      fontWeight: 700,
                      border: 'none',
                      borderRadius: 6,
                      background: copied ? '#1D4ED8' : '#059669',
                      color: 'white',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <i className={copied ? 'fa-solid fa-check' : 'fa-solid fa-copy'}></i>
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                );
              })()}
              {onPublishStudent && (
                <button
                  type="button"
                  onClick={publishStudent}
                  disabled={publishing || saving}
                  title="Update this student in the assessment table, the scan list, and the student list"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '4px 10px',
                    fontSize: 12,
                    fontWeight: 700,
                    border: 'none',
                    borderRadius: 6,
                    background: (publishing || saving) ? '#cbd5e1' : '#1d4ed8',
                    color: 'white',
                    cursor: (publishing || saving) ? 'default' : 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <i className={publishing ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-cloud-arrow-up'}></i>
                  {publishing ? 'Updating' : 'Update'}
                </button>
              )}
            </span>
          )}
      </div>

      {activeTab === 'table' && severalGrades && (
        <div
          ref={gradeStampScrollRef}
          className="grade-stamp-scroll"
          onScroll={(e) => syncGradeScroll(e.currentTarget, gradeColumnsScrollRef)}
          style={{ flex: '0 0 auto', paddingRight: gradeGutter }}
        >
          <GradeCompareView
            part="stamps"
            columns={gradeColumns}
            showDiff={showTableDiff}
            linkedQuestion={linkedQuestion}
            onFocusQuestion={onFocusQuestion}
            onDeleteBelowRow={deleteBelowTotalRow}
            onSelect={setTableSelection}
            fallback={renderMarkdownTable}
          />
        </div>
      )}

      <div ref={gradeScrollRef} style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', scrollbarGutter: 'stable' }}>
      {activeTab === 'oneline' && (
        <>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Comment is empty"
            style={{
              width: '100%',
              padding: 14,
              border: '2px solid var(--input-border, #c4b5fd)',
              borderRadius: 8,
              fontSize: 15,
              fontFamily: 'inherit',
              lineHeight: 1.65,
              resize: 'none',
              overflow: 'hidden',
              minHeight: 120,
              outline: 'none',
              boxSizing: 'border-box',
              background: 'var(--input-bg, #ffffff)',
              color: 'var(--text-primary, #0f172a)'
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, fontSize: 11, color: 'var(--text-muted, #94a3b8)' }}>
            <span>Ctrl+Enter to save · Esc to reset</span>
            <span>{draft.length} chars</span>
          </div>

          {(viewingComment?.comments || '').trim() && (
            <div style={{ marginTop: 14, padding: 12, background: 'var(--surface, #f8fafc)', borderRadius: 8, border: '1px solid var(--border-color, #e2e8f0)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted, #64748b)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Current saved version (with highlights)
              </div>
              <p className="modal-comments-text" style={{ margin: 0 }}>
                {renderHighlights(viewingComment.comments)}
              </p>
            </div>
          )}
        </>
      )}

      {activeTab === 'table' && (
        <div>
          {focusAside && asideSpan && (
            <textarea
              ref={asideBoxRef}
              value={tableDraft.slice(asideSpan.start, asideSpan.end)}
              onChange={onAsideChange}
              onKeyDown={onKeyDown}
              aria-label="Thinking text"
              style={{
                width: '100%',
                padding: 14,
                border: '2px solid #D97706',
                borderRadius: 8,
                fontSize: 15,
                fontFamily: 'inherit',
                lineHeight: 1.55,
                resize: 'none',
                overflow: 'hidden',
                minHeight: 72,
                outline: 'none',
                boxSizing: 'border-box',
                background: '#FFFBEB',
                color: 'var(--text-primary, #0f172a)',
                marginBottom: 14
              }}
            />
          )}
          {(tableDraft || '').trim() || severalGrades ? (
            <div
              ref={gradeColumnsScrollRef}
              onScroll={(e) => syncGradeScroll(e.currentTarget, gradeStampScrollRef)}
              style={{ overflowX: 'auto' }}
            >
              <GradeCompareView
                part="body"
                columns={gradeColumns}
                showDiff={showTableDiff}
                linkedQuestion={linkedQuestion}
                onFocusQuestion={onFocusQuestion}
                onDeleteBelowRow={deleteBelowTotalRow}
                onSelect={setTableSelection}
                fallback={renderMarkdownTable}
              />
            </div>
          ) : (
            <div style={{
              padding: 16,
              background: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: 8,
              color: '#92400e',
              fontSize: 13,
              marginBottom: 14
            }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />
              No verification table on file. Paste a markdown table below (3 columns: Question, Marks, Deduction Summary), then Save — the one-line comments will be regenerated automatically.
            </div>
          )}

          <textarea
            ref={tableTextareaRef}
            value={tableDraft}
            onChange={(e) => setTableDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={`| Question | Marks | Deduction Summary |\n| --- | --- | --- |\n| Q1.i | 1.8/2 | Spelling -0.2 |\n| **Q1 Total** | **11.1/14** |  |\n| **Grand Total** | **17.4/40** |  |`}
            style={{
              width: '100%',
              padding: 14,
              border: '2px solid var(--input-border, #c4b5fd)',
              borderRadius: 8,
              fontSize: 13,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              lineHeight: 1.55,
              resize: 'none',
              overflow: 'hidden',
              minHeight: 180,
              outline: 'none',
              boxSizing: 'border-box',
              background: 'var(--input-bg, #ffffff)',
              color: 'var(--text-primary, #0f172a)',
              marginTop: 14
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, fontSize: 11, color: 'var(--text-muted, #94a3b8)' }}>
            <span>Select text, then Blue, Red, or Green. Editing the table regenerates the one-line on save. Ctrl+Enter to save · Esc to reset</span>
            <span>{tableDraft.length} chars</span>
          </div>
        </div>
      )}
      </div>
    </div>
    <ConfirmDialog
      confirm={deleteConfirm}
      onConfirm={confirmDeleteRow}
      onCancel={cancelDeleteRow}
      zIndex={10050}
    />
    </>
  );
}

function parseOcrHistory(raw) {
  if (!raw) return [];
  let value = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try { value = JSON.parse(trimmed); } catch { return []; }
  }
  if (!Array.isArray(value)) return [];
  return value
    .map((run) => {
      const item = {
        text: String((run && run.text) || ''),
        model: String((run && run.model) || ''),
        at: run && run.at ? String(run.at) : null
      };
      const host = String((run && run.host) || '').trim();
      if (host) item.host = host;
      const marks = Array.isArray(run && run.marks)
        ? run.marks
          .map((mark) => ({
            start: Number(mark && mark.start),
            end: Number(mark && mark.end),
            text: String((mark && mark.text) || ''),
            color: mark && (mark.color === 'red' || mark.color === 'green') ? mark.color : 'blue'
          }))
          .filter((mark) => Number.isInteger(mark.start) && Number.isInteger(mark.end) && mark.end > mark.start && mark.text.trim())
        : [];
      if (marks.length) item.marks = marks;
      return item;
    })
    .filter((run) => run.text.trim());
}

function ocrRunsForView(row) {
  const history = parseOcrHistory(row && row.ocr_history).slice(-3);
  const text = String((row && row.ocr_text) || '');
  if (!history.length) {
    return [{ text, model: (row && row.ocr_model) || '', at: null, latest: true }];
  }
  const runs = history.slice();
  if (runs[runs.length - 1].text !== text) {
    runs[runs.length - 1] = { ...runs[runs.length - 1], text };
  }
  return runs.map((run, index) => ({ ...run, latest: index === runs.length - 1 }));
}

function parseTableHistory(raw) {
  if (!raw) return [];
  let value = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try { value = JSON.parse(trimmed); } catch { return []; }
  }
  if (!Array.isArray(value)) return [];
  return value
    .map((run) => {
      const item = {
        table: String((run && run.table) || ''),
        comments: String((run && run.comments) || ''),
        marks: run && run.marks != null && String(run.marks).trim() !== '' ? String(run.marks) : '',
        model: String((run && run.model) || ''),
        host: String((run && run.host) || '').trim(),
        at: run && run.at ? String(run.at) : null
      };
      const highlights = readTableHighlights(run && run.highlights);
      if (highlights.length) item.highlights = highlights;
      return item;
    })
    .filter((run) => run.table.trim());
}

function readTableHighlights(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((mark) => ({
      row: String((mark && mark.row) || ''),
      col: Number(mark && mark.col),
      start: Number(mark && mark.start),
      end: Number(mark && mark.end),
      text: String((mark && mark.text) || ''),
      color: mark && (mark.color === 'red' || mark.color === 'green') ? mark.color : 'blue'
    }))
    .filter((mark) => mark.row && Number.isInteger(mark.col) && mark.col >= 0
      && Number.isInteger(mark.start) && Number.isInteger(mark.end) && mark.end > mark.start && mark.text.trim());
}

function tableRunsForView(row) {
  const history = parseTableHistory(row && row.table_history).slice(-3);
  const table = String((row && row.table_comments) || '');
  if (!history.length) {
    if (!table.trim()) return [];
    return [{
      table,
      comments: (row && row.comments) || '',
      marks: row && row.marks != null ? String(row.marks) : '',
      model: (row && row.grading_model) || '',
      host: (row && row.grading_host) || '',
      at: null,
      latest: true
    }];
  }
  const runs = history.slice();
  if (runs[runs.length - 1].table !== table) {
    runs[runs.length - 1] = {
      ...runs[runs.length - 1],
      table,
      comments: (row && row.comments) || runs[runs.length - 1].comments,
      marks: row && row.marks != null ? String(row.marks) : runs[runs.length - 1].marks
    };
  }
  return runs.map((run, index) => ({ ...run, latest: index === runs.length - 1 }));
}

function commentSideLayout({ image, transcript, rubric, wide }) {
  const count = (image ? 1 : 0) + (transcript ? 1 : 0) + (rubric ? 1 : 0);
  if (!count) {
    return wide ? { paddingRight: null, commentsWidth: 'min(96vw, 1400px)' } : null;
  }
  let imageW = 0;
  let transcriptW = 0;
  let rubricW = 0;
  if (count === 3) {
    [imageW, transcriptW, rubricW] = wide ? [16, 14, 14] : [24, 24, 24];
  } else if (image && transcript) {
    [imageW, transcriptW] = wide ? [18, 18] : [30, 30];
  } else if (image && rubric) {
    [imageW, rubricW] = wide ? [18, 18] : [30, 30];
  } else if (transcript && rubric) {
    [transcriptW, rubricW] = wide ? [18, 18] : [30, 30];
  } else if (image) {
    imageW = wide ? 22 : 36;
  } else if (transcript) {
    transcriptW = wide ? 22 : 36;
  } else {
    rubricW = wide ? 28 : 42;
  }
  const total = imageW + transcriptW + rubricW;
  const imageRight = rubricW + transcriptW;
  return {
    paddingRight: `${total}vw`,
    commentsWidth: `calc(${100 - total}vw - 24px)`,
    imageLeft: image ? `${100 - total}vw` : null,
    imageRight: image ? `${imageRight}vw` : null,
    closeRight: image ? `calc(${imageRight}vw + 16px)` : null,
    imagePic: image ? `calc(${imageW}vw - 16px)` : null,
    ocrRight: transcript ? `${rubricW}vw` : null,
    ocrWidth: transcript ? `${transcriptW}vw` : null,
    rubricWidth: rubric ? `${rubricW}vw` : null
  };
}

function ocrHistoryAfterEdit(raw, newText) {
  const runs = parseOcrHistory(raw);
  if (!runs.length) return raw || null;
  const next = runs.slice();
  next[next.length - 1] = { ...next[next.length - 1], text: String(newText || '') };
  return JSON.stringify(next);
}

function splitKeepSpace(text) {
  return String(text || '').split(/(\s+)/);
}

// Words in `text` that are not the same, in order, as words in `reference`.
// Whitespace stays unmarked so the highlight lines up with the transcript.
function wordMismatch(text, reference) {
  const left = splitKeepSpace(text);
  const right = splitKeepSpace(reference);
  const leftWords = [];
  const rightWords = [];
  left.forEach((part, index) => { if (part.trim()) leftWords.push(index); });
  right.forEach((part, index) => { if (part.trim()) rightWords.push(index); });
  const n = leftWords.length;
  const m = rightWords.length;
  const dp = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    const word = left[leftWords[i]];
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = word === right[rightWords[j]]
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const matched = new Set();
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (left[leftWords[i]] === right[rightWords[j]]) {
      matched.add(leftWords[i]);
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i += 1;
    } else {
      j += 1;
    }
  }
  return left.map((part, index) => !!part.trim() && !matched.has(index));
}

function combinedMismatch(text, others) {
  const masks = (others || []).map((other) => wordMismatch(text, other));
  if (!masks.length) return [];
  return masks[0].map((_, index) => masks.some((mask) => mask[index]));
}

function ocrMarkColor(mark) {
  return mark && (mark.color === 'red' || mark.color === 'green') ? mark.color : 'blue';
}

function mergeOcrMarks(text, marks, next) {
  const source = String(text || '');
  const quote = source.slice(next.start, next.end);
  if (!quote.trim()) return marks || [];
  const color = ocrMarkColor(next);
  const kept = [];
  (marks || []).forEach((mark) => {
    const tone = ocrMarkColor(mark);
    if (next.end <= mark.start || next.start >= mark.end) {
      kept.push({ start: mark.start, end: mark.end, text: mark.text, color: tone });
      return;
    }
    if (next.start > mark.start) {
      kept.push({
        start: mark.start,
        end: next.start,
        text: source.slice(mark.start, next.start),
        color: tone
      });
    }
    if (next.end < mark.end) {
      kept.push({
        start: next.end,
        end: mark.end,
        text: source.slice(next.end, mark.end),
        color: tone
      });
    }
  });
  kept.push({ start: next.start, end: next.end, text: quote, color });
  kept.sort((a, b) => a.start - b.start || a.end - b.end);
  const merged = [];
  kept.forEach((mark) => {
    if (!String(mark.text || '').trim()) return;
    const last = merged[merged.length - 1];
    if (last && last.color === mark.color && mark.start <= last.end) {
      if (mark.end > last.end) {
        last.end = mark.end;
        last.text = source.slice(last.start, last.end);
      }
    } else {
      merged.push({ start: mark.start, end: mark.end, text: mark.text, color: mark.color });
    }
  });
  return merged;
}

function visibleMarks(text, marks) {
  const shown = [];
  (marks || []).forEach((mark) => {
    const quote = String(mark.text || '');
    if (!quote) return;
    const color = ocrMarkColor(mark);
    if (text.slice(mark.start, mark.end) === quote) {
      shown.push({ start: mark.start, end: mark.end, color });
      return;
    }
    let from = 0;
    let best = -1;
    while (from <= text.length) {
      const at = text.indexOf(quote, from);
      if (at < 0) break;
      if (best < 0 || Math.abs(at - Number(mark.start)) < Math.abs(best - Number(mark.start))) best = at;
      from = at + 1;
    }
    if (best >= 0) shown.push({ start: best, end: best + quote.length, color });
  });
  return shown;
}

function paintSegments(text, mask, marks, selectSpan) {
  const source = String(text || '');
  const yellow = [];
  splitKeepSpace(source).forEach((part, index) => {
    const on = !!(mask && mask[index]);
    for (let i = 0; i < part.length; i += 1) yellow.push(on);
  });
  const toneCode = { blue: 1, red: 2, green: 3 };
  const toneName = ['', 'blue', 'red', 'green'];
  const ink = new Uint8Array(source.length);
  visibleMarks(source, marks).forEach((range) => {
    const code = toneCode[range.color] || 1;
    const start = Math.max(0, range.start);
    const end = Math.min(source.length, range.end);
    for (let i = start; i < end; i += 1) ink[i] = code;
  });
  const selectedAt = (index) => {
    if (!selectSpan) return false;
    const list = Array.isArray(selectSpan) ? selectSpan : [selectSpan];
    return list.some((range) => {
      if (!range) return false;
      const start = Math.max(0, Number(range.start) || 0);
      const end = Math.min(source.length, Number(range.end) || 0);
      return index >= start && index < end;
    });
  };
  const segments = [];
  let i = 0;
  while (i < source.length) {
    const isYellow = yellow[i] ? 1 : 0;
    const tone = ink[i];
    const selected = selectedAt(i);
    let j = i + 1;
    while (j < source.length && (yellow[j] ? 1 : 0) === isYellow && ink[j] === tone && selectedAt(j) === selected) j += 1;
    segments.push({ text: source.slice(i, j), yellow: !!isYellow, color: toneName[tone] || '', selected });
    i = j;
  }
  return segments;
}

function selectionInBox(container) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (!container || !container.contains(range.commonAncestorContainer)) return null;
  const before = range.cloneRange();
  before.selectNodeContents(container);
  before.setEnd(range.startContainer, range.startOffset);
  const start = before.toString().length;
  const selected = range.toString();
  if (!selected.trim()) return null;
  return { start, end: start + selected.length, text: selected };
}

function HighlightedTranscript({ text, mask, marks, minHeight, onMouseUp, selectSpan }) {
  const boxRef = React.useRef(null);
  const segments = paintSegments(text, mask, marks, selectSpan);
  React.useEffect(() => {
    const root = boxRef.current;
    if (!root || !selectSpan) return;
    const mark = root.querySelector('[data-page-select]');
    if (!mark) return;
    const delta = mark.getBoundingClientRect().top - root.getBoundingClientRect().top;
    root.scrollTop += delta - 16;
  }, [selectSpan && selectSpan.start, selectSpan && selectSpan.end, text]);
  let pageMarked = false;
  return (
    <div
      ref={boxRef}
      dir="auto"
      onMouseUp={() => {
        if (onMouseUp) onMouseUp(selectionInBox(boxRef.current));
      }}
      style={{
        width: '100%',
        padding: 12,
        border: '1px solid #E5E7EB',
        borderRadius: 8,
        fontSize: 15,
        fontFamily: 'inherit',
        lineHeight: 1.7,
        flex: '1 1 0',
        minHeight: 0,
        height: 0,
        overflow: 'auto',
        boxSizing: 'border-box',
        background: '#F8FAFC',
        color: '#0f172a',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        userSelect: 'text'
      }}
    >
      {segments.map((segment, index) => {
        const fill = { blue: '#BFDBFE', red: '#FECACA', green: '#BBF7D0' };
        const line = { blue: '#2563EB', red: '#DC2626', green: '#15803D' };
        const pageSelect = segment.selected && !pageMarked;
        if (pageSelect) pageMarked = true;
        return segment.yellow || segment.color || segment.selected
          ? (
            <mark
              key={index}
              data-page-select={pageSelect ? '1' : undefined}
              style={{
                background: segment.selected
                  ? 'rgba(37, 99, 235, 0.35)'
                  : (segment.color && !segment.yellow ? fill[segment.color] : '#FDE68A'),
                boxShadow: segment.color ? `inset 0 -2px 0 ${line[segment.color]}` : 'none',
                color: 'inherit',
                borderRadius: 3,
                padding: '0 1px'
              }}
            >
              {segment.text}
            </mark>
          )
          : <span key={index}>{segment.text}</span>;
      })}
    </div>
  );
}

function OcrDeleteButton({ onDelete, deleting }) {
  return (
    <button
      type="button"
      title="Delete this OCR"
      onClick={onDelete}
      disabled={deleting}
      style={{
        marginLeft: 'auto',
        background: 'white',
        color: '#B91C1C',
        border: '1px solid #FECACA',
        borderRadius: 8,
        padding: '4px 8px',
        cursor: deleting ? 'not-allowed' : 'pointer',
        flex: '0 0 auto'
      }}
    >
      <i className={deleting ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-trash'} />
    </button>
  );
}

function formatRunWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function StageStamp({ label, model, when }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 800 }}>{label}</span>
      <span>{model || '—'}</span>
      <span style={{ color: '#64748b', fontWeight: 600 }}>{when || '—'}</span>
    </span>
  );
}

function OcrRunColumn({ run, index, total, highlight, mismatchMask, onDelete, deleting, onSelect, onClearPage, pageNumber, imageName }) {
  const painted = highlight || (run.marks && run.marks.length > 0);
  const selectSpan = pageNumber ? pageSpanFromTranscript(run.text, pageNumber, imageName) : null;
  const boxStyle = {
    width: '100%',
    padding: 12,
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    fontSize: 15,
    fontFamily: 'inherit',
    lineHeight: 1.7,
    resize: 'none',
    flex: '1 1 0',
    minHeight: 0,
    height: 0,
    overflow: 'auto',
    boxSizing: 'border-box',
    background: '#F8FAFC',
    color: '#0f172a'
  };
  const reportTextarea = (el) => {
    if (!onSelect || !el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (end <= start) return;
    const selected = el.value.slice(start, end);
    if (!selected.trim()) return;
    onSelect({ index, start, end, text: selected, dirty: false });
  };
  return (
    <div style={{ flex: '1 1 0', minWidth: 320, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flex: '0 0 auto' }}>
        <div className="modal-info-label" style={{ margin: 0, display: 'flex', flexWrap: 'wrap', gap: '2px 14px' }}>
          <span style={{ fontWeight: 800 }}>{index === total - 1 ? 'Latest' : `Run ${index + 1}`}</span>
          <StageStamp label="OCR" model={stageModelLabel(run.model, run.host)} when={formatRunWhen(run.at)} />
        </div>
        <OcrDeleteButton onDelete={onDelete} deleting={deleting} />
      </div>
      {painted ? (
        <HighlightedTranscript
          text={run.text}
          mask={highlight ? mismatchMask : null}
          marks={run.marks}
          minHeight="100%"
          selectSpan={selectSpan}
          onMouseUp={(range) => {
            if (range && onSelect) onSelect({ index, ...range, dirty: false });
            else if (!range && onClearPage) onClearPage();
          }}
        />
      ) : (
        <MarkedTextarea
          readOnly
          value={run.text}
          mark={selectSpan}
          markColor="rgba(37, 99, 235, 0.35)"
          scrollTick={selectSpan ? `${pageNumber}:${selectSpan.start}` : ''}
          dir="auto"
          onMouseUp={(e) => {
            reportTextarea(e.target);
            if (onClearPage && e.target.selectionStart === e.target.selectionEnd) onClearPage();
          }}
          onKeyUp={(e) => reportTextarea(e.target)}
          style={boxStyle}
        />
      )}
    </div>
  );
}

// Editable OCR-transcript editor for the OCR modal. Mirrors ModalCommentsEditor
// but PUTs to the `ocr_text` field. Saving an edited transcript also clears
// the unclear/failed status server-side (the user has manually fixed it).
function ModalOcrEditor({ viewingOcr, apiBaseUrl, onSaved, onError, heading, highlight, mismatchMask, marks, runIndex, onSelect, onClearPage, onDelete, deleting, onDraftChange, pageNumber, imageName, onAdvance, onBeginAdvance, onCancelAdvance, advanceLabel, blankAction, onAcceptChatter, onRerunOcr }) {
  const [draft, setDraft] = React.useState(viewingOcr?.ocr_text || '');
  const [saving, setSaving] = React.useState(false);
  const [advancing, setAdvancing] = React.useState(false);
  const [acceptingChatter, setAcceptingChatter] = React.useState(false);
  const [chatterNotice, setChatterNotice] = React.useState(null);
  const [preferEdit, setPreferEdit] = React.useState(false);
  const transcriptRef = React.useRef(null);
  const lastLoadedIdRef = React.useRef(viewingOcr?.id);
  const textKey = `${viewingOcr?.id || ''}\n${viewingOcr?.ocr_text || ''}`;
  const textKeyRef = React.useRef(textKey);

  React.useEffect(() => {
    if (textKey !== textKeyRef.current) {
      textKeyRef.current = textKey;
      lastLoadedIdRef.current = viewingOcr?.id;
      setDraft(viewingOcr?.ocr_text || '');
      setPreferEdit(false);
      setChatterNotice(null);
    }
  }, [textKey, viewingOcr]);

  React.useEffect(() => {
    if (highlight && onDraftChange) onDraftChange(draft);
  }, [draft, highlight, onDraftChange]);

  React.useEffect(() => {
    if (viewingOcr && viewingOcr.id === lastLoadedIdRef.current) {
      setDraft(prev => (prev === '' || prev === (viewingOcr.ocr_text || '') ? (viewingOcr.ocr_text || '') : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingOcr?.ocr_text]);

  const original = viewingOcr?.ocr_text || '';
  const dirty = draft !== original;
  const hasMarks = !!(marks && marks.length);
  const showPainted = (highlight || hasMarks) && !preferEdit;
  const selectSpan = pageNumber ? pageSpanFromTranscript(draft, pageNumber, imageName) : null;
  const chatterFindingsHere = activeChatterFindings(draft, viewingOcr && viewingOcr.ocr_chatter_ok)
    .filter((finding) => !pageNumber || Number(finding.page) === Number(pageNumber));
  const chatterLines = chatterFindingsHere.flatMap((finding) => finding.lines || []);
  const chatterPhrases = [...new Set(chatterFindingsHere.flatMap((finding) => finding.phrases || []))];
  const chatterKind = chatterFindingsHere.some((finding) => finding.kind === 'thinking') && chatterFindingsHere.every((finding) => finding.kind === 'thinking')
    ? 'thinking'
    : 'chatter';
  const acceptChatter = async () => {
    if (acceptingChatter || saving || !chatterFindingsHere.length || typeof onAcceptChatter !== 'function') return;
    const pages = [];
    for (const finding of chatterFindingsHere) {
      const page = Number(finding.page) || Number(pageNumber) || 0;
      if (page > 0 && !pages.includes(page)) pages.push(page);
    }
    if (!pages.length) {
      setChatterNotice({ type: 'error', text: 'Could not tell which page this warning is on.' });
      return;
    }
    setAcceptingChatter(true);
    setChatterNotice(null);
    try {
      if (dirty) {
        const ok = await save();
        if (!ok) {
          setChatterNotice({ type: 'error', text: 'The transcript was not saved, so the warning was left in place.' });
          return;
        }
      }
      for (const page of pages) {
        const localOk = {};
        for (const finding of chatterFindingsHere) {
          if ((Number(finding.page) || Number(pageNumber) || 0) === page && finding.bodyKey) {
            localOk[String(page)] = finding.bodyKey;
          }
        }
        await onAcceptChatter(page, localOk);
      }
      setChatterNotice({ type: 'ok', text: 'Marked as the student\'s handwriting. The sentence was kept.' });
    } catch (err) {
      const text = err.message || 'Could not clear the flag';
      setChatterNotice({ type: 'error', text });
      if (onError) onError(text);
    } finally {
      setAcceptingChatter(false);
    }
  };
  const editTranscript = () => {
    setPreferEdit(true);
    const span = chatterLines[0];
    const scrollToMatch = (el) => {
      const marked = el.parentElement && el.parentElement.querySelector('mark');
      if (marked) el.scrollTop = Math.max(0, marked.offsetTop - 16);
    };
    const place = () => {
      const el = transcriptRef.current;
      if (!el) return;
      if (span && Number.isFinite(Number(span.start))) {
        revealTextareaSpan(el, Number(span.start), Number(span.end));
        scrollToMatch(el);
        requestAnimationFrame(() => scrollToMatch(el));
      } else {
        try { el.focus({ preventScroll: true }); } catch (_) { el.focus(); }
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(place));
  };
  const reportTextarea = (el) => {
    if (!onSelect || !el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    if (end <= start) return;
    const selected = el.value.slice(start, end);
    if (!selected.trim()) return;
    onSelect({ index: runIndex, start, end, text: selected, dirty });
  };

  const save = async () => {
    if (saving) return false;
    if (!dirty) return true;
    setSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      // Write the snapshot and the live row together. Saving only the live
      // row left the snapshot on the old text, so "It is OK" cleared a
      // different copy and the warning stayed on screen.
      const quickSave = !!(viewingOcr.folder_path && viewingOcr.student_name);
      const res = await fetch(
        quickSave ? `${apiBaseUrl}/assess/snapshots/ocr` : `${apiBaseUrl}/assess/results/${viewingOcr.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          // Saving a manual edit also clears the failure flags so the row is
          // re-grade-eligible without a fresh OCR. A Sort-card open has no
          // live row yet, so that edit is stored on the saved transcript.
          body: JSON.stringify(quickSave
            ? { folder_path: viewingOcr.folder_path, student_name: viewingOcr.student_name, ocr_text: draft }
            : { ocr_text: draft, ocr_status: 'ok', ocr_unclear_ratio: null })
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      if (onSaved) onSaved(draft);
      return true;
    } catch (err) {
      if (onError) onError('Save failed: ' + err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAndNext = async () => {
    if (saving || advancing || typeof onAdvance !== 'function') return;
    setAdvancing(true);
    if (typeof onBeginAdvance === 'function') onBeginAdvance();
    try {
      if (dirty) {
        const ok = await save();
        if (!ok) {
          if (typeof onCancelAdvance === 'function') onCancelAdvance();
          return;
        }
      }
      onAdvance();
    } finally {
      setAdvancing(false);
    }
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(original);
    }
  };

  // Word-level (unclear) tally for the on-screen badge. Backend authoritative
  // value lives on the row; this is a quick local recount as the user edits.
  const unclearCount = (draft.match(/[([]unclear[)\]]/gi) || []).length;
  const wordCount = (draft.match(/\S+/g) || []).length;
  const formatTokens = (value) => {
    if (value == null || value === '') return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return Math.round(n).toLocaleString('en-US');
  };
  const inputTokens = formatTokens(viewingOcr?.ocr_prompt_tokens);
  const outputTokens = formatTokens(viewingOcr?.ocr_completion_tokens);

  return (
    <div className="modal-comments-card" style={{ flex: '1 1 auto', minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 12, flexWrap: 'wrap', flex: '0 0 auto' }}>
        <div className="modal-info-label" style={{ margin: 0 }}>{heading || 'OCR Transcript (editable)'}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {(inputTokens || outputTokens) && (
            <span
              style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}
              title="Input is the OCR prompt plus the page image. Output is the transcript the model wrote. A multi-page paper is the sum of every page."
            >
              {inputTokens ? `Input ${inputTokens}` : ''}
              {inputTokens && outputTokens ? ' · ' : ''}
              {outputTokens ? `Output ${outputTokens}` : ''}
            </span>
          )}
          <span style={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>
            {wordCount} word{wordCount !== 1 ? 's' : ''} · {unclearCount} [unclear]
          </span>
          {dirty && (
            <span style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>
              Unsaved changes
            </span>
          )}
          <button
            onClick={save}
            disabled={!dirty || saving || advancing}
            style={{
              background: (!dirty || saving || advancing) ? '#cbd5e1' : 'linear-gradient(135deg, #7C3AED, #6D28D9)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 700,
              cursor: (!dirty || saving || advancing) ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: (!dirty || saving || advancing) ? 'none' : '0 2px 6px rgba(124, 58, 237, 0.35)'
            }}
          >
            <i className={saving ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-check'} />
            {saving ? 'Saving' : 'Save'}
          </button>
          {blankAction && (
            <button
              type="button"
              onClick={blankAction.onClick}
              disabled={!!blankAction.busy || saving || advancing}
              title="Change this page from blank page to actual blank. The unexpected blank error is removed."
              style={{
                background: blankAction.busy ? '#DCFCE7' : '#166534',
                color: blankAction.busy ? '#166534' : '#fff',
                border: '1px solid #15803D',
                borderRadius: 8,
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 700,
                cursor: blankAction.busy ? 'wait' : 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {blankAction.busy ? 'Saving…' : 'Actual blank'}
            </button>
          )}
          {typeof onAdvance === 'function' && (
            <button
              type="button"
              onClick={saveAndNext}
              disabled={saving || advancing}
              title={advanceLabel === 'Save & Close'
                ? 'Save this transcript, close the window, and refresh the blank-page list'
                : 'Save this transcript and open the next flagged student'}
              style={{
                background: (saving || advancing) ? '#cbd5e1' : 'linear-gradient(135deg, #166534, #15803D)',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 700,
                cursor: (saving || advancing) ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                whiteSpace: 'nowrap',
                boxShadow: (saving || advancing) ? 'none' : '0 2px 6px rgba(22, 101, 52, 0.35)'
              }}
            >
              <i className={advancing ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-forward'} />
              {advancing ? 'Saving' : (advanceLabel || 'Save & Next')}
            </button>
          )}
          {dirty && (
            <button
              onClick={() => setDraft(original)}
              disabled={saving}
              style={{
                background: 'white',
                color: '#475569',
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Reset
            </button>
          )}
          {showPainted && (
            <button
              type="button"
              onClick={() => setPreferEdit(true)}
              style={{
                background: 'white',
                color: '#1D4ED8',
                border: '1px solid #BFDBFE',
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Edit
            </button>
          )}
          {preferEdit && hasMarks && (
            <button
              type="button"
              onClick={() => setPreferEdit(false)}
              style={{
                background: '#BFDBFE',
                color: '#1E3A8A',
                border: '1px solid #93C5FD',
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Show highlights
            </button>
          )}
          {onDelete && <OcrDeleteButton onDelete={onDelete} deleting={deleting || saving} />}
        </div>
      </div>

      {chatterNotice && (
        <div style={{
          flex: '0 0 auto',
          marginBottom: 8,
          padding: '8px 10px',
          borderRadius: 8,
          border: chatterNotice.type === 'ok' ? '1px solid #16A34A' : '1px solid #DC2626',
          background: chatterNotice.type === 'ok' ? '#F0FDF4' : '#FEF2F2',
          color: chatterNotice.type === 'ok' ? '#166534' : '#991B1B',
          fontSize: 13,
          fontWeight: 700
        }}>
          {chatterNotice.text}
        </div>
      )}

      {chatterFindingsHere.length > 0 && (
        <div style={{
          flex: '0 0 auto',
          marginBottom: 8,
          padding: '8px 10px',
          borderRadius: 8,
          border: '1px solid #F59E0B',
          background: '#FFFBEB',
          color: '#92400E'
        }}>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 4 }}>
            {chatterKind === 'thinking' ? 'Suspected thinking text' : 'Suspected chatter'}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.45, marginBottom: 8 }}>
            {chatterPhrases.length
              ? `Matched: ${chatterPhrases.join(', ')}. The transcript was kept. Confirm it is the student's handwriting, or read the page again.`
              : 'This page may be model chatter rather than the student\'s handwriting. The transcript was kept.'}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={acceptChatter}
              disabled={acceptingChatter || saving || advancing}
              style={{
                background: '#166534',
                color: '#fff',
                border: '1px solid #15803D',
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 700,
                cursor: acceptingChatter ? 'wait' : 'pointer'
              }}
            >
              {acceptingChatter ? 'Saving…' : 'It is OK'}
            </button>
            <button
              type="button"
              onClick={onRerunOcr}
              disabled={typeof onRerunOcr !== 'function' || saving || advancing}
              style={{
                background: 'linear-gradient(135deg, #EA580C, #C2410C)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Re-run OCR
            </button>
            <button
              type="button"
              onClick={editTranscript}
              style={{
                background: '#fff',
                color: '#1D4ED8',
                border: '1px solid #BFDBFE',
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      {showPainted && !chatterLines.length ? (
        <HighlightedTranscript
          text={draft}
          mask={highlight ? mismatchMask : null}
          marks={marks}
          minHeight="100%"
          selectSpan={selectSpan}
          onMouseUp={(range) => {
            if (range && onSelect) onSelect({ index: runIndex, ...range, dirty });
            else if (!range && onClearPage) onClearPage();
          }}
        />
      ) : (
      <MarkedTextarea
        textareaRef={transcriptRef}
        onMouseUp={(e) => {
          reportTextarea(e.target);
          if (onClearPage && e.target.selectionStart === e.target.selectionEnd) onClearPage();
        }}
        onKeyUp={(e) => reportTextarea(e.target)}
        value={draft}
        mark={chatterLines.length ? null : selectSpan}
        extraMarks={chatterLines}
        markColor="rgba(37, 99, 235, 0.35)"
        scrollTick={chatterLines.length ? `chatter:${chatterLines[0].start}` : (selectSpan ? `${pageNumber}:${selectSpan.start}` : '')}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        dir="auto"
        placeholder="OCR transcript is empty"
        style={{
          width: '100%',
          padding: 12,
          border: '2px solid var(--input-border, #c4b5fd)',
          borderRadius: 8,
          fontSize: 15,
          fontFamily: 'inherit',
          lineHeight: 1.7,
          resize: 'none',
          flex: '1 1 0',
          minHeight: 0,
          height: 0,
          overflow: 'auto',
          outline: 'none',
          boxSizing: 'border-box',
          background: selectSpan ? 'transparent' : 'var(--input-bg, #ffffff)',
          color: 'var(--text-primary, #0f172a)'
        }}
      />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, fontSize: 11, color: 'var(--text-muted, #94a3b8)', flex: '0 0 auto' }}>
        <span>{showPainted ? 'Yellow words differ. Blue, red, and green marks stay until you clear them.' : 'Select text, then Blue, Red, or Green. Ctrl+Enter to save · Esc to reset'}</span>
        <span>{draft.length} chars</span>
      </div>
    </div>
  );
}
